// ============================================================================
// src/app/data/routeDataCatalog.ts — B3.3 Prerequisite A
// ============================================================================
// Stable resource IDs, subject/farm-scoped query keys, and PURE query
// functions. Imports no React and no context — a query function takes a client,
// params and an AbortSignal, and returns normalized data or throws.
//
// This is deliberately shaped as the TanStack Query seam. At B3.5 the interim
// runtime is replaced by useQuery/useQueries over the SAME key and the SAME
// queryFn; invalidation maps to Query invalidation. Nothing in this file should
// need rewriting for that, which is why the impure parts live elsewhere:
//
//   * no React setters — a query function that calls setState cannot be reused
//     by a different runtime, and cannot be tested without a renderer;
//   * no navigation, no migrations, no writes — a render read that mutates is
//     not idempotent and cannot be safely retried or deduped;
//   * no context reads — the scope is passed in, so the same function serves a
//     component, a test, and a prefetch.
//
// KEY SCOPE IS PART OF CORRECTNESS, NOT BOOKKEEPING
//
// Every key carries the full QueryScope. Without that, a cache entry outlives a
// sign-out and user B can be served user A's rows on a shared browser — exactly
// the defect that makes the existing module-global cattle cache unusable here.
// Keys are plain JSON-serializable arrays so they can be compared structurally
// and logged.
//
// The scope is not a shape invented here. It is the durable QueryScope
// contract in PROJECT.md:
//
//   type QueryScope =
//     | { kind: 'authenticated'; subjectId: string; effectiveRole: string;
//         previewRole: string | null }
//     | { kind: 'public' }
//
// The first version of this file got that wrong in three ways at once, and each
// one was a real cache-poisoning route rather than a style problem:
//
//   * it omitted `effectiveRole` and `previewRole`, so an admin who switches
//     role preview keeps the previous role's cached rows under an identical
//     key. Both fields are needed, not one: an admin PREVIEWING as `light` is
//     not the same subject as a real `light` user, because the server still
//     answers with admin permissions. Collapsing them would serve one the
//     other's rows.
//   * it invented `farmId`. The contract says explicitly: no farm ID, WCF is
//     single-farm and has no source for one. A key field with no source is
//     always `null`, which is a field that looks like isolation and provides
//     none.
//   * it defaulted every missing field to `null`, so `{}` produced the same key
//     as a real scope with null fields, and a caller that simply forgot to pass
//     scope was silently served a shared cache entry. A malformed scope is now
//     a throw. Fail closed: an unkeyed read is worse than no read.
// ============================================================================

import {loadCattleWeighIns} from '../../lib/cattleCache.js';
import type {WeighInRow} from '../../lib/cattleCache.js';
import {CATTLE_HERDS} from '../../lib/cattle.js';
import {wcfSelectAll} from '../../lib/pagination.js';
import {listCattleLogEntries, loadCattleLogMentionableProfiles} from '../../lib/cattleLogApi.js';
import type {CattleLogCursor} from '../../lib/cattleLogApi.js';
import {forecastSettingsFromRow} from '../../lib/cattleForecastApi.js';
import type {ForecastHiddenSelectedRow, ForecastSettingsRow} from '../../lib/cattleForecastApi.js';
import type {ForecastSettings} from '../../lib/cattleForecast.js';
import {loadSheepWeighIns} from '../../lib/sheepCache.js';
import type {WeighInRow as SheepWeighInRow} from '../../lib/sheepCache.js';
import {listProcessingRecords} from '../../lib/processingApi.js';
import type {ProcessingRecordListRow} from '../../lib/processingApi.js';
import {pigTripOrdinalsBySource} from '../../lib/processingSourceLink.js';
import {loadProductionSources} from '../../lib/productionApi.js';
import type {ProductionSources as ProductionApiSources} from '../../lib/productionApi.js';
import type {EquipmentRow, MaterialClearRow, MaterialRow} from '../../lib/equipmentMaterials.js';
import {
  listNewsletterIssuesAdmin,
  getNewsletterIssueAdmin,
  getNewsletterSettings,
  getNewsletterRecentPublishedAdmin,
  listNewsletterRunsAdmin,
} from '../../lib/newsletterApi.js';
import type {NewsletterJson} from '../../lib/newsletterApi.js';
import {loadGlobalActivity} from '../../lib/globalActivityApi.js';
import {listImportedBreederNotes} from '../../lib/commentsApi.js';
import {
  SOWS_GROUP_VALUE,
  SOW_GROUP_NUMBERS,
  activeSowTagsByGroup,
  sowGroupHistoryLabels,
  sowGroupLabel,
} from '../../lib/pigSowGroups.js';
import type {SowTagsByGroup} from '../../lib/pigSowGroups.js';
import type {ImportedBreederNotes} from '../../lib/commentsApi.js';
import {loadClientErrors} from '../../lib/clientErrorsApi.js';
import {LAYER_GROUP_READ_FAILED, LAYER_GROUP_READ_RESOLVED} from '../../lib/layerGroupStatus.js';
import type {LayerGroupReadState} from '../../lib/layerGroupStatus.js';
// The pasture SECDEF READ wrappers, called rather than re-issued. land_areas,
// pasture_move_events and their siblings are deny-all RLS: these RPCs are the
// only client door, and pastureMapReads is their single literal reader — the
// same limited shared-wrapper boundary productionApi holds for /production.
// They live in pastureMapReads rather than pastureMapApi because that module
// binds the live browser client at import time, which this one may never do.
import {
  listLandAreas,
  listPastureHistoryReport,
  listPastureMeasurements,
  listPastureMoves,
  listPastureRotations,
} from '../../lib/pastureMapReads.js';
import type {ActivityLogExportRow} from '../../lib/operationalExportColumns.js';
import type {SupabaseClient} from '../../lib/supabaseTypes.js';
import {addDays, toISO} from '../../lib/dateUtils.js';
import {shouldAutoActivateBroilerBatch, shouldAutoProcessBroilerBatch} from '../../lib/broiler.js';
import type {BroilerBatch} from '../../lib/broiler.js';
import {foldEquipmentFuelings} from '../../dashboard/homeAlerts.js';
import type {
  EquipmentFuelingRow as LightHomeEquipmentFuelingRow,
  EquipmentHomeRow as LightHomeEquipmentRow,
  FoldedEquipmentFuelings,
} from '../../dashboard/homeAlerts.js';
import type {EggDailyRow, LayerDailyRow} from '../../layer/layerBatchStats.js';
// The boot-time poultry_dailys loader, called rather than re-implemented — see
// broiler.feed.core below. It is the module that stopped a mid-pagination
// failure publishing a truncated history as a complete one.
import {loadAllPoultryDailys} from '../../broiler/poultryDailysLoader.js';
import {
  TASKS_PUBLIC_ASSIGNEE_AVAILABILITY_KEY,
  visiblePublicAssignees,
  TASK_PHOTOS_BUCKET,
  TASK_REQUEST_PHOTOS_BUCKET,
  stripCompletionPhotoBucket,
  stripTaskRequestPhotoBucket,
} from '../../lib/tasks.js';
import {listTodoItems, stripTodoPhotoBucket} from '../../lib/todoApi.js';
import {listProjectJobs, type ProjectJobsData} from '../../lib/projectJobsApi.js';
import type {
  DueDateEditHistoryRow,
  TaskInstancePhotoRow,
  TaskInstanceRow,
  TaskProfile,
  TaskSystemRuleRow,
  TaskTemplateRow,
} from '../../lib/tasksCenterApi.js';
// The direct Pig Dailys form's config document is the SEEDED blob App's boot
// published, not the raw app_store row — see queryWebformPigDailysConfig. Both
// imports are pure data/derivation: DEFAULT_WEBFORMS_CONFIG is the same constant
// WebformsConfigContext already starts on, and applyWebformsConfigSeeds never
// mutates its input and never writes.
import {applyWebformsConfigSeeds} from '../boot/webformsConfigSeed.js';
import {applyBroilerBootRepair} from '../boot/broilerBootRepair.js';
import {DEFAULT_WEBFORMS_CONFIG} from '../../lib/defaults.js';
// App's feed-cost LOAD boundary, reused by webforms.admin.appStore rather than
// re-derived: an earlier build persisted these rates as strings and every
// consumer calls .toFixed() on them.
import {normalizeFeedCosts} from '../../lib/feedCosts.js';
import type {FeedCosts} from '../../lib/feedCosts.js';
import {
  DEFAULT_BOAR_NAMES,
  DEFAULT_BREED_OPTIONS,
  DEFAULT_ORIGIN_OPTIONS,
  reconcileFeederGroupsFromBreeders,
} from '../../lib/pig.js';
import type {PigFeederGroup} from '../../lib/pig.js';
// The pig daily history's ONE physical reader, shared with App's legacy boot
// rather than re-implemented here. It fails whole on a page error instead of
// returning a truncated history, which is the property pig.home.core needs and
// the reason app_pig_refresh_retirement_static names it the shared loader site.
import {loadAllPigDailys} from '../../pig/pigDailysLoader.js';
import {INITIAL_BREEDERS, INITIAL_FARROWING} from './pigSeedData.js';
import {loadBackupStatus} from '../../lib/backupStatusApi.js';
import type {AdminBackupStatus} from '../../lib/backupStatus.js';
import {
  OWNER_EQUIPMENT_REPORTS_RESOURCE,
  OWNER_REPORT_RECOVERY_RESOURCE,
  queryOwnerEquipmentReports,
  queryOwnerReportRecovery,
} from './ownerEquipmentReportsData.js';

export const RESOURCE = Object.freeze({
  CATTLE_HERDS_CORE: 'cattle.herds.core',
  CATTLE_HOME_CORE: 'cattle.home.core',
  CATTLE_BATCHES_CORE: 'cattle.batches.core',
  CATTLE_ANIMAL_DETAIL: 'cattle.animal.detail',
  CATTLE_WEIGHINS_LIST: 'cattle.weighins.list',
  SHEEP_WEIGHINS_LIST: 'sheep.weighins.list',
  LIVESTOCK_WEIGHINS_LIST: 'livestock.weighins.list',
  PIG_WEIGHINS_METRICS: 'pig.weighins.metrics',
  WEIGHIN_SESSION_RECORD: 'weighin.session.record',
  WEIGHIN_SESSION_DETAIL: 'weighin.session.detail',
  WEIGHIN_SESSION_PIG_METRICS: 'weighin.session.pigMetrics',
  BROILER_DAILYS_LIST: 'broiler.dailys.list',
  BROILER_DAILY_DETAIL: 'broiler.daily.detail',
  BROILER_DAILYS_BATCH_DIRECTORY: 'broiler.dailys.batchDirectory',
  BROILER_TIMELINE_CORE: 'broiler.timeline.core',
  BROILER_HOME_CORE: 'broiler.home.core',
  BROILER_BATCHES_HUB: 'broiler.batches.hub',
  BROILER_BATCH_FORM_ACTION_STATE: 'broiler.batchForm.actionState',
  BROILER_BATCH_FORM_DAILYS: 'broiler.batchForm.dailys',
  WEIGHIN_WEBFORM_SPECIES_DIRECTORY: 'weighin.webform.speciesDirectory',
  WEIGHIN_WEBFORM_BROILER_SCHOONERS: 'weighin.webform.broilerSchooners',
  WEIGHIN_WEBFORM_DRAFTS: 'weighin.webform.drafts',
  WEIGHIN_WEBFORM_PRIOR_WEIGHTS: 'weighin.webform.priorWeights',
  WEIGHIN_WEBFORM_SESSION_ENTRIES: 'weighin.webform.sessionEntries',
  WEIGHIN_WEBFORM_PIG_METRICS: 'weighin.webform.pigMetrics',
  SHEEP_HOME_CORE: 'sheep.home.core',
  SHEEP_FLOCKS_CORE: 'sheep.flocks.core',
  SHEEP_ANIMAL_DETAIL: 'sheep.animal.detail',
  SHEEP_BATCHES_CORE: 'sheep.batches.core',
  SHEEP_BATCH_DETAIL: 'sheep.batch.detail',
  PROCESSING_CALENDAR_CORE: 'processing.calendar.core',
  PROCESSING_CUT_SHEET_CORE: 'processing.cutSheet.core',
  NEWSLETTER_ISSUES_CORE: 'newsletter.issues.core',
  NEWSLETTER_SETTINGS_CORE: 'newsletter.settings.core',
  NEWSLETTER_ISSUE_DETAIL: 'newsletter.issue.detail',
  EQUIPMENT_FLEET_CORE: 'equipment.fleet.core',
  EQUIPMENT_ADMIN_RECORD_CORE: 'equipment.adminRecord.core',
  EQUIPMENT_FUELING_HUB_CORE: 'equipment.fuelingHub.core',
  EQUIPMENT_OWN_REPORTS: OWNER_EQUIPMENT_REPORTS_RESOURCE,
  OWNER_REPORT_RECOVERY: OWNER_REPORT_RECOVERY_RESOURCE,
  EQUIPMENT_FUELING_FORM_HISTORY: 'equipment.fuelingForm.history',
  HOME_STANDARD_ALERT_INPUTS: 'home.standard.alert.inputs',
  HOME_STANDARD_ANIMAL_LAYER_EVIDENCE: 'home.standard.animal.layerEvidence',
  HOME_STANDARD_ANIMAL_DAILY_HISTORY: 'home.standard.animal.dailyHistory',
  HOME_STANDARD_EQUIPMENT_ATTENTION: 'home.standard.equipment.attention',
  HOME_STANDARD_EQUIPMENT_MATERIALS: 'home.standard.equipment.materials',
  HOME_STANDARD_PRODUCTION_SOURCES: 'home.standard.production.sources',
  HOME_STANDARD_PLANNER: 'home.standard.planner',
  HOME_LIGHT_EQUIPMENT_ATTENTION: 'home.light.equipment.attention',
  HOME_LIGHT_PLANNER: 'home.light.planner',
  HOME_LIGHT_MISSED_INPUTS: 'home.light.missed.inputs',
  LAYER_BATCHES_CORE: 'layer.batches.core',
  LAYER_GROUPS_CORE: 'layer.groups.core',
  BROILER_FEED_CORE: 'broiler.feed.core',
  LAYER_DAILYS_LIST: 'layer.dailys.list',
  LAYER_DAILY_DETAIL: 'layer.daily.detail',
  LAYER_DAILYS_GROUP_DIRECTORY: 'layer.dailys.groupDirectory',
  EGG_DAILYS_LIST: 'layer.eggDailys.list',
  EGG_DAILY_DETAIL: 'layer.eggDaily.detail',
  EGG_DAILYS_GROUPS: 'layer.eggDailys.groups',
  LAYERS_HOME_CORE: 'layer.home.core',
  LAYER_RECORD_CORE: 'layer.record.core',
  LAYER_RECORD_APP_STORE: 'layer.record.appStore',
  PIG_DAILYS_LIST: 'pig.dailys.list',
  PIG_DAILY_DETAIL: 'pig.daily.detail',
  PIG_DAILYS_GROUP_DIRECTORY: 'pig.dailys.groupDirectory',
  ANIMAL_HISTORY_CORE: 'animal.history.core',
  ANIMAL_HISTORY_PLANNER: 'animal.history.planner',
  ANIMAL_HISTORY_LAYER_DIRECTORY: 'animal.history.layerDirectory',
  ANIMAL_HISTORY_DAILYS: 'animal.history.dailys',
  WEBFORM_ADD_FEED_INPUTS: 'webform.addfeed.inputs',
  WEBFORM_ADD_FEED_CONFIG: 'webform.addfeed.config',
  PIG_BATCHES_CORE: 'pig.batches.core',
  WEBFORM_HUB_INPUTS: 'webform.hub.inputs',
  WEBFORM_HUB_CONFIG: 'webform.hub.config',
  WEBFORM_HUB_LAYER_DIRECTORY: 'webform.hub.layerDirectory',
  WEBFORM_PIG_DAILYS_CONFIG: 'webform.pigDailys.config',
  WEBFORM_HUB_PIG_CONTEXT: 'webform.hub.pigContext',
  WEBFORMS_ADMIN_APP_STORE: 'webforms.admin.appStore',
  TASKS_WEBFORM_ASSIGNEES: 'tasks.webform.assignees',
  CATTLE_BREEDING_CORE: 'cattle.breeding.core',
  TASKS_CENTER_PROFILES: 'tasks.center.profiles',
  TASKS_CENTER_MINE: 'tasks.center.mine',
  TASKS_CENTER_RECURRING: 'tasks.center.recurring',
  PIG_HOME_CORE: 'pig.home.core',
  PIG_BREEDING_CORE: 'pig.breeding.core',
  PIG_FARROWING_CORE: 'pig.farrowing.core',
  PIG_SOWS_CORE: 'pig.sows.core',
  PIG_FEED_CORE: 'pig.feed.core',
  TASKS_CENTER_COMPLETED: 'tasks.center.completed',
  TASKS_CENTER_SYSTEM: 'tasks.center.system',
  TASKS_TODO_LIST: 'tasks.todo.list',
  TASKS_ASSIGNABLE_PROFILES: 'tasks.assignable.profiles',
  PROJECT_JOBS: 'projects.jobs',
  TASKS_INSTANCE_CORE: 'tasks.instance.core',
  TASKS_INSTANCE_INTERACTION_PHOTOS: 'tasks.instance.interaction.photos',
  TASKS_INSTANCE_DUE_DATE_HISTORY: 'tasks.instance.dueDateHistory',
  TASKS_INSTANCE_SIGNED_PHOTO_URL: 'tasks.instance.signedPhotoUrl',
  TASKS_TODO_SIGNED_PHOTO_URL: 'tasks.todo.signedPhotoUrl',
  CATTLE_DAILYS_LIST: 'cattle.dailys.list',
  CATTLE_DAILYS_FORM_INPUTS: 'cattle.dailys.formInputs',
  CATTLE_DAILYS_SAVED_VIEWS: 'cattle.dailys.savedViews',
  CATTLE_DAILY_DETAIL: 'cattle.daily.detail',
  SHEEP_DAILY_DETAIL: 'sheep.daily.detail',
  SHEEP_DAILYS_LIST: 'sheep.dailys.list',
  SHEEP_DAILYS_FORM_INPUTS: 'sheep.dailys.formInputs',
  CATTLE_FORECAST_BUNDLE: 'cattle.forecast.bundle',
  CATTLE_FORECAST_CORE: 'cattle.forecast.core',
  CATTLE_BATCH_DETAIL: 'cattle.batch.detail',
  PRODUCTION_SOURCES_CORE: 'production.sources.core',
  CATTLE_LOG_PAGE: 'cattle.log.page',
  CATTLE_LOG_CATTLE_PREVIEW: 'cattle.log.cattle.preview',
  CATTLE_LOG_MENTIONABLE_PROFILES: 'cattle.log.mentionable.profiles',
  ACTIVITY_LOG_PAGE: 'activity.log.page',
  CLIENT_ERRORS_PAGE: 'clientErrors.page',
  PASTURE_MAP_CORE: 'pasture.map.core',
  PASTURE_AREA_HISTORY: 'pasture.area.history',
  PASTURE_GROUP_HISTORY: 'pasture.group.history',
  PASTURE_PLANNER_ROSTER: 'pasture.planner.roster',
  ADMIN_BACKUP_STATUS: 'admin.backup.status',
  // The Admin Repair Bills read. The id is spelled here as a literal because
  // the query is loaded lazily (see the catalog entry) — an eager import of
  // the API module dragged repairBillsApi and its money-math dependencies into
  // the initial bundle and broke the budget. Drift between this literal and
  // the panel's REPAIR_BILLS_ADMIN_RESOURCE is pinned by
  // tests/repair_bills_admin_route.test.tsx's identity check, which imports
  // both and asserts equality. The #448 crash was this entry missing
  // entirely: RepairBillsPanel's useRouteResource threw 'unknown resource' on
  // the real surface, and no source-text test could see it.
  REPAIR_BILLS_ADMIN: 'repairBills.admin',
});

// ---------------------------------------------------------------------------
// Row shapes
// ---------------------------------------------------------------------------
// Both resources read `select('*')` and hand whole rows to surfaces that
// consume columns this module never names, so a row is modelled as an open
// record. That is the same decision cattleCache.WeighInRow already records for
// the same reason: only the columns a module actually reads are modelled, and
// the rest stay `unknown` rather than being invented into a schema TypeScript
// cannot verify against the SQL.
//
// The named aliases are not decoration. `unwrap` cannot say WHICH table it
// returned, so without them every field of the resource payload would be the
// same anonymous type and a caller could pass a breed where a herd belongs.
export type CattleRow = {[column: string]: unknown};
export type CattleCalvingRecordRow = {[column: string]: unknown};
export type CattleBreedRow = {[column: string]: unknown};
export type CattleOriginRow = {[column: string]: unknown};
export type CattleTransferRow = {[column: string]: unknown};

export type CattleWeighInSessionRow = {[column: string]: unknown};

// The ONE narrowed read in this file: named columns, not `*`. Its four columns
// are knowable from the query, so they are declared.
export type CattleProcessingBatchRow = {
  id: unknown;
  name: unknown;
  actual_process_date: unknown;
  planned_process_date: unknown;
};

/** What cattle.herds.core resolves with — the hub's six independent reads. */
export type CattleHerdsCoreData = {
  cattle: CattleRow[];
  weighIns: WeighInRow[];
  calvingRecords: CattleCalvingRecordRow[];
  breedOptions: CattleBreedRow[];
  originOptions: CattleOriginRow[];
  transfers: CattleTransferRow[];
};

/** What cattleHome resolves with — dashboard rows plus forecast inputs. */
export type CattleHomeCoreData = {
  cattle: CattleRow[];
  dailys: CattleRow[];
  weighIns: WeighInRow[];
  calvingRecords: CattleCalvingRecordRow[];
  nutritionTargets: CattleRow[];
  processingBatches: CattleRow[];
  forecastSettings: ForecastSettings | null;
  forecastIncludes: Set<string>;
  forecastHidden: ForecastHiddenSelectedRow[];
};

/** What cattle.batches.index resolves with — index rows plus forecast inputs. */
export type CattleBatchesCoreData = {
  batches: CattleRow[];
  cattle: CattleRow[];
  weighIns: WeighInRow[];
  calvingRecords: CattleCalvingRecordRow[];
  forecastSettings: ForecastSettings | null;
  forecastIncludes: Set<string>;
  forecastHidden: ForecastHiddenSelectedRow[];
  forecastInputsReliable: boolean;
};

/** What cattle.animal.detail resolves with — the record page's seven reads. */
export type CattleAnimalDetailData = {
  /** null is DATA, not an error: this animal does not exist. */
  cow: CattleRow | null;
  cattle: CattleRow[];
  weighIns: WeighInRow[];
  calvingRecords: CattleCalvingRecordRow[];
  breedOptions: CattleBreedRow[];
  originOptions: CattleOriginRow[];
  processingBatches: CattleProcessingBatchRow[];
};

/** What cattle.weighins.list resolves with — the list's two reads. */
export type CattleWeighInsListData = {
  sessions: CattleWeighInSessionRow[];
  entries: Record<string, WeighInRow[]>;
};

// The broiler and pig list routes share one component and one physical session
// census. These are named separately from the ruminant list rows because this
// surface derives both its broiler average and pig forecast columns from them.
export type LivestockWeighInSessionRow = {
  id: string;
  species?: string | null;
  batch_id?: string | null;
  date?: string | null;
  status?: string | null;
  team_member?: string | null;
  started_at?: string | null;
  broiler_week?: number | string | null;
  [column: string]: unknown;
};

export type LivestockWeighInEntryRow = {
  session_id: string;
  weight?: number | string | null;
  [column: string]: unknown;
};

export type PigSessionMetricsRow = {
  available?: boolean;
  age_min_days?: number | string | null;
  age_max_days?: number | string | null;
  has_actual_farrowing?: boolean;
  feed_per_pig_lbs?: number | string | null;
  group_adg_lbs_per_day?: number | string | null;
  avg_weight_lbs?: number | string | null;
  // Declared for the RECORD page, which follows them to a second read: the pig
  // session's prior weigh-in window. The list surface ignores both, but the RPC
  // returns one shape and modelling it twice is how the two drift.
  prior_session_id?: string | null;
  prior_session_date?: string | null;
  [key: string]: unknown;
};

/** The shared broiler/pig list's required session and entry census. */
export type LivestockWeighInsListData = {
  sessions: LivestockWeighInSessionRow[];
  entries: Record<string, LivestockWeighInEntryRow[]>;
};

/** Pig-only, progressive derived metrics keyed to the visible sessions. */
export type PigWeighInsMetricsData = {
  metrics: Record<string, PigSessionMetricsRow>;
};

// ---------------------------------------------------------------------------
// /weigh-in-sessions/<id> — the shared four-species record page's reads.
// ---------------------------------------------------------------------------
// Rows stay open records with only the columns the RECORD PAGE names declared,
// the same decision src/livestock/WeighInSessionPage.tsx records for its own
// copies. `id` and `species` are required on the session row for the reason
// that page gives: `session.species.charAt(0)` and the species-keyed export
// helpers already depend on species being a string, and every writer sets it.
export type WeighInSessionRecordRow = {
  id: string;
  species: string;
  herd?: string | null;
  batch_id?: string | null;
  date?: string | null;
  team_member?: string | null;
  status?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  notes?: string | null;
  broiler_week?: number | string | null;
  [column: string]: unknown;
};

/** A weigh_ins row as the record page keys and forwards it — by primary key. */
export type WeighInSessionEntryRow = {id: string; [column: string]: unknown};

/** A cattle or sheep directory row. The group column differs by species. */
export type WeighInSessionAnimalRow = {id: string; [column: string]: unknown};

/**
 * What weighin.session.record resolves with.
 *
 * An OBJECT and not a bare row, for the reason layer.daily.detail records: the
 * one-shot runtime reports an unresolved resource as `data: null`, so a bare
 * `Row | null` payload would make "not loaded yet" and "no such session" the
 * same value, and this page renders the second as "Session not found."
 */
export type WeighInSessionRecordData = {record: WeighInSessionRecordRow | null};

/**
 * What weighin.session.detail resolves with — every read loadAll performed
 * AFTER it had a session row and had cleared the species and program_access
 * gates. The species branches stay branches: a cattle session resolves with
 * empty pig/broiler fields rather than paying for reads it never rendered.
 */
export type WeighInSessionDetailData = {
  entries: WeighInSessionEntryRow[];
  animals: WeighInSessionAnimalRow[];
  allSessions: WeighInSessionRecordRow[];
  allEntries: WeighInSessionEntryRow[];
  feederGroups: unknown[];
  broilerBatchRecs: unknown[];
};

/** The pig record page's prior weigh-in, resolved from the metrics RPC. */
export type WeighInSessionPriorSession = {
  id: string;
  date: string;
  entries: WeighInSessionEntryRow[];
};

/**
 * What weighin.session.pigMetrics resolves with.
 *
 * Separate from the detail payload because its refresh is separate: an entry
 * autosave re-runs THIS read alone, and folding it into the detail resource
 * would make every weight edit re-read the animal directory and the species
 * weigh-in history as well — and re-seed the per-row drafts the operator is
 * typing into. `pig.weighins.metrics` is split from `livestock.weighins.list`
 * for the neighbouring reason.
 */
export type WeighInSessionPigMetricsData = {
  metrics: PigSessionMetricsRow | null;
  priorSession: WeighInSessionPriorSession | null;
};

// ---------------------------------------------------------------------------
// /weighins — the public weigh-in FORM's six reads.
// ---------------------------------------------------------------------------
// Different surface, different shape: this form's reads are keyed on what the
// operator has picked so far — a species at the first stage, then a session —
// not on a route parameter. Each stays its own resource because each was its
// own independent request, with its own failure posture, in the effects they
// replace.
export type WeighInWebformCattleRow = {
  id: string;
  tag?: string | null;
  herd?: string | null;
  birth_date?: string | null;
  sex?: string | null;
  breed?: string | null;
  old_tags?: unknown;
  breeding_blacklist?: boolean | null;
  [column: string]: unknown;
};

export type WeighInWebformSheepRow = {
  id: string;
  tag?: string | null;
  flock?: string | null;
  birth_date?: string | null;
  sex?: string | null;
  breed?: string | null;
  old_tags?: unknown;
  [column: string]: unknown;
};

/** A weigh_in_sessions row as this form reads it: `select('*')` or `id, date`. */
export type WeighInWebformSessionRow = {id: string; [column: string]: unknown};

/** A weigh_ins row. `select('*')` for the session list, three columns for priors. */
export type WeighInWebformEntryRow = {[column: string]: unknown};

/**
 * The species-scoped picker inputs, one branch per species and never more.
 *
 * Cattle and sheep resolve a whole animal directory; pig and broiler resolve a
 * batch-name list out of webform_config. The three fields the chosen species
 * does not use stay empty rather than being read — the effect this replaces was
 * an if/else-if chain, not four parallel requests.
 */
export type WeighInWebformSpeciesDirectoryData = {
  cattleList: WeighInWebformCattleRow[];
  sheepList: WeighInWebformSheepRow[];
  pigBatches: string[];
  broilerBatches: string[];
};

/**
 * The public broiler schooner mirror, and the ONLY read on this form that is
 * fail-soft.
 *
 * It is separate from the directory above because its failure must not be the
 * directory's: the effect it replaces caught its own rejection and still ran
 * `.finally(() => setBroilerBatchMetaLoaded(true))`, because that marker is what
 * lets Start Session fail CLOSED on a batch with no resolvable schooners. Fold
 * it into the directory and a broiler_groups failure would leave the marker
 * unset forever, which reads as "still loading" and blocks the form outright.
 */
export type WeighInWebformBroilerSchoonersData = {broilerBatchMeta: unknown[]};

/** The last seven days of draft sessions for the picked species. */
export type WeighInWebformDraftsData = {drafts: WeighInWebformSessionRow[]};

/** The most recent COMPLETED weight per tag, excluding today. */
export type WeighInWebformPriorWeight = {weight: number; date: string | null | undefined};

export type WeighInWebformPriorWeightsData = {priorByTag: Record<string, WeighInWebformPriorWeight>};

/** The persisted entries of the session currently open, in insertion order. */
export type WeighInWebformSessionEntriesData = {entries: WeighInWebformEntryRow[]};

/** The public form's own pig metric read — one session, not the list's fanout. */
export type WeighInWebformPigMetricsData = {metrics: PigSessionMetricsRow | null};

/** A Cattle Log entry comes from a SECURITY DEFINER JSONB RPC. */
export type CattleLogEntryRow = {[column: string]: unknown};

/** The mention RPC exposes only the two fields the picker needs. */
export type CattleLogMentionableProfile = {id?: string; full_name?: string | null; [column: string]: unknown};

/** What cattle.dailys.detail resolves with — the record plus its feed inputs. */
export type CattleDailyDetailData = {
  /** null is DATA: the daily record was deleted or does not exist. */
  record: CattleRow | null;
  feedInputs: CattleRow[];
};

/** What cattle.batches.detail resolves with — the record, cattle directory, and rename census. */
export type CattleBatchDetailData = {
  /** null is DATA: this processing batch does not exist. */
  batch: CattleRow | null;
  cattle: CattleRow[];
  allBatches: CattleRow[];
};

/**
 * What cattle.forecast.bundle resolves with — every input the canonical
 * forecast composition needs. This is intentionally fail-closed: an omitted
 * setting, include, hidden row, batch, or weigh-in list changes cohort
 * membership, so none may degrade to a fabricated empty value.
 */
export type CattleForecastBundleData = {
  cattle: CattleRow[];
  weighIns: WeighInRow[];
  settings: ForecastSettings;
  includes: Set<string>;
  hidden: ForecastHiddenSelectedRow[];
  batches: CattleRow[];
  realBatches: CattleRow[];
  scheduledBatches: CattleRow[];
};

/**
 * What view.cattleforecast resolves with â€” the forecast editor's complete
 * render input. Unlike cattle.forecast.bundle, this includes the editor-only
 * calving/comment/option rows consumed by the Include Heifers detail panel.
 * Every member is required: presenting partial forecast input as a real plan
 * would fabricate cohort membership and hide an unavailable dependency.
 */
export type CattleForecastCoreData = {
  cattle: CattleRow[];
  weighIns: WeighInRow[];
  calvingRecords: CattleCalvingRecordRow[];
  comments: CattleRow[];
  breedOptions: CattleBreedRow[];
  originOptions: CattleOriginRow[];
  processingBatches: CattleRow[];
  settings: ForecastSettings;
  includes: Set<string>;
  hidden: ForecastHiddenSelectedRow[];
};

export type SheepRow = {[column: string]: unknown};
export type SheepProcessingBatchRow = {[column: string]: unknown};
export type SheepLambingRecordRow = {[column: string]: unknown};
export type SheepBreedRow = {[column: string]: unknown};
export type SheepOriginRow = {[column: string]: unknown};
export type SheepTransferRow = {[column: string]: unknown};

/** Every source that Production's model consumes, including per-source errors. */
export type ProductionSourcesCoreData = ProductionApiSources;

/**
 * The standard Home maintenance card's required active-equipment and fueling
 * inputs. Unlike the Light portal's optional attention block, either read
 * failing makes this card visibly unavailable rather than fabricating empty
 * maintenance state.
 */
export type HomeStandardEquipmentAttentionData = {
  equipment: EquipmentRow[];
  equipmentCompletions: FoldedEquipmentFuelings['equipmentCompletions'];
  equipmentFuelings: FoldedEquipmentFuelings['equipmentFuelings'];
};

/**
 * The standard Home materials card's two sidecar reads. `tablesMissing` is
 * data, not an error: older installations legitimately lack migration 048 and
 * retain the card's established feature-absent empty state.
 */
export type HomeStandardEquipmentMaterialsData = {
  materials: MaterialRow[];
  materialClears: MaterialClearRow[];
  tablesMissing: boolean;
};

/** The compact Home production card's current-calendar-year source bundle. */
export type HomeStandardProductionSourcesData = ProductionApiSources;

/**
 * The six bounded projections App's legacy boot sidecar used to publish into
 * DailysRecentContext / CattleHomeContext / SheepHomeContext for the standard
 * dashboard: four recent daily-report windows plus the two lightweight
 * livestock directories. They feed the missed-report alerts, the Last 5 Days
 * feed, and the Animals on Farm cattle/sheep counts.
 *
 * Deliberately NOT the boot's full-history poultry/pig reads: those still serve
 * the Animals on Farm broiler/pig/layer counts through App-owned context and
 * remain declared residuals of this surface.
 */
export type HomeStandardAlertInputsData = {
  layerDailysRecent: CattleRow[];
  eggDailysRecent: CattleRow[];
  cattleDailysRecent: CattleRow[];
  sheepDailysRecent: CattleRow[];
  cattleForHome: CattleRow[];
  sheepForHome: CattleRow[];
  // Names of the slices above whose read failed (each one is then []).
  sliceErrors?: string[];
};

/**
 * One layer_dailys row as the Animals on Farm layer count reads it.
 *
 * FOUR COLUMNS, AND THE SET IS CLOSED BY CODE, NOT BY TASTE. Every touch of a
 * layer daily inside animalHistory's layer path is one of three things:
 * `isoDate(d.date)`, `int(d.layer_count)`, or the shared housing matcher in
 * src/lib/layerHousing.ts, which reads `daily.batch_label` and `daily.batch_id`
 * and nothing else. `deleted_at` is applied server-side, exactly as the boot
 * sidecar applied it, so it is not projected either.
 */
export type HomeStandardLayerEvidenceRow = {
  date?: string | null;
  layer_count?: number | null;
  batch_label?: string | null;
  batch_id?: string | null;
};

/**
 * The COMPLETE layer_dailys count evidence behind Home's "Layer Hens" figure
 * and its freshness disclosure.
 *
 * COMPLETE IN TIME, AND THAT HALF IS NOT NEGOTIABLE. layersOnFarmAt takes the
 * newest matching report at or before the snapshot date; a housing whose last
 * count predates the window contributes nothing, and its stale
 * `housing.current_count` anchor is ZERO on every active housing on this farm.
 * A bounded window therefore does not degrade the figure, it erases it. This
 * was measured, not assumed: at the 45-day window home.standard.alert.inputs
 * uses, the live layer total reads 0 against a true 564, and the cliff is at
 * 131 days and moves one day closer every day.
 *
 * NARROW IN ROWS AND COLUMNS, AND THAT HALF IS FREE. "Complete history" never
 * required `select('*')` over every row: `layerEvidenceEntries` gates every
 * layer-daily read on `int(d.layer_count) > 0`, and `layer_count` is a
 * Postgres integer, so `.gt('layer_count', 0)` selects exactly the same
 * partition PostgREST drops NULLs from. On live data that is 23 rows of 2553
 * and 2.1 KB against 1.2 MB, byte-identical in output.
 *
 * SEPARATE FROM animal.history.dailys ON PURPOSE. That resource feeds
 * inferAnimalHistoryStartMonth, which pushes EVERY `d.date` with no
 * `layer_count` gate — earliest-of-all and earliest-of-positive diverge on live
 * data (2024-05-22 against 2024-06-10) and are equal today only because the
 * ANIMAL_HISTORY_START_MONTH floor hides it. Sharing this filtered read there
 * would be a silent behaviour change waiting for the floor to move.
 */
export type HomeStandardLayerEvidenceData = {layerEvidence: HomeStandardLayerEvidenceRow[]};

/**
 * The COMPLETE broiler and pig daily-report histories behind Home's "Broilers"
 * and "Pigs" figures.
 *
 * COMPLETE IN TIME, for the same reason its layer sibling is, and by a
 * different mechanism. `broilersOnFarmAt` SUMS `mortality_count` across every
 * matching report at or before the snapshot date, and `pigsOnFarmAt` takes the
 * newest positive `pig_count` per feeder target and then nets out later trips,
 * mortalities and transfers. Neither reads a window; both walk the whole
 * history, so a cutoff does not narrow the answer, it changes it.
 *
 * OPEN IN COLUMNS, WHICH ITS LAYER SIBLING IS NOT, AND THAT IS DELIBERATE.
 * These two arrays have three consumers on this page, not one. The snapshot
 * reads four columns; `buildMissedDailyReports` reads `date` and `batch_label`;
 * and the admin "Last 5 Days" feed passes `raw: d` straight into
 * `buildHomeDailyModel`, which reads twelve to sixteen columns per species
 * (team_member, source, photos, feed_lbs, feed_type, grit_lbs, group_moved,
 * waterer_checked, mortality_count, mortality_reason, comments for broilers;
 * pig_count, fence_voltage, nipple_drinker_working, fence_walked, issues for
 * pigs). Narrowing the projection to the snapshot's four columns would empty
 * every cell of that table. Measured rather than assumed: the narrow read is
 * 400 KB against 2.2 MB, and it costs the whole feed.
 *
 * NO FILTER ON `mortality_count`, ALSO DELIBERATE. Its layer sibling can gate
 * on `layer_count > 0` because layerEvidenceEntries applies the identical gate
 * itself. `broilersOnFarmAt` does not: it branches on
 * `matchingDailys.length > 0 || !/^b-24-/i.test(name)`, choosing between summing
 * dailys and falling back to `batch.mortalityCumulative`, so removing zero-
 * mortality rows can flip that branch. It happens to be output-identical on
 * today's data only because the three B-24-* batches carry no hatchDate and
 * exit earlier — a data-dependent argument, not a structural one.
 *
 * FAIL-CLOSED, unlike the boot arrays it replaces. App's boot degraded a failed
 * history to `[]` (`loadAllPigDailys(...).catch(() => [])` and its poultry
 * twin). On live data that is not a smaller number, it is a WRONG one: an empty
 * poultry history stops mortality ever being subtracted and paints 2269
 * broilers against a true 2172. Same criterion home.standard.animal.layerEvidence
 * was written under — this input feeds a NUMBER, and an empty array does not
 * make a number absent.
 */
export type HomeStandardAnimalDailyHistoryData = {
  broilerDailys: CattleRow[];
  pigDailys: CattleRow[];
};

/**
 * The seven persisted `app_store` planner values the STANDARD dashboard reads.
 *
 * Field for field the same set the Light portal owns through
 * `home.light.planner`, because the two home surfaces need the same persisted
 * blobs: the broiler batch list, the four pig planner values, the layer-group
 * directory and the cleared-missed key set. It is a separate resource rather
 * than a shared one because a catalog resource names the surfaces that own it,
 * and ownership is declared per surface.
 *
 * Deliberately NOT App's broad `app_store` boot: that transaction also REPAIRS
 * and PERSISTS `ppp-v4` and `ppp-feeders-v1`. This resource reproduces the
 * display semantics of those repairs as an immutable in-memory projection and
 * writes nothing, exactly as the Light portal's copy does.
 */
export type HomeStandardPlannerData = {
  batches: CattleRow[];
  breedingCycles: CattleRow[];
  farrowingRecs: CattleRow[];
  feederGroups: CattleRow[];
  breeders: CattleRow[];
  layerGroups: CattleRow[];
  missedCleared: unknown[];
};

/** The two named fields SheepWeighInsView groups and renders from whole rows. */
export type SheepWeighInListEntry = SheepWeighInRow & {
  session_id: string;
  tag: string | null;
  new_tag_flag: boolean | null;
};

export type SheepWeighInSessionRow = {[column: string]: unknown};

/** What sheep.weighins.list resolves with — the list's two reads. */
export type SheepWeighInsListData = {
  sessions: SheepWeighInSessionRow[];
  entries: Record<string, SheepWeighInListEntry[]>;
};

/** What sheep.home.core resolves with — required dashboard rows plus a tolerant batch-count tile. */
export type SheepHomeCoreData = {
  sheep: SheepRow[];
  dailys: SheepRow[];
  weighIns: SheepWeighInRow[];
  batchCounts: {total: number; planned: number};
};

export type SheepFlocksCoreData = {
  sheep: SheepRow[];
  weighIns: SheepWeighInRow[];
  lambingRecords: SheepLambingRecordRow[];
  breedOptions: SheepBreedRow[];
  originOptions: SheepOriginRow[];
  transfers: SheepTransferRow[];
};

export type SheepAnimalDetailData = {
  animal: SheepRow | null;
  sheep: SheepRow[];
  weighIns: SheepWeighInRow[];
  lambingRecords: SheepLambingRecordRow[];
  breedOptions: SheepBreedRow[];
  originOptions: SheepOriginRow[];
};

/** What sheep.batches.index resolves with — the processing-batch summaries. */
export type SheepBatchesCoreData = {
  batches: SheepProcessingBatchRow[];
};

/** What sheep.batches.detail resolves with — the keyed batch and active sheep directory. */
export type SheepBatchDetailData = {
  /** null is DATA: this processing batch does not exist. */
  batch: SheepProcessingBatchRow | null;
  sheep: SheepRow[];
};

/** What sheep.dailys.detail resolves with — the record plus its feed inputs. */
export type SheepDailyDetailData = {
  /** null is DATA: the daily record was deleted or does not exist. */
  record: SheepRow | null;
  feedInputs: CattleRow[];
};

/** What the equipment fleet container resolves for every /fleet sub-route. */
export type EquipmentFleetData = {
  equipment: {[column: string]: unknown}[];
  fuelings: {[column: string]: unknown}[];
  maintenance: {[column: string]: unknown}[];
  /** The equipment module is optional on older installations. */
  missingSchema: boolean;
};

/**
 * The operator fueling hub's rows. An open record for the same reason
 * EquipmentFleetData's arrays are: the 16 selected columns are consumed by
 * FuelingHub and by EquipmentFuelingWebform, which model them themselves, and
 * inventing a column schema here would claim something the SQL never checked.
 */
export type FuelingHubEquipmentRow = {[column: string]: unknown};

/** What equipment.fuelingHub.core resolves — the hub's ONE container read. */
export type EquipmentFuelingHubData = {
  equipment: FuelingHubEquipmentRow[];
  /** The equipment module is optional on older installations (migration 016). */
  missingSchema: boolean;
};

/** One prior fueling on ONE machine, as the form's narrowed 6-column read returns it. */
export type EquipmentFuelingHistoryRow = {[column: string]: unknown};

/** What equipment.fuelingForm.history resolves — prior fuelings on one machine. */
export type EquipmentFuelingFormHistoryData = {
  history: EquipmentFuelingHistoryRow[];
};

/**
 * The Light home portal's optional equipment-attention inputs. Each read was
 * independently ignored by the page before migration, so an individual
 * failure remains empty data rather than becoming a portal-level error state.
 */
export type LightHomeEquipmentAttentionData = {
  equipment: LightHomeEquipmentRow[];
  equipmentCompletions: FoldedEquipmentFuelings['equipmentCompletions'];
  equipmentFuelings: FoldedEquipmentFuelings['equipmentFuelings'];
};

/**
 * The layer-batch and housing rows stay open because the hub renders and
 * creates columns maintained outside this TypeScript module.  The route core
 * deliberately reproduces the legacy `select('*')` projections rather than
 * inventing a partial schema here.
 */
export type LayerBatchCoreRow = {[column: string]: unknown};
export type LayerHousingCoreRow = {[column: string]: unknown};

/**
 * The seven persisted values the Light portal needs for its planner and
 * alert presentation. This deliberately is not App's broad `app_store`
 * bootstrap: it performs no legacy migrations, repairs, or writes.
 */
export const HOME_LIGHT_PLANNER_APP_STORE_KEYS = Object.freeze([
  'ppp-v4',
  'ppp-breeding-v1',
  'ppp-farrowing-v1',
  'ppp-feeders-v1',
  'ppp-breeders-v1',
  'ppp-layer-groups-v1',
  'ppp-missed-cleared-v1',
] as const);

export type LightHomePlannerData = {
  batches: CattleRow[];
  breedingCycles: CattleRow[];
  farrowingRecs: CattleRow[];
  feederGroups: CattleRow[];
  breeders: CattleRow[];
  layerGroups: CattleRow[];
  missedCleared: unknown[];
};

/** The seven independent inputs the missed-daily alert builder consumes. */
export type LightHomeMissedInputsData = {
  broilerDailys: CattleRow[];
  pigDailys: CattleRow[];
  layerDailysRecent: CattleRow[];
  cattleDailysRecent: CattleRow[];
  sheepDailysRecent: CattleRow[];
  cattleForHome: CattleRow[];
  sheepForHome: CattleRow[];
  // Names of the slices above whose read failed (each one is then []).
  sliceErrors?: string[];
};

/** What view.cattlebreeding resolves with — the cycle list's three reads. */
export type CattleBreedingCoreData = {
  cycles: CattleRow[];
  calvingRecords: CattleCalvingRecordRow[];
  cattle: CattleRow[];
};

/** What layer.batches.index resolves with — its independent hub inputs. */
export type LayerBatchesCoreData = {
  layerBatches: LayerBatchCoreRow[];
  layerHousings: LayerHousingCoreRow[];
  layerDailys: LayerDailyRow[];
  eggDailys: EggDailyRow[];
};

/** The eight layer_dailys columns the Layer Groups cards name, and no more. */
export type LayerGroupsRecentDailyRow = {
  batch_label?: string | null;
  feed_lbs?: number | null;
  grit_lbs?: number | null;
  layer_count?: number | null;
  date?: string | null;
  team_member?: string | null;
  comments?: string | null;
  mortality_count?: number | null;
};

/**
 * What view.layers resolves with.
 *
 * `layerGroups` is the ppp-layer-groups-v1 app_store blob — a persisted JSON
 * array, not a table, which is why it is `unknown[]` here exactly as
 * LayerContext hands it out. The two directories and the recent-daily window
 * are the same reads the page rendered from before: the directories arrived as
 * App boot arrays through a LayerEntry prop, the dailys from an effect the
 * presenter owned.
 */
export type LayerGroupsCoreData = {
  layerGroups: unknown[];
  layerGroupsUpdatedAt: string | null;
  layerBatches: LayerBatchCoreRow[];
  layerHousings: LayerHousingCoreRow[];
  recentDailys: LayerGroupsRecentDailyRow[];
};

/** A layer_dailys row. `select('*')`, so the columns stay open. */
export type LayerDailyListRow = {[column: string]: unknown};

/**
 * What layer.daily.detail resolves with.
 *
 * An OBJECT and not a bare row, deliberately. The one-shot runtime reports an
 * unresolved resource as `data: null`, so a bare `Row | null` payload would
 * make "not loaded yet" and "no such report" the same value and the record
 * page's not-found branch would fire for one paint on every open.
 */
export type LayerDailyDetailData = {record: LayerDailyListRow | null};

/**
 * The three directories the layer daily surfaces build their group dropdown
 * and their batch_label -> batch_id resolution from. All three used to arrive
 * as LayerEntry props carrying App's former-eager boot results.
 *
 * Deliberately NOT layer.groups.core, even though the first three fields
 * match: that resource also pulls a 200-row recent-daily window the daily
 * surfaces have no use for, and sharing it would make /layer/dailys pay for
 * /layer/groups' cards.
 */
export type LayerDailysGroupDirectoryData = {
  layerGroups: unknown[];
  // THE BLOB READ'S OWN VERDICT, CARRIED BESIDE THE ROWS.
  //
  // This reader is deliberately fail-soft — a blob outage must mean "fewer
  // group options", never "no daily history" — so it cannot signal failure by
  // throwing. Without this field a failed read and an empty blob are the same
  // `[]`, and a consumer that vetoes stale mirror rows by canonical name would
  // silently veto NOTHING at exactly the moment the canonical answer is
  // unavailable. `pending` is the ABSENCE of a payload and is never written
  // here; see src/lib/layerGroupStatus.ts.
  layerGroupsStatus: LayerGroupReadState;
  layerBatches: LayerBatchCoreRow[];
  layerHousings: LayerHousingCoreRow[];
};

/** An egg_dailys row. `select('*')`, so the columns stay open. */
export type EggDailyListRow = {[column: string]: unknown};

/**
 * What layer.eggDaily.detail resolves with.
 *
 * An OBJECT and not a bare row, for the same reason layer.daily.detail is one:
 * the one-shot runtime reports an unresolved resource as `data: null`, so a
 * bare `Row | null` payload would make "not loaded yet" and "no such report"
 * the same value and the record page's not-found branch would fire for one
 * paint on every open.
 */
export type EggDailyDetailData = {record: EggDailyListRow | null};

/**
 * The ppp-layer-groups-v1 blob the egg hub names its four group slots from.
 *
 * ONE field, not three. The egg hub only ever read `layerGroups` — it filters
 * the blob to `status === 'active'` and puts those names in the add/edit form's
 * four group selects — so the two layer directories that
 * layer.dailys.groupDirectory also carries would be a read this surface never
 * had. `unknown[]` because the blob's element shape lives in SQL and in the
 * persisted JSON, not in any TypeScript declaration this repo owns.
 */
export type EggDailysGroupsData = {
  layerGroups: unknown[];
  /** The blob read's own verdict — see LayerDailysGroupDirectoryData above. */
  layerGroupsStatus: LayerGroupReadState;
};

/**
 * The five $/lb rates the Layers Home dashboard falls back to when a batch
 * carries no per-batch cost snapshot of its own.
 *
 * The same shape FeedCostsContext's `useState` initializer infers, named here
 * rather than imported because src/lib/feedCosts.ts deliberately declares its
 * return as an OPEN record — its contract is that unknown keys survive, not
 * that these five are the whole object. App casts the normalizer's result to
 * the five-key shape at its own boundary for the same reason; this is that
 * cast's twin on the read-local side.
 */
export type LayersHomeFeedCosts = {starter: number; grower: number; layer: number; pig: number; grit: number};

/**
 * What view.layersHome resolves with — the five values LayersHomeView used to
 * read off three App-owned providers.
 *
 * The two full histories are COMPLETE, not windowed, and that is load-bearing:
 * the dashboard's active-batch cards run from each batch's brooder/arrival
 * anchor to today, so any bounded window would silently truncate a card's
 * lifetime feed, mortality and egg totals. They are the same reads App's boot
 * sidecar performs, page size and ordering included.
 */
export type LayersHomeCoreData = {
  layerBatches: LayerBatchCoreRow[];
  layerHousings: LayerHousingCoreRow[];
  allLayerDailys: LayerDailyListRow[];
  allEggDailys: EggDailyListRow[];
  feedCosts: LayersHomeFeedCosts;
};

/** A poultry_dailys row. `select('*')`, so the columns stay open. */
export type BroilerDailyListRow = {[column: string]: unknown};

/**
 * What broiler.daily.detail resolves with.
 *
 * An OBJECT and not a bare row, for the same reason layer.daily.detail is one:
 * the one-shot runtime reports an unresolved resource as `data: null`, so a
 * bare `Row | null` payload would make "not loaded yet" and "no such report"
 * the same value and PoultryDailyPage's not-found branch would fire for one
 * paint on every open.
 */
export type BroilerDailyDetailData = {record: BroilerDailyListRow | null};

/**
 * The broiler batch directory both /broiler/dailys surfaces build their group
 * dropdown from — the ppp-v4 app_store blob, which reached them as a single
 * `batches` prop off BroilerEntry carrying App's former-eager boot result.
 *
 * `unknown[]` exactly as BatchesContext hands it out: this is a persisted JSON
 * array, not a table, and the narrowing to BroilerBatchRow happens in
 * BroilerDailysRouter, at the one point the payload and its consumers meet.
 *
 * NOT raw, unlike layer.record.appStore's read of the same blob, and the
 * difference is load-bearing. Both consumers filter `status === 'active'`, and
 * `status` is exactly what App's cold-boot repair rewrites: a planned batch
 * becomes active on its hatch day and an active batch becomes processed on its
 * processing date. Handing the stored value over unrepaired would drop a batch
 * hatching today out of the group dropdown and keep a finished one in it.
 */
export type BroilerDailysBatchDirectoryData = {batches: unknown[]};

/**
 * What view.timeline resolves with — the two directories the Gantt draws.
 *
 * Both used to arrive through provider hooks fed by App's global boot:
 * `batches` off BatchesContext (the ppp-v4 blob) and `layerBatches` off
 * LayerContext. Normalized for the same reason the daily directory is: the
 * bars read `brooderIn` and the conflict chevron reads `status`, and both are
 * columns App's cold-boot ppp-v4 repair rewrites.
 *
 * Deliberately NOT broiler.dailys.batchDirectory, even though the first field
 * matches: that resource is fail-soft, because a missing dropdown option is not
 * a missing page. Here the batch list IS the page.
 */
export type BroilerTimelineCoreData = {
  batches: unknown[];
  layerBatches: LayerBatchCoreRow[];
};

/**
 * The six poultry_dailys columns the broiler dashboard names, and no more.
 *
 * App's boot loader reads `select('*')` because six different consumers share
 * its one array. This surface reads five columns through
 * calcBroilerStatsFromDailys / computeBroilerOnFarmCounts (batch_label,
 * feed_type, feed_lbs, grit_lbs, mortality_count) and `date` for its own
 * report-rate and recent-report windows.
 */
export type BroilerHomeDailyRow = {
  batch_label?: string | null;
  feed_type?: string | null;
  feed_lbs?: number | string | null;
  grit_lbs?: number | string | null;
  mortality_count?: number | string | null;
  date?: string | null;
};

/**
 * What view.broilerHome resolves with.
 *
 * Both values were App's former-eager boot results reached through provider
 * hooks: the ppp-v4 blob off BatchesContext and the COMPLETE poultry_dailys
 * history off DailysRecentContext. The history is complete rather than
 * windowed on purpose — the dashboard's per-batch feed and mortality totals
 * match rows by `batch_label` alone, with no date bound, so any window would
 * silently shrink the totals of an older batch.
 */
export type BroilerHomeCoreData = {
  batches: unknown[];
  broilerDailys: BroilerHomeDailyRow[];
};

/**
 * What broiler.batches.index resolves with — the two App-boot values the
 * Broiler Batches hub read off provider hooks that are NOT the ppp-v4 batch
 * array itself.
 *
 * `broilerDailys` is the COMPLETE poultry_dailys history, `select('*')`, for
 * the same reason view.broilerHome's is: every per-batch feed and mortality
 * total on this hub matches daily rows by `batch_label` alone with no date
 * bound, so a window would silently shrink the totals of every batch older
 * than it. It is `BroilerDailyListRow` rather than the narrowed home row
 * because it comes back from `loadAllPoultryDailys`, which is App's own boot
 * loader called rather than re-implemented, and that loader selects every
 * column.
 *
 * `feedCosts` is the ppp-feed-costs-v1 blob FeedCostsContext held. The hub
 * reads exactly three of its five rates, and only when an operator reopens a
 * processed batch that never had a cost snapshot stamped on it.
 */
/**
 * What the broiler batch FORM resolves with, in both of the surfaces that mount
 * it — the full-screen `broiler.batchForm.fullscreen` and the embedded copy
 * inside `broiler.batches.detail`.
 *
 * One value, and it is the COMPLETE poultry_dailys history for the same reason
 * broiler.home.core's is complete: calcBroilerStatsFromDailys matches rows to a
 * batch by `batch_label` alone with no date bound, and the form's Feed & Grit
 * and Mortality panels ARE those totals. A window would silently shrink the
 * lifetime totals of any batch older than it, on the screen an operator uses to
 * check them.
 */
export type BroilerBatchFormDailysData = {
  broilerDailys: BroilerDailyListRow[];
};

/**
 * The three boot values retained App closures need while the full-screen batch
 * form is open. BatchForm itself does not render from any of them: this is the
 * operational mirror that keeps `upd`, `submit`, and `del` from building a
 * write over cold provider defaults after the fullscreen surface bypasses
 * legacy boot.
 */
export type BroilerBatchFormActionStateData = {
  batches: BroilerBatch[];
  feedCosts: LayersHomeFeedCosts;
  layerBatches: LayerBatchCoreRow[];
};

export type BroilerBatchesHubData = {
  batches: BroilerBatch[];
  broilerDailys: BroilerDailyListRow[];
  // The same five-key shape FeedCostsContext's useState initializer infers,
  // named at view.layersHome's declaration above because that surface localized
  // this blob first. Reused rather than re-declared: a second identical alias
  // would be two names for one contract.
  feedCosts: LayersHomeFeedCosts;
};

/**
 * What the two layer RECORD surfaces resolve with.
 *
 * The same four reads layer.batches.core performs, and deliberately NOT that
 * resource. layer.batches.core is pinned to exactly one surface — the
 * boot-released /layer/batches hub — and layer_batches_boot_release_static
 * asserts that pinning. Widening it so the record pages could share it would
 * loosen a released surface's ownership to save one query definition.
 *
 * The batch and housing directories are here because they used to arrive as
 * LayerEntry props off App's boot; the two metric feeds are here because each
 * record page paginated them itself through a `fetchAll(table, columns)` helper
 * whose table was a PARAMETER — so the physical-call inventory could only ever
 * record it as `from:~table`, attributable to no literal table at all.
 */
export type LayerRecordCoreData = {
  layerBatches: LayerBatchCoreRow[];
  layerHousings: LayerHousingCoreRow[];
  layerDailys: LayerDailyRow[];
  eggDailys: EggDailyRow[];
};

/**
 * The two persisted blobs the layer record pages read.
 *
 * `layerGroups` (ppp-layer-groups-v1) names the housing slots on both pages.
 * `broilerBatches` (ppp-v4) is read ONLY by the batch page, to flag a brooder
 * or schooner slot already claimed by a broiler batch. Both keys come back in
 * one read because that is one round trip instead of two for a page that needs
 * at least one of them either way.
 *
 * The rows are handed over RAW, without App's boot migration of ppp-v4. That is
 * safe rather than lucky: the batch page's conflict rows already read both the
 * legacy camelCase (`brooderIn`/`brooderOut`) and the migrated snake_case
 * (`brooder_entry_date`/`brooder_exit_date`) shapes through `||` fallbacks,
 * because the blob has always carried both.
 */
export type LayerRecordAppStoreData = {
  layerGroups: unknown[];
  layerGroupsUpdatedAt: string | null;
  broilerBatches: unknown[];
};

/** A pig_dailys row. `select('*')`, so the columns stay open. */
export type PigDailyListRow = {[column: string]: unknown};

/**
 * What pig.daily.detail resolves with.
 *
 * An OBJECT and not a bare row, for the same reason layer.daily.detail is one:
 * the one-shot runtime reports an unresolved resource as `data: null`, so a
 * bare `Row | null` payload would make "not loaded yet" and "no such report"
 * the same value and PigDailyPage's not-found branch would fire for one paint
 * on every open.
 */
export type PigDailyDetailData = {record: PigDailyListRow | null};

/**
 * The feeder-batch directory both pig daily surfaces build their group
 * dropdown and their batch_label -> batch_id resolution from.
 *
 * It is the ppp-feeders-v1 app_store blob — a persisted JSON array, not a
 * table, which is why it is `unknown[]` here exactly as PigContext hands it
 * out. It used to arrive as the single `feederGroups` prop PigEntry threaded
 * from App's former-eager boot results.
 */
export type PigDailysGroupDirectoryData = {feederGroups: unknown[]};

/** What view.animalHistory resolves with — all four page-owned animal timelines. */
export type AnimalHistoryCoreData = {
  cattle: CattleRow[];
  sheep: SheepRow[];
  cattleTransfers: CattleTransferRow[];
  sheepTransfers: SheepTransferRow[];
};

/**
 * The three persisted planner blobs the animal-history reconstruction reads.
 *
 * `batches` is ppp-v4, projected through the SAME in-memory repair Light Home
 * applies — the two lifecycle rules are what decide whether a batch counts as
 * on-farm at a past month end, so handing the raw blob over would silently
 * change the broiler line. `feederGroups` is ppp-feeders-v1 after the same
 * sub-batch reconciliation App's boot performs, and `breeders` falls back to
 * INITIAL_BREEDERS on an empty list exactly as the boot fan-out does.
 *
 * Every one of those is a PROJECTION and never a write: this resource performs
 * no upsert and queues no repair, unlike the boot transaction these values used
 * to arrive from.
 */
export type AnimalHistoryPlannerData = {
  batches: CattleRow[];
  feederGroups: CattleRow[];
  breeders: CattleRow[];
};

/**
 * The two layer directories the layer timeline is rebuilt from, in App's boot
 * orderings (batches by name, housings by start_date). Kept apart from the
 * daily-report resource because these are directory rows, not dated evidence,
 * and only they decide which housings exist at all.
 */
export type AnimalHistoryLayerDirectoryData = {
  layerBatches: LayerBatchCoreRow[];
  layerHousings: LayerHousingCoreRow[];
};

/**
 * The three daily-report histories the reconstruction reads as COMPLETE
 * histories — every month-end snapshot back to 2024-10 is derived from the
 * latest report on or before that date, so no bounded window reproduces them.
 * Rows stay open (`select('*')`) because the model reads a handful of columns
 * off tables it does not own.
 */
export type AnimalHistoryDailysData = {
  layerDailys: LayerDailyRow[];
  broilerDailys: CattleRow[];
  pigDailys: CattleRow[];
};

/**
 * The Add Feed form reads the master feed table with `select('*')`, then
 * filters inactive rows in its presenter. The row remains open because the
 * form consumes the operator-configured nutrition fields directly.
 */
export type AddFeedInputRow = {
  id: string;
  name?: string | null;
  category?: string | null;
  unit?: string | null;
  unit_weight_lbs?: number | string | null;
  status?: string | null;
  herd_scope?: string[] | null;
  moisture_pct?: number | null;
  nfc_pct?: number | null;
  protein_pct?: number | null;
};

/** The independent, fail-soft master-feed read used by /addfeed. */
export type AddFeedInputsData = {feedInputs: AddFeedInputRow[]};

/**
 * The six operator-edited webform_config payloads. They intentionally stay
 * unknown: this is jsonb whose live shapes are edited through the admin UI,
 * and the presenter proves each member it consumes. A missing/no-data row is
 * represented as null, matching the form's former empty-state fallback.
 */
export type AddFeedConfigData = {
  housingBatchMap: unknown | null;
  broilerGroups: unknown | null;
  broilerBatchMeta: unknown | null;
  activeGroups: unknown | null;
  fullConfig: unknown | null;
  webformSettings: unknown | null;
};

/** The authenticated /dailys hub's unfiltered cattle and sheep feed master. */
export type WebformHubFeedInputRow = {[column: string]: unknown};

/** The hub keeps the inactive exclusion at its presenter boundary. */
export type WebformHubInputsData = {feedInputs: WebformHubFeedInputRow[]};

/**
 * The two layer directories the /dailys hub reads to build its layer-daily
 * group dropdown and to resolve a batch label back to a batch id at submit
 * time. Both were handed down from App's boot arrays as props; owning them
 * here is what lets view.webformhub stop reading the former-eager
 * layer_batches / layer_housings results.
 *
 * The rows stay open for the same reason layer.batches.core keeps them open:
 * the columns are maintained in SQL, and the legacy `select('*')` projection is
 * reproduced rather than a partial schema invented here.
 */
export type WebformHubLayerDirectoryData = {
  layerBatches: LayerBatchCoreRow[];
  layerHousings: LayerHousingCoreRow[];
};

/**
 * The pig daily form's two extra inputs (owner request 2026-11-04).
 * `sowTagsByGroup` is null when the breeder registry could not be read.
 * `moveHistory` is keyed by the form's group label (a sow group by its
 * 'SOWS · Group n' label); a label is ABSENT when its read failed, which the
 * form shows as "unavailable" rather than "No move recorded". `complete` is
 * false when the newest-N window was full, so older history may exist.
 */
export type WebformHubPigMoveHistory = {dates: string[]; complete: boolean};
export type WebformHubPigContextData = {
  sowTagsByGroup: SowTagsByGroup | null;
  moveHistory: Record<string, WebformHubPigMoveHistory>;
};

/**
 * The same six config rows the /dailys presenter previously loaded on mount.
 * Values remain unknown because the operator-edited jsonb is intentionally
 * narrowed only where each form consumes it.
 */
export type WebformHubConfigData = {
  fullConfig: unknown | null;
  broilerGroups: unknown | null;
  broilerBatchMeta: unknown | null;
  webformSettings: unknown | null;
  activeGroups: unknown | null;
  housingBatchMap: unknown | null;
};

/**
 * The Task webform's only render-time lookup. The RPC deliberately returns
 * only id + display name; availability is then applied as a separate
 * operator-controlled security filter before the presenter receives it.
 */
export type TasksWebformAssignee = {id: string; full_name: string | null};

export type TasksWebformAssigneesData = {assignees: TasksWebformAssignee[]};

/**
 * What view.webform renders its field configuration from.
 *
 * `webformsConfig` stays `unknown` for the same reason WebformsConfigContext
 * handed it out as `unknown`: it is the operator-edited ppp-webforms-v1 jsonb
 * document, whose live shapes the admin panel edits well past anything the
 * seed in lib/defaults.ts describes. PigDailysWebform proves every hop into it
 * with its own `isRecord` narrowing, and that boundary is deliberately left
 * where it is.
 */
export type WebformPigDailysConfigData = {
  webformsConfig: unknown;
};

/**
 * What view.webforms renders its two editors from.
 *
 * Both are app_store documents App's boot published into a context: the
 * ppp-webforms-v1 admin document the /admin Webforms tab EDITS, and the
 * ppp-feed-costs-v1 rates its Feed tab edits. `webformsConfig` stays `unknown`
 * for the reason WebformsConfigContext kept it `unknown` — the admin panel is
 * what edits it past any declared shape — while `feedCosts` carries
 * normalizeFeedCosts' own open record, whose contract is "the five keys are
 * numeric and unknown keys survive".
 */
export type WebformsAdminAppStoreData = {
  webformsConfig: unknown;
  feedCosts: FeedCosts;
};

/**
 * What newsletter.issue.detail resolves with — IssueEditor.load()'s four reads,
 * with its tolerances preserved as data rather than converted into errors.
 */
export type NewsletterIssueDetailData = {
  issue: NewsletterJson | null;
  /** {} when the settings read fails — tolerated today. */
  settings: NewsletterJson;
  /** [] when the recent-published read fails — tolerated today. */
  recentPublished: NewsletterJson[];
  /** [] when the runs read fails — tolerated today. */
  runs: NewsletterJson[];
};

/** id -> profile, the shape every Task Center panel resolves names through. */
export type TaskProfilesById = Record<string, TaskProfile>;

/** The Task Center shell's own prefetch for the New Task modal's dropdown. */
export type TasksCenterProfilesData = {
  profilesById: TaskProfilesById;
  assignableProfilesById: TaskProfilesById;
};

/** My Tasks tab — one atomic load, exactly as its Promise.all is today. */
export type TasksCenterMineData = {
  tasks: TaskInstanceRow[];
  profilesById: TaskProfilesById;
  assignableProfilesById: TaskProfilesById;
};

export type TasksCenterRecurringData = {
  templates: TaskTemplateRow[];
  openInstances: TaskInstanceRow[];
  profilesById: TaskProfilesById;
  assignableProfilesById: TaskProfilesById;
};

export type TasksCenterCompletedData = {
  tasks: TaskInstanceRow[];
  profilesById: TaskProfilesById;
};

export type TasksCenterSystemData = {
  rules: TaskSystemRuleRow[];
  openInstances: TaskInstanceRow[];
  profilesById: TaskProfilesById;
  assignableProfilesById: TaskProfilesById;
};

/**
 * The To Do list RPC envelope, verbatim. Rows stay `unknown` because mig 115
 * builds each one as jsonb inside _todo_item_summary — todoApi refuses to claim
 * a schema it cannot verify, and this resource is not the place to start.
 */
export type TasksTodoListData = {items: unknown[]; completed: unknown[]};

/** The task record page's four reads. */
export type TaskInstanceCoreData = {
  /** null is DATA, not an error: this task does not exist. */
  task: TaskInstanceRow | null;
  profilesById: TaskProfilesById;
  assignableProfilesById: TaskProfilesById;
  photos: TaskInstancePhotoRow[];
};

/** A private task or To Do image after its short-lived URL has been minted. */
export type TaskSignedPhotoUrlData = {url: string};

/** A row returned by the authenticated global Activity timeline RPC. */
export type ActivityLogPageRow = ActivityLogExportRow & Record<string, unknown> & {id: string};

/**
 * What view.feed resolves with — seven values from five tables, and every one
 * of them used to reach /broiler/feed through App.
 *
 * The five row arrays are `unknown[]` and that is not a placeholder to be
 * improved later. BatchesContext, LayerContext and DailysRecentContext all hand
 * these arrays out as `unknown[]`, and BroilerFeedView narrows them ONCE,
 * inside itself, at the boundary that knows which columns it reads. Typing them
 * here would move that claim to a boundary that does not read a single one of
 * those columns. The two blob values keep the shapes BroilerEntry already
 * declared for the props they replace, so the presenter's narrowing is
 * unchanged on both sides.
 */
export type BroilerFeedCoreData = {
  batches: unknown[];
  feedOrders: Record<string, Record<string, unknown>>;
  poultryFeedInventory: Record<string, unknown> | null;
  layerBatches: unknown[];
  layerHousings: unknown[];
  allLayerDailys: unknown[];
  broilerDailys: unknown[];
};

/** A resource's params: plain JSON-serializable values, keyed by name. */
export type ResourceParams = Readonly<Record<string, unknown>>;

// A key segment is whatever survives JSON.stringify, which is what makes a key
// structurally comparable and loggable.
export type QueryKeySegment = string | null;
export type QueryKey = [string, QueryKeySegment[], Record<string, unknown>];

// Params are normalized so key identity does not depend on property order or on
// undefined-vs-absent. Two callers asking for the same thing must produce the
// same key, or they will not dedupe and the cache will hold duplicates.
export function normalizeParams(params: ResourceParams = {}): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k of Object.keys(params).sort()) {
    const v = params[k];
    if (v === undefined) continue;
    out[k] = v;
  }
  return out;
}

// The two durable QueryScope shapes, written out so the union is checkable
// rather than described.
// There is deliberately NO farm id: WCF is single-farm and has no source for
// one, and a key field with no source is isolation that isolates nothing.
export type PublicScope = {readonly kind: 'public'};
export type AuthenticatedScope = {
  readonly kind: 'authenticated';
  readonly subjectId: string;
  readonly effectiveRole: string;
  readonly previewRole: string | null;
};
export type QueryScope = PublicScope | AuthenticatedScope;

export const PUBLIC_SCOPE: PublicScope = Object.freeze({kind: 'public'});

const AUTHENTICATED_FIELDS = ['kind', 'subjectId', 'effectiveRole', 'previewRole'];

/**
 * The auth state this module is handed. `false` is the RESOLVED public state;
 * `null`/`undefined` mean auth has not settled at all, which is deliberately
 * not a scope (see queryScope).
 */
export type AuthStateInput = false | null | undefined | Record<string, unknown>;

// Matches the original `!value || typeof value !== 'object'` guard exactly —
// arrays included — so a malformed scope lands on the same rejection branch it
// always did rather than a new one.
function isObjectLike(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

// Build a QueryScope from the app's auth state. Kept next to the key builder so
// there is exactly one place that decides what a scope is.
//
// Unresolved auth (`authState == null`) is deliberately NOT a scope. It is
// neither public nor authenticated, and inventing a scope for it is how a boot
// read gets cached under a namespace that no later state ever matches again.
// Callers must not query until auth resolves; `resolveSurfaceAccess` already
// requires a loaded profile for the same reason.
export function queryScope(authState: AuthStateInput, rolePreview: unknown = ''): QueryScope {
  if (authState === false) return PUBLIC_SCOPE;
  if (!authState || typeof authState !== 'object') {
    throw new Error('queryScope: auth is unresolved; no query may run before it settles');
  }
  // `authState` is whatever AuthContext currently holds, so the two nested
  // reads below are proved rather than assumed. A non-object `user`/`profile`
  // yields the same `undefined` the optional chain produced before.
  const user = isObjectLike(authState.user) ? authState.user : null;
  const profile = isObjectLike(authState.profile) ? authState.profile : null;
  const subjectId = user?.id ?? authState.id ?? null;
  if (!subjectId) throw new Error('queryScope: an authenticated scope needs a subject id');
  const effectiveRole = authState.role ?? profile?.role ?? null;
  if (!effectiveRole) throw new Error('queryScope: an authenticated scope needs an effective role');
  return Object.freeze({
    kind: 'authenticated',
    subjectId: String(subjectId),
    effectiveRole: String(effectiveRole),
    previewRole: rolePreview ? String(rolePreview) : null,
  });
}

// `queryScope` throws on unresolved auth by design — that is the fail-closed
// contract, not an error to surface. A caller that renders during boot needs to
// ask "is there a scope yet?" without treating "not yet" as a failure, so that
// question lives here beside the constructor rather than being re-implemented
// (differently) by every consumer.
export function scopeOrNull(authState: AuthStateInput, rolePreview?: unknown): QueryScope | null {
  if (authState === undefined || authState === null) return null;
  try {
    return queryScope(authState, rolePreview);
  } catch {
    return null;
  }
}

// A scope is valid or it is an error. There is no permissive default, because
// the permissive default was the defect: `{}` used to key identically to a real
// scope whose fields happened to be null.
// The parameter is `unknown` because validating it IS this function's job: a
// declared QueryScope would let the compiler assume what the runtime is here to
// prove, and every caller in the tree is still JavaScript.
function scopeSegment(scope: unknown): QueryKeySegment[] {
  if (!isObjectLike(scope)) throw new Error('resourceKey: scope is required');
  if (scope.kind === 'public') {
    const extra = Object.keys(scope).filter((k) => k !== 'kind');
    if (extra.length) throw new Error(`resourceKey: a public scope carries no other field (got ${extra.join(', ')})`);
    return ['public'];
  }
  if (scope.kind !== 'authenticated') {
    throw new Error(`resourceKey: scope.kind must be 'authenticated' or 'public' (got ${JSON.stringify(scope.kind)})`);
  }
  // Reject unknown fields rather than ignoring them. This is what stops
  // `farmId` — or any other invented dimension — from being reintroduced and
  // sitting in the scope object looking meaningful while keying nothing.
  const extra = Object.keys(scope).filter((k) => !AUTHENTICATED_FIELDS.includes(k));
  if (extra.length) {
    throw new Error(`resourceKey: unknown scope field(s) ${extra.join(', ')}; the contract defines no farm id`);
  }
  // Read once into locals. The checks below are the contract; binding them
  // keeps what each check proved attached to the value that goes into the key,
  // so the returned segment cannot drift from what was validated.
  const subjectId = scope.subjectId;
  const effectiveRole = scope.effectiveRole;
  const previewRole = scope.previewRole;
  if (typeof subjectId !== 'string' || !subjectId) {
    throw new Error('resourceKey: an authenticated scope needs a non-empty subjectId');
  }
  if (typeof effectiveRole !== 'string' || !effectiveRole) {
    throw new Error('resourceKey: an authenticated scope needs a non-empty effectiveRole');
  }
  if (previewRole !== null && (typeof previewRole !== 'string' || !previewRole)) {
    throw new Error('resourceKey: previewRole must be a non-empty string or null');
  }
  return ['authenticated', subjectId, effectiveRole, previewRole];
}

export function resourceKey(resourceId: string, scope: unknown, params: ResourceParams = {}): QueryKey {
  if (!resourceId) throw new Error('resourceKey: resourceId is required');
  // Nested, so `params` stays at a fixed index and a prefix match on
  // [resourceId] still selects every scope's entries for invalidation.
  return [resourceId, scopeSegment(scope), normalizeParams(params)];
}

// B3.5. The inert key a not-yet-runnable query is parked under.
//
// `useQuery` demands a queryKey on every render, including the renders where
// there is no scope to build one from and no query may run. Something has to go
// in that slot, and the tempting answer — assemble a placeholder array at the
// call site — is exactly the "raw domain queryKey arrays outside the reviewed
// builder" the scope contract forbids, and the exact shape of the defect it
// forbids it for: an inline key is a key with no subject in it.
//
// So the placeholder lives HERE, with the builder, as a CONSTANT rather than a
// second key function. A constant cannot grow a scope-forgetting variant.
//
// It is safe to share across every parked observer because nothing is ever
// fetched or written under it: the runtime passes `enabled: false` AND
// `skipToken` for the query function, and its `refetch` is a no-op until a
// scope exists. It is deliberately unreachable from `resourceKey`, whose scope
// segment only ever begins 'public' or with the authenticated marker — so this
// can never collide with a real key.
export const UNRESOLVED_RESOURCE_KEY = Object.freeze(['route-resource', 'scope-unresolved']);

// Apply an AbortSignal only when the builder exposes one, matching the
// convention already used in the maintenance layer so plain fakes work in tests.
//
// The generic says exactly that: whatever builder comes in comes back out, and
// `abortSignal` is OPTIONAL on it because the fakes are the reason the runtime
// check exists at all. Same shape as cattleCache's, deliberately.
//
// `then` is named as a REQUIRED member, and it carries weight beyond
// documenting that these are thenables: a constraint whose members are all
// optional is a "weak type", and TypeScript then rejects any builder that
// happens to share no property with it. `.maybeSingle()` resolves to exactly
// such a builder — it drops `abortSignal` from its surface — so without a
// required member the animal-detail read would not compile at all.
type MaybeAbortable<Builder> = {then: unknown; abortSignal?: (signal: AbortSignal) => Builder};

const withSignal = <Builder extends MaybeAbortable<Builder>>(builder: Builder, signal?: AbortSignal): Builder =>
  signal && builder && typeof builder.abortSignal === 'function' ? builder.abortSignal(signal) : builder;

// The two PostgREST result shapes these readers consume. Narrower than the
// library's own response union on purpose: only `data` and `error` are read,
// and naming just those keeps the row type the caller pins visible instead of
// buried under count/status members nothing here uses.
type ListResponse<Row> = {data: Row[] | null; error: SupabaseErrorLike | null};
type SingleResponse<Row> = {data: Row | null; error: SupabaseErrorLike | null};

// What these readers actually use off a failure. See cattleCache's note: a
// narrowing stays correct as the library evolves; a hand-copied mirror drifts.
type SupabaseErrorLike = {message: string};

// Throw on a failed read rather than substituting []. A fake-empty result is
// indistinguishable from a genuinely empty farm, and the difference decides
// whether the UI shows "no cattle yet" or an error with Retry.
//
// The row type is a parameter the CALLER pins, because `unwrap` cannot know
// which table it just unwrapped and returning an anonymous row would make the
// six fields of the resource payload mutually interchangeable.
function unwrap<Row>(res: ListResponse<Row>, label: string): Row[] {
  if (res.error) throw new Error(`${label}: ${res.error.message || res.error}`);
  return res.data || [];
}

// ---------------------------------------------------------------------------
// cattle.herds.core — the hub's six independent reads.
//
// Reproduces CattleHerdsView.loadAll exactly, including sort orders, except
// that it returns ONE normalized object instead of calling six setters, and it
// throws instead of blanking all six lists. The existing catch sets every list
// to [] and shows an error, which renders "no cattle" and an error banner at
// the same time; the resource contract makes that a single error state.
// ---------------------------------------------------------------------------
async function queryCattleHerdsCore(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<CattleHerdsCoreData> {
  const [cR, weighIns, calR, brR, orR, trR] = await Promise.all([
    withSignal(sb.from('cattle').select('*').is('deleted_at', null).order('tag'), signal),
    loadCattleWeighIns(sb, {signal}),
    withSignal(sb.from('cattle_calving_records').select('*').order('calving_date', {ascending: false}), signal),
    withSignal(sb.from('cattle_breeds').select('*').order('label'), signal),
    withSignal(sb.from('cattle_origins').select('*').order('label'), signal),
    withSignal(sb.from('cattle_transfers').select('*').order('transferred_at', {ascending: false}), signal),
  ]);

  return {
    cattle: unwrap<CattleRow>(cR, 'cattle'),
    weighIns: weighIns || [],
    calvingRecords: unwrap<CattleCalvingRecordRow>(calR, 'cattle_calving_records'),
    breedOptions: unwrap<CattleBreedRow>(brR, 'cattle_breeds'),
    originOptions: unwrap<CattleOriginRow>(orR, 'cattle_origins'),
    transfers: unwrap<CattleTransferRow>(trR, 'cattle_transfers'),
  };
}

// ---------------------------------------------------------------------------
// cattle.home.core — cattle dashboard rows plus the forecast tile inputs.
// ---------------------------------------------------------------------------
async function queryCattleHomeCore(
  sb: SupabaseClient,
  params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<CattleHomeCoreData> {
  const cutoff120 =
    typeof params?.cutoff120 === 'string'
      ? params.cutoff120
      : new Date(Date.now() - 120 * 86400000).toISOString().slice(0, 10);
  const settings = Promise.resolve(
    withSignal(sb.from('cattle_forecast_settings').select('*').eq('id', 'global').maybeSingle(), signal),
  )
    .then((res) => {
      if (res.error) throw new Error('cattle_forecast_settings: ' + res.error.message);
      return forecastSettingsFromRow((res.data || null) as ForecastSettingsRow | null);
    })
    .catch((error) => {
      if (signal?.aborted) throw error;
      return null;
    });
  const includes = Promise.resolve(withSignal(sb.from('cattle_forecast_heifer_includes').select('cattle_id'), signal))
    .then((res) => {
      if (res.error) throw new Error('cattle_forecast_heifer_includes: ' + res.error.message);
      return new Set((res.data || []).map((row) => row.cattle_id));
    })
    .catch((error) => {
      if (signal?.aborted) throw error;
      return new Set<string>();
    });
  const hidden = Promise.resolve(
    withSignal(sb.from('cattle_forecast_hidden').select('cattle_id, month_key, hidden_at, hidden_by'), signal),
  )
    .then((res) => {
      if (res.error) throw new Error('cattle_forecast_hidden: ' + res.error.message);
      return (res.data || []) as ForecastHiddenSelectedRow[];
    })
    .catch((error) => {
      if (signal?.aborted) throw error;
      return [] as ForecastHiddenSelectedRow[];
    });
  const [
    cattle,
    dailys,
    weighIns,
    calvingRecords,
    nutritionTargets,
    processingBatches,
    forecastSettings,
    forecastIncludes,
    forecastHidden,
  ] = await Promise.all([
    withSignal(sb.from('cattle').select('*').is('deleted_at', null), signal),
    withSignal(sb.from('cattle_dailys').select('*').is('deleted_at', null).gte('date', cutoff120), signal),
    loadCattleWeighIns(sb, {signal}),
    withSignal(sb.from('cattle_calving_records').select('*'), signal),
    withSignal(sb.from('cattle_nutrition_targets').select('*'), signal),
    withSignal(sb.from('cattle_processing_batches').select('*'), signal),
    settings,
    includes,
    hidden,
  ]);

  return {
    cattle: unwrap<CattleRow>(cattle, 'cattle'),
    dailys: unwrap<CattleRow>(dailys, 'cattle_dailys'),
    weighIns,
    calvingRecords: unwrap<CattleCalvingRecordRow>(calvingRecords, 'cattle_calving_records'),
    nutritionTargets: unwrap<CattleRow>(nutritionTargets, 'cattle_nutrition_targets'),
    processingBatches: unwrap<CattleRow>(processingBatches, 'cattle_processing_batches'),
    forecastSettings,
    forecastIncludes,
    forecastHidden,
  };
}

// ---------------------------------------------------------------------------
// cattle.batches.core — the processing-batch index plus forecast inputs.
// ---------------------------------------------------------------------------
async function queryCattleBatchesCore(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<CattleBatchesCoreData> {
  let forecastInputsReliable = true;
  const settings = Promise.resolve(
    withSignal(sb.from('cattle_forecast_settings').select('*').eq('id', 'global').maybeSingle(), signal),
  )
    .then((res) => {
      if (res.error) throw new Error('cattle_forecast_settings: ' + res.error.message);
      return forecastSettingsFromRow((res.data || null) as ForecastSettingsRow | null);
    })
    .catch((error) => {
      forecastInputsReliable = false;
      if (signal?.aborted) throw error;
      return null;
    });
  const includes = Promise.resolve(withSignal(sb.from('cattle_forecast_heifer_includes').select('cattle_id'), signal))
    .then((res) => {
      if (res.error) throw new Error('cattle_forecast_heifer_includes: ' + res.error.message);
      return new Set((res.data || []).map((row) => row.cattle_id));
    })
    .catch((error) => {
      forecastInputsReliable = false;
      if (signal?.aborted) throw error;
      return new Set<string>();
    });
  const hidden = Promise.resolve(
    withSignal(sb.from('cattle_forecast_hidden').select('cattle_id, month_key, hidden_at, hidden_by'), signal),
  )
    .then((res) => {
      if (res.error) throw new Error('cattle_forecast_hidden: ' + res.error.message);
      return (res.data || []) as ForecastHiddenSelectedRow[];
    })
    .catch((error) => {
      forecastInputsReliable = false;
      if (signal?.aborted) throw error;
      return [] as ForecastHiddenSelectedRow[];
    });
  const [batches, cattle, weighIns, calvingRecords, forecastSettings, forecastIncludes, forecastHidden] =
    await Promise.all([
      withSignal(
        sb.from('cattle_processing_batches').select('*').order('actual_process_date', {ascending: false}),
        signal,
      ),
      withSignal(sb.from('cattle').select('*').is('deleted_at', null), signal),
      loadCattleWeighIns(sb, {signal}),
      withSignal(sb.from('cattle_calving_records').select('*'), signal),
      settings,
      includes,
      hidden,
    ]);

  return {
    batches: unwrap<CattleRow>(batches, 'cattle_processing_batches'),
    cattle: unwrap<CattleRow>(cattle, 'cattle'),
    weighIns,
    calvingRecords: unwrap<CattleCalvingRecordRow>(calvingRecords, 'cattle_calving_records'),
    forecastSettings,
    forecastIncludes,
    forecastHidden,
    forecastInputsReliable,
  };
}

// ---------------------------------------------------------------------------
// cattle.weighins.list — the cattle weigh-in list's two read obligations.
// ---------------------------------------------------------------------------
// A maybeSingle() read: a missing row is `null` DATA, not an error. Collapsing
// the two would turn "this animal does not exist" into "the page failed", which
// are different screens and different user actions.
function unwrapSingle<Row>(res: SingleResponse<Row>, label: string): Row | null {
  if (res.error) throw new Error(`${label}: ${res.error.message || res.error}`);
  return res.data ?? null;
}

// ---------------------------------------------------------------------------
// cattle.animal.detail — the record page's seven reads.
//
// Keyed on cattleId, which makes an A -> B navigation a KEY CHANGE rather than
// a refresh. That distinction is the whole reason this is a separate resource:
// on a key change the old subscription is released and data resets, so a late
// response for animal A can never paint into animal B's page. A refresh may
// keep stale data on screen; a key change may not.
// ---------------------------------------------------------------------------
async function queryCattleAnimalDetail(
  sb: SupabaseClient,
  params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<CattleAnimalDetailData> {
  const cattleId = params?.cattleId;
  if (!cattleId) throw new Error('cattle.animal.detail: cattleId is required');

  const [cR, allCattle, weighIns, calR, brR, orR, batchR] = await Promise.all([
    withSignal(sb.from('cattle').select('*').eq('id', cattleId).is('deleted_at', null).maybeSingle(), signal),
    withSignal(sb.from('cattle').select('*').is('deleted_at', null).order('tag'), signal),
    loadCattleWeighIns(sb, {signal}),
    withSignal(sb.from('cattle_calving_records').select('*').order('calving_date', {ascending: false}), signal),
    withSignal(sb.from('cattle_breeds').select('*').order('label'), signal),
    withSignal(sb.from('cattle_origins').select('*').order('label'), signal),
    // Named columns, not '*': this table is wide and only these four are read.
    withSignal(sb.from('cattle_processing_batches').select('id,name,actual_process_date,planned_process_date'), signal),
  ]);

  return {
    cow: unwrapSingle<CattleRow>(cR, 'cattle'),
    cattle: unwrap<CattleRow>(allCattle, 'cattle list'),
    weighIns: weighIns || [],
    calvingRecords: unwrap<CattleCalvingRecordRow>(calR, 'cattle_calving_records'),
    breedOptions: unwrap<CattleBreedRow>(brR, 'cattle_breeds'),
    originOptions: unwrap<CattleOriginRow>(orR, 'cattle_origins'),
    processingBatches: unwrap<CattleProcessingBatchRow>(batchR, 'cattle_processing_batches'),
  };
}

// ---------------------------------------------------------------------------
// cattle.daily.detail — the daily record page's two reads.
//
// `recordId`, `recR`, and `fiR` deliberately match CattleDailyPage.loadAll.
// The physical-call inventory includes identifier argument shapes, so changing
// those locals would make an otherwise identical move look like a new read.
// ---------------------------------------------------------------------------
async function queryCattleDailyDetail(
  sb: SupabaseClient,
  params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<CattleDailyDetailData> {
  const recordId = params?.recordId;
  if (!recordId) throw new Error('cattle.daily.detail: recordId is required');

  const [recR, fiR] = await Promise.all([
    withSignal(sb.from('cattle_dailys').select('*').eq('id', recordId).is('deleted_at', null).maybeSingle(), signal),
    withSignal(sb.from('cattle_feed_inputs').select('*').order('category').order('name'), signal),
  ]);

  return {
    record: unwrapSingle<CattleRow>(recR, 'cattle_dailys'),
    feedInputs: unwrap<CattleRow>(fiR, 'cattle_feed_inputs'),
  };
}

// ---------------------------------------------------------------------------
// cattle.dailys.index â€” full-history list pages plus edit-form inputs.
// ---------------------------------------------------------------------------
// The list keeps the hub's existing 1,000-row page size and auto-append
// behavior, but its cursor is now owned by the scoped infinite resource. The
// feed-input read stays separate because it is fail-soft: an input-list outage
// must not turn a readable history into a false load failure.
const CATTLE_DAILYS_PAGE_SIZE = 1000;

async function queryCattleDailysList(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  pageParam: unknown,
  signal?: AbortSignal,
): Promise<PaginatedPage<CattleRow, unknown>> {
  const page = typeof pageParam === 'number' && Number.isSafeInteger(pageParam) && pageParam >= 0 ? pageParam : 0;
  const rows = unwrap<CattleRow>(
    await withSignal(
      sb
        .from('cattle_dailys')
        .select('*')
        .is('deleted_at', null)
        .order('date', {ascending: false})
        .order('submitted_at', {ascending: false})
        .range(page * CATTLE_DAILYS_PAGE_SIZE, (page + 1) * CATTLE_DAILYS_PAGE_SIZE - 1),
      signal,
    ),
    'cattle_dailys',
  );
  return {rows, nextPage: rows.length === CATTLE_DAILYS_PAGE_SIZE ? page + 1 : null};
}

async function queryCattleDailysFormInputs(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<CattleRow[]> {
  const result = await withSignal(sb.from('cattle_feed_inputs').select('*').order('category').order('name'), signal);
  return unwrap<CattleRow>(result, 'cattle_feed_inputs');
}

// Saved views are visible render data too. Keep their historic independent
// load (and action-time refetch) separate from the daily-list Retry key: a
// failed list Retry never reloaded this picker, and a saved-view write must not
// start a new full-history page sequence.
async function queryCattleDailysSavedViews(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<CattleRow[]> {
  const result = await withSignal(
    sb
      .from('app_saved_views')
      .select('id, surface_key, name, visibility, view_state, owner_profile_id, created_at, updated_at')
      .eq('surface_key', 'cattle.dailys')
      .order('name', {ascending: true}),
    signal,
  );
  return unwrap<CattleRow>(result, 'app_saved_views');
}

// ---------------------------------------------------------------------------
// sheep.dailys.index — full-history list pages plus edit-form inputs.
// ---------------------------------------------------------------------------
// The same two reads SheepDailysHub owned in one mount effect, moved whole: the
// 1,000-row page size, the deleted_at filter and the date/submitted_at DESC
// order are unchanged, so only the cursor's owner moved. `reload` is a page KEY
// param on the list for the reason cattle.dailys.list already records — a Retry
// must start a NEW complete sequence rather than same-key refetching a list that
// is already partial.
//
// THEY STAY TWO RESOURCES BECAUSE THEIR FAILURE POSTURES ALWAYS DIFFERED.
//
// The list read fails the page closed: `setLoadError(...)` plus a Retry. The
// input read was written `.then(({data}) => { if (!cancelled && data) ... })`
// followed by `.catch(() => {})`, so an input-list outage left the history
// readable and only cost the edit dropdowns their fresh options. Folding the two
// into one resource would promote that silent degradation into a false
// "Could not load daily reports", which is a behaviour change, not a refactor.
//
// The herd_scope narrowing deliberately did NOT come with the read. It is a
// presenter decision about which loaded rows a sheep flock may CHOOSE, and
// cattle_sheep_inputs_consistency pins the unfiltered array as the thing
// save-time `.find()` resolves a historical inactive feed against.
const SHEEP_DAILYS_PAGE_SIZE = 1000;

async function querySheepDailysList(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  pageParam: unknown,
  signal?: AbortSignal,
): Promise<PaginatedPage<SheepRow, unknown>> {
  const page = typeof pageParam === 'number' && Number.isSafeInteger(pageParam) && pageParam >= 0 ? pageParam : 0;
  const rows = unwrap<SheepRow>(
    await withSignal(
      sb
        .from('sheep_dailys')
        .select('*')
        .is('deleted_at', null)
        .order('date', {ascending: false})
        .order('submitted_at', {ascending: false})
        .range(page * SHEEP_DAILYS_PAGE_SIZE, (page + 1) * SHEEP_DAILYS_PAGE_SIZE - 1),
      signal,
    ),
    'sheep_dailys',
  );
  return {rows, nextPage: rows.length === SHEEP_DAILYS_PAGE_SIZE ? page + 1 : null};
}

async function querySheepDailysFormInputs(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<CattleRow[]> {
  const result = await withSignal(sb.from('cattle_feed_inputs').select('*').order('category').order('name'), signal);
  return unwrap<CattleRow>(result, 'cattle_feed_inputs');
}

// ---------------------------------------------------------------------------
// sheep.daily.detail — the daily record page's two reads.
// ---------------------------------------------------------------------------
async function querySheepDailyDetail(
  sb: SupabaseClient,
  params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<SheepDailyDetailData> {
  const recordId = params?.recordId;
  if (!recordId) throw new Error('sheep.daily.detail: recordId is required');

  const [recR, fiR] = await Promise.all([
    withSignal(sb.from('sheep_dailys').select('*').eq('id', recordId).is('deleted_at', null).maybeSingle(), signal),
    withSignal(sb.from('cattle_feed_inputs').select('*').order('category').order('name'), signal),
  ]);

  return {
    record: unwrapSingle<SheepRow>(recR, 'sheep_dailys'),
    feedInputs: unwrap<CattleRow>(fiR, 'cattle_feed_inputs'),
  };
}

// ---------------------------------------------------------------------------
// cattle.batch.detail — the persisted processing-batch record page's reads.
//
// The local identifiers are intentionally the page's `batchId`, `bR`, `cR`,
// and `allR`, preserving the retired/add call-site fingerprint pair.
// ---------------------------------------------------------------------------
async function queryCattleBatchDetail(
  sb: SupabaseClient,
  params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<CattleBatchDetailData> {
  const batchId = params?.batchId;
  if (!batchId) throw new Error('cattle.batch.detail: batchId is required');

  const [bR, cR, allR] = await Promise.all([
    withSignal(sb.from('cattle_processing_batches').select('*').eq('id', batchId).maybeSingle(), signal),
    withSignal(sb.from('cattle').select('*').is('deleted_at', null), signal),
    withSignal(sb.from('cattle_processing_batches').select('id, name, status'), signal),
  ]);

  return {
    batch: unwrapSingle<CattleRow>(bR, 'cattle_processing_batches'),
    cattle: unwrap<CattleRow>(cR, 'cattle'),
    allBatches: unwrap<CattleRow>(allR, 'cattle_processing_batches list'),
  };
}

// ---------------------------------------------------------------------------
// cattle.forecast.bundle — shared, scope-keyed canonical forecast inputs.
//
// This is deliberately separate from the broad forecast editor: its query is
// pure and fail-closed, while that editor currently performs a cache write in
// its load path and maintains local input mirrors after high-frequency writes.
// Both forecast-only batch detail and persisted scheduled batch detail consume
// this one bundle and compose it through forecastFromBundle/projectPlannedRoster.
// ---------------------------------------------------------------------------
async function queryCattleForecastBundle(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<CattleForecastBundleData> {
  const [cattleR, weighIns, settingsR, includesR, hiddenR, batchesR] = await Promise.all([
    withSignal(sb.from('cattle').select('*').is('deleted_at', null), signal),
    loadCattleWeighIns(sb, {signal}),
    withSignal(sb.from('cattle_forecast_settings').select('*').eq('id', 'global').maybeSingle(), signal),
    withSignal(sb.from('cattle_forecast_heifer_includes').select('cattle_id'), signal),
    withSignal(sb.from('cattle_forecast_hidden').select('cattle_id, month_key, hidden_at, hidden_by'), signal),
    withSignal(sb.from('cattle_processing_batches').select('*'), signal),
  ]);

  const batches = unwrap<CattleRow>(batchesR, 'cattle_processing_batches');
  return {
    cattle: unwrap<CattleRow>(cattleR, 'cattle'),
    weighIns: weighIns || [],
    settings: forecastSettingsFromRow(unwrapSingle<ForecastSettingsRow>(settingsR, 'cattle_forecast_settings')),
    includes: new Set(
      unwrap<{cattle_id: string}>(includesR, 'cattle_forecast_heifer_includes').map((row) => row.cattle_id),
    ),
    hidden: unwrap<ForecastHiddenSelectedRow>(hiddenR, 'cattle_forecast_hidden'),
    batches,
    realBatches: batches.filter((batch) => batch.status === 'active' || batch.status === 'complete'),
    scheduledBatches: batches.filter((batch) => batch.status === 'scheduled'),
  };
}

// ---------------------------------------------------------------------------
// cattle.forecast.core — the forecast editor's complete render input.
//
// The editor has a wider read set than the shared batch projection: its
// Include Heifers modal expands CowDetail rows and therefore needs calving
// history, comments, and breed/origin options as well. Keeping this separate
// prevents a batch-detail surface from fetching editor-only data, while moving
// every render read out of CattleForecastView and under the same scoped,
// abortable resource contract as the rest of the route layer.
// ---------------------------------------------------------------------------
async function queryCattleForecastCore(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<CattleForecastCoreData> {
  const [cattleR, weighIns, calvingR, commentsR, breedsR, originsR, batchesR, settingsR, includesR, hiddenR] =
    await Promise.all([
      withSignal(sb.from('cattle').select('*').is('deleted_at', null).order('tag'), signal),
      loadCattleWeighIns(sb, {signal}),
      withSignal(sb.from('cattle_calving_records').select('*').order('calving_date', {ascending: false}), signal),
      withSignal(sb.from('cattle_comments').select('*').order('created_at', {ascending: false}), signal),
      withSignal(sb.from('cattle_breeds').select('*').order('label'), signal),
      withSignal(sb.from('cattle_origins').select('*').order('label'), signal),
      withSignal(
        sb.from('cattle_processing_batches').select('*').order('actual_process_date', {ascending: false}),
        signal,
      ),
      withSignal(sb.from('cattle_forecast_settings').select('*').eq('id', 'global').maybeSingle(), signal),
      withSignal(sb.from('cattle_forecast_heifer_includes').select('cattle_id'), signal),
      withSignal(sb.from('cattle_forecast_hidden').select('cattle_id, month_key, hidden_at, hidden_by'), signal),
    ]);

  return {
    cattle: unwrap<CattleRow>(cattleR, 'cattle'),
    weighIns,
    calvingRecords: unwrap<CattleCalvingRecordRow>(calvingR, 'cattle_calving_records'),
    comments: unwrap<CattleRow>(commentsR, 'cattle_comments'),
    breedOptions: unwrap<CattleBreedRow>(breedsR, 'cattle_breeds'),
    originOptions: unwrap<CattleOriginRow>(originsR, 'cattle_origins'),
    processingBatches: unwrap<CattleRow>(batchesR, 'cattle_processing_batches'),
    settings: forecastSettingsFromRow(unwrapSingle<ForecastSettingsRow>(settingsR, 'cattle_forecast_settings')),
    includes: new Set(
      unwrap<{cattle_id: string}>(includesR, 'cattle_forecast_heifer_includes').map((row) => row.cattle_id),
    ),
    hidden: unwrap<ForecastHiddenSelectedRow>(hiddenR, 'cattle_forecast_hidden'),
  };
}

// ---------------------------------------------------------------------------
// cattle.weighins.list — the cattle weigh-in list's two reads.
// ---------------------------------------------------------------------------
// Reproduces CattleWeighInsView.loadAll exactly, including its session sort and
// all-or-nothing failure path. The uncached cattle weigh-in primitive is used
// here so the route resource owns scope-keying and cancellation rather than
// inheriting the module-global 30-second cache.
async function queryCattleWeighInsList(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<CattleWeighInsListData> {
  const [sR, entries] = await Promise.all([
    withSignal(
      sb
        .from('weigh_in_sessions')
        .select('*')
        .eq('species', 'cattle')
        .order('date', {ascending: false})
        .order('started_at', {ascending: false}),
      signal,
    ),
    loadCattleWeighIns(sb, {signal}),
  ]);
  if (sR.error) throw new Error('weigh_in_sessions: ' + (sR.error.message || sR.error));

  const bySession: Record<string, WeighInRow[]> = {};
  (entries || []).forEach((entry) => {
    const sessionId = String(entry.session_id);
    (bySession[sessionId] ??= []).push(entry);
  });
  return {sessions: sR.data || [], entries: bySession};
}

// ---------------------------------------------------------------------------
// sheep.weighins.list — the sheep weigh-in list's two reads.
// ---------------------------------------------------------------------------
// Reproduces SheepWeighInsView.loadAll exactly, including its session sort and
// all-or-nothing failure path. The uncached sheep weigh-in primitive is used
// here so the route resource owns scope-keying and cancellation rather than
// inheriting the module-global 30-second cache.
async function querySheepWeighInsList(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<SheepWeighInsListData> {
  const [sR, eAll] = await Promise.all([
    withSignal(
      sb
        .from('weigh_in_sessions')
        .select('*')
        .eq('species', 'sheep')
        .order('date', {ascending: false})
        .order('started_at', {ascending: false}),
      signal,
    ),
    loadSheepWeighIns(sb, {signal}),
  ]);
  if (sR.error) throw new Error('weigh_in_sessions: ' + (sR.error.message || sR.error));

  const m: Record<string, SheepWeighInListEntry[]> = {};
  // The page groups the route-owned rows by session before deriving filters,
  // totals, exports, and visible-order navigation. Keep both the grouping and
  // the entry order from loadSheepWeighIns intact.
  ((eAll || []) as SheepWeighInListEntry[]).forEach((e) => {
    if (!m[e.session_id]) m[e.session_id] = [];
    m[e.session_id].push(e);
  });
  return {sessions: sR.data || [], entries: m};
}

// ---------------------------------------------------------------------------
// livestock.weighins.list — the shared broiler/pig session census.
// ---------------------------------------------------------------------------
// The legacy component used this exact session query and delegated the entry
// census to a session-windowed wcfSelectAll helper. Keep both properties here:
// individual PostgREST pages stay bounded and deterministic, while an error on
// EITHER the first or a later page rejects the whole list rather than rendering
// a partial history as if it were complete.
const LIVESTOCK_WEIGH_IN_SESSION_WINDOW_SIZE = 200;

function chunkLivestockWeighInSessionIds(ids: string[], size: number): string[][] {
  const windows: string[][] = [];
  for (let index = 0; index < ids.length; index += size) windows.push(ids.slice(index, index + size));
  return windows;
}

async function loadLivestockWeighInEntriesBySession(
  sb: SupabaseClient,
  sessionIds: string[],
  signal?: AbortSignal,
): Promise<Record<string, LivestockWeighInEntryRow[]>> {
  if (sessionIds.length === 0) return {};

  const all: LivestockWeighInEntryRow[] = [];
  for (const windowIds of chunkLivestockWeighInSessionIds(sessionIds, LIVESTOCK_WEIGH_IN_SESSION_WINDOW_SIZE)) {
    // wcfSelectAll stops at a failed page and returns the rows it already has.
    // Capture the response error outside that helper so partial results never
    // cross this resource boundary as a complete session history.
    const pageErrorSlot: SupabaseErrorLike[] = [];
    const rows = await wcfSelectAll<LivestockWeighInEntryRow>(async (from, to) => {
      const res = (await withSignal(
        sb
          .from('weigh_ins')
          .select('*')
          .in('session_id', windowIds)
          .order('entered_at', {ascending: true})
          .order('id', {ascending: true})
          .range(from, to),
        signal,
      )) as ListResponse<LivestockWeighInEntryRow>;
      if (res.error) pageErrorSlot.push(res.error);
      return res;
    });
    const pageError = pageErrorSlot.at(-1);
    if (pageError) throw new Error('weigh_ins: ' + (pageError.message || pageError));
    all.push(...rows);
  }

  const entries: Record<string, LivestockWeighInEntryRow[]> = {};
  for (const entry of all) {
    if (!entries[entry.session_id]) entries[entry.session_id] = [];
    entries[entry.session_id].push(entry);
  }
  return entries;
}

function livestockWeighInSpecies(params: ResourceParams | undefined): 'broiler' | 'pig' {
  const species = params?.species;
  if (species !== 'broiler' && species !== 'pig') {
    throw new Error('livestock.weighins.list: species must be broiler or pig');
  }
  return species;
}

async function queryLivestockWeighInsList(
  sb: SupabaseClient,
  params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<LivestockWeighInsListData> {
  const species = livestockWeighInSpecies(params);
  const sR = (await withSignal(
    sb
      .from('weigh_in_sessions')
      .select('*')
      .eq('species', species)
      .order('date', {ascending: false})
      .order('started_at', {ascending: false}),
    signal,
  )) as ListResponse<LivestockWeighInSessionRow>;
  if (sR.error) throw new Error('weigh_in_sessions: ' + (sR.error.message || sR.error));

  const sessionRows = sR.data || [];
  const ids = sessionRows.map((s) => s.id);
  const entries = await loadLivestockWeighInEntriesBySession(sb, ids, signal);
  return {sessions: sessionRows, entries};
}

// ---------------------------------------------------------------------------
// pig.weighins.metrics — nonblocking per-session pig projections.
// ---------------------------------------------------------------------------
// The legacy list intentionally rendered its session/entry table BEFORE this
// fanout settled, and it treated every individual RPC failure as an unavailable
// metric rather than failing the list. It is therefore a separate route
// resource: folding it into livestock.weighins.list would make the required
// list wait for every metric request and visibly change the loading boundary.
function pigMetricSessionIds(params: ResourceParams | undefined): string[] {
  const ids = params?.sessionIds;
  if (!Array.isArray(ids) || ids.some((id) => typeof id !== 'string' || !id)) {
    throw new Error('pig.weighins.metrics: sessionIds must be non-empty strings');
  }
  return ids as string[];
}

async function queryPigWeighInsMetrics(
  sb: SupabaseClient,
  params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<PigWeighInsMetricsData> {
  const sessionIds = pigMetricSessionIds(params);
  const results = await Promise.all(
    sessionIds.map(async (sessionId) => {
      try {
        const {data, error} = (await withSignal(sb.rpc('pig_session_metrics', {session_id_in: sessionId}), signal)) as {
          data: PigSessionMetricsRow | null;
          error: SupabaseErrorLike | null;
        };
        if (error) {
          // A cancellation must remain a cancellation so Query drops it. A
          // normal RPC failure retains the legacy per-session fallback.
          if (signal?.aborted) throw error;
          return {id: sessionId, data: {available: false} as PigSessionMetricsRow};
        }
        return {id: sessionId, data: data || ({available: false} as PigSessionMetricsRow)};
      } catch (error) {
        if (signal?.aborted) throw error;
        return {id: sessionId, data: {available: false} as PigSessionMetricsRow};
      }
    }),
  );

  const metrics: Record<string, PigSessionMetricsRow> = {};
  for (const result of results) metrics[result.id] = result.data;
  return {metrics};
}

// ---------------------------------------------------------------------------
// weighin.session.* — the /weigh-in-sessions/<id> record page's nine reads.
// ---------------------------------------------------------------------------
// THREE RESOURCES, AND THE SPLIT IS THE POINT.
//
//   weighin.session.record     the session row. It decides the species, and the
//                              species decides everything below it, so it
//                              cannot share a read with them.
//   weighin.session.detail     everything WeighInSessionPage.loadAll read once
//                              it had a species it supports AND the caller had
//                              cleared the program_access gate. Its `species`
//                              param is what makes those branches reachable at
//                              all, and what keeps a cattle response from ever
//                              painting into a pig mount.
//   weighin.session.pigMetrics the pig metrics RPC and the prior session it
//                              names. Split out because it has its OWN refresh:
//                              a per-entry autosave re-runs refreshPigMetrics
//                              alone, and folding it into the detail resource
//                              would make one weight edit re-read the animal
//                              directory and re-seed every row's draft.
//
// Cattle/sheep history inputs fail closed and the page offers Retry. The other
// reads retain the original loader's soft-result behavior. Cancellation stays
// fatal in catches, so Query does not treat an aborted request as a result.
//
// TWO PLACES WHERE THIS IS NOT LITERALLY IDENTICAL, RECORDED RATHER THAN HIDDEN:
//
//   * the species weigh-in history uses the UNCACHED, signal-aware primitive,
//     as cattle.weighins.list does and for the same three reasons cattleCache's
//     own header gives. The cached wrapper it replaces answers a failed page
//     with the last-good module-global cache or a partial page. History failures
//     now reject the detail resource so its consumer can show an error and Retry.
//     Empty or partial history must not be presented as a reliable prior-weight
//     input, and a resource may not serve another subject's cached rows.
//   * the broiler ppp-v4 read is deliberately NOT wrapped. loadAll did not wrap
//     it either, and its rejection left the page's spinner up forever. That is a
//     live latent defect, and healing it here would change a broiler session's
//     behaviour under cover of a refactor — it is reported, not fixed.
const NO_WEIGHIN_SESSION_RECORD: WeighInSessionRecordData = Object.freeze({record: null});
const NO_WEIGHIN_SESSION_ENTRIES: WeighInSessionEntryRow[] = [];
const NO_WEIGHIN_SESSION_ANIMALS: WeighInSessionAnimalRow[] = [];
const NO_WEIGHIN_SESSION_SPECIES_SESSIONS: WeighInSessionRecordRow[] = [];
const NO_WEIGHIN_SESSION_FEEDER_GROUPS: unknown[] = [];
const NO_WEIGHIN_SESSION_BROILER_BATCH_RECS: unknown[] = [];

// The abort escape hatch for the one detail branch that returns mid-function. A
// frozen constant rather than an inline object literal, so the empty payload has
// one definition and cannot drift from the fields it stands in for.
const NO_WEIGHIN_SESSION_DETAIL_ABORTED: WeighInSessionDetailData = Object.freeze({
  entries: NO_WEIGHIN_SESSION_ENTRIES,
  animals: NO_WEIGHIN_SESSION_ANIMALS,
  allSessions: NO_WEIGHIN_SESSION_SPECIES_SESSIONS,
  allEntries: NO_WEIGHIN_SESSION_ENTRIES,
  feederGroups: NO_WEIGHIN_SESSION_FEEDER_GROUPS,
  broilerBatchRecs: NO_WEIGHIN_SESSION_BROILER_BATCH_RECS,
});

function weighInSessionId(params: ResourceParams | undefined, resourceId: string): string {
  const sessionId = params?.sessionId;
  if (typeof sessionId !== 'string' || !sessionId) throw new Error(`${resourceId}: sessionId is required`);
  return sessionId;
}

// The four species this record page renders. loadAll returned early for anything
// else, which is why the detail resource refuses to run for one: the page has no
// branch that could display the result.
function weighInSessionSpecies(params: ResourceParams | undefined): 'cattle' | 'sheep' | 'pig' | 'broiler' {
  const species = params?.species;
  if (species !== 'cattle' && species !== 'sheep' && species !== 'pig' && species !== 'broiler') {
    throw new Error('weighin.session.detail: species must be cattle, sheep, pig or broiler');
  }
  return species;
}

// A named reader rather than a bare try/catch at each site, because the empty
// value must be a named CONSTANT: query_scope_contract rejects an array literal
// returned from anything in src/app/data, since a raw array leaving this layer
// is indistinguishable from a query key that skipped resourceKey.
async function softWeighInSessionRows<Row>(
  read: () => PromiseLike<ListResponse<Row>>,
  empty: Row[],
  signal?: AbortSignal,
): Promise<Row[]> {
  try {
    const response = await read();
    if (signal?.aborted) return empty;
    return response.data || empty;
  } catch (error) {
    if (signal?.aborted) throw error;
    return empty;
  }
}

async function queryWeighInSessionRecord(
  sb: SupabaseClient,
  params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<WeighInSessionRecordData> {
  const sessionId = weighInSessionId(params, 'weighin.session.record');
  try {
    // The projection literal is INLINE rather than a named constant, and the
    // parameter is still called `sessionId`: both are what keep this site's
    // fingerprint identical to the loadAll read it replaces, which is the
    // evidence that the projection, the eq(id) shape and the .single() posture
    // moved unchanged rather than were rewritten.
    const response = (await withSignal(
      sb
        .from('weigh_in_sessions')
        .select('id, species, herd, batch_id, date, team_member, status, started_at, completed_at, notes, broiler_week')
        .eq('id', sessionId)
        .single(),
      signal,
    )) as SingleResponse<WeighInSessionRecordRow>;
    if (signal?.aborted) return NO_WEIGHIN_SESSION_RECORD;
    // `.single()` answers a missing row with an error and a null `data`, and
    // loadAll read only `data`. So a deleted session and an unreachable one both
    // resolve to "no record", which the page renders as "Session not found." —
    // exactly the frame it renders today.
    return {record: response.data || null};
  } catch (error) {
    if (signal?.aborted) throw error;
    return NO_WEIGHIN_SESSION_RECORD;
  }
}

async function queryWeighInSessionDetail(
  sb: SupabaseClient,
  params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<WeighInSessionDetailData> {
  const sessionId = weighInSessionId(params, 'weighin.session.detail');
  const sp = weighInSessionSpecies(params);

  const entries = await softWeighInSessionRows<WeighInSessionEntryRow>(
    () =>
      withSignal(sb.from('weigh_ins').select('*').eq('session_id', sessionId), signal) as PromiseLike<
        ListResponse<WeighInSessionEntryRow>
      >,
    NO_WEIGHIN_SESSION_ENTRIES,
    signal,
  );

  let animals = NO_WEIGHIN_SESSION_ANIMALS;
  let allSessions = NO_WEIGHIN_SESSION_SPECIES_SESSIONS;
  let allEntries = NO_WEIGHIN_SESSION_ENTRIES;
  if (sp === 'cattle' || sp === 'sheep') {
    // The animal directory and the species session census stay one round trip,
    // as loadAll issued them; the weigh-in history stays sequential after them,
    // also as loadAll issued it.
    const [animalResponse, sessionResponse] = await Promise.all([
      withSignal(
        sp === 'cattle'
          ? sb.from('cattle').select('*').is('deleted_at', null)
          : sb.from('sheep').select('*').is('deleted_at', null),
        signal,
      ) as PromiseLike<ListResponse<WeighInSessionAnimalRow>>,
      withSignal(
        sb.from('weigh_in_sessions').select('*').eq('species', sp).order('date', {ascending: false}),
        signal,
      ) as PromiseLike<ListResponse<WeighInSessionRecordRow>>,
    ]);
    if (signal?.aborted) return NO_WEIGHIN_SESSION_DETAIL_ABORTED;
    animals = unwrap(animalResponse, `${sp} history animals`);
    allSessions = unwrap(sessionResponse, `${sp} history sessions`);
    const history = sp === 'cattle' ? await loadCattleWeighIns(sb, {signal}) : await loadSheepWeighIns(sb, {signal});
    allEntries = (history || NO_WEIGHIN_SESSION_ENTRIES) as WeighInSessionEntryRow[];
  }

  let feederGroups = NO_WEIGHIN_SESSION_FEEDER_GROUPS;
  if (sp === 'pig') {
    try {
      const response = (await withSignal(
        sb.from('app_store').select('data').eq('key', 'ppp-feeders-v1').maybeSingle(),
        signal,
      )) as SingleResponse<{data?: unknown}>;
      if (signal?.aborted) return NO_WEIGHIN_SESSION_DETAIL_ABORTED;
      const blob = response.data?.data;
      feederGroups = Array.isArray(blob) ? (blob as unknown[]) : NO_WEIGHIN_SESSION_FEEDER_GROUPS;
    } catch (error) {
      if (signal?.aborted) throw error;
      feederGroups = NO_WEIGHIN_SESSION_FEEDER_GROUPS;
    }
  }

  let broilerBatchRecs = NO_WEIGHIN_SESSION_BROILER_BATCH_RECS;
  if (sp === 'broiler') {
    // Unwrapped on purpose — see the header. loadAll left this one bare and its
    // rejection is a live defect; reproducing it is the honest port.
    const batchR = (await withSignal(
      sb.from('app_store').select('data').eq('key', 'ppp-v4').maybeSingle(),
      signal,
    )) as SingleResponse<{data?: unknown}>;
    broilerBatchRecs =
      batchR && batchR.data && Array.isArray(batchR.data.data)
        ? (batchR.data.data as unknown[])
        : NO_WEIGHIN_SESSION_BROILER_BATCH_RECS;
  }

  return {entries, animals, allSessions, allEntries, feederGroups, broilerBatchRecs};
}

async function queryWeighInSessionPigMetrics(
  sb: SupabaseClient,
  params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<WeighInSessionPigMetricsData> {
  const sessionId = weighInSessionId(params, 'weighin.session.pigMetrics');

  // `metricsData` keeps loadAll's own local name, and the prior-session read
  // below keeps its member expression inline, so that read's fingerprint stays
  // identical to the one it retires.
  let metricsData: PigSessionMetricsRow | null = null;
  try {
    const {data} = (await withSignal(sb.rpc('pig_session_metrics', {session_id_in: sessionId}), signal)) as {
      data: PigSessionMetricsRow | null;
      error: SupabaseErrorLike | null;
    };
    // `available === false` is the RPC's own "no metric for this session", which
    // this page renders as no metrics block at all rather than as an empty one.
    metricsData = data && data.available !== false ? data : null;
  } catch (error) {
    if (signal?.aborted) throw error;
    metricsData = null;
  }
  if (!metricsData || !metricsData.prior_session_id || !metricsData.prior_session_date) {
    return {metrics: metricsData, priorSession: null};
  }

  try {
    const response = (await withSignal(
      sb
        .from('weigh_ins')
        .select('*')
        .eq('session_id', metricsData.prior_session_id)
        .order('entered_at', {ascending: true}),
      signal,
    )) as ListResponse<WeighInSessionEntryRow>;
    if (signal?.aborted) return {metrics: metricsData, priorSession: null};
    // The prior session is published even when its entry read comes back empty,
    // because that is what the page did: `entries: priorEntries || []`. An
    // absent prior WINDOW and an empty one render differently — inventing a null
    // here would drop the date label the page shows beside each per-entry ADG.
    return {
      metrics: metricsData,
      priorSession: {
        id: metricsData.prior_session_id,
        date: metricsData.prior_session_date,
        entries: response.data || NO_WEIGHIN_SESSION_ENTRIES,
      },
    };
  } catch (error) {
    if (signal?.aborted) throw error;
    return {metrics: metricsData, priorSession: null};
  }
}

// ---------------------------------------------------------------------------
// weighin.webform.* — the /weighins form's six reads.
// ---------------------------------------------------------------------------
// SIX RESOURCES FOR SIX EFFECTS, AND THE COUNT IS THE CONSERVATIVE ANSWER.
//
// The record page above collapses ten reads into three because loadAll ran them
// as one chain with one failure. This form did the opposite: six independent
// requests, three of them fired together on a species pick and three more as
// the operator moves through the stages, each publishing into its own state
// with its own posture. Merging any two would couple failures that are not
// coupled today — a directory outage would take the resumable-draft list with
// it, or a broiler_groups failure would strand the schooner marker that Start
// Session fails closed on.
//
// FAILURE POSTURE, PER RESOURCE, AND WHY IT IS NOT UNIFORM:
//
//   speciesDirectory   THROWS. The effect wrote `if (data) setCattleList(data)`,
//                      so a failed read left the previous value in place. A
//                      thrown error leaves `data` null, the presenter publishes
//                      nothing, and the previous value stays — the same frame.
//   broilerSchooners   FAIL-SOFT, always resolves. See the type above: the
//                      settled marker is what makes Start Session fail closed,
//                      and it was set from a `.finally()`.
//   drafts             THROWS, same reasoning as the directory.
//   priorWeights       FAIL-SOFT to an empty map, because the effect it replaces
//                      published `{}` on both of its failure paths — a null
//                      session list took the `sessIds.length === 0` branch, and
//                      a null entry list folded to an empty map. Throwing would
//                      leave the PREVIOUS species' priors on screen.
//   sessionEntries     THROWS. The presenter clears entries to [] before the
//                      read, exactly as it did, so a failure shows an empty
//                      session rather than the last one's rows.
//   pigMetrics         THROWS, and the presenter maps the error to
//                      `{available: false}` and keeps its console.warn. That
//                      mapping stays in the presenter because the warn is the
//                      only observability this read has and moving it would
//                      make the catalog impure for no gain.
const NO_WEIGHIN_WEBFORM_CATTLE: WeighInWebformCattleRow[] = [];
const NO_WEIGHIN_WEBFORM_SHEEP: WeighInWebformSheepRow[] = [];
const NO_WEIGHIN_WEBFORM_BATCH_NAMES: string[] = [];
const NO_WEIGHIN_WEBFORM_BROILER_BATCH_META: unknown[] = [];
const NO_WEIGHIN_WEBFORM_PRIOR_BY_TAG: Record<string, WeighInWebformPriorWeight> = {};
const EMPTY_WEIGHIN_WEBFORM_BROILER_SCHOONERS: WeighInWebformBroilerSchoonersData = Object.freeze({
  broilerBatchMeta: NO_WEIGHIN_WEBFORM_BROILER_BATCH_META,
});
const EMPTY_WEIGHIN_WEBFORM_PRIOR_WEIGHTS: WeighInWebformPriorWeightsData = Object.freeze({
  priorByTag: NO_WEIGHIN_WEBFORM_PRIOR_BY_TAG,
});

function weighInWebformSpecies(params: ResourceParams | undefined, resourceId: string): string {
  const species = params?.species;
  if (typeof species !== 'string' || !species) throw new Error(`${resourceId}: species is required`);
  return species;
}

function weighInWebformSessionId(params: ResourceParams | undefined, resourceId: string): string {
  const sessionId = params?.sessionId;
  if (typeof sessionId !== 'string' || !sessionId) throw new Error(`${resourceId}: sessionId is required`);
  return sessionId;
}

async function queryWeighInWebformSpeciesDirectory(
  sb: SupabaseClient,
  params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<WeighInWebformSpeciesDirectoryData> {
  const species = weighInWebformSpecies(params, 'weighin.webform.speciesDirectory');
  const empty: WeighInWebformSpeciesDirectoryData = {
    cattleList: NO_WEIGHIN_WEBFORM_CATTLE,
    sheepList: NO_WEIGHIN_WEBFORM_SHEEP,
    pigBatches: NO_WEIGHIN_WEBFORM_BATCH_NAMES,
    broilerBatches: NO_WEIGHIN_WEBFORM_BATCH_NAMES,
  };

  if (species === 'cattle') {
    const response = (await withSignal(
      sb
        .from('cattle')
        .select('id, tag, herd, birth_date, sex, breed, old_tags, breeding_blacklist')
        .is('deleted_at', null),
      signal,
    )) as ListResponse<WeighInWebformCattleRow>;
    return {...empty, cattleList: unwrap<WeighInWebformCattleRow>(response, 'cattle')};
  }
  if (species === 'sheep') {
    const response = (await withSignal(
      sb.from('sheep').select('id, tag, flock, birth_date, sex, breed, old_tags').is('deleted_at', null),
      signal,
    )) as ListResponse<WeighInWebformSheepRow>;
    return {...empty, sheepList: unwrap<WeighInWebformSheepRow>(response, 'sheep')};
  }
  if (species === 'pig') {
    const response = (await withSignal(
      sb.from('webform_config').select('data').eq('key', 'active_groups').maybeSingle(),
      signal,
    )) as SingleResponse<{data?: unknown}>;
    const row = unwrapSingle<{data?: unknown}>(response, 'webform_config');
    const names = Array.isArray(row?.data) ? (row.data as unknown[]) : NO_WEIGHIN_WEBFORM_BATCH_NAMES;
    return {
      ...empty,
      // SOWS and BOARS stay excluded here, not at the picker: pig weigh-ins are
      // for feeder batches only, and the exclusion is a property of the option
      // list rather than of one dropdown that renders it.
      pigBatches: (names as string[]).filter(
        (n) => n && String(n).toUpperCase() !== 'SOWS' && String(n).toUpperCase() !== 'BOARS',
      ),
    };
  }
  if (species === 'broiler') {
    const response = (await withSignal(
      sb.from('webform_config').select('data').eq('key', 'broiler_groups').maybeSingle(),
      signal,
    )) as SingleResponse<{data?: unknown}>;
    const row = unwrapSingle<{data?: unknown}>(response, 'webform_config');
    return {
      ...empty,
      broilerBatches: Array.isArray(row?.data) ? (row.data as string[]) : NO_WEIGHIN_WEBFORM_BATCH_NAMES,
    };
  }
  throw new Error('weighin.webform.speciesDirectory: species must be cattle, sheep, pig or broiler');
}

async function queryWeighInWebformBroilerSchooners(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<WeighInWebformBroilerSchoonersData> {
  try {
    const response = (await withSignal(
      sb.from('webform_config').select('data').eq('key', 'broiler_batch_meta').maybeSingle(),
      signal,
    )) as SingleResponse<{data?: unknown}>;
    if (signal?.aborted) return EMPTY_WEIGHIN_WEBFORM_BROILER_SCHOONERS;
    const blob = response.data?.data;
    return {
      broilerBatchMeta: Array.isArray(blob) ? (blob as unknown[]) : NO_WEIGHIN_WEBFORM_BROILER_BATCH_META,
    };
  } catch (error) {
    if (signal?.aborted) throw error;
    return EMPTY_WEIGHIN_WEBFORM_BROILER_SCHOONERS;
  }
}

async function queryWeighInWebformDrafts(
  sb: SupabaseClient,
  params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<WeighInWebformDraftsData> {
  const species = weighInWebformSpecies(params, 'weighin.webform.drafts');
  // The seven-day window is the PRESENTER's policy and stays there: it is a
  // Central-time cutoff computed with centralISOFor, and this layer neither
  // knows nor decides the farm's timezone.
  const cutoff = params?.cutoff;
  if (typeof cutoff !== 'string' || !cutoff) throw new Error('weighin.webform.drafts: cutoff is required');
  const response = (await withSignal(
    sb
      .from('weigh_in_sessions')
      .select('*')
      .eq('species', species)
      .eq('status', 'draft')
      .gte('date', cutoff)
      .order('started_at', {ascending: false}),
    signal,
  )) as ListResponse<WeighInWebformSessionRow>;
  return {drafts: unwrap<WeighInWebformSessionRow>(response, 'weigh_in_sessions')};
}

async function queryWeighInWebformPriorWeights(
  sb: SupabaseClient,
  params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<WeighInWebformPriorWeightsData> {
  const species = weighInWebformSpecies(params, 'weighin.webform.priorWeights');
  const today = params?.today;
  if (typeof today !== 'string' || !today) throw new Error('weighin.webform.priorWeights: today is required');
  try {
    const sessionsResponse = (await withSignal(
      sb
        .from('weigh_in_sessions')
        .select('id, date')
        .eq('species', species)
        .eq('status', 'complete')
        .lt('date', today)
        .order('date', {ascending: false}),
      signal,
    )) as ListResponse<WeighInWebformSessionRow>;
    if (signal?.aborted) return EMPTY_WEIGHIN_WEBFORM_PRIOR_WEIGHTS;
    const sessions = sessionsResponse.data || [];
    const sessionById: Record<string, WeighInWebformSessionRow> = {};
    for (const session of sessions) sessionById[String(session.id)] = session;
    const sessIds = sessions.map((session) => session.id);
    if (sessIds.length === 0) return EMPTY_WEIGHIN_WEBFORM_PRIOR_WEIGHTS;

    const entriesResponse = (await withSignal(
      sb.from('weigh_ins').select('tag, weight, session_id').in('session_id', sessIds),
      signal,
    )) as ListResponse<WeighInWebformEntryRow>;
    if (signal?.aborted) return EMPTY_WEIGHIN_WEBFORM_PRIOR_WEIGHTS;
    const byTag: Record<string, WeighInWebformPriorWeight> = {};
    for (const entry of entriesResponse.data || []) {
      const tag = entry.tag as string | null | undefined;
      if (!tag) continue;
      const sd = sessionById[String(entry.session_id)]?.date as string | null | undefined;
      if (!sd) continue;
      const existing = byTag[tag];
      // Latest date wins, string-compared, exactly as the fold this replaces
      // did — ISO dates order lexically and the sessions arrive date DESC.
      if (!existing || (existing.date != null && sd > existing.date)) {
        byTag[tag] = {weight: parseFloat(String(entry.weight)) || 0, date: sd};
      }
    }
    return {priorByTag: byTag};
  } catch (error) {
    if (signal?.aborted) throw error;
    return EMPTY_WEIGHIN_WEBFORM_PRIOR_WEIGHTS;
  }
}

async function queryWeighInWebformSessionEntries(
  sb: SupabaseClient,
  params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<WeighInWebformSessionEntriesData> {
  const sessionId = weighInWebformSessionId(params, 'weighin.webform.sessionEntries');
  const response = (await withSignal(
    sb.from('weigh_ins').select('*').eq('session_id', sessionId).order('entered_at', {ascending: true}),
    signal,
  )) as ListResponse<WeighInWebformEntryRow>;
  // entered_at ASC is INSERTION ORDER and it is load-bearing: the pig screen
  // stamps `_entryNum` from this order and only then sorts a copy by weight
  // descending for display, so the operator's "#N" memory survives a re-sort.
  return {entries: unwrap<WeighInWebformEntryRow>(response, 'weigh_ins')};
}

async function queryWeighInWebformPigMetrics(
  sb: SupabaseClient,
  params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<WeighInWebformPigMetricsData> {
  const sessionId = weighInWebformSessionId(params, 'weighin.webform.pigMetrics');
  const {data, error} = (await withSignal(sb.rpc('pig_session_metrics', {session_id_in: sessionId}), signal)) as {
    data: PigSessionMetricsRow | null;
    error: SupabaseErrorLike | null;
  };
  if (error) throw new Error('pig_session_metrics: ' + (error.message || error));
  return {metrics: data};
}

// ---------------------------------------------------------------------------
// sheep.home.core — dashboard rows, retaining the original query sequence.
//
// The dashboard's lastWeight() uses Array.find(), so its result depends on the
// database return order. This intentionally does not reuse loadSheepWeighIns:
// that helper sorts newest-first, while the original home loader did not.
// The processing-batch tile is intentionally fail-soft for pre-migration-028
// schemas; an aborted request is the one error that must still escape.
// ---------------------------------------------------------------------------
async function querySheepHomeCore(
  sb: SupabaseClient,
  params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<SheepHomeCoreData> {
  const cutoff120 =
    typeof params?.cutoff120 === 'string'
      ? params.cutoff120
      : new Date(Date.now() - 120 * 86400000).toISOString().slice(0, 10);

  // Keep sessR -> sessIds -> wR sequential. The page's weight lookup consumes
  // this unsorted result with .find(), so its query shape and row order are
  // part of the dashboard contract rather than an implementation detail.
  const sessR = await withSignal(sb.from('weigh_in_sessions').select('id').eq('species', 'sheep'), signal);
  if (sessR.error) throw new Error('weigh_in_sessions: ' + (sessR.error.message || sessR.error));
  const sessIds = (sessR.data || []).map((s) => s.id);
  const wR: {data: SheepWeighInRow[] | null; error?: {message?: string} | null} =
    sessIds.length > 0
      ? await withSignal(sb.from('weigh_ins').select('*').in('session_id', sessIds), signal)
      : {data: []};
  if (wR.error) throw new Error('weigh_ins: ' + (wR.error.message || wR.error));

  const [sR, dR] = await Promise.all([
    withSignal(sb.from('sheep').select('*').is('deleted_at', null), signal),
    withSignal(sb.from('sheep_dailys').select('*').is('deleted_at', null).gte('date', cutoff120), signal),
  ]);
  if (sR.error) throw new Error('sheep: ' + (sR.error.message || sR.error));
  if (dR.error) throw new Error('sheep_dailys: ' + (dR.error.message || dR.error));

  // Processing-batch counts were deliberately tolerant in the original view:
  // a legacy schema without this table leaves the dashboard usable and shows
  // the neutral zero-count tile. Preserve that disposition without turning a
  // real AbortSignal cancellation into fabricated successful data.
  const batchCounts = await Promise.resolve(withSignal(sb.from('sheep_processing_batches').select('id,status'), signal))
    .then((bR) => {
      if (bR.error) throw bR.error;
      const all = (bR.data || []).length;
      const planned = (bR.data || []).filter((b) => b.status === 'planned').length;
      return {total: all, planned};
    })
    .catch((error) => {
      if (signal?.aborted) throw error;
      return {total: 0, planned: 0};
    });

  return {
    sheep: sR.data || [],
    dailys: dR.data || [],
    weighIns: wR.data || [],
    batchCounts,
  };
}

// ---------------------------------------------------------------------------
// sheep.flocks.core — the flock hub's six independent reads.
// ---------------------------------------------------------------------------
async function querySheepFlocksCore(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<SheepFlocksCoreData> {
  const [sR, weighIns, lR, brR, orR, trR] = await Promise.all([
    withSignal(sb.from('sheep').select('*').is('deleted_at', null).order('tag'), signal),
    loadSheepWeighIns(sb, {signal}),
    withSignal(sb.from('sheep_lambing_records').select('*').order('lambing_date', {ascending: false}), signal),
    withSignal(sb.from('sheep_breeds').select('*').order('label'), signal),
    withSignal(sb.from('sheep_origins').select('*').order('label'), signal),
    withSignal(sb.from('sheep_transfers').select('*').order('transferred_at', {ascending: false}), signal),
  ]);

  return {
    sheep: unwrap<SheepRow>(sR, 'sheep'),
    weighIns: weighIns || [],
    lambingRecords: unwrap<SheepLambingRecordRow>(lR, 'sheep_lambing_records'),
    breedOptions: unwrap<SheepBreedRow>(brR, 'sheep_breeds'),
    originOptions: unwrap<SheepOriginRow>(orR, 'sheep_origins'),
    transfers: unwrap<SheepTransferRow>(trR, 'sheep_transfers'),
  };
}

// ---------------------------------------------------------------------------
// sheep.animal.detail — the record page's six reads.
// ---------------------------------------------------------------------------
async function querySheepAnimalDetail(
  sb: SupabaseClient,
  params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<SheepAnimalDetailData> {
  const sheepId = params?.sheepId;
  if (!sheepId) throw new Error('sheep.animal.detail: sheepId is required');

  const [sR, allSheep, weighIns, lR, brR, orR] = await Promise.all([
    withSignal(sb.from('sheep').select('*').eq('id', sheepId).is('deleted_at', null).maybeSingle(), signal),
    withSignal(sb.from('sheep').select('*').is('deleted_at', null).order('tag'), signal),
    loadSheepWeighIns(sb, {signal}),
    withSignal(sb.from('sheep_lambing_records').select('*').order('lambing_date', {ascending: false}), signal),
    withSignal(sb.from('sheep_breeds').select('*').order('label'), signal),
    withSignal(sb.from('sheep_origins').select('*').order('label'), signal),
  ]);

  return {
    animal: unwrapSingle<SheepRow>(sR, 'sheep'),
    sheep: unwrap<SheepRow>(allSheep, 'sheep list'),
    weighIns: weighIns || [],
    lambingRecords: unwrap<SheepLambingRecordRow>(lR, 'sheep_lambing_records'),
    breedOptions: unwrap<SheepBreedRow>(brR, 'sheep_breeds'),
    originOptions: unwrap<SheepOriginRow>(orR, 'sheep_origins'),
  };
}

// ---------------------------------------------------------------------------
// sheep.batches.core — the processing-batch index.
//
// The hub still applies its established actual/planned/created date sort after
// this request resolves. Keeping that presentation sort in the consumer means
// the query preserves the original database order exactly while one scoped
// resource owns the render read and its cancellation.
// ---------------------------------------------------------------------------
async function querySheepBatchesCore(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<SheepBatchesCoreData> {
  const bR = await withSignal(
    sb.from('sheep_processing_batches').select('*').order('planned_process_date', {ascending: false}),
    signal,
  );

  return {batches: unwrap<SheepProcessingBatchRow>(bR, 'sheep_processing_batches')};
}

// ---------------------------------------------------------------------------
// sheep.batch.detail — the persisted processing-batch record page's reads.
//
// `batchId`, `bR`, and `sR` deliberately remain the page's identifiers. The
// physical inventory joins retirement to the catalog call shape, so changing a
// local name for tidiness would obscure an otherwise exact relocation.
// ---------------------------------------------------------------------------
async function querySheepBatchDetail(
  sb: SupabaseClient,
  params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<SheepBatchDetailData> {
  const batchId = params?.batchId;
  if (!batchId) throw new Error('sheep.batch.detail: batchId is required');

  const [bR, sR] = await Promise.all([
    withSignal(sb.from('sheep_processing_batches').select('*').eq('id', batchId).maybeSingle(), signal),
    withSignal(sb.from('sheep').select('*').is('deleted_at', null), signal),
  ]);

  return {
    batch: unwrapSingle<SheepProcessingBatchRow>(bR, 'sheep_processing_batches'),
    sheep: unwrap<SheepRow>(sR, 'sheep'),
  };
}

// view.processing — the RPC-backed processing schedule read.
// ---------------------------------------------------------------------------
// Processing records are deliberately not selected from processing_* tables:
// those tables are deny-all to the browser and the SECURITY DEFINER RPC owns
// the live planner projection, effective status, and source snapshot. The
// freshness reconciliation remains a caller-owned, tolerated pre-read step in
// ProcessingCalendarView; keeping it out of this pure query means Query may
// retry, dedupe, and cancel this resource without turning a render into a
// write. `reload` is a key-only parameter used by the surface to preserve its
// fail-closed retry (a new attempt must not paint cached rows before the RPC
// resolves); the server query arguments remain the original null/false tuple.
async function queryProcessingView(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<ProcessingRecordListRow[]> {
  return listProcessingRecords(sb, {
    year: null,
    program: null,
    includeArchived: false,
    signal,
  });
}

// ---------------------------------------------------------------------------
// processing.cutSheet.core — one Processing record's cut sheet.
//
// RPC-backed like the calendar beside it, so the wrapper in
// processingCutSheetApi owns the physical call and this entry owns the surface
// request. `get_processing_cut_sheet` returns the record, the sheet head and
// the candidate list in ONE envelope, which is what makes the editor's opening
// state a single read rather than three that can disagree with each other.
//
// `recordId` is a KEY-ONLY parameter in the same sense as tasks.todo.list, and
// for a sharper reason: here it is also an ARGUMENT. Leaving it out of the key
// would let an A -> B navigation between two cut sheets paint B's editor with
// A's answers — and the answers are what gets published to a processor.
//
// The RPC wrapper takes no AbortSignal, so an in-flight read is not cancelled
// on navigation; the resource layer discards the late result by key instead.
// Threading a signal through the wrapper would be the better fix and is not
// this change.
// ---------------------------------------------------------------------------
async function queryProcessingCutSheetCore(
  sb: SupabaseClient,
  params: ResourceParams | undefined,
  _signal?: AbortSignal,
): Promise<unknown> {
  const recordId = typeof params?.recordId === 'string' ? params.recordId : '';
  if (!recordId) return null;
  const {getCutSheet} = await import('../../lib/processingCutSheetApi.js');
  return getCutSheet(sb, recordId);
}

// ---------------------------------------------------------------------------
// equipment.adminRecord.core — the Admin equipment record page (A40).
//
// The same read the /admin Equipment tab makes: every equipment row, category
// then name, through listEquipmentForAdmin. The wrapper owns the one physical
// PostgREST call (render-read-sites.json lists it as that call's intended home),
// so this entry adds a surface request, not a second read site. The page picks
// its piece out of the list by slug, then id, the same order the Fleet record
// page uses, so the key carries no slug: two record pages share one list.
//
// Migration 016 is optional on deployments without the equipment module. The
// tab already shows a dedicated banner for that, so a missing relation comes
// back as data (missingSchema) rather than as a generic query error.
//
// Imported lazily so the equipment admin wrapper stays out of the boot bundle.
// ---------------------------------------------------------------------------
export type EquipmentAdminRecordData = {equipment: unknown[]; missingSchema: boolean};

async function queryEquipmentAdminRecordCore(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  _signal?: AbortSignal,
): Promise<EquipmentAdminRecordData> {
  const {listEquipmentForAdmin} = await import('../../lib/equipmentAdminApi.js');
  const {data, error} = await listEquipmentForAdmin(sb);
  if (error && /does not exist|relation/i.test(error.message || '')) return {equipment: [], missingSchema: true};
  if (error) throw new Error('equipment: ' + error.message);
  return {equipment: Array.isArray(data) ? data : [], missingSchema: false};
}

// ---------------------------------------------------------------------------
// view.production: every source behind the production matrix and ledger.
// ---------------------------------------------------------------------------
// Production's five inputs already live in productionApi because Home uses the
// same source bundle for its compact card. Keeping that API as the shared
// reader avoids duplicating the model inputs, while this resource is the scoped
// owner of the Production page's render request. Its Promise.allSettled
// contract is deliberately inside the API: one failed source becomes an empty
// input plus sourceErrors, never a fabricated failure for every program.
//
// The AbortSignal is explicit even though the no-date-range call uses the
// default bounds. A superseded authenticated scope must cancel every wrapper
// read, rather than let a late partial result settle into the old Query key.
async function queryProductionSourcesCore(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<ProductionSourcesCoreData> {
  return loadProductionSources(sb, {}, signal);
}

// ---------------------------------------------------------------------------
// app.home.standard production: the compact current-calendar-year card.
// ---------------------------------------------------------------------------
// Home intentionally has a narrower date range than /production's all-years
// matrix. The year belongs in the key so crossing a calendar year is a key
// change, never a response from the old year repainting the new card. Its old
// effect treated an unexpected aggregate rejection as five unavailable source
// totals rather than a page-wide failure; preserve that established output as
// payload data, while an AbortSignal still rejects so Query can cancel a stale
// scope/request safely.
async function queryHomeStandardProductionSources(
  sb: SupabaseClient,
  params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<HomeStandardProductionSourcesData> {
  const year = params?.year;
  if (typeof year !== 'number' || !Number.isInteger(year)) {
    throw new Error('home.standard.production.sources: year is required');
  }
  try {
    return await loadProductionSources(
      sb,
      {
        fromDate: `${year}-01-01`,
        toDate: `${year}-12-31`,
      },
      signal,
    );
  } catch (error) {
    if (signal?.aborted) throw error;
    return {
      batches: [],
      feederGroups: [],
      legacyEvents: [],
      cattleProcessingBatches: [],
      sheepProcessingBatches: [],
      eggDailys: [],
      sourceErrors: {appStore: error, legacyEvents: error, cattle: error, sheep: error, egg: error},
    };
  }
}

// newsletter.*: the admin workspace's three mutually-exclusive mode loads.
// All newsletter_* tables are deny-all RLS to the browser, so every read is a
// SECURITY DEFINER RPC. The catalog declares RPCs and no tables accordingly.
// Do not re-wrap wrapper errors here: friendlyNewsletterError strips exactly one
// leading prefix, and a second wrapper would leak the inner prefix to the UI.
async function queryNewsletterIssuesCore(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<NewsletterJson[]> {
  return listNewsletterIssuesAdmin(sb, {signal});
}

async function queryNewsletterSettingsCore(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<NewsletterJson> {
  return getNewsletterSettings(sb, {signal});
}

// Reproduce IssueEditor.load()'s sequence: runs begin only after the initial
// trio settles, so a fatal issue failure does not add a runs request.
async function queryNewsletterIssueDetail(
  sb: SupabaseClient,
  params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<NewsletterIssueDetailData> {
  const issueId = typeof params?.issueId === 'string' ? params.issueId : '';
  if (!issueId) throw new Error('newsletter.issue.detail: issueId is required');

  const [issue, settings, recentPublished] = await Promise.all([
    getNewsletterIssueAdmin(sb, issueId, {signal}),
    getNewsletterSettings(sb, {signal}).catch((error) => {
      if (signal?.aborted) throw error;
      return {} as NewsletterJson;
    }),
    getNewsletterRecentPublishedAdmin(sb, {limit: 3, excludeId: issueId, signal}).catch((error) => {
      if (signal?.aborted) throw error;
      return [] as NewsletterJson[];
    }),
  ]);

  const runs = await listNewsletterRunsAdmin(sb, issueId, {signal}).catch((error) => {
    if (signal?.aborted) throw error;
    return [] as NewsletterJson[];
  });

  return {issue, settings: settings || {}, recentPublished: recentPublished || [], runs};
}

// ---------------------------------------------------------------------------
// equipment.fleet.core — the shared /fleet container read.
// ---------------------------------------------------------------------------
// EquipmentHome owns these three reads for the fleet, fuel-log, detail and
// single-entry routes. Keep them together: those pages intentionally resolve
// their active record from the same arrays, so splitting one table into a
// second owner would reintroduce the stale-record/duplicate-fetch race this
// catalog is meant to remove.
async function queryEquipmentFleetCore(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<EquipmentFleetData> {
  const [equipment, fuelings, maintenance] = await Promise.all([
    withSignal(sb.from('equipment').select('*').order('name'), signal),
    withSignal(sb.from('equipment_fuelings').select('*').order('date', {ascending: false}).limit(5000), signal),
    withSignal(
      sb.from('equipment_maintenance_events').select('*').order('event_date', {ascending: false}).limit(500),
      signal,
    ),
  ]);

  // Migration 016 is optional on deployments that have not enabled the
  // equipment module yet. EquipmentHome already has a dedicated banner for
  // this case; preserve that distinction as data rather than turning a known
  // setup state into a generic query error.
  if (equipment.error && /does not exist|relation/i.test(equipment.error.message || '')) {
    return {equipment: [], fuelings: [], maintenance: [], missingSchema: true};
  }
  if (equipment.error) throw new Error('equipment: ' + equipment.error.message);
  if (fuelings.error) throw new Error('equipment_fuelings: ' + fuelings.error.message);
  if (maintenance.error) throw new Error('equipment_maintenance_events: ' + maintenance.error.message);

  return {
    equipment: equipment.data || [],
    fuelings: fuelings.data || [],
    maintenance: maintenance.data || [],
    missingSchema: false,
  };
}

// ---------------------------------------------------------------------------
// equipment.fuelingHub.core — the operator fueling hub's container read.
// ---------------------------------------------------------------------------
// FuelingHub owns this read for BOTH the tile hub and the per-machine form:
// the form's active machine is resolved out of this same array
// (`equipment.find(e => e.slug === slug)`) and the whole array is handed on as
// `equipmentList`. Splitting it would reintroduce the duplicate-fetch race the
// catalog exists to remove — the same argument equipment.fleet.core records.
//
// THIS IS NOT equipment.fleet.core AND MUST NOT BE FOLDED INTO IT. That
// resource reads `select('*')` with no status filter, for the internal /fleet
// module. This one reads 16 NAMED columns and filters `status = 'active'`,
// because the operator hub must never offer a retired machine. Same table, two
// genuinely different reads, two different surface families.
//
// The missing-schema branch is data, not an error, exactly as in
// equipment.fleet.core — FuelingHub has a dedicated migration-016 notice for it.
// The OTHER error branch is a real behaviour change and is deliberate: today a
// non-schema failure leaves `data` null, so the hub paints as though the farm
// owns no equipment and the form paints "No equipment with slug <x>" at an
// operator standing in front of that machine. A fake-empty is indistinguishable
// from an empty farm; this throws instead, and the consumer grows the error
// branch it never had.
async function queryEquipmentFuelingHubCore(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<EquipmentFuelingHubData> {
  const equipment = await withSignal(
    sb
      .from('equipment')
      .select(
        'id,name,slug,category,tracking_unit,fuel_type,every_fillup_items,every_fillup_help,fuel_gallons_help,operator_notes,service_intervals,attachment_checklists,current_hours,current_km,takes_def,manuals',
      )
      .eq('status', 'active')
      .order('name'),
    signal,
  );

  if (equipment.error && /does not exist|relation/i.test(equipment.error.message || '')) {
    return {equipment: [], missingSchema: true};
  }
  if (equipment.error) throw new Error('equipment: ' + equipment.error.message);

  return {equipment: equipment.data || [], missingSchema: false};
}

// ---------------------------------------------------------------------------
// equipment.fuelingForm.history — prior fuelings on ONE machine.
// ---------------------------------------------------------------------------
// Keyed on equipmentId, which makes a machine switch a KEY CHANGE rather than a
// refresh — and that is not a preference here, it is the isolation contract
// tests/static/equipment_fueling_quick_switch_static.test.js pins and
// tests/equipment_fueling_intervals.spec.js (Test 12, round 2) proves live: a
// delayed response for machine A must never drive machine B's due/next
// projection. On a key change the settle lands in A's own cache entry, which no
// observer is reading; the hand-written `cancelled` flag it replaces fenced the
// same race, this removes it.
//
// Six NAMED columns, not '*': equipment_fuelings is wide and only these six
// reach the due-interval and miss-streak math.
async function queryEquipmentFuelingFormHistory(
  sb: SupabaseClient,
  params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<EquipmentFuelingFormHistoryData> {
  const equipmentId = params?.equipmentId;
  if (!equipmentId) throw new Error('equipment.fuelingForm.history: equipmentId is required');

  const res = await withSignal(
    sb
      .from('equipment_fuelings')
      .select('date,team_member,hours_reading,km_reading,service_intervals_completed,every_fillup_check')
      .eq('equipment_id', equipmentId)
      .order('date', {ascending: false})
      .limit(500),
    signal,
  );

  return {history: unwrap<EquipmentFuelingHistoryRow>(res, 'equipment_fuelings')};
}

// ---------------------------------------------------------------------------
// app.home.standard: independently retryable maintenance and materials cards.
// ---------------------------------------------------------------------------
// These are deliberately separate resources. The existing dashboard gives the
// two cards independent loading, error, retry, and migration-048 absence
// states; folding them into one all-or-nothing request would make a materials
// failure hide real maintenance data (or vice versa).
async function queryHomeStandardEquipmentAttention(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<HomeStandardEquipmentAttentionData> {
  const [equipment, fuelings] = await Promise.all([
    withSignal(
      sb
        .from('equipment')
        .select(
          'id,slug,name,status,tracking_unit,current_hours,current_km,warranty_expiration,service_intervals,attachment_checklists,every_fillup_items',
        )
        .eq('status', 'active'),
      signal,
    ),
    withSignal(
      sb
        .from('equipment_fuelings')
        .select('equipment_id,date,team_member,hours_reading,km_reading,service_intervals_completed,every_fillup_check')
        .order('date', {ascending: false})
        .limit(5000),
      signal,
    ),
  ]);
  const equipmentRows = unwrap<EquipmentRow>(equipment, 'equipment');
  const fuelingRows = unwrap<LightHomeEquipmentFuelingRow>(fuelings, 'equipment_fuelings');
  const folded = foldEquipmentFuelings(fuelingRows);
  return {
    equipment: equipmentRows,
    equipmentCompletions: folded.equipmentCompletions,
    equipmentFuelings: folded.equipmentFuelings,
  };
}

async function queryHomeStandardEquipmentMaterials(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<HomeStandardEquipmentMaterialsData> {
  const [materials, clears] = await Promise.all([
    withSignal(
      sb
        .from('equipment_service_materials')
        .select('*')
        .eq('active', true)
        .order('equipment_id', {ascending: true})
        .order('source_kind', {ascending: true})
        .order('interval_unit', {ascending: true})
        .order('interval_value', {ascending: true})
        .order('attachment_name', {ascending: true})
        .order('sort_order', {ascending: true})
        .order('material_name', {ascending: true}),
      signal,
    ),
    withSignal(
      sb
        .from('equipment_material_clears')
        .select('*')
        .order('material_id', {ascending: true})
        .order('due_bucket_unit', {ascending: true})
        .order('due_bucket_value', {ascending: true})
        .order('cleared_at', {ascending: true}),
      signal,
    ),
  ]);

  if (materials.error && /does not exist|relation/i.test(materials.error.message || '')) {
    return {materials: [], materialClears: [], tablesMissing: true};
  }
  if (materials.error) throw new Error('equipment_service_materials: ' + materials.error.message);
  // Do not substitute an empty clears list on a real failure: that would
  // resurface operator-cleared materials. Migration absence remains the one
  // established feature-absent result, matching the old page-local branch.
  if (clears.error && /does not exist|relation/i.test(clears.error.message || '')) {
    return {materials: [], materialClears: [], tablesMissing: true};
  }
  if (clears.error) throw new Error('equipment_material_clears: ' + clears.error.message);
  return {
    materials: (materials.data || []) as MaterialRow[],
    materialClears: (clears.data || []) as MaterialClearRow[],
    tablesMissing: false,
  };
}

// ---------------------------------------------------------------------------
// home.standard.alert.inputs — the six bounded boot-sidecar projections.
// ---------------------------------------------------------------------------
// This is the first read the standard dashboard takes back from App's legacy
// boot rather than from itself. The six queries reproduce the sidecar in
// src/App (the `layer_dailys` / `egg_dailys` / `cattle_dailys` /
// `sheep_dailys` / `cattle` / `sheep` requests) column for column, filter for
// filter, and order for order, because Home renders the rows directly: the
// Last 5 Days feed groups by date and then by kind with a STABLE sort, so the
// server order decides tile order inside a day.
//
// PER-SLICE TOLERANCE IS THE INHERITED CONTRACT, NOT A SHORTCUT. Each sidecar
// request published independently (`.then(({data}) => data && set(...))`) and
// swallowed its own rejection, so one denied table has never blanked the other
// five, and none of them has ever been able to fail the page. Bundling them
// into one all-or-nothing read would be a new failure mode on a surface whose
// established behaviour is to render whatever resolved. The one thing that
// must escape is cancellation: a superseded scope has to stay cancelled
// instead of writing an invented empty payload under the old key.
//
// The window cutoffs come in as params so the cache identity rolls forward
// with the farm day instead of pinning yesterday's narrower window under an
// unchanged key. They are required rather than defaulted: a missing cutoff
// would silently widen or narrow what Home shows.
async function optionalHomeStandardRows(
  read: () => unknown,
  signal?: AbortSignal,
  onFailure?: () => void,
): Promise<CattleRow[]> {
  try {
    const response = (await read()) as ListResponse<CattleRow>;
    if (signal?.aborted) throw homeStandardAbortError();
    if (response.error) onFailure?.();
    return response.error ? [] : response.data || [];
  } catch (error) {
    if (signal?.aborted) throw error;
    onFailure?.();
    return [];
  }
}

function homeStandardAbortError(): Error {
  const error = new Error(`${RESOURCE.HOME_STANDARD_ALERT_INPUTS}: aborted`);
  error.name = 'AbortError';
  return error;
}

function requiredHomeStandardCutoff(params: ResourceParams | undefined, name: string): string {
  const value = params?.[name];
  if (typeof value !== 'string' || value.trim() === '') {
    throw new Error(`${RESOURCE.HOME_STANDARD_ALERT_INPUTS}: ${name} is required`);
  }
  return value;
}

async function queryHomeStandardAlertInputs(
  sb: SupabaseClient,
  params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<HomeStandardAlertInputsData> {
  // 45 days for the layer/egg projections, 14 for cattle/sheep — the two
  // windows the boot sidecar has always used.
  const recentCutoff = requiredHomeStandardCutoff(params, 'recentCutoff');
  const dailyCutoff = requiredHomeStandardCutoff(params, 'dailyCutoff');
  // Slices whose read failed. Each still resolves to [] (the fail-soft
  // default every other consumer relies on); only the missed-report list
  // reads this, so it can say Unavailable instead of listing false misses.
  const sliceErrors: string[] = [];
  const track = (slice: string) => () => sliceErrors.push(slice);
  const [layerDailysRecent, eggDailysRecent, cattleDailysRecent, sheepDailysRecent, cattleForHome, sheepForHome] =
    await Promise.all([
      optionalHomeStandardRows(
        () =>
          withSignal(
            sb
              .from('layer_dailys')
              .select('*')
              .is('deleted_at', null)
              .gte('date', recentCutoff)
              .order('date', {ascending: false}),
            signal,
          ),
        signal,
        track('layerDailysRecent'),
      ),
      optionalHomeStandardRows(
        () =>
          withSignal(
            sb
              .from('egg_dailys')
              .select('*')
              .is('deleted_at', null)
              .gte('date', recentCutoff)
              .order('date', {ascending: false}),
            signal,
          ),
        signal,
        track('eggDailysRecent'),
      ),
      optionalHomeStandardRows(
        () =>
          withSignal(
            sb
              .from('cattle_dailys')
              .select('*')
              .is('deleted_at', null)
              .gte('date', dailyCutoff)
              .order('date', {ascending: false}),
            signal,
          ),
        signal,
        track('cattleDailysRecent'),
      ),
      optionalHomeStandardRows(
        () =>
          withSignal(
            sb
              .from('sheep_dailys')
              .select('*')
              .is('deleted_at', null)
              .gte('date', dailyCutoff)
              .order('date', {ascending: false}),
            signal,
          ),
        signal,
        track('sheepDailysRecent'),
      ),
      optionalHomeStandardRows(
        () =>
          withSignal(
            sb
              .from('cattle')
              .select('id,herd,purchase_date,birth_date,created_at,sale_date,death_date,deleted_at')
              .is('deleted_at', null),
            signal,
          ),
        signal,
        track('cattleForHome'),
      ),
      optionalHomeStandardRows(
        () =>
          withSignal(
            sb
              .from('sheep')
              .select('id,flock,purchase_date,birth_date,created_at,sale_date,death_date,deleted_at')
              .is('deleted_at', null),
            signal,
          ),
        signal,
        track('sheepForHome'),
      ),
    ]);

  return {
    layerDailysRecent,
    eggDailysRecent,
    cattleDailysRecent,
    sheepDailysRecent,
    cattleForHome,
    sheepForHome,
    sliceErrors: sliceErrors.sort(),
  };
}

// ---------------------------------------------------------------------------
// home.standard.animal.layerEvidence — the complete layer count evidence.
// ---------------------------------------------------------------------------
// The last read the Animals on Farm card took off App's boot sidecar, and the
// only one of the sidecar's eight publications that any component ever read
// back: LayerContext.allLayerDailys, consumed here through
// buildAnimalHistorySnapshot -> layersOnFarmAt / layersFreshnessAt.
//
// FAIL-CLOSED, DELIBERATELY, AND UNLIKE ITS ALERT-INPUTS NEIGHBOUR.
//
// home.standard.alert.inputs is fail-soft because each of its six legs feeds a
// LIST — an absent layer feed shows fewer Last 5 Days tiles and one fewer
// missed-report row, which is legible. This one feeds a NUMBER, and an empty
// array does not make that number absent, it makes it 0: the anchor fallback
// reads `housing.current_count`, which is 0 on every active housing on this
// farm. A transport failure would therefore paint an empty farm as confidently
// as a real one. The card's error branch says "Unavailable" and offers Retry
// instead, which is the same refusal home.light.planner and layer.batches.core
// already make.
//
// The narrow filter is what makes fail-closed affordable rather than reckless.
// At 23 rows there is exactly one page, so a partial-then-stop publication is
// not a reachable state and the throw is total and honest. Nothing here uses
// the shared wcfSelectAll helper for the same reason: it BREAKS on a page error
// and returns the rows it already gathered, which is precisely the silent
// under-count this resource exists to end.
//
// Pagination is written out inline rather than called through a shared
// fetchAll, matching queryAnimalHistoryDailys: a `sb.from(table)` helper
// inventories as a `from:~table` site that can prove ownership of no literal
// table at all.
async function queryHomeStandardAnimalLayerEvidence(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<HomeStandardLayerEvidenceData> {
  const PAGE = 1000;
  const rows: HomeStandardLayerEvidenceRow[] = [];
  for (let from = 0; ; from += PAGE) {
    const {data, error} = (await withSignal(
      sb
        .from('layer_dailys')
        .select('date,layer_count,batch_label,batch_id')
        .is('deleted_at', null)
        .gt('layer_count', 0)
        .order('date', {ascending: false})
        .range(from, from + PAGE - 1),
      signal,
    )) as ListResponse<HomeStandardLayerEvidenceRow>;
    if (error) {
      const message = error instanceof Error ? error.message : String(error.message || error);
      throw new Error('layer_dailys: ' + message);
    }
    const page = Array.isArray(data) ? data : [];
    if (page.length === 0) break;
    rows.push(...page);
    if (page.length < PAGE) break;
  }
  return {layerEvidence: rows};
}

// ---------------------------------------------------------------------------
// home.standard.animal.dailyHistory — the broiler and pig daily histories.
// ---------------------------------------------------------------------------
// The last two reads the Animals on Farm card took off App's legacy boot:
// DailysRecentContext.broilerDailys and .pigDailys, which loadAllPoultryDailys
// and loadAllPigDailys fill once per cold boot.
//
// THE LOADERS ARE CALLED, NOT COPIED, and that is the whole reason this
// resource adds no new physical call site — the same reason broiler.feed.core,
// broiler.batchForm.dailys, pig.home.core, pig.sows.core, pig.feed.core and
// pig.batches.core call them. It also keeps the throw-after-a-failed-page fix
// in one module: wcfSelectAll swallows a page error and returns what it
// gathered, and each loader captures that error and throws afterward, so a
// truncated history can never be published as a complete one.
//
// FAIL-CLOSED, AND THIS IS THE ONE PLACE THAT DIVERGES FROM ITS BOOT ORIGIN.
// Every other resource that calls these loaders wraps them in the boot's own
// `.catch(() => [])`, because on those pages the history feeds a LIST or a
// COLUMN and an absent payload legibly shows less. Here it feeds two NUMBERS,
// and an empty array does not make a number absent. Measured on live data:
// with poultry_dailys empty this card paints 2269 broilers against a true 2172,
// because broilersOnFarmAt never subtracts mortality it cannot see. That is an
// outage rendered as a fact about the farm, which is exactly the refusal
// home.standard.animal.layerEvidence, home.light.planner and layer.batches.core
// already make. The two figures show "Unavailable" with a Retry instead.
//
// Two lists also read these arrays, and they now differ. The missed-report
// alerts show "Loading daily reports…" until this read lands and "Unavailable"
// with a Retry if it fails (Ronnie's note 6, 2026-10-08): an empty history
// would name every active target as missed. The admin Last 5 Days feed keeps
// the boot's fail-soft behaviour: a failed read leaves it the empty array it
// already received when the boot degraded.
//
// NO PARAMS, for the reason its layer sibling has none: a complete history has
// no farm-day identity to key on.
async function queryHomeStandardAnimalDailyHistory(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<HomeStandardAnimalDailyHistoryData> {
  const [broilerDailys, pigDailys] = await Promise.all([
    loadAllPoultryDailys(sb, signal ?? null) as Promise<CattleRow[]>,
    loadAllPigDailys(sb, signal ?? null) as Promise<CattleRow[]>,
  ]);
  return {broilerDailys, pigDailys};
}

// ---------------------------------------------------------------------------
// home.standard.planner — the seven persisted planner blobs, read route-locally.
// ---------------------------------------------------------------------------
// The standard dashboard's broiler batch list, pig planner set, layer-group
// directory and cleared-missed key set arrived through App's `ppp-*` app_store
// boot transaction. That transaction is not just a read: it REPAIRS and
// PERSISTS `ppp-v4` and `ppp-feeders-v1`. Taking the read route-local therefore
// means reproducing the repair as an immutable in-memory projection — which is
// what `home.light.planner` already does for the Light portal, over the SAME
// seven keys.
//
// So this reuses the Light portal's projection helpers verbatim rather than
// carrying a third copy of the broiler repair. They are declared further down
// in this file next to `queryLightHomePlanner`; the forward reference is
// deliberate, because moving them up here would sit inside the source ranges
// tests/static/boot_release_home_light_static.test.js slices around that
// function. There is exactly one behavioural difference between the two home
// surfaces and it is not in this projection: the standard dashboard also WRITES
// `ppp-missed-cleared-v1` when an operator clears a missed report, and that
// write stays where it always was, in the component. Nothing here writes.
//
// FAIL-CLOSED, unlike `home.standard.alert.inputs` above. The six bounded
// sidecar projections each swallowed their own rejection and have never been
// able to fail the dashboard, so that resource tolerates per-slice failure. The
// app_store blob is the opposite: this resource rejects on its error and Home
// renders its explicit unavailable state.
// Substituting empty arrays here would paint an empty farm — no missed reports,
// no upcoming events, zero animals — out of a transport failure.
async function queryHomeStandardPlanner(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<HomeStandardPlannerData> {
  const response = await withSignal(
    sb.from('app_store').select('key,data').in('key', HOME_LIGHT_PLANNER_APP_STORE_KEYS),
    signal,
  );
  if (signal?.aborted) throw lightHomeAbortError(RESOURCE.HOME_STANDARD_PLANNER);
  if (response.error) throw new Error(`app_store: ${response.error.message || response.error}`);

  const store: Record<string, unknown> = Object.create(null) as Record<string, unknown>;
  for (const row of (response.data || []) as Array<{key?: unknown; data?: unknown}>) {
    if (typeof row?.key === 'string') store[row.key] = row.data;
  }

  const farrowingValue = store['ppp-farrowing-v1'];
  const farrowing = lightHomeRows(farrowingValue);
  const breeders = lightHomeRows(store['ppp-breeders-v1']);
  const feederGroups = reconcileFeederGroupsFromBreeders(
    lightHomeRows(store['ppp-feeders-v1']) as unknown as PigFeederGroup[],
  ).groups as unknown as CattleRow[];
  return {
    batches: normalizedLightHomeBroilerBatches(store['ppp-v4']),
    breedingCycles: lightHomeRows(store['ppp-breeding-v1']),
    // The same display defaults App's boot fan-out seeds, and for the same
    // reason: neither is persisted or reconciled here.
    farrowingRecs: Array.isArray(farrowingValue) ? farrowing : (INITIAL_FARROWING as CattleRow[]),
    feederGroups,
    breeders: breeders.length > 0 ? breeders : (INITIAL_BREEDERS as CattleRow[]),
    layerGroups: lightHomeRows(store['ppp-layer-groups-v1']),
    missedCleared: Array.isArray(store['ppp-missed-cleared-v1']) ? store['ppp-missed-cleared-v1'] : [],
  };
}

// ---------------------------------------------------------------------------
// home.light.equipment.attention — optional operational attention on the
// Light-role landing portal.
// ---------------------------------------------------------------------------
// LightHomePortal historically fired these two reads independently and ignored
// each failure. Preserve that behaviour exactly: a denied/offline equipment
// read gives the alert builder an empty equipment list, and a denied/offline
// fueling read gives it an empty folded history. Neither condition may replace
// the Field Portal, its tiles, missed-report alerts, or next-30 list with an
// error screen. The sole exception is cancellation: a superseded scope must
// stay cancelled rather than write an invented empty payload to the old key.
async function queryLightHomeEquipmentAttention(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<LightHomeEquipmentAttentionData> {
  const [equipment, fuelings] = await Promise.all([
    withSignal(
      sb
        .from('equipment')
        .select(
          'id,slug,name,status,tracking_unit,current_hours,current_km,warranty_expiration,service_intervals,attachment_checklists,every_fillup_items',
        )
        .eq('status', 'active'),
      signal,
    ),
    withSignal(
      sb
        .from('equipment_fuelings')
        .select('equipment_id,date,team_member,hours_reading,km_reading,service_intervals_completed,every_fillup_check')
        .order('date', {ascending: false})
        .limit(5000),
      signal,
    ),
  ]);

  if (signal?.aborted && (equipment.error || fuelings.error)) {
    throw equipment.error || fuelings.error;
  }

  const folded = foldEquipmentFuelings((fuelings.error ? [] : fuelings.data || []) as LightHomeEquipmentFuelingRow[]);
  return {
    equipment: (equipment.error ? [] : equipment.data || []) as LightHomeEquipmentRow[],
    ...folded,
  };
}

// ---------------------------------------------------------------------------
// home.light.planner and home.light.missed.inputs: independent Light portal
// data owners.
// ---------------------------------------------------------------------------
// These bundles replace only the values LightHomePortal reads from App-owned
// contexts. They intentionally do not reuse the broad global app_store boot:
// the former boot path performs migrations and may queue repairs, neither of
// which is a render read or belongs to the Light root release.

function lightHomeRows(value: unknown): CattleRow[] {
  return Array.isArray(value) ? (value as CattleRow[]) : [];
}

// The global App boot repairs these persisted planner rows before its Home
// alert builders consume them. Light Home intentionally bypasses that broad
// boot (and its whole-blob maintenance writes), but it still needs the same
// *display semantics*. Keep the repair local and immutable: this is a pure
// projection of the seven values read above, never an app_store write.
//
// The legacy auto-activation path also stamps feed costs from
// ppp-feed-costs-v1. Light Home neither reads nor displays feed costs, so it
// deliberately does not widen this route's audited seven-key dependency just
// to reproduce an otherwise-unobservable persisted write.
const LIGHT_HOME_LEGACY_FR_BATCH_NAMES = Object.freeze(['B-24-02', 'B-24-03', 'B-24-04', 'B-25-01']);

function normalizeLightHomeBroilerBatch(batch: CattleRow): CattleRow {
  let normalized = batch;

  // Keep the legacy ordering. In particular, a planned batch can become
  // active and then become processed on the same projection when both dates
  // are due, exactly as it did during the former global boot.
  if (batch.status === 'archived') normalized = {...normalized, status: 'processed'};
  if (shouldAutoActivateBroilerBatch(normalized as BroilerBatch)) normalized = {...normalized, status: 'active'};
  if (shouldAutoProcessBroilerBatch(normalized as BroilerBatch)) normalized = {...normalized, status: 'processed'};

  const nameUpper = String(normalized.name || '').toUpperCase();
  if (LIGHT_HOME_LEGACY_FR_BATCH_NAMES.includes(nameUpper)) {
    if (normalized.breed !== 'FR') normalized = {...normalized, breed: 'FR'};
    if (normalized.hatchery !== 'Freedom Ranger Hatchery') {
      normalized = {...normalized, hatchery: 'Freedom Ranger Hatchery'};
    }
  }
  if (nameUpper === 'B-25-02' && normalized.hatchery !== 'VALLEY FARMS') {
    normalized = {...normalized, hatchery: 'VALLEY FARMS'};
  }
  if (nameUpper === 'B-25-03' && normalized.hatchery !== 'CREDO FARMS') {
    normalized = {...normalized, hatchery: 'CREDO FARMS'};
  }

  return normalized;
}

function normalizedLightHomeBroilerBatches(value: unknown): CattleRow[] {
  return lightHomeRows(value).map((batch) => normalizeLightHomeBroilerBatch(batch));
}

function lightHomeAbortError(resource: string): Error {
  const error = new Error(`${resource}: aborted`);
  error.name = 'AbortError';
  return error;
}

async function queryLightHomePlanner(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<LightHomePlannerData> {
  const response = await withSignal(
    sb.from('app_store').select('key,data').in('key', HOME_LIGHT_PLANNER_APP_STORE_KEYS),
    signal,
  );
  if (signal?.aborted) throw lightHomeAbortError(RESOURCE.HOME_LIGHT_PLANNER);
  if (response.error) throw new Error(`app_store: ${response.error.message || response.error}`);

  const store: Record<string, unknown> = Object.create(null) as Record<string, unknown>;
  for (const row of (response.data || []) as Array<{key?: unknown; data?: unknown}>) {
    if (typeof row?.key === 'string') store[row.key] = row.data;
  }

  const farrowingValue = store['ppp-farrowing-v1'];
  const farrowing = lightHomeRows(farrowingValue);
  const breeders = lightHomeRows(store['ppp-breeders-v1']);
  const feederGroups = reconcileFeederGroupsFromBreeders(
    lightHomeRows(store['ppp-feeders-v1']) as unknown as PigFeederGroup[],
  ).groups as unknown as CattleRow[];
  return {
    batches: normalizedLightHomeBroilerBatches(store['ppp-v4']),
    breedingCycles: lightHomeRows(store['ppp-breeding-v1']),
    // These initial records are existing display defaults. They are not
    // persisted or reconciled here, unlike the legacy App boot path.
    farrowingRecs: Array.isArray(farrowingValue) ? farrowing : (INITIAL_FARROWING as CattleRow[]),
    feederGroups,
    breeders: breeders.length > 0 ? breeders : (INITIAL_BREEDERS as CattleRow[]),
    layerGroups: lightHomeRows(store['ppp-layer-groups-v1']),
    missedCleared: Array.isArray(store['ppp-missed-cleared-v1']) ? store['ppp-missed-cleared-v1'] : [],
  };
}

/**
 * Each historic Light-home dependency ignored its own transport/schema
 * failure. Preserve that per-slice availability: one denied daily table must
 * not erase the unrelated missed-report inputs. Cancellation is different: it
 * belongs to the route runtime and must escape rather than becoming an empty
 * payload under a stale scope.
 */
async function optionalLightHomeMissedRows(
  read: () => unknown,
  signal?: AbortSignal,
  onFailure?: () => void,
): Promise<CattleRow[]> {
  try {
    const response = (await read()) as ListResponse<CattleRow>;
    if (signal?.aborted) throw lightHomeAbortError(RESOURCE.HOME_LIGHT_MISSED_INPUTS);
    if (response.error) onFailure?.();
    return response.error ? [] : response.data || [];
  } catch (error) {
    if (signal?.aborted) throw error;
    onFailure?.();
    return [];
  }
}

async function queryLightHomeMissedInputs(
  sb: SupabaseClient,
  params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<LightHomeMissedInputsData> {
  // The builder checks the preceding seven dates. Keeping the cutoff in the
  // resource params makes the cache identity roll forward with the day rather
  // than retaining yesterday's narrow result under an unchanged key.
  const requestedCutoff = params?.cutoff;
  const cutoff =
    typeof requestedCutoff === 'string' && requestedCutoff.trim() !== ''
      ? requestedCutoff
      : toISO(addDays(new Date(), -7));
  // Slices whose read failed. Each still resolves to [] (the fail-soft
  // default every other consumer relies on); only the missed-report list
  // reads this, so it can say Unavailable instead of listing false misses.
  const sliceErrors: string[] = [];
  const track = (slice: string) => () => sliceErrors.push(slice);
  const [
    broilerDailys,
    pigDailys,
    layerDailysRecent,
    cattleDailysRecent,
    sheepDailysRecent,
    cattleForHome,
    sheepForHome,
  ] = await Promise.all([
    optionalLightHomeMissedRows(
      () =>
        withSignal(
          sb
            .from('poultry_dailys')
            .select('*')
            .is('deleted_at', null)
            .gte('date', cutoff)
            .order('date', {ascending: false})
            .order('submitted_at', {ascending: false}),
          signal,
        ),
      signal,
      track('broilerDailys'),
    ),
    optionalLightHomeMissedRows(
      () =>
        withSignal(
          sb
            .from('pig_dailys')
            .select('*')
            .is('deleted_at', null)
            .gte('date', cutoff)
            .order('date', {ascending: false}),
          signal,
        ),
      signal,
      track('pigDailys'),
    ),
    optionalLightHomeMissedRows(
      () =>
        withSignal(
          sb
            .from('layer_dailys')
            .select('*')
            .is('deleted_at', null)
            .gte('date', cutoff)
            .order('date', {ascending: false}),
          signal,
        ),
      signal,
      track('layerDailysRecent'),
    ),
    optionalLightHomeMissedRows(
      () =>
        withSignal(
          sb
            .from('cattle_dailys')
            .select('*')
            .is('deleted_at', null)
            .gte('date', cutoff)
            .order('date', {ascending: false}),
          signal,
        ),
      signal,
      track('cattleDailysRecent'),
    ),
    optionalLightHomeMissedRows(
      () =>
        withSignal(
          sb
            .from('sheep_dailys')
            .select('*')
            .is('deleted_at', null)
            .gte('date', cutoff)
            .order('date', {ascending: false}),
          signal,
        ),
      signal,
      track('sheepDailysRecent'),
    ),
    optionalLightHomeMissedRows(
      () =>
        withSignal(
          sb
            .from('cattle')
            .select('id,herd,purchase_date,birth_date,created_at,sale_date,death_date,deleted_at')
            .is('deleted_at', null),
          signal,
        ),
      signal,
      track('cattleForHome'),
    ),
    optionalLightHomeMissedRows(
      () =>
        withSignal(
          sb
            .from('sheep')
            .select('id,flock,purchase_date,birth_date,created_at,sale_date,death_date,deleted_at')
            .is('deleted_at', null),
          signal,
        ),
      signal,
      track('sheepForHome'),
    ),
  ]);

  return {
    broilerDailys,
    pigDailys,
    layerDailysRecent,
    cattleDailysRecent,
    sheepDailysRecent,
    cattleForHome,
    sheepForHome,
    sliceErrors: sliceErrors.sort(),
  };
}

// ---------------------------------------------------------------------------
// animal.history.core — complete cattle/sheep history inputs.
// ---------------------------------------------------------------------------
// AnimalHistoryPage's model also consumes batches, dailys, layer data, and pig
// data from their existing app-store-backed contexts. This resource owns ONLY
// the four reads that page performed for itself; it does not claim boot
// independence or ownership of those shared inputs.
//
// The page's former fetchAllRows(table, options) helper could only be
// inventoried as `from:~table`, which cannot prove a declaration for any
// literal table. The full pagination is deliberately written out per table so
// every physical read is attributable. The four IIFEs retain the original
// Promise.all atomicity: any required read failing rejects the whole resource,
// and the consumer presents its existing Retry state instead of fabricating an
// incomplete animal history.
async function queryAnimalHistoryCore(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<AnimalHistoryCoreData> {
  const PAGE = 1000;
  const [cattle, sheep, cattleTransfers, sheepTransfers] = await Promise.all([
    (async () => {
      const rows: CattleRow[] = [];
      for (let from = 0; ; from += PAGE) {
        const {data, error} = (await withSignal(
          sb
            .from('cattle')
            .select('*')
            .is('deleted_at', null)
            .order('tag', {ascending: true})
            .range(from, from + PAGE - 1),
          signal,
        )) as ListResponse<CattleRow>;
        if (error) {
          const message = error instanceof Error ? error.message : String(error);
          throw new Error('cattle: ' + message);
        }
        const page = Array.isArray(data) ? data : [];
        if (page.length === 0) break;
        rows.push(...page);
        if (page.length < PAGE) break;
      }
      return rows;
    })(),
    (async () => {
      const rows: SheepRow[] = [];
      for (let from = 0; ; from += PAGE) {
        const {data, error} = (await withSignal(
          sb
            .from('sheep')
            .select('*')
            .is('deleted_at', null)
            .order('tag', {ascending: true})
            .range(from, from + PAGE - 1),
          signal,
        )) as ListResponse<SheepRow>;
        if (error) {
          const message = error instanceof Error ? error.message : String(error);
          throw new Error('sheep: ' + message);
        }
        const page = Array.isArray(data) ? data : [];
        if (page.length === 0) break;
        rows.push(...page);
        if (page.length < PAGE) break;
      }
      return rows;
    })(),
    (async () => {
      const rows: CattleTransferRow[] = [];
      for (let from = 0; ; from += PAGE) {
        const {data, error} = (await withSignal(
          sb
            .from('cattle_transfers')
            .select('*')
            .order('transferred_at', {ascending: true})
            .range(from, from + PAGE - 1),
          signal,
        )) as ListResponse<CattleTransferRow>;
        if (error) {
          const message = error instanceof Error ? error.message : String(error);
          throw new Error('cattle_transfers: ' + message);
        }
        const page = Array.isArray(data) ? data : [];
        if (page.length === 0) break;
        rows.push(...page);
        if (page.length < PAGE) break;
      }
      return rows;
    })(),
    (async () => {
      const rows: SheepTransferRow[] = [];
      for (let from = 0; ; from += PAGE) {
        const {data, error} = (await withSignal(
          sb
            .from('sheep_transfers')
            .select('*')
            .order('transferred_at', {ascending: true})
            .range(from, from + PAGE - 1),
          signal,
        )) as ListResponse<SheepTransferRow>;
        if (error) {
          const message = error instanceof Error ? error.message : String(error);
          throw new Error('sheep_transfers: ' + message);
        }
        const page = Array.isArray(data) ? data : [];
        if (page.length === 0) break;
        rows.push(...page);
        if (page.length < PAGE) break;
      }
      return rows;
    })(),
  ]);

  return {cattle, sheep, cattleTransfers, sheepTransfers};
}

// ---------------------------------------------------------------------------
// animal.history.planner / .layerDirectory / .dailys — the other three species.
// ---------------------------------------------------------------------------
// animal.history.core owned the ruminant half of this page. These three own the
// half that still arrived from App's global boot through four provider hooks:
// BatchesContext (`batches`), PigContext (`feederGroups`, `breeders`),
// LayerContext (`layerBatches`, `layerHousings`, `allLayerDailys`) and
// DailysRecentContext (`broilerDailys`, `pigDailys`). Removing those reads is
// what retires `animalHistory` from all five frozen consumer lists in
// tests/fixtures/former-eager-dependencies.json.
//
// THREE RESOURCES AND NOT ONE, BECAUSE THEY ARE THREE DIFFERENT KINDS OF READ.
//
//   planner          the persisted ppp-* JSON, in ONE keyed round trip rather
//                    than three, the way layer.record.appStore batches its two.
//   layerDirectory   the two layer TABLES, which decide which housings exist.
//   dailys           the three complete daily-report histories.
//
// ALL THREE THROW, AND THAT IS A DELIBERATE DEPARTURE FROM THE BOOT PATH.
//
// App's boot degraded each of these to an empty array on failure: the layer
// pair through loadLayerBootData's `lbHealthy`/`lhHealthy` fallback, the two
// daily loaders through their caller's `.catch()`. On this page that fallback
// is the empty-farm lie — a layer outage would draw a truthful-looking layer
// line at zero for every month, with no notice and no Retry. animal.history.core
// already refuses that for cattle and sheep ("the consumer presents its existing
// Retry state instead of fabricating an incomplete animal history"); these three
// extend the same rule to the other three species rather than inheriting a
// fallback that was correct for a global boot and is wrong for one chart.
//
// EACH READ NAMES ITS OWN TABLE LITERALLY, for the reason spelled out on
// layer.batches.core: a shared `fetchAll(table)` helper inventories as
// `from:~table` and can prove ownership of no literal table at all. App's
// layer_dailys loader and the two daily loaders in src/broiler and src/pig are
// exactly that shape, so the pagination is written out per table here instead of
// being called through them.
const ANIMAL_HISTORY_APP_STORE_KEYS = Object.freeze(['ppp-v4', 'ppp-feeders-v1', 'ppp-breeders-v1'] as const);

async function queryAnimalHistoryPlanner(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<AnimalHistoryPlannerData> {
  const response = await withSignal(
    sb.from('app_store').select('key,data').in('key', ANIMAL_HISTORY_APP_STORE_KEYS),
    signal,
  );
  if (response.error) throw new Error(`app_store: ${response.error.message || response.error}`);

  const store: Record<string, unknown> = Object.create(null) as Record<string, unknown>;
  for (const row of (response.data || []) as Array<{key?: unknown; data?: unknown}>) {
    if (typeof row?.key === 'string') store[row.key] = row.data;
  }

  const breeders = lightHomeRows(store['ppp-breeders-v1']);
  return {
    // The shared Light Home projection, reused rather than re-implemented: it is
    // already the boot repair minus the feed-cost stamp and minus the write, and
    // this page reads no cost off a batch. broilerBootRepair.test.ts pins that
    // one catalog copy against the boot module's constants.
    batches: normalizedLightHomeBroilerBatches(store['ppp-v4']),
    feederGroups: reconcileFeederGroupsFromBreeders(
      lightHomeRows(store['ppp-feeders-v1']) as unknown as PigFeederGroup[],
    ).groups as unknown as CattleRow[],
    // The boot fan-out seeds this list unconditionally when the stored one is
    // missing OR empty, and pigsOnFarmAt counts breeders, so dropping the seed
    // would erase the sow/boar half of the pig line.
    breeders: breeders.length > 0 ? breeders : (INITIAL_BREEDERS as CattleRow[]),
  };
}

async function queryAnimalHistoryLayerDirectory(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<AnimalHistoryLayerDirectoryData> {
  // App's boot orderings, preserved. The reconstruction keys housings to batches
  // by id, so order only decides which of two same-named rows wins — which is
  // exactly why it is reproduced here rather than re-chosen.
  const [layerBatches, layerHousings] = await Promise.all([
    withSignal(sb.from('layer_batches').select('*').order('name'), signal),
    withSignal(sb.from('layer_housings').select('*').order('start_date'), signal),
  ]);
  return {
    layerBatches: unwrap<LayerBatchCoreRow>(layerBatches as ListResponse<LayerBatchCoreRow>, 'layer_batches'),
    layerHousings: unwrap<LayerHousingCoreRow>(layerHousings as ListResponse<LayerHousingCoreRow>, 'layer_housings'),
  };
}

async function queryAnimalHistoryDailys(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<AnimalHistoryDailysData> {
  const PAGE = 1000;
  const [layerDailys, broilerDailys, pigDailys] = await Promise.all([
    (async () => {
      const rows: LayerDailyRow[] = [];
      for (let from = 0; ; from += PAGE) {
        const {data, error} = (await withSignal(
          sb
            .from('layer_dailys')
            .select('*')
            .is('deleted_at', null)
            .order('date', {ascending: false})
            .range(from, from + PAGE - 1),
          signal,
        )) as ListResponse<LayerDailyRow>;
        if (error) {
          const message = error instanceof Error ? error.message : String(error);
          throw new Error('layer_dailys: ' + message);
        }
        const page = Array.isArray(data) ? data : [];
        if (page.length === 0) break;
        rows.push(...page);
        if (page.length < PAGE) break;
      }
      return rows;
    })(),
    (async () => {
      const rows: CattleRow[] = [];
      for (let from = 0; ; from += PAGE) {
        // The second sort key is loadAllPoultryDailys's, kept because two
        // reports filed for one batch on one date must resolve to the same
        // survivor here as they do on the Broiler surfaces.
        const {data, error} = (await withSignal(
          sb
            .from('poultry_dailys')
            .select('*')
            .is('deleted_at', null)
            .order('date', {ascending: false})
            .order('submitted_at', {ascending: false})
            .range(from, from + PAGE - 1),
          signal,
        )) as ListResponse<CattleRow>;
        if (error) {
          const message = error instanceof Error ? error.message : String(error);
          throw new Error('poultry_dailys: ' + message);
        }
        const page = Array.isArray(data) ? data : [];
        if (page.length === 0) break;
        rows.push(...page);
        if (page.length < PAGE) break;
      }
      return rows;
    })(),
    (async () => {
      const rows: CattleRow[] = [];
      for (let from = 0; ; from += PAGE) {
        const {data, error} = (await withSignal(
          sb
            .from('pig_dailys')
            .select('*')
            .is('deleted_at', null)
            .order('date', {ascending: false})
            .range(from, from + PAGE - 1),
          signal,
        )) as ListResponse<CattleRow>;
        if (error) {
          const message = error instanceof Error ? error.message : String(error);
          throw new Error('pig_dailys: ' + message);
        }
        const page = Array.isArray(data) ? data : [];
        if (page.length === 0) break;
        rows.push(...page);
        if (page.length < PAGE) break;
      }
      return rows;
    })(),
  ]);

  return {layerDailys, broilerDailys, pigDailys};
}

// ---------------------------------------------------------------------------
// webform.addfeed.* — authenticated Add Feed lookup bridge.
// ---------------------------------------------------------------------------
//
// /addfeed is login-required, but its presenter deliberately stays in
// src/webforms without an AuthContext dependency. These two resources are the
// app-side bridge: the authenticated container owns QueryScope and hands the
// resolved lookup values in as plain props.
//
// The old form began two independent effects on mount. Feed inputs failed
// soft to an empty picker. The six config reads were one Promise.all batch and
// each response was applied independently; a missing/error config row simply
// left that one operator option at its initial default. Preserve that exact
// distinction here. In particular, do not turn a config outage into a page
// error: logging a broiler feed must remain possible when a nonessential
// config row is unavailable.
// ---------------------------------------------------------------------------

type AddFeedConfigRow = {data?: unknown | null};

const EMPTY_ADD_FEED_CONFIG: AddFeedConfigData = Object.freeze({
  housingBatchMap: null,
  broilerGroups: null,
  broilerBatchMeta: null,
  activeGroups: null,
  fullConfig: null,
  webformSettings: null,
});

const EMPTY_WEBFORM_HUB_CONFIG: WebformHubConfigData = Object.freeze({
  fullConfig: null,
  broilerGroups: null,
  broilerBatchMeta: null,
  webformSettings: null,
  activeGroups: null,
  housingBatchMap: null,
});

function addFeedConfigValue(result: SingleResponse<AddFeedConfigRow>): unknown | null {
  // The former effect inspected data, not error. Preserve that exactly: a
  // malformed/partial transport response that carries usable row data still
  // drives this one option, while an absent row leaves only this option at its
  // presenter default.
  if (!result.data) return null;
  return result.data.data ?? null;
}

function throwIfAddFeedLookupAborted(signal?: AbortSignal): void {
  if (signal?.aborted) throw new DOMException('Add Feed lookup aborted', 'AbortError');
}

async function queryAddFeedInputs(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<AddFeedInputsData> {
  try {
    const result = (await withSignal(
      sb.from('cattle_feed_inputs').select('*').order('category').order('name'),
      signal,
    )) as ListResponse<AddFeedInputRow>;
    throwIfAddFeedLookupAborted(signal);
    // Same data-first condition as the old `.then`: a usable payload wins even
    // if a transport wrapper also supplied an error field.
    return {feedInputs: result.data || []};
  } catch (error) {
    if (signal?.aborted) throw error;
    // The former effect left the feed list empty if its request rejected. The
    // form remains usable for poultry/pig/layer logging, so keep that soft
    // failure local rather than reporting a route-level error.
    return {feedInputs: []};
  }
}

async function queryAddFeedConfig(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<AddFeedConfigData> {
  try {
    const [hbm, bg, bbm, ag, fc, ws] = (await Promise.all([
      withSignal(sb.from('webform_config').select('data').eq('key', 'housing_batch_map').maybeSingle(), signal),
      withSignal(sb.from('webform_config').select('data').eq('key', 'broiler_groups').maybeSingle(), signal),
      withSignal(sb.from('webform_config').select('data').eq('key', 'broiler_batch_meta').maybeSingle(), signal),
      withSignal(sb.from('webform_config').select('data').eq('key', 'active_groups').maybeSingle(), signal),
      withSignal(sb.from('webform_config').select('data').eq('key', 'full_config').maybeSingle(), signal),
      withSignal(sb.from('webform_config').select('data').eq('key', 'webform_settings').maybeSingle(), signal),
    ])) as [
      SingleResponse<AddFeedConfigRow>,
      SingleResponse<AddFeedConfigRow>,
      SingleResponse<AddFeedConfigRow>,
      SingleResponse<AddFeedConfigRow>,
      SingleResponse<AddFeedConfigRow>,
      SingleResponse<AddFeedConfigRow>,
    ];
    throwIfAddFeedLookupAborted(signal);
    return {
      housingBatchMap: addFeedConfigValue(hbm),
      broilerGroups: addFeedConfigValue(bg),
      broilerBatchMeta: addFeedConfigValue(bbm),
      activeGroups: addFeedConfigValue(ag),
      fullConfig: addFeedConfigValue(fc),
      webformSettings: addFeedConfigValue(ws),
    };
  } catch (error) {
    if (signal?.aborted) throw error;
    // Same as the previous Promise.all continuation: an unavailable config
    // batch leaves every field at its initial fallback, never replaces the
    // operator's form with a fatal resource error.
    return EMPTY_ADD_FEED_CONFIG;
  }
}

// ---------------------------------------------------------------------------
// webform.hub.* — /dailys lookup reads.
// ---------------------------------------------------------------------------
//
// WebformHub previously owned one independent feed-input effect plus a
// six-row config Promise.all. The authenticated bridge now owns those reads,
// while the presenter still decides how each raw jsonb member drives one of
// its six sub-forms. Keep the old request order and tolerant data-first
// semantics: a resolved row with usable data wins even if a transport wrapper
// also carries an error, while a rejected Promise.all leaves every config
// control at its initial fallback.

function webformHubConfigValue(result: SingleResponse<AddFeedConfigRow>): unknown | null {
  if (!result.data) return null;
  return result.data.data ?? null;
}

function throwIfWebformHubLookupAborted(signal?: AbortSignal): void {
  if (signal?.aborted) throw new DOMException('WebformHub lookup aborted', 'AbortError');
}

async function queryWebformHubInputs(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<WebformHubInputsData> {
  try {
    const result = (await withSignal(
      sb.from('cattle_feed_inputs').select('*').order('category').order('name'),
      signal,
    )) as ListResponse<WebformHubFeedInputRow>;
    throwIfWebformHubLookupAborted(signal);
    // The former `.then(({data}) => { if (data) ... })` kept the form's empty
    // controls on a failed request but still used any payload a wrapper gave it.
    return {feedInputs: result.data || []};
  } catch (error) {
    if (signal?.aborted) throw error;
    return {feedInputs: []};
  }
}

async function queryWebformHubConfig(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<WebformHubConfigData> {
  try {
    const [fc, bg, bbm, ws, ag, hbm] = (await Promise.all([
      withSignal(sb.from('webform_config').select('data').eq('key', 'full_config').maybeSingle(), signal),
      withSignal(sb.from('webform_config').select('data').eq('key', 'broiler_groups').maybeSingle(), signal),
      withSignal(sb.from('webform_config').select('data').eq('key', 'broiler_batch_meta').maybeSingle(), signal),
      withSignal(sb.from('webform_config').select('data').eq('key', 'webform_settings').maybeSingle(), signal),
      withSignal(sb.from('webform_config').select('data').eq('key', 'active_groups').maybeSingle(), signal),
      withSignal(sb.from('webform_config').select('data').eq('key', 'housing_batch_map').maybeSingle(), signal),
    ])) as [
      SingleResponse<AddFeedConfigRow>,
      SingleResponse<AddFeedConfigRow>,
      SingleResponse<AddFeedConfigRow>,
      SingleResponse<AddFeedConfigRow>,
      SingleResponse<AddFeedConfigRow>,
      SingleResponse<AddFeedConfigRow>,
    ];
    throwIfWebformHubLookupAborted(signal);
    return {
      fullConfig: webformHubConfigValue(fc),
      broilerGroups: webformHubConfigValue(bg),
      broilerBatchMeta: webformHubConfigValue(bbm),
      webformSettings: webformHubConfigValue(ws),
      activeGroups: webformHubConfigValue(ag),
      housingBatchMap: webformHubConfigValue(hbm),
    };
  } catch (error) {
    if (signal?.aborted) throw error;
    return EMPTY_WEBFORM_HUB_CONFIG;
  }
}

// webform.hub.layerDirectory — the /dailys hub's two layer directories.
//
// These were NOT a presenter effect: App read layer_batches / layer_housings
// once at boot and threaded both arrays down through WebformsEntry as props.
// This resource is what replaces that hand-off, so the hub reads the two
// directories in its own authenticated scope instead of consuming the global
// boot result.
//
// TOLERANT, DELIBERATELY, AND IT IS NOT A COPY OF layer.batches.core's POSTURE.
// The layer batches hub throws on failure because an independent hub must not
// render an outage as a truthful empty farm — its whole page IS the directory.
// Here the directory only seeds a dropdown and a label→id lookup, and the
// presenter already has a documented fallback for both being empty
// (`loadedConfig?.layerGroups`). Returning empty arrays on failure therefore
// reproduces exactly what the operator saw when the boot arrays had not
// arrived, which is the same tolerance webform.hub.inputs and
// webform.hub.config keep: a lookup failure leaves the form's controls at
// their existing defaults rather than replacing the form with an error screen.
const EMPTY_WEBFORM_HUB_LAYER_DIRECTORY: WebformHubLayerDirectoryData = Object.freeze({
  layerBatches: [] as LayerBatchCoreRow[],
  layerHousings: [] as LayerHousingCoreRow[],
});

async function queryWebformHubLayerDirectory(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<WebformHubLayerDirectoryData> {
  try {
    // The legacy select/order shapes, preserved: App ordered batches by name
    // and housings by start_date, and the presenter's `find` lookups are
    // order-sensitive when two rows share a label.
    const [layerBatches, layerHousings] = (await Promise.all([
      withSignal(sb.from('layer_batches').select('*').order('name'), signal),
      withSignal(sb.from('layer_housings').select('*').order('start_date'), signal),
    ])) as [ListResponse<LayerBatchCoreRow>, ListResponse<LayerHousingCoreRow>];
    throwIfWebformHubLookupAborted(signal);
    return {
      layerBatches: layerBatches.data || [],
      layerHousings: layerHousings.data || [],
    };
  } catch (error) {
    if (signal?.aborted) throw error;
    return EMPTY_WEBFORM_HUB_LAYER_DIRECTORY;
  }
}

// webform.hub.pigContext — the pig daily form's sow tags and move history.
//
// Owner request 2026-11-04: picking SOWS shows three sow-group sections with
// each group's sow tags, and every group shows "Days since last move". The
// presenter in src/webforms may not read app_store or pig_dailys itself
// (tests/static/public_webforms_boundary_static.test.js), so both reads live
// here and reach it as a bridge prop, like every other hub lookup.
//
// PARAMS: `groups` is the hub's active pig-group list (webform.hub.config's
// active_groups). The 'SOWS' entry expands to the three sow-group labels, and
// each sow group's history also includes legacy combined 'SOWS' rows.
//
// THE MOVE READ: per label, the newest MOVE_HISTORY_WINDOW reports with
// group_moved = true and deleted_at null. One request per label keeps each
// result small and exact (a single all-groups read would hit PostgREST's row
// cap while past rows still default to moved = Yes). The window is
// newest-first, so a back-dated report older than the whole window is shown as
// unknown rather than as "No move recorded".
//
// FAIL-SOFT, label by label: this only decorates the form. A failed label is
// left out of `moveHistory`, a failed breeder read gives null tags, and neither
// blocks a submit. Cancellation still escapes as an abort.
const MOVE_HISTORY_WINDOW = 30;

function pigContextLabelSets(groups: unknown): Array<{key: string; labels: string[]}> {
  const out: Array<{key: string; labels: string[]}> = [];
  const seen = new Set<string>();
  for (const raw of Array.isArray(groups) ? groups : []) {
    const name = typeof raw === 'string' ? raw.trim() : '';
    if (!name) continue;
    if (name.toUpperCase() === SOWS_GROUP_VALUE) {
      for (const n of SOW_GROUP_NUMBERS) {
        const key = sowGroupLabel(n);
        if (!seen.has(key)) out.push({key, labels: sowGroupHistoryLabels(n)});
        seen.add(key);
      }
      continue;
    }
    if (!seen.has(name)) out.push({key: name, labels: [name]});
    seen.add(name);
  }
  return out;
}

async function queryWebformHubPigContext(
  sb: SupabaseClient,
  params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<WebformHubPigContextData> {
  const sets = pigContextLabelSets(params?.groups);
  const breedersRead = (async (): Promise<SowTagsByGroup | null> => {
    try {
      const response = (await withSignal(
        sb.from('app_store').select('key,data').eq('key', 'ppp-breeders-v1').maybeSingle(),
        signal,
      )) as SingleResponse<{key?: unknown; data?: unknown}>;
      if (response.error) return null;
      const rows = Array.isArray(response.data?.data) ? (response.data?.data as Record<string, unknown>[]) : [];
      return activeSowTagsByGroup(rows);
    } catch (error) {
      if (signal?.aborted) throw error;
      return null;
    }
  })();
  const moveReads = sets.map(
    async ({key, labels}): Promise<{key: string; history: WebformHubPigMoveHistory} | null> => {
      try {
        const response = (await withSignal(
          sb
            .from('pig_dailys')
            .select('date,batch_label')
            .eq('group_moved', true)
            .is('deleted_at', null)
            .in('batch_label', labels)
            .or('source.is.null,source.neq.add_feed_webform')
            .order('date', {ascending: false})
            .limit(MOVE_HISTORY_WINDOW),
          signal,
        )) as ListResponse<{date?: string | null; batch_label?: string | null}>;
        if (response.error) return null;
        const rows = response.data || [];
        const dates = rows.map((r) => String(r.date || '').slice(0, 10)).filter(Boolean);
        return {key, history: {dates, complete: rows.length < MOVE_HISTORY_WINDOW}};
      } catch (error) {
        if (signal?.aborted) throw error;
        return null;
      }
    },
  );
  const [sowTagsByGroup, ...moves] = await Promise.all([breedersRead, ...moveReads]);
  throwIfWebformHubLookupAborted(signal);
  const moveHistory: Record<string, WebformHubPigMoveHistory> = {};
  for (const entry of moves as Array<{key: string; history: WebformHubPigMoveHistory} | null>) {
    if (entry) moveHistory[entry.key] = entry.history;
  }
  return {sowTagsByGroup: sowTagsByGroup as SowTagsByGroup | null, moveHistory};
}

// ---------------------------------------------------------------------------
// tasks.webform.assignees — /dailys/tasks' security-filtered assignee picker.
// ---------------------------------------------------------------------------
//
// The Task form's assignee list is one compound render input, not two
// independently tolerable lookups. An eligible-directory result without its
// availability policy would re-expose people an admin deliberately hid, while
// a policy result without the directory cannot render a valid option. Both
// reads therefore live in the same scoped resource and EITHER transport error
// rejects.
//
// A missing config row is ordinary data: it means there are no extra
// exclusions, matching the server-side submit function's coalesce to `[]`. An
// errored config request is NOT equivalent to a missing row. It rejects so the
// bridge clears any prior options and offers a retry instead of leaving
// stale or unfiltered people selectable.
//
// WHY THIS DOES NOT REUSE readAssignableProfiles(). That helper serves the
// Task Center, where a failed availability read degrades to `{}` behind a tab
// that still renders. It returns a map keyed by id; this surface renders an
// ORDERED <select>, and its whole form is unusable without the list, so it
// reports the failure rather than silently presenting an empty picker as a
// settled answer.
async function queryTasksWebformAssignees(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<TasksWebformAssigneesData> {
  const [eligibleResult, availabilityResult] = await Promise.all([
    withSignal(sb.rpc('list_eligible_assignees'), signal),
    withSignal(
      sb.from('webform_config').select('data').eq('key', TASKS_PUBLIC_ASSIGNEE_AVAILABILITY_KEY).maybeSingle(),
      signal,
    ),
  ]);

  if (signal?.aborted) throw new DOMException('Tasks webform assignee lookup aborted', 'AbortError');

  const eligible = eligibleResult as {data: unknown; error: SupabaseErrorLike | null};
  const availability = availabilityResult as SingleResponse<{data?: unknown}>;
  if (eligible.error) throw new Error(`list_eligible_assignees: ${eligible.error.message || eligible.error}`);
  if (availability.error)
    throw new Error(`tasks_public_assignee_availability: ${availability.error.message || availability.error}`);

  const candidates = Array.isArray(eligible.data)
    ? eligible.data
        .filter(
          (value): value is TasksWebformAssignee =>
            !!value &&
            typeof value === 'object' &&
            typeof (value as {id?: unknown}).id === 'string' &&
            (value as {id: string}).id.length > 0 &&
            ((value as {full_name?: unknown}).full_name === null ||
              typeof (value as {full_name?: unknown}).full_name === 'string'),
        )
        .map((value) => ({id: value.id, full_name: value.full_name ?? null}))
    : [];
  const availabilityData = availability.data && 'data' in availability.data ? availability.data.data : null;
  return {assignees: visiblePublicAssignees(candidates, availabilityData)};
}

// ---------------------------------------------------------------------------
// webform.pigDailys.config — the direct Pig Dailys form's field configuration.
// ---------------------------------------------------------------------------
//
// This one did NOT replace a presenter effect. `/webform-pigs` read the
// ppp-webforms-v1 admin document off App's global boot, through
// WebformsConfigContext, and used it for exactly two things: which optional
// fields are `required` (submit-time validation) and which carry an asterisk.
// Owning the read here is what lets `webform` retire from the frozen app_store
// consumer list in tests/fixtures/former-eager-dependencies.json.
//
// THE SEEDING IS PART OF THE VALUE, NOT A REPAIR BOLTED ON AFTERWARDS.
//
// App never published the stored blob raw: appStoreBootFanOut passes it through
// applyWebformsConfigSeeds first, which appends any missing form definition and
// strips the retired cattle `s-mortality` section. A definition absent from that
// merge is a form absent from the admin editor and from the anon mirror (see
// src/app/boot/webformsConfigSeed.ts), so reading the blob without it would be a
// different document, not the same one from a new owner. It is a pure in-memory
// projection — no upsert, no repair write — exactly like the legacy
// normalizations home.light.planner reproduces over its own seven keys.
//
// FAIL-SOFT, AND THE FALLBACK IS NOT INVENTED HERE.
//
// DEFAULT_WEBFORMS_CONFIG is what WebformsConfigContext already holds before
// boot resolves — main.tsx threads it in as `configInit`, and App's boot guards
// with `if (store['ppp-webforms-v1'])`, so a missing row leaves the form on the
// default document rather than erroring. Resolving a failed or absent read to
// that same constant keeps that behaviour: an offline field form must still
// submit when a nonessential config row is unavailable, which is the posture
// webform.addfeed.config and webform.hub.config already hold for their own
// operator options.
type WebformPigDailysStoreRow = {key?: unknown; data?: unknown};

/** The seed merge's own parameter type, borrowed so the two cannot drift. */
type WebformsConfigDocument = Parameters<typeof applyWebformsConfigSeeds>[0];

async function queryWebformPigDailysConfig(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<WebformPigDailysConfigData> {
  try {
    const response = (await withSignal(
      sb.from('app_store').select('key,data').eq('key', 'ppp-webforms-v1').maybeSingle(),
      signal,
    )) as SingleResponse<WebformPigDailysStoreRow>;
    // Cancellation is the route runtime's, not this form's: it must escape as an
    // abort rather than resolve to the default document under a stale scope.
    if (signal?.aborted) throw new DOMException('Pig Dailys config aborted', 'AbortError');
    // `if (store['ppp-webforms-v1'])`, reproduced: a missing row, a null blob or
    // a transport error all leave the default document in place.
    const blob = response.error ? null : response.data?.data;
    if (!blob) return {webformsConfig: DEFAULT_WEBFORMS_CONFIG};
    return {webformsConfig: applyWebformsConfigSeeds(blob as WebformsConfigDocument)};
  } catch (error) {
    if (signal?.aborted) throw error;
    return {webformsConfig: DEFAULT_WEBFORMS_CONFIG};
  }
}

// ---------------------------------------------------------------------------
// webforms.admin.appStore — the two documents /admin edits.
// ---------------------------------------------------------------------------
//
// WebformsAdminView took both off App's boot contexts: the ppp-webforms-v1
// admin document through WebformsConfigContext, and the ppp-feed-costs-v1 rates
// through FeedCostsContext. Owning both here is what lets `webforms` retire from
// the frozen app_store consumer list, and it takes BOTH — a view leaves that
// list only when every hydrated field it reads has gone, and these two were the
// whole of it.
//
// ONE READ FOR TWO KEYS, like layer.record.appStore. A page that needs at least
// one of them either way does not need two round trips, and neither key needs
// its own cache lifetime here: the tabs share a mount.
//
// IT THROWS, AND THAT IS NOT THE POSTURE ITS webform.* NEIGHBOURS KEEP.
//
// webform.addfeed.config and webform.hub.config fail soft because a missing
// config row there costs an operator ONE dropdown option on a form whose job is
// to submit a report. Here the document is the SUBJECT: /admin lists it, edits
// it, and writes the edited copy back with `{...webformsConfig, webforms: …}`.
// Substituting a default on a failed read would show the operator a document
// the farm does not have and then persist it over the one it does. A read
// failure must reach the editor as an error with a Retry, never as an empty
// editor — which is the same argument layer.batches.core makes about rendering
// an outage as a truthful empty farm, with a write on the end of it.
//
// A MISSING ROW IS STILL NOT AN ERROR. App's boot guards each key with
// `if (store[key])` and otherwise leaves the context on its initial value, so an
// absent row resolves to the same two defaults it always did.
const WEBFORMS_ADMIN_APP_STORE_KEYS = Object.freeze(['ppp-webforms-v1', 'ppp-feed-costs-v1'] as const);

/** FeedCostsContext's own initial value, which an absent row still resolves to. */
const WEBFORMS_ADMIN_DEFAULT_FEED_COSTS: FeedCosts = Object.freeze({
  starter: 0,
  grower: 0,
  layer: 0,
  pig: 0,
  grit: 0,
});

async function queryWebformsAdminAppStore(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<WebformsAdminAppStoreData> {
  const response = await withSignal(
    sb.from('app_store').select('key,data').in('key', WEBFORMS_ADMIN_APP_STORE_KEYS),
    signal,
  );
  if (response.error) throw new Error(`app_store: ${response.error.message || response.error}`);

  const store: Record<string, unknown> = Object.create(null) as Record<string, unknown>;
  for (const row of (response.data || []) as Array<{key?: unknown; data?: unknown}>) {
    if (typeof row?.key === 'string') store[row.key] = row.data;
  }
  const storedConfig = store['ppp-webforms-v1'];
  const storedFeedCosts = store['ppp-feed-costs-v1'];
  return {
    // The SEEDED document, not the raw blob — see queryWebformPigDailysConfig
    // for why the merge is part of the value. It matters more here: the seeds
    // are what put a missing form definition back in front of the operator, and
    // this editor is where they would notice it is gone.
    webformsConfig: storedConfig
      ? applyWebformsConfigSeeds(storedConfig as WebformsConfigDocument)
      : DEFAULT_WEBFORMS_CONFIG,
    // App's LOAD boundary, reproduced: an earlier build persisted these rates as
    // strings, and every consumer calls .toFixed() on them.
    feedCosts: storedFeedCosts ? normalizeFeedCosts(storedFeedCosts) : WEBFORMS_ADMIN_DEFAULT_FEED_COSTS,
  };
}

// ---------------------------------------------------------------------------
// layer.batches.core — the index's independent hub reads.
// ---------------------------------------------------------------------------
// This resource owns every render input of the exact no-suffix hub: the batch
// and housing directories plus the two paginated metric feeds.  Its record
// siblings still receive App's legacy arrays through LayerBatchesRouter, so
// this is intentionally NOT a retirement of App's layer_batches/layer_housings
// readers.  The released /layer/batches presenter never mounts that router.
//
// EACH READ NAMES ITS OWN TABLE LITERALLY, AND THAT IS THE POINT.
//
// The page's loader paginated through a shared `fetchAll(table, columns)`
// helper. Because the table arrived as a PARAMETER, the physical-call inventory
// could only ever record it as
// `src/layer/LayerBatchesView.jsx::from:~table@LayerBatchesHub/useCallback()/fetchAll`
// — one site attributable to no particular table. A resource that DECLARES
// `layer_dailys` and `egg_dailys` has to be provable against those literals,
// and surface_router_derivation rejects it otherwise ("declares table X but no
// inventoried call site reads it"). Lifting the same helper into the catalog
// would have carried the defect across rather than fixing it.
//
// So the pagination is written out once per table. The duplication is
// deliberate and small; it buys an attributable call site per declared table.
// All four inputs stay CONCURRENT via Promise.all. The two directory queries
// intentionally preserve App's legacy select/order shapes, but unlike the
// global boot fallback they throw on failure: an independent hub must not
// render an outage as a truthful empty farm.
async function queryLayerBatchesCore(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<LayerBatchesCoreData> {
  const PAGE = 1000;
  const LAYER_COLUMNS = 'batch_label,batch_id,feed_lbs,grit_lbs,mortality_count,layer_count,date,feed_type';
  const EGG_COLUMNS =
    'group1_name,group1_count,group2_name,group2_count,group3_name,group3_count,group4_name,group4_count,date';

  const [layerBatches, layerHousings, layerDailys, eggDailys] = await Promise.all([
    withSignal(sb.from('layer_batches').select('*').order('name'), signal),
    withSignal(sb.from('layer_housings').select('*').order('start_date'), signal),
    (async () => {
      const rows: LayerDailyRow[] = [];
      for (let offset = 0; ; offset += PAGE) {
        const {data, error} = (await withSignal(
          sb
            .from('layer_dailys')
            .select(LAYER_COLUMNS)
            .is('deleted_at', null)
            .range(offset, offset + PAGE - 1),
          signal,
        )) as ListResponse<LayerDailyRow>;
        if (error) throw new Error(`layer_dailys: ${error.message}`);
        if (!data || data.length === 0) break;
        rows.push(...data);
        if (data.length < PAGE) break;
      }
      return rows;
    })(),
    (async () => {
      const rows: EggDailyRow[] = [];
      for (let offset = 0; ; offset += PAGE) {
        const {data, error} = (await withSignal(
          sb
            .from('egg_dailys')
            .select(EGG_COLUMNS)
            .is('deleted_at', null)
            .range(offset, offset + PAGE - 1),
          signal,
        )) as ListResponse<EggDailyRow>;
        if (error) throw new Error(`egg_dailys: ${error.message}`);
        if (!data || data.length === 0) break;
        rows.push(...data);
        if (data.length < PAGE) break;
      }
      return rows;
    })(),
  ]);

  return {
    layerBatches: unwrap<LayerBatchCoreRow>(layerBatches as ListResponse<LayerBatchCoreRow>, 'layer_batches'),
    layerHousings: unwrap<LayerHousingCoreRow>(layerHousings as ListResponse<LayerHousingCoreRow>, 'layer_housings'),
    layerDailys,
    eggDailys,
  };
}

// ---------------------------------------------------------------------------
// layer.groups.core — everything /layer/groups renders from.
// ---------------------------------------------------------------------------
// Four reads, arriving here from THREE different owners, which is the point:
//
//   ppp-layer-groups-v1   App's boot app_store transaction, threaded down as
//                         the `layerGroups` prop. This is the page's subject.
//   layer_batches         App's boot table read, same prop hand-off.
//   layer_housings        ditto.
//   layer_dailys          a mount effect the PRESENTER owned, moved here so the
//                         surface has one owner rather than one owner and a
//                         leftover.
//
// ERROR POSTURE IS PER-READ, DELIBERATELY, AND NOT UNIFORM.
//
// The first three throw. The page IS the group directory plus its occupancy
// badges; rendering an outage as "No layer groups yet." would be the exact lie
// layer.batches.core's comment above rejects.
//
// The recent-daily window does NOT throw, because the effect it replaces did
// not: `.then(({data}) => { if (data) setRecentDailys(data); })` left the
// cards' last-report strip absent and the rest of the page intact. Making it
// fatal here would turn a silently-degraded strip into a blank page — a
// behaviour change smuggled in under a refactor. It is caught in its own IIFE
// so a failure resolves to [] without touching the other three.
const NO_LAYER_GROUPS_RECENT_DAILYS: LayerGroupsRecentDailyRow[] = [];

async function queryLayerGroupsCore(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<LayerGroupsCoreData> {
  const [store, layerBatches, layerHousings, recentDailys] = await Promise.all([
    withSignal(
      sb.from('app_store').select('key,data,updated_at').eq('key', 'ppp-layer-groups-v1').maybeSingle(),
      signal,
    ),
    // App's boot projections, reproduced: batches by name, housings by
    // start_date. The occupancy badge resolves a housing to its batch by id, so
    // the order only decides which of two same-named rows wins — which is
    // exactly why it is preserved rather than re-chosen here.
    withSignal(sb.from('layer_batches').select('*').order('name'), signal),
    withSignal(sb.from('layer_housings').select('*').order('start_date'), signal),
    (async () => {
      try {
        const response = (await withSignal(
          sb
            .from('layer_dailys')
            .select('batch_label,feed_lbs,grit_lbs,layer_count,date,team_member,comments,mortality_count')
            .is('deleted_at', null)
            .order('date', {ascending: false})
            .limit(200),
          signal,
        )) as ListResponse<LayerGroupsRecentDailyRow>;
        // A named constant rather than a bare `[]`, because query_scope_contract
        // rejects an array LITERAL returned from anything in src/app/data — a
        // raw array leaving this layer is indistinguishable from a query key
        // that skipped resourceKey.
        if (signal?.aborted) return NO_LAYER_GROUPS_RECENT_DAILYS;
        return response.data || NO_LAYER_GROUPS_RECENT_DAILYS;
      } catch (error) {
        if (signal?.aborted) throw error;
        return NO_LAYER_GROUPS_RECENT_DAILYS;
      }
    })(),
  ]);

  const storeRow = unwrapSingle<{key?: unknown; data?: unknown; updated_at?: unknown}>(
    store as SingleResponse<{key?: unknown; data?: unknown; updated_at?: unknown}>,
    'app_store',
  );
  const blob = storeRow?.data;
  return {
    // The boot loader guards with `if (store['ppp-layer-groups-v1'])` and leaves
    // the state at [] otherwise, so a missing row is an empty directory rather
    // than an error. A non-array value would have reached the view unchanged
    // before; it is normalized to [] here because every consumer spreads it.
    layerGroups: Array.isArray(blob) ? (blob as unknown[]) : [],
    layerGroupsUpdatedAt: typeof storeRow?.updated_at === 'string' ? storeRow.updated_at : null,
    layerBatches: unwrap<LayerBatchCoreRow>(layerBatches as ListResponse<LayerBatchCoreRow>, 'layer_batches'),
    layerHousings: unwrap<LayerHousingCoreRow>(layerHousings as ListResponse<LayerHousingCoreRow>, 'layer_housings'),
    recentDailys,
  };
}

// ---------------------------------------------------------------------------
// layer.record.* — the layer batch and housing record pages.
// ---------------------------------------------------------------------------
// Both pages used to render from FOUR values threaded down by LayerEntry off
// App's boot (the two directories, the layer-groups blob, the broiler batches)
// plus two metric feeds each page paginated for itself.
//
// THE PAGINATION IS WRITTEN OUT PER TABLE, AND THAT IS THE POINT — the same
// point layer.batches.core makes. Each page's helper took the table as a
// PARAMETER, so both of its reads inventoried as `from:~table` and could never
// prove a declaration for `layer_dailys` or `egg_dailys`. Lifting the helper
// would have carried that defect across instead of fixing it.
//
// The projections are the ones the pages already selected, and the ordering is
// App's: batches by name, housings by start_date. It THROWS, because each
// page's own loader threw into a RecordPageLoadError with a Retry and a record
// page that renders an outage as an empty batch is worse than one that says so.
const LAYER_RECORD_PAGE = 1000;
const LAYER_RECORD_DAILY_COLUMNS = 'batch_label,batch_id,feed_lbs,grit_lbs,mortality_count,layer_count,date,feed_type';
const LAYER_RECORD_EGG_COLUMNS =
  'group1_name,group1_count,group2_name,group2_count,group3_name,group3_count,group4_name,group4_count,date';

async function queryLayerRecordCore(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<LayerRecordCoreData> {
  const [layerBatches, layerHousings, layerDailys, eggDailys] = await Promise.all([
    withSignal(sb.from('layer_batches').select('*').order('name'), signal),
    withSignal(sb.from('layer_housings').select('*').order('start_date'), signal),
    (async () => {
      const rows: LayerDailyRow[] = [];
      for (let offset = 0; ; offset += LAYER_RECORD_PAGE) {
        const {data, error} = (await withSignal(
          sb
            .from('layer_dailys')
            .select(LAYER_RECORD_DAILY_COLUMNS)
            .is('deleted_at', null)
            .range(offset, offset + LAYER_RECORD_PAGE - 1),
          signal,
        )) as ListResponse<LayerDailyRow>;
        if (error) throw new Error(`layer_dailys: ${error.message}`);
        if (!data || data.length === 0) break;
        rows.push(...data);
        if (data.length < LAYER_RECORD_PAGE) break;
      }
      return rows;
    })(),
    (async () => {
      const rows: EggDailyRow[] = [];
      for (let offset = 0; ; offset += LAYER_RECORD_PAGE) {
        const {data, error} = (await withSignal(
          sb
            .from('egg_dailys')
            .select(LAYER_RECORD_EGG_COLUMNS)
            .is('deleted_at', null)
            .range(offset, offset + LAYER_RECORD_PAGE - 1),
          signal,
        )) as ListResponse<EggDailyRow>;
        if (error) throw new Error(`egg_dailys: ${error.message}`);
        if (!data || data.length === 0) break;
        rows.push(...data);
        if (data.length < LAYER_RECORD_PAGE) break;
      }
      return rows;
    })(),
  ]);

  return {
    layerBatches: unwrap<LayerBatchCoreRow>(layerBatches as ListResponse<LayerBatchCoreRow>, 'layer_batches'),
    layerHousings: unwrap<LayerHousingCoreRow>(layerHousings as ListResponse<LayerHousingCoreRow>, 'layer_housings'),
    layerDailys,
    eggDailys,
  };
}

const LAYER_RECORD_APP_STORE_KEYS = Object.freeze(['ppp-layer-groups-v1', 'ppp-v4'] as const);

async function queryLayerRecordAppStore(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<LayerRecordAppStoreData> {
  const response = await withSignal(
    sb.from('app_store').select('key,data,updated_at').in('key', LAYER_RECORD_APP_STORE_KEYS),
    signal,
  );
  if (response.error) throw new Error(`app_store: ${response.error.message || response.error}`);

  const store: Record<string, unknown> = Object.create(null) as Record<string, unknown>;
  const updatedAtByKey: Record<string, string | null> = Object.create(null) as Record<string, string | null>;
  for (const row of (response.data || []) as Array<{key?: unknown; data?: unknown; updated_at?: unknown}>) {
    if (typeof row?.key === 'string') {
      store[row.key] = row.data;
      updatedAtByKey[row.key] = typeof row.updated_at === 'string' ? row.updated_at : null;
    }
  }
  const rows = (value: unknown): unknown[] => (Array.isArray(value) ? (value as unknown[]) : []);
  return {
    layerGroups: rows(store['ppp-layer-groups-v1']),
    layerGroupsUpdatedAt: updatedAtByKey['ppp-layer-groups-v1'] ?? null,
    broilerBatches: rows(store['ppp-v4']),
  };
}

// ---------------------------------------------------------------------------
// layer.dailys.* — the layer daily hub, its record page, and their shared
// group directory.
// ---------------------------------------------------------------------------
// The hub's page size and eager auto-append are unchanged; only the cursor
// moves, from presenter state to the paginated resource. The record read keeps
// LayerDailyPage's exact projection and its deleted_at filter, so opening a
// soft-deleted report still resolves to "not found" rather than to a row.
//
// The DIRECTORY is the reason both surfaces are migrated in one change. Its
// three values reached them through a single LayerEntry prop hand-off off
// App's boot results, and a resource may only name surfaces the manifest
// declares migrated — so localizing the dropdown for the hub without also
// localizing the record page would have left the record page consuming a
// permanent resource it did not own.
const LAYER_DAILYS_PAGE_SIZE = 1000;

async function queryLayerDailysList(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  pageParam: unknown,
  signal?: AbortSignal,
): Promise<PaginatedPage<LayerDailyListRow, unknown>> {
  const page = typeof pageParam === 'number' && Number.isSafeInteger(pageParam) && pageParam >= 0 ? pageParam : 0;
  const rows = unwrap<LayerDailyListRow>(
    await withSignal(
      sb
        .from('layer_dailys')
        .select('*')
        .is('deleted_at', null)
        .order('date', {ascending: false})
        .range(page * LAYER_DAILYS_PAGE_SIZE, (page + 1) * LAYER_DAILYS_PAGE_SIZE - 1),
      signal,
    ),
    'layer_dailys',
  );
  return {rows, nextPage: rows.length === LAYER_DAILYS_PAGE_SIZE ? page + 1 : null};
}

async function queryLayerDailyDetail(
  sb: SupabaseClient,
  params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<LayerDailyDetailData> {
  const recordId = params?.recordId;
  if (!recordId) throw new Error('layer.daily.detail: recordId is required');

  const result = await withSignal(
    sb.from('layer_dailys').select('*').eq('id', recordId).is('deleted_at', null).maybeSingle(),
    signal,
  );
  return {record: unwrapSingle<LayerDailyListRow>(result, 'layer_dailys')};
}

// FAIL-SOFT, like the two webform lookups and unlike the two reads above.
//
// The dropdown this feeds already renders from whatever subset of the three
// arrives — buildLayerDailyGroupOptions takes them as optional and the hub's
// own destructure defaults two of them to []. A directory outage therefore used
// to mean "fewer options", never "no daily history", because the arrays came
// from a boot payload that had already tolerated its own failure. Throwing here
// would newly couple the whole daily list to a directory read that never gated
// it.
const FAILED_LAYER_DAILYS_GROUP_DIRECTORY: LayerDailysGroupDirectoryData = Object.freeze({
  layerGroups: [] as unknown[],
  layerGroupsStatus: LAYER_GROUP_READ_FAILED,
  layerBatches: [] as LayerBatchCoreRow[],
  layerHousings: [] as LayerHousingCoreRow[],
});

async function queryLayerDailysGroupDirectory(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<LayerDailysGroupDirectoryData> {
  try {
    const [store, layerBatches, layerHousings] = (await Promise.all([
      withSignal(sb.from('app_store').select('key,data').eq('key', 'ppp-layer-groups-v1').maybeSingle(), signal),
      withSignal(sb.from('layer_batches').select('*').order('name'), signal),
      withSignal(sb.from('layer_housings').select('*').order('start_date'), signal),
    ])) as [
      SingleResponse<{key?: unknown; data?: unknown}>,
      ListResponse<LayerBatchCoreRow>,
      ListResponse<LayerHousingCoreRow>,
    ];
    if (signal?.aborted) return FAILED_LAYER_DAILYS_GROUP_DIRECTORY;
    // The ordinary {error} response, for the same reason as
    // layer.eggDailys.groups: PostgREST resolves with an error member for RLS
    // denials, timeouts and 5xx, so `store.data?.data` would silently become
    // an empty blob that vetoes nothing.
    //
    // BUT THE OTHER TWO READS SUCCEEDED, AND THROWING THEM AWAY COSTS HISTORY.
    // These three requests are independent. Returning the all-empty constant
    // here would discard batches and housings that arrived fine, and those are
    // what `resolveLayerDailyBatchId` uses to resolve a historical report's
    // saved label back to its batch_id. A canonical outage must narrow what can
    // be CHOSEN, not break what has already been filed. The failed verdict
    // still blocks every new selection on its own.
    if (store.error) {
      return {
        layerGroups: [],
        layerGroupsStatus: LAYER_GROUP_READ_FAILED,
        layerBatches: layerBatches.data || [],
        layerHousings: layerHousings.data || [],
      };
    }
    const blob = store.data?.data;
    return {
      layerGroups: Array.isArray(blob) ? (blob as unknown[]) : [],
      layerGroupsStatus: LAYER_GROUP_READ_RESOLVED,
      layerBatches: layerBatches.data || [],
      layerHousings: layerHousings.data || [],
    };
  } catch (error) {
    if (signal?.aborted) throw error;
    return FAILED_LAYER_DAILYS_GROUP_DIRECTORY;
  }
}

// ---------------------------------------------------------------------------
// pig.dailys.* — the pig daily hub, its record page, and their shared feeder
// directory.
// ---------------------------------------------------------------------------
// The hub already owned its own pig_dailys history: a `while (true)` loop in a
// mount effect that pulled every 1,000-row page before publishing. Only the
// CURSOR moves here — the page size, the deleted_at filter and the date DESC
// order are the loop's, unchanged — so what the list renders is the same set of
// rows in the same order, owned by a resource instead of by presenter state.
//
// The record read keeps PigDailyPage's exact projection and its deleted_at
// filter, so opening a soft-deleted report still resolves to "not found"
// rather than to a row.
//
// The DIRECTORY is the reason both surfaces migrate in one change. `feederGroups`
// reached them through a single PigEntry prop hand-off carrying App's boot
// ppp-feeders-v1 blob, and PigDailysRouter is where that hand-off landed — the
// hub used it for the group dropdown and the batch_id derivation, and forwarded
// the same array to the record page for its own dropdown. A resource may only
// name surfaces the manifest declares migrated, so localizing the hub's dropdown
// alone would have left the record page consuming a permanent resource it did
// not own.
const PIG_DAILYS_PAGE_SIZE = 1000;

async function queryPigDailysList(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  pageParam: unknown,
  signal?: AbortSignal,
): Promise<PaginatedPage<PigDailyListRow, unknown>> {
  const page = typeof pageParam === 'number' && Number.isSafeInteger(pageParam) && pageParam >= 0 ? pageParam : 0;
  const rows = unwrap<PigDailyListRow>(
    await withSignal(
      sb
        .from('pig_dailys')
        .select('*')
        .is('deleted_at', null)
        .order('date', {ascending: false})
        .range(page * PIG_DAILYS_PAGE_SIZE, (page + 1) * PIG_DAILYS_PAGE_SIZE - 1),
      signal,
    ),
    'pig_dailys',
  );
  return {rows, nextPage: rows.length === PIG_DAILYS_PAGE_SIZE ? page + 1 : null};
}

async function queryPigDailyDetail(
  sb: SupabaseClient,
  params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<PigDailyDetailData> {
  const recordId = params?.recordId;
  if (!recordId) throw new Error('pig.daily.detail: recordId is required');

  const result = await withSignal(
    sb.from('pig_dailys').select('*').eq('id', recordId).is('deleted_at', null).maybeSingle(),
    signal,
  );
  return {record: unwrapSingle<PigDailyListRow>(result, 'pig_dailys')};
}

// FAIL-SOFT, like layer.dailys.groupDirectory and unlike the two reads above.
//
// The dropdown this feeds already renders from whatever arrives:
// buildPigGroupOptions takes the array as optional and defaults it to [], and
// the hub's own `save()` falls back to a slugified label when no feeder group
// matches — the SOWS/BOARS path, which has never had a directory row at all. A
// directory outage therefore used to mean "fewer options", never "no daily
// history", because the array came from a boot payload that had already
// tolerated its own failure. Throwing here would newly couple the whole daily
// list to a directory read that never gated it.
const EMPTY_PIG_DAILYS_GROUP_DIRECTORY: PigDailysGroupDirectoryData = Object.freeze({feederGroups: [] as unknown[]});

async function queryPigDailysGroupDirectory(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<PigDailysGroupDirectoryData> {
  try {
    const store = (await withSignal(
      sb.from('app_store').select('key,data').eq('key', 'ppp-feeders-v1').maybeSingle(),
      signal,
    )) as SingleResponse<{key?: unknown; data?: unknown}>;
    if (signal?.aborted) return EMPTY_PIG_DAILYS_GROUP_DIRECTORY;
    const blob = store.data?.data;
    // App's boot loader guards with `if (store['ppp-feeders-v1'])` and leaves
    // the state at [] otherwise, so a missing row is an empty directory rather
    // than an error. A non-array value would have reached the view unchanged
    // before; it is normalized to [] here because every consumer maps over it.
    return {feederGroups: Array.isArray(blob) ? (blob as unknown[]) : []};
  } catch (error) {
    if (signal?.aborted) throw error;
    return EMPTY_PIG_DAILYS_GROUP_DIRECTORY;
  }
}

// ---------------------------------------------------------------------------
// layer.eggDailys.* — the egg daily hub, its record page, and the hub's groups.
// ---------------------------------------------------------------------------
// The two reads that MOVED OWNER rather than shape: the hub's page sequence and
// the record page's by-id read. Page size, deleted_at filter and date DESC
// order are the presenter's own, unchanged; the record read keeps EggDailyPage's
// exact projection and its deleted_at filter, so opening a soft-deleted report
// still resolves to "not found" rather than to a row.
//
// The THIRD read is the one that changes channel. `layerGroups` was the last
// former-eager value LayerEntry still threaded to any route — the
// ppp-layer-groups-v1 app_store blob, carried down from App's boot transaction
// as a prop. The hub reads it here instead, which is what lets `eggdailys`
// retire from the frozen app_store consumer list.
//
// UNLIKE layer.dailys.groupDirectory, this is ONE surface and ONE table. The
// record page never read the blob — EggDailyPage's group fields are free-text
// inputs, not a select — so hoisting the read above the index/record branch
// would give /layer/eggs/<id> a query it has never issued.
const EGG_DAILYS_PAGE_SIZE = 1000;

async function queryEggDailysList(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  pageParam: unknown,
  signal?: AbortSignal,
): Promise<PaginatedPage<EggDailyListRow, unknown>> {
  const page = typeof pageParam === 'number' && Number.isSafeInteger(pageParam) && pageParam >= 0 ? pageParam : 0;
  const rows = unwrap<EggDailyListRow>(
    await withSignal(
      sb
        .from('egg_dailys')
        .select('*')
        .is('deleted_at', null)
        .order('date', {ascending: false})
        .range(page * EGG_DAILYS_PAGE_SIZE, (page + 1) * EGG_DAILYS_PAGE_SIZE - 1),
      signal,
    ),
    'egg_dailys',
  );
  return {rows, nextPage: rows.length === EGG_DAILYS_PAGE_SIZE ? page + 1 : null};
}

async function queryEggDailyDetail(
  sb: SupabaseClient,
  params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<EggDailyDetailData> {
  const recordId = params?.recordId;
  if (!recordId) throw new Error('layer.eggDaily.detail: recordId is required');

  const result = await withSignal(
    sb.from('egg_dailys').select('*').eq('id', recordId).is('deleted_at', null).maybeSingle(),
    signal,
  );
  return {record: unwrapSingle<EggDailyListRow>(result, 'egg_dailys')};
}

// FAIL-SOFT, like layer.dailys.groupDirectory and unlike the two reads above.
//
// The blob reached this hub through App's boot app_store transaction, which
// tolerated its own failure and left LayerContext's array at []. The hub then
// rendered the whole daily history with an empty group select. Throwing here
// would newly couple "no egg reports" to a directory read that never gated the
// list, so a blob outage still means "fewer group options" and never "no egg
// history".
// FAILED, not empty. Both exits below that cannot vouch for the blob return
// this value, and its `layerGroupsStatus` is what stops a consumer from reading
// the empty array as "resolved, and nothing is retired".
const FAILED_EGG_DAILYS_GROUPS: EggDailysGroupsData = Object.freeze({
  layerGroups: [] as unknown[],
  layerGroupsStatus: LAYER_GROUP_READ_FAILED,
});

async function queryEggDailysGroups(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<EggDailysGroupsData> {
  try {
    const store = (await withSignal(
      sb.from('app_store').select('key,data').eq('key', 'ppp-layer-groups-v1').maybeSingle(),
      signal,
    )) as SingleResponse<{key?: unknown; data?: unknown}>;
    if (signal?.aborted) return FAILED_EGG_DAILYS_GROUPS;
    // THE ORDINARY {error} RESPONSE, WHICH IS THE COMMON CASE AND WAS NOT
    // CHECKED. PostgREST reports RLS denials, timeouts and 5xx by RESOLVING
    // with an error member; only a network/abort failure rejects. Reading
    // `store.data?.data` on such a response yields undefined -> [], which is
    // indistinguishable from a farm that has retired nothing.
    if (store.error) return FAILED_EGG_DAILYS_GROUPS;
    const blob = store.data?.data;
    // A MISSING ROW IS RESOLVED, NOT FAILED. The boot loader guarded with
    // `if (store['ppp-layer-groups-v1'])` and left the state at [], so a farm
    // that has never saved the document has an empty group list rather than an
    // error. A non-array value is normalized here because the hub filters it
    // immediately — it is a malformed document, not an unreadable one.
    return {
      layerGroups: Array.isArray(blob) ? (blob as unknown[]) : [],
      layerGroupsStatus: LAYER_GROUP_READ_RESOLVED,
    };
  } catch (error) {
    if (signal?.aborted) throw error;
    return FAILED_EGG_DAILYS_GROUPS;
  }
}

// ---------------------------------------------------------------------------
// layer.home.core — everything the Layers Home dashboard renders from.
// ---------------------------------------------------------------------------
// The former provider-backed Layers Home reached these five values through THREE
// App-owned providers and not one physical read of its own:
//
//   layer_batches / layer_housings   LayerContext, filled by src/app/boot/
//                                    layerBootLoad.ts. Same projections, same
//                                    order('name') / order('start_date').
//   layer_dailys / egg_dailys        LayerContext's allLayerDailys/allEggDailys,
//                                    filled by App's boot SIDECAR — the
//                                    paginate-to-exhaustion `fetchAll` in the
//                                    former boot sidecar. Complete histories, not
//                                    windows.
//   ppp-feed-costs-v1                FeedCostsContext, filled from App's boot
//                                    app_store transaction and used here only as
//                                    the per-lb fallback for a batch with no
//                                    cost snapshot of its own.
//
// EVERY READ IS FAIL-SOFT, UNIFORMLY, AND THAT IS A REPRODUCTION RATHER THAN A
// CHOICE.
//
// This is the one place in the catalog where "throw so the page can show an
// error" would be the WRONG port, because there is no error to show: the
// dashboard has never had an error branch. Each of these five values arrived
// from a loader that swallowed its own failure and published an empty value —
// layerBootLoad degrades each table to [] independently and logs, the daily
// sidecar's catch is documented as "non-fatal, this load has never been a
// global boot dependency", and a missing ppp-feed-costs-v1 row leaves
// FeedCostsContext at its all-zero initializer. So a degraded read has always
// rendered the dashboard with fewer cards and "No data yet" metrics. Making any
// of them fatal here would introduce a blank page and an unhandled resource
// error that no existing UI catches — a behaviour change smuggled in under a
// refactor. Each read is therefore caught on its own, so one denied table
// cannot empty the other four.
//
// The whole payload is INDEPENDENT of the LayerContext mirror it stops reading.
// App still publishes allLayerDailys for HomeDashboard, AnimalHistoryPage and
// BroilerFeedView, and the layer/egg action-time projection refreshes still
// keep that mirror in step for them. This surface re-reads on every mount
// instead (queryClient keeps staleTime at 0), which is the same freshness
// contract the mirror gave it and one fewer thing to invalidate.
const NO_LAYERS_HOME_ROWS: LayerDailyListRow[] = [];
const NO_LAYERS_HOME_BATCHES: LayerBatchCoreRow[] = [];
const NO_LAYERS_HOME_HOUSINGS: LayerHousingCoreRow[] = [];
const NO_LAYERS_HOME_EGG_ROWS: EggDailyListRow[] = [];
// FeedCostsContext's own `useState` initializer, reproduced: a boot with no
// ppp-feed-costs-v1 row leaves every rate at 0, and computeLayerFeedCost then
// costs an un-snapshotted batch at zero rather than at nothing.
const NO_LAYERS_HOME_FEED_COSTS: LayersHomeFeedCosts = Object.freeze({
  starter: 0,
  grower: 0,
  layer: 0,
  pig: 0,
  grit: 0,
});

// One read, degraded to its own empty value. Same disposition — and the same
// reason — as optionalHomeStandardRows above: App's boot published each of
// these independently and swallowed its own failure, so an individual denied
// table has to stay empty DATA rather than become a dashboard-level error.
async function optionalLayersHomeRows<Row>(read: () => Promise<Row[]>, empty: Row[], signal?: AbortSignal) {
  try {
    const rows = await read();
    if (signal?.aborted) return empty;
    return rows;
  } catch (error) {
    if (signal?.aborted) throw error;
    return empty;
  }
}

async function queryLayersHomeCore(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<LayersHomeCoreData> {
  const [layerBatches, layerHousings, allLayerDailys, allEggDailys, feedCosts] = await Promise.all([
    optionalLayersHomeRows<LayerBatchCoreRow>(
      async () => {
        const res = (await withSignal(
          sb.from('layer_batches').select('*').order('name'),
          signal,
        )) as ListResponse<LayerBatchCoreRow>;
        return unwrap<LayerBatchCoreRow>(res, 'layer_batches');
      },
      NO_LAYERS_HOME_BATCHES,
      signal,
    ),
    optionalLayersHomeRows<LayerHousingCoreRow>(
      async () => {
        const res = (await withSignal(
          sb.from('layer_housings').select('*').order('start_date'),
          signal,
        )) as ListResponse<LayerHousingCoreRow>;
        return unwrap<LayerHousingCoreRow>(res, 'layer_housings');
      },
      NO_LAYERS_HOME_HOUSINGS,
      signal,
    ),
    // wcfSelectAll IS App's sidecar `fetchAll`, member for member: 1,000-row
    // pages, stop on an error, an empty page or a short page, and return
    // whatever was gathered. Using the shared helper rather than a second copy
    // is what keeps the two from drifting apart.
    optionalLayersHomeRows<LayerDailyListRow>(
      () =>
        wcfSelectAll<LayerDailyListRow>((from, to) =>
          withSignal(
            sb
              .from('layer_dailys')
              .select('*')
              .is('deleted_at', null)
              .order('date', {ascending: false})
              .range(from, to),
            signal,
          ),
        ),
      NO_LAYERS_HOME_ROWS,
      signal,
    ),
    optionalLayersHomeRows<EggDailyListRow>(
      () =>
        wcfSelectAll<EggDailyListRow>((from, to) =>
          withSignal(
            sb.from('egg_dailys').select('*').is('deleted_at', null).order('date', {ascending: false}).range(from, to),
            signal,
          ),
        ),
      NO_LAYERS_HOME_EGG_ROWS,
      signal,
    ),
    (async () => {
      try {
        const store = (await withSignal(
          sb.from('app_store').select('key,data').eq('key', 'ppp-feed-costs-v1').maybeSingle(),
          signal,
        )) as SingleResponse<{key?: unknown; data?: unknown}>;
        if (signal?.aborted) return NO_LAYERS_HOME_FEED_COSTS;
        const blob = store.data?.data;
        // App's boot guards with `if (store['ppp-feed-costs-v1'])` and normalizes
        // only then, so an absent row keeps the context initializer. The cast is
        // App's own: normalizeFeedCosts declares an OPEN record because unknown
        // keys must survive, and the five rates are numeric after it runs.
        if (!blob) return NO_LAYERS_HOME_FEED_COSTS;
        return normalizeFeedCosts(blob) as LayersHomeFeedCosts;
      } catch (error) {
        if (signal?.aborted) throw error;
        return NO_LAYERS_HOME_FEED_COSTS;
      }
    })(),
  ]);

  return {layerBatches, layerHousings, allLayerDailys, allEggDailys, feedCosts};
}

// ---------------------------------------------------------------------------
// broiler.dailys.* — the broiler daily hub, its record page, and their shared
// batch directory.
// ---------------------------------------------------------------------------
// The same three-resource shape as the layer daily surfaces above, for the same
// reason. The hub's 1,000-row page size, its deleted_at filter and its
// date DESC / submitted_at DESC ordering are unchanged; only the cursor's owner
// moves, from presenter state to the paginated resource. The record read keeps
// PoultryDailyPage's exact projection and its deleted_at filter, so opening a
// soft-deleted report still resolves to "not found" rather than to a row.
//
// The DIRECTORY is what forces both surfaces to migrate in one change. The
// ppp-v4 batch list reached them through a single `batches` prop on
// BroilerEntry carrying App's former-eager boot result, and a resource may only
// name surfaces the manifest declares migrated — so localizing the hub's
// dropdown without also localizing the record page would have left the record
// page consuming a permanent resource it did not own.
const BROILER_DAILYS_PAGE_SIZE = 1000;

async function queryBroilerDailysList(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  pageParam: unknown,
  signal?: AbortSignal,
): Promise<PaginatedPage<BroilerDailyListRow, unknown>> {
  const page = typeof pageParam === 'number' && Number.isSafeInteger(pageParam) && pageParam >= 0 ? pageParam : 0;
  const rows = unwrap<BroilerDailyListRow>(
    await withSignal(
      sb
        .from('poultry_dailys')
        .select('*')
        .is('deleted_at', null)
        .order('date', {ascending: false})
        .order('submitted_at', {ascending: false})
        .range(page * BROILER_DAILYS_PAGE_SIZE, (page + 1) * BROILER_DAILYS_PAGE_SIZE - 1),
      signal,
    ),
    'poultry_dailys',
  );
  return {rows, nextPage: rows.length === BROILER_DAILYS_PAGE_SIZE ? page + 1 : null};
}

async function queryBroilerDailyDetail(
  sb: SupabaseClient,
  params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<BroilerDailyDetailData> {
  const recordId = params?.recordId;
  if (!recordId) throw new Error('broiler.daily.detail: recordId is required');

  const result = await withSignal(
    sb.from('poultry_dailys').select('*').eq('id', recordId).is('deleted_at', null).maybeSingle(),
    signal,
  );
  return {record: unwrapSingle<BroilerDailyListRow>(result, 'poultry_dailys')};
}

// FAIL-SOFT, like layer.dailys.groupDirectory and unlike the two reads above.
//
// The group dropdown this feeds already renders from whatever it is given:
// buildBroilerGroupOptions defaults `batches` to [] and the hub's own
// `batchRows` is only ever filtered and mapped. A directory outage therefore
// used to mean "fewer options", never "no daily history", because the array
// came from a boot payload that had already tolerated its own failure.
// Throwing here would newly couple the whole daily list to a blob read that
// never gated it.
//
// The rows go through normalizedLightHomeBroilerBatches, which is the catalog's
// existing write-free re-implementation of App's cold-boot ppp-v4 repair. Both
// consumers filter `status === 'active'`, and `status` is precisely what that
// repair rewrites — a planned batch activates on its hatch day, an active one
// processes on its processing date. Handing the stored value over unrepaired
// would silently change which batches the dropdown offers.
const EMPTY_BROILER_DAILYS_BATCH_DIRECTORY: BroilerDailysBatchDirectoryData = Object.freeze({
  batches: [] as unknown[],
});

async function queryBroilerDailysBatchDirectory(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<BroilerDailysBatchDirectoryData> {
  try {
    const store = (await withSignal(
      sb.from('app_store').select('key,data').eq('key', 'ppp-v4').maybeSingle(),
      signal,
    )) as SingleResponse<{key?: unknown; data?: unknown}>;
    if (signal?.aborted) return EMPTY_BROILER_DAILYS_BATCH_DIRECTORY;
    // The boot loader guards with `if (store['ppp-v4'])` and leaves the state at
    // [] otherwise, so a missing row is an empty directory rather than an error.
    return {batches: normalizedLightHomeBroilerBatches(store.data?.data)};
  } catch (error) {
    if (signal?.aborted) throw error;
    return EMPTY_BROILER_DAILYS_BATCH_DIRECTORY;
  }
}

// ---------------------------------------------------------------------------
// broiler.timeline.core — the two directories /broiler/timeline draws.
// ---------------------------------------------------------------------------
// Both used to reach the Gantt through provider hooks fed by App's global boot:
// the ppp-v4 blob off BatchesContext, the layer directory off LayerContext.
// App's boot ordering is preserved for the layer read (`order('name')`), which
// is what fixes the order of the layer rows drawn under the broiler ones.
//
// BOTH READS THROW, unlike the daily directory above, and the difference is the
// consumer. There a missing option is a smaller dropdown; here the batch list
// IS the page, and this view renders an absence as
// `No batches yet — click "+ Add Batch" to get started`. Resolving an outage to
// [] would print that sentence over a farm full of live batches — the
// empty-farm lie layer.batches.core already rejects — so the surface reports a
// load error and offers Retry instead.
async function queryBroilerTimelineCore(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<BroilerTimelineCoreData> {
  const [store, layerBatches] = await Promise.all([
    withSignal(sb.from('app_store').select('key,data').eq('key', 'ppp-v4').maybeSingle(), signal),
    withSignal(sb.from('layer_batches').select('*').order('name'), signal),
  ]);

  const storeRow = unwrapSingle<{key?: unknown; data?: unknown}>(
    store as SingleResponse<{key?: unknown; data?: unknown}>,
    'app_store',
  );
  return {
    // The boot loader guards with `if (store['ppp-v4'])` and leaves the state at
    // [] otherwise, so a missing row is an empty timeline rather than an error.
    batches: normalizedLightHomeBroilerBatches(storeRow?.data),
    layerBatches: unwrap<LayerBatchCoreRow>(layerBatches as ListResponse<LayerBatchCoreRow>, 'layer_batches'),
  };
}

// ---------------------------------------------------------------------------
// broiler.home.core — the two inputs the /broiler dashboard renders from.
// ---------------------------------------------------------------------------
// ERROR POSTURE IS PER-READ, AND IT IS COPIED FROM THE BOOT LOADER RATHER THAN
// CHOSEN HERE.
//
// The blob throws. It reached this page through App's app_store boot
// transaction, which had no per-key tolerance, and every tile on the page is
// derived from it — an outage resolved to [] would render a farm full of live
// batches as "0 In Process Batches / 0 Birds on Farm".
//
// The daily history does NOT throw, because App's own loader already refuses
// to: `loadAllPoultryDailys(...).catch((e) => { console.warn(...); return []; })`
// is the one boot-time owner of broilerDailys, and it degrades to an empty
// array after a fully-failed load. Making it fatal here would turn a dashboard
// whose feed and mortality columns went blank into no dashboard at all. It is
// caught in its own IIFE so a failure cannot touch the blob.
//
// The pagination is the loader's, not new: loadAllPoultryDailys throws as a
// whole on any page-read failure rather than returning a partial set, which is
// exactly what the loop below does, and is why the catch above can be trusted
// to mean "nothing usable" rather than "silently truncated".
const BROILER_HOME_DAILY_PAGE = 1000;
const BROILER_HOME_DAILY_COLUMNS = 'batch_label,feed_type,feed_lbs,grit_lbs,mortality_count,date';
const NO_BROILER_HOME_DAILYS: BroilerHomeDailyRow[] = [];

async function queryBroilerHomeCore(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<BroilerHomeCoreData> {
  const [store, broilerDailys] = await Promise.all([
    withSignal(sb.from('app_store').select('key,data').eq('key', 'ppp-v4').maybeSingle(), signal),
    (async () => {
      try {
        const rows: BroilerHomeDailyRow[] = [];
        for (let offset = 0; ; offset += BROILER_HOME_DAILY_PAGE) {
          const {data, error} = (await withSignal(
            sb
              .from('poultry_dailys')
              .select(BROILER_HOME_DAILY_COLUMNS)
              .is('deleted_at', null)
              .order('date', {ascending: false})
              .order('submitted_at', {ascending: false})
              .range(offset, offset + BROILER_HOME_DAILY_PAGE - 1),
            signal,
          )) as ListResponse<BroilerHomeDailyRow>;
          if (error) throw new Error(`poultry_dailys: ${error.message}`);
          if (!data || data.length === 0) break;
          rows.push(...data);
          if (data.length < BROILER_HOME_DAILY_PAGE) break;
        }
        // A named constant rather than a bare `[]`, because query_scope_contract
        // rejects an array LITERAL returned from anything in src/app/data.
        if (signal?.aborted) return NO_BROILER_HOME_DAILYS;
        return rows;
      } catch (error) {
        if (signal?.aborted) throw error;
        return NO_BROILER_HOME_DAILYS;
      }
    })(),
  ]);

  const storeRow = unwrapSingle<{key?: unknown; data?: unknown}>(
    store as SingleResponse<{key?: unknown; data?: unknown}>,
    'app_store',
  );
  return {
    // Normalized for the same reason the timeline's copy is: this page filters
    // `status === 'processed'` and reads `hatchDate` / `breed` / `hatchery`,
    // and all four are columns App's cold-boot ppp-v4 repair rewrites.
    batches: normalizedLightHomeBroilerBatches(storeRow?.data),
    broilerDailys,
  };
}

// ---------------------------------------------------------------------------
// broiler.batches.hub — the two provider reads the Broiler Batches hub keeps.
// ---------------------------------------------------------------------------
// ppp-v4 is deliberately read with ppp-feed-costs-v1 and repaired through the
// same applyBroilerBootRepair derivation BatchesContext gets during legacy
// boot. The retained context is now a write-closure mirror, never the list or
// record page's rendered source of truth. The remaining route-owned values are:
//
//   poultry_dailys      DailysRecentContext.broilerDailys, the COMPLETE history.
//   ppp-feed-costs-v1   FeedCostsContext.feedCosts.
//
// poultry_dailys and absent feed costs remain fail-soft as legacy boot did;
// the keyed app_store transport is fatal so a denied ppp-v4 row never becomes
// a plausible empty batch directory.
//
// The daily history's boot owner is
// `loadAllPoultryDailys(...).catch((e) => { console.warn(…); return []; })` —
// one degrade-to-empty, after a fully-failed load. The loader is CALLED rather
// than re-implemented, exactly as broiler.feed.core calls it: it is the module
// that fixed the silently-truncated history by capturing the page error and
// throwing afterwards, so a second copy of the loop here would be a second copy
// of that fix, and eventually a drift from it. Calling it also means this
// resource's poultry_dailys read adds no new physical call site.
//
// The rates degrade to FeedCostsContext's own all-zero initializer, because
// App's fan-out guards with `if (store['ppp-feed-costs-v1'])` and a farm that
// has never opened the Feed panel genuinely has no row. Making either fatal
// would put an error branch on a hub that has never had one — and would do it
// over a list whose own subject, `batches`, still arrives from the boot the
// error would be reporting on.
const NO_BROILER_BATCHES_HUB_DAILYS: BroilerDailyListRow[] = [];
// FeedCostsContext's `useState` initializer, reproduced for the same reason
// NO_LAYERS_HOME_FEED_COSTS reproduces it: an absent row leaves every rate at
// 0, and reopening an un-snapshotted batch then stamps zeros rather than
// undefined.
const NO_BROILER_BATCHES_HUB_FEED_COSTS: LayersHomeFeedCosts = Object.freeze({
  starter: 0,
  grower: 0,
  layer: 0,
  pig: 0,
  grit: 0,
});
const BROILER_BATCHES_HUB_APP_STORE_KEYS = Object.freeze(['ppp-v4', 'ppp-feed-costs-v1'] as const);

async function queryBroilerBatchesHub(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<BroilerBatchesHubData> {
  const [broilerDailys, store] = await Promise.all([
    (async () => {
      try {
        return (await loadAllPoultryDailys(sb, signal ?? null)) as BroilerDailyListRow[];
      } catch (error) {
        if (signal?.aborted) throw error;
        return NO_BROILER_BATCHES_HUB_DAILYS;
      }
    })(),
    withSignal(sb.from('app_store').select('key,data').in('key', BROILER_BATCHES_HUB_APP_STORE_KEYS), signal),
  ]);

  const rows = unwrap<{key?: unknown; data?: unknown}>(
    store as ListResponse<{key?: unknown; data?: unknown}>,
    'app_store',
  );
  const blobs: Record<string, unknown> = Object.create(null) as Record<string, unknown>;
  for (const row of rows) if (typeof row?.key === 'string') blobs[row.key] = row.data;

  const storedBatches = blobs['ppp-v4'];
  const storedFeedCosts = blobs['ppp-feed-costs-v1'];
  // Keep App boot's truthiness guard: a malformed truthy ppp-v4 row must fail
  // loudly rather than turn the farm's batch directory into an empty list.
  const batches = storedBatches ? applyBroilerBootRepair(storedBatches as BroilerBatch[], storedFeedCosts).batches : [];
  const feedCosts = storedFeedCosts
    ? (normalizeFeedCosts(storedFeedCosts) as LayersHomeFeedCosts)
    : NO_BROILER_BATCHES_HUB_FEED_COSTS;

  return {batches, broilerDailys, feedCosts};
}

// ---------------------------------------------------------------------------
// broiler.batchForm.actionState — the retained App form closures' cold base.
// ---------------------------------------------------------------------------
//
// The fullscreen form is a stateful overlay, not a route component that owns
// its submit/delete helpers. Those helpers remain in App and close over the
// three values below:
//
//   ppp-v4               BatchesContext.batches, for update/create/delete
//   ppp-feed-costs-v1    FeedCostsContext.feedCosts, for active-rate stamping
//   layer_batches        LayerContext.layerBatches, for schedule conflicts
//
// A released form has no legacy boot to fill those mirrors. Supplying only the
// callbacks would therefore preserve their identities while changing what they
// save: an edit over an empty `batches` array is carried through by the
// save-time merge and silently drops the operator's changed row. The fullscreen
// renderer owns this resource and commits it into the three write mirrors in a
// layout effect before it attaches BatchForm's handlers.
//
// ppp-v4 remains fatal: a form that cannot establish its write base must not
// offer a delete or edit that looks successful. The layer directory keeps the
// legacy boot's fail-soft posture — its conflict is soft, and a layer-table
// outage historically left the form usable with no layer conflicts. Missing
// feed costs likewise retain FeedCostsContext's all-zero initializer.
const NO_BROILER_BATCH_FORM_ACTION_LAYER_BATCHES: LayerBatchCoreRow[] = [];
const NO_BROILER_BATCH_FORM_ACTION_FEED_COSTS: LayersHomeFeedCosts = Object.freeze({
  starter: 0,
  grower: 0,
  layer: 0,
  pig: 0,
  grit: 0,
});
const BROILER_BATCH_FORM_ACTION_STORE_KEYS = Object.freeze(['ppp-v4', 'ppp-feed-costs-v1'] as const);

async function queryBroilerBatchFormActionState(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<BroilerBatchFormActionStateData> {
  const [store, layerBatches] = await Promise.all([
    withSignal(sb.from('app_store').select('key,data').in('key', BROILER_BATCH_FORM_ACTION_STORE_KEYS), signal),
    (async () => {
      try {
        const result = await withSignal(sb.from('layer_batches').select('*').order('name'), signal);
        return unwrap<LayerBatchCoreRow>(result as ListResponse<LayerBatchCoreRow>, 'layer_batches');
      } catch (error) {
        if (signal?.aborted) throw error;
        return NO_BROILER_BATCH_FORM_ACTION_LAYER_BATCHES;
      }
    })(),
  ]);

  const rows = unwrap<{key?: unknown; data?: unknown}>(
    store as ListResponse<{key?: unknown; data?: unknown}>,
    'app_store',
  );
  const blobs: Record<string, unknown> = Object.create(null) as Record<string, unknown>;
  for (const row of rows) if (typeof row?.key === 'string') blobs[row.key] = row.data;

  const storedBatches = blobs['ppp-v4'];
  const storedFeedCosts = blobs['ppp-feed-costs-v1'];
  return {
    // Keep App's truthiness guard and its repair order. A truthy non-array is
    // intentionally allowed to throw through applyBroilerBootRepair: treating
    // it as an empty directory would turn a malformed write base into a save.
    batches: storedBatches ? applyBroilerBootRepair(storedBatches as BroilerBatch[], storedFeedCosts).batches : [],
    feedCosts: storedFeedCosts
      ? (normalizeFeedCosts(storedFeedCosts) as LayersHomeFeedCosts)
      : NO_BROILER_BATCH_FORM_ACTION_FEED_COSTS,
    layerBatches,
  };
}

// ---------------------------------------------------------------------------
// broiler.batchForm.dailys — the one read the broiler batch form owns.
// ---------------------------------------------------------------------------
// TWO SURFACES, ONE RESOURCE, AND THAT WAS FORCED RATHER THAN PREFERRED.
//
// BatchForm is mounted in two places: App renders it full-screen behind
// `showForm && inBroilerSpace && !pathname.startsWith('/broiler/batches/')`,
// and BroilerBatchPage renders the same component `embedded` on the record
// page. A catalog resource may only name surfaces the manifest declares
// migrated, so localizing the form's read for one of them would have left the
// other consuming a permanent resource it did not own. Same shape as
// layer.dailys.groupDirectory, and the same reason.
//
// FAIL-SOFT, AND THE LOADER IS CALLED RATHER THAN COPIED. The boot owner of
// this history is `loadAllPoultryDailys(...).catch(() => [])`; making it fatal
// here would blank the batch EDIT FORM on a daily-history outage, which is the
// one screen an operator needs when the reports are the thing going wrong.
// Calling the loader also means this resource adds no new physical call site,
// and keeps the throw-after-a-failed-page fix in one module.
const NO_BROILER_BATCH_FORM_DAILYS: BroilerDailyListRow[] = [];

async function queryBroilerBatchFormDailys(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<BroilerBatchFormDailysData> {
  try {
    return {broilerDailys: (await loadAllPoultryDailys(sb, signal ?? null)) as BroilerDailyListRow[]};
  } catch (error) {
    if (signal?.aborted) throw error;
    return {broilerDailys: NO_BROILER_BATCH_FORM_DAILYS};
  }
}

// ---------------------------------------------------------------------------
// cattle.breeding.core — the breeding-cycle list's three reads.
// ---------------------------------------------------------------------------
// Reproduces CattleBreedingView.loadAll exactly — the same three tables, the
// same narrowed `id,tag,herd` projection, the same deleted_at filter and the
// same bull_exposure_start DESC sort — with ONE deliberate difference: a failed
// read throws instead of being swallowed.
//
// The page previously wrote `if (cR.data) setCycles(cR.data)` and then
// unconditionally cleared loading. A failed read therefore left the lists empty
// and rendered "No breeding cycles yet. Click + New Cycle to record one." —
// inviting the operator to create a cycle that may already exist. That is the
// one state an operator must never be shown for a read that did not happen, so
// the consumer gains an error branch in the same change.
//
// The identifier names below deliberately match the page's own locals: the
// physical-call inventory fingerprints a call site from the surrounding chain,
// so renaming them for tidiness would break the pairwise retire/add evidence.
// ---------------------------------------------------------------------------
async function queryCattleBreedingCore(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<CattleBreedingCoreData> {
  const [cR, calR, ctR] = await Promise.all([
    withSignal(sb.from('cattle_breeding_cycles').select('*').order('bull_exposure_start', {ascending: false}), signal),
    withSignal(sb.from('cattle_calving_records').select('*'), signal),
    withSignal(sb.from('cattle').select('id,tag,herd').is('deleted_at', null), signal),
  ]);

  return {
    cycles: unwrap<CattleRow>(cR, 'cattle_breeding_cycles'),
    calvingRecords: unwrap<CattleCalvingRecordRow>(calR, 'cattle_calving_records'),
    cattle: unwrap<CattleRow>(ctR, 'cattle'),
  };
}

// ---------------------------------------------------------------------------
// tasks — the two profile maps every Task Center panel loads for itself.
// ---------------------------------------------------------------------------
// WRITTEN OUT HERE RATHER THAN DELEGATED, for two independent reasons.
//
// The first is mechanical: surface_router_derivation joins a declared table to
// an inventoried call site INSIDE THIS FILE (`routeDataCatalog.js::from:X@`),
// so `webform_config` can only be declared by a resource if this module names
// it literally. Calling tasksCenterApi.loadTaskAssignableProfilesById would
// leave the declaration unprovable — the same defect that got layer.batches
// dropped from an earlier wave.
//
// The second is that both lib helpers stay LIVE for callers this migration does
// not touch: ProcessingCalendarView and ProcessingTemplatesModal use the
// eligible map, MentionTextarea uses the assignable one. They cannot move, so
// this is a deliberate sibling — the same decision cattleCache's uncached
// primitive records, and for the same reason.
//
// The fail-CLOSED contract of the assignable map is reproduced exactly, because
// it is a SECURITY FILTER and not an error-handling style: a webform_config
// read that errors or throws yields an EMPTY assignable map, never the
// unfiltered eligible list, so a transient failure cannot re-expose a profile
// hidden through Team Availability -> Public Tasks. Only an abort is rethrown,
// matching the forecast reads above.
async function readEligibleProfiles(sb: SupabaseClient, signal?: AbortSignal): Promise<TaskProfilesById> {
  const {data, error} = await withSignal(sb.rpc('list_eligible_assignees'), signal);
  // The error TEXT is the lib helper's, unchanged: it reaches the operator
  // through each tab's error banner, so changing the prefix would change what
  // the farm reads on a failure.
  if (error) throw new Error(`loadEligibleProfilesById: ${error.message}`);
  const out: TaskProfilesById = {};
  for (const p of (data || []) as TaskProfile[]) out[p.id] = {id: p.id, full_name: p.full_name};
  return out;
}

async function readAssignableProfiles(sb: SupabaseClient, signal?: AbortSignal): Promise<TaskProfilesById> {
  const {data: rpcData, error: rpcErr} = await withSignal(sb.rpc('list_eligible_assignees'), signal);
  if (rpcErr) throw new Error(`loadTaskAssignableProfilesById: ${rpcErr.message}`);
  const eligible = ((rpcData || []) as TaskProfile[]).map((p) => ({id: p.id, full_name: p.full_name}));

  let availability: unknown = null;
  try {
    const {data: row, error: rowErr} = await withSignal(
      sb.from('webform_config').select('data').eq('key', TASKS_PUBLIC_ASSIGNEE_AVAILABILITY_KEY).maybeSingle(),
      signal,
    );
    if (rowErr) {
      if (signal?.aborted) throw new Error(`webform_config: ${rowErr.message}`);
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('loadTaskAssignableProfilesById availability read failed:', rowErr.message);
      }
      return {};
    }
    if (row && row.data) availability = row.data;
  } catch (e) {
    if (signal?.aborted) throw e;
    if (typeof console !== 'undefined' && console.warn) {
      console.warn(
        'loadTaskAssignableProfilesById availability read threw:',
        typeof e === 'object' && e !== null ? (e as {message?: unknown}).message : undefined,
      );
    }
    return {};
  }

  const out: TaskProfilesById = {};
  for (const p of visiblePublicAssignees(eligible, availability)) out[p.id] = {id: p.id, full_name: p.full_name};
  return out;
}

// ---------------------------------------------------------------------------
// tasks.center.profiles — the Task Center shell's own prefetch.
//
// Soft-fail at the consumer, not here: the shell swallows this failure today so
// the page still renders and the New Task modal simply opens with an empty
// assignee list. Throwing is still correct HERE — the resource reports what
// happened and the surface decides to ignore it, which is the only arrangement
// in which "ignored" is a visible decision rather than a lost error.
// ---------------------------------------------------------------------------
async function queryTasksCenterProfiles(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<TasksCenterProfilesData> {
  const [profilesById, assignableProfilesById] = await Promise.all([
    readEligibleProfiles(sb, signal),
    readAssignableProfiles(sb, signal),
  ]);
  return {profilesById, assignableProfilesById};
}

// ---------------------------------------------------------------------------
// tasks.center.mine — the My Tasks tab's three reads, as ONE load.
//
// They stay together because the tab's catch is all-or-nothing: a failed
// profile read clears the task list too. Splitting the profile maps into their
// own resource would leave a task list rendered under a failed name lookup,
// which is a different screen from the one this tab has always shown.
// ---------------------------------------------------------------------------
async function queryTasksCenterMine(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<TasksCenterMineData> {
  const [tasks, profilesById, assignableProfilesById] = await Promise.all([
    withSignal(
      sb
        .from('task_instances')
        .select('*')
        .eq('status', 'open')
        .order('due_date', {ascending: true})
        .order('title', {ascending: true}),
      signal,
    ),
    readEligibleProfiles(sb, signal),
    readAssignableProfiles(sb, signal),
  ]);
  return {tasks: unwrap<TaskInstanceRow>(tasks, 'loadOpenTaskInstances'), profilesById, assignableProfilesById};
}

// ---------------------------------------------------------------------------
// tasks.center.recurring — templates plus their open instances.
// ---------------------------------------------------------------------------
async function queryTasksCenterRecurring(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<TasksCenterRecurringData> {
  const [templates, openInstances, profilesById, assignableProfilesById] = await Promise.all([
    withSignal(
      sb.from('task_templates').select('*').order('active', {ascending: false}).order('title', {ascending: true}),
      signal,
    ),
    withSignal(
      sb
        .from('task_instances')
        .select('*')
        .eq('status', 'open')
        .eq('designation', 'recurring')
        .order('due_date', {ascending: true})
        .order('title', {ascending: true}),
      signal,
    ),
    readEligibleProfiles(sb, signal),
    readAssignableProfiles(sb, signal),
  ]);
  return {
    templates: unwrap<TaskTemplateRow>(templates, 'loadRecurringTaskTemplates'),
    openInstances: unwrap<TaskInstanceRow>(openInstances, 'loadOpenRecurringInstances'),
    profilesById,
    assignableProfilesById,
  };
}

// ---------------------------------------------------------------------------
// tasks.center.completed — the capped completion review.
//
// The cap is 200 and it is a LIMIT, not a page: the tab is a read-only review
// pane and older completions were always left to direct DB access. Written as a
// constant rather than a param because the one caller has only ever used the
// helper's default, and a param that is never varied is a key dimension that
// isolates nothing.
// ---------------------------------------------------------------------------
const TASKS_COMPLETED_LIMIT = 200;

async function queryTasksCenterCompleted(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<TasksCenterCompletedData> {
  const [tasks, profilesById] = await Promise.all([
    withSignal(
      sb
        .from('task_instances')
        .select('*')
        .eq('status', 'completed')
        .order('completed_at', {ascending: false, nullsFirst: false})
        .order('title', {ascending: true})
        .limit(TASKS_COMPLETED_LIMIT),
      signal,
    ),
    readEligibleProfiles(sb, signal),
  ]);
  return {tasks: unwrap<TaskInstanceRow>(tasks, 'loadCompletedTaskInstances'), profilesById};
}

// ---------------------------------------------------------------------------
// tasks.center.system — admin-only rules plus the instances they generated.
//
// The .or() is the only way to express the union and it is load-bearing: mig
// 053's BEFORE INSERT trigger sets designation='system' for new rule-generated
// rows, but legacy paths wrote one without the other, so matching on
// designation alone silently drops a whole row population.
// ---------------------------------------------------------------------------
async function queryTasksCenterSystem(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<TasksCenterSystemData> {
  const [rules, openInstances, profilesById, assignableProfilesById] = await Promise.all([
    withSignal(
      sb.from('task_system_rules').select('*').order('active', {ascending: false}).order('name', {ascending: true}),
      signal,
    ),
    withSignal(
      sb
        .from('task_instances')
        .select('*')
        .eq('status', 'open')
        .or('designation.eq.system,from_system_rule_id.not.is.null')
        .order('due_date', {ascending: true})
        .order('title', {ascending: true}),
      signal,
    ),
    readEligibleProfiles(sb, signal),
    readAssignableProfiles(sb, signal),
  ]);
  return {
    rules: unwrap<TaskSystemRuleRow>(rules, 'loadSystemTaskRules'),
    openInstances: unwrap<TaskInstanceRow>(openInstances, 'loadOpenSystemTaskInstances'),
    profilesById,
    assignableProfilesById,
  };
}

// ---------------------------------------------------------------------------
// tasks.todo.list — the shared To Do projection.
//
// RPC-backed, so it goes through todoApi's wrapper rather than being rewritten
// here: mig 115 owns the whole projection (sections, photo aggregation,
// participant visibility) and there is no table to declare. Same arrangement as
// processing.calendar.core, and the same benefit — one owner of the envelope.
//
// `recordId` is a KEY-ONLY parameter. The list surface passes nothing and the
// record surface passes its id, so an A -> B record navigation is a key change
// that refetches, which is what the record page's effect does today. Without it
// the two surfaces would share one key and a record navigation would paint a
// list fetched for some other record with no refresh at all.
// ---------------------------------------------------------------------------
async function queryTasksTodoList(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<TasksTodoListData> {
  return listTodoItems(sb, {includeCompleted: true, signal});
}

// ---------------------------------------------------------------------------
// tasks.assignable.profiles — the manager-gated dropdown map, on its own.
//
// Separate from the panel resources ON PURPOSE: on the To Do surfaces this read
// is gated on canManage and fails SOFT, while the list read is ungated and
// fails HARD. Folding them together would either blank the To Do list on a
// dropdown failure or stop the list failing closed. Two contracts, two
// resources.
// ---------------------------------------------------------------------------
async function queryTasksAssignableProfiles(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<TaskProfilesById> {
  return readAssignableProfiles(sb, signal);
}

// ---------------------------------------------------------------------------
// projects.jobs — the Projects page's one read (owner decisions D1-D17).
//
// list_project_jobs returns every live job with its people and photos, plus the
// people who may be assigned (the hidden-assignee list is applied on the
// server, D10). The reply is validated by readProjectJobsReply and fails
// closed, so a malformed row shows the error banner instead of a wrong bar.
// ---------------------------------------------------------------------------
async function queryProjectJobs(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<ProjectJobsData> {
  return listProjectJobs(sb, signal);
}

// ---------------------------------------------------------------------------
// tasks.instance.core — the task record page's four reads.
//
// Keyed on instanceId, so navigating task A -> B is a KEY CHANGE and a late
// response for A can never paint into B.
//
// The photo read stays SEQUENTIAL, CONDITIONAL and FAIL-SOFT, exactly as the
// page has it: no task means no photo query at all, and a photo failure yields
// an empty gallery rather than destroying a record page that has already
// loaded. Only an abort is rethrown, so cancellation still propagates.
// ---------------------------------------------------------------------------
async function queryTaskInstanceCore(
  sb: SupabaseClient,
  params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<TaskInstanceCoreData> {
  const instanceId = params?.instanceId;
  if (!instanceId) throw new Error('tasks.instance.core: instanceId is required');

  const [taskRes, profilesById, assignableProfilesById] = await Promise.all([
    withSignal(sb.from('task_instances').select('*').eq('id', instanceId).maybeSingle(), signal),
    readEligibleProfiles(sb, signal),
    readAssignableProfiles(sb, signal),
  ]);
  const task = unwrapSingle<TaskInstanceRow>(taskRes, 'loadTaskInstanceById');

  let photos: TaskInstancePhotoRow[] = [];
  if (task) {
    try {
      const photoRes = await withSignal(
        sb
          .from('task_instance_photos')
          .select('id, instance_id, kind, storage_path, sort_order, uploaded_at, uploaded_by_profile_id')
          .eq('instance_id', instanceId)
          .order('kind', {ascending: true})
          .order('sort_order', {ascending: true}),
        signal,
      );
      photos = unwrap<TaskInstancePhotoRow>(photoRes, 'loadTaskInstancePhotos');
    } catch (e) {
      if (signal?.aborted) throw e;
      photos = [];
    }
  }

  return {task, profilesById, assignableProfilesById, photos};
}

// cattle.log.page — the Cattle Log's keyset-paginated RPC.
// ---------------------------------------------------------------------------
// The cursor is the exact {created_at, id} pair from the final RAW RPC row.
// It stays in Query's pageParam rather than the scoped cache key, so a filter,
// search, or reload starts a new authenticated sequence while a later page
// stays attached to the first page that produced it. This is especially
// important for Cattle Log's optimistic issue/delete projection: visual rows
// may disappear locally, but the next cursor must still come from the unedited
// server page.
// ---------------------------------------------------------------------------
// Task interaction resources — deliberately NOT folded into tasks.instance.core.
//
// These reads begin only when an operator opens the relevant modal/viewer (or
// when an already-visible thumbnail mounts). Moving them into the record or
// tab resource would change the request timing, overfetch every task row, and
// turn independently fail-soft interaction data into a page-level failure.
// They are still ordinary keyed catalog resources: their callers are scoped to
// the authenticated subject, and the manifest can account for them exactly.
// ---------------------------------------------------------------------------

function requiredTaskInteractionParam(params: ResourceParams | undefined, name: string): string {
  const value = params?.[name];
  if (typeof value !== 'string' || !value) {
    throw new Error(`tasks interaction resource: ${name} is required`);
  }
  return value;
}

async function queryTaskInstanceInteractionPhotos(
  sb: SupabaseClient,
  params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<TaskInstancePhotoRow[]> {
  const instanceId = requiredTaskInteractionParam(params, 'instanceId');
  const photoRes = await withSignal(
    sb
      .from('task_instance_photos')
      .select('id, instance_id, kind, storage_path, sort_order, uploaded_at, uploaded_by_profile_id')
      .eq('instance_id', instanceId)
      .order('kind', {ascending: true})
      .order('sort_order', {ascending: true}),
    signal,
  );
  return unwrap<TaskInstancePhotoRow>(photoRes, 'taskInstanceInteractionPhotos');
}

async function queryTaskInstanceDueDateHistory(
  sb: SupabaseClient,
  params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<DueDateEditHistoryRow[]> {
  const instanceId = requiredTaskInteractionParam(params, 'instanceId');
  const historyRes = await withSignal(
    sb
      .from('task_instance_due_date_edits')
      .select('id, instance_id, edited_at, edited_by_profile_id, edited_by_role, prior_due_date, new_due_date')
      .eq('instance_id', instanceId)
      .order('edited_at', {ascending: false}),
    signal,
  );
  return unwrap<DueDateEditHistoryRow>(historyRes, 'taskInstanceDueDateHistory');
}

// Signed URLs live for ten minutes. The previous task viewers held successful
// URLs for eight, so this cache keeps the useful headroom while making its key
// include the full subject/role/preview scope supplied by the interaction hook.
// An authenticated user can therefore never receive another user's private URL
// from a module-local cache after a sign-out or role-preview transition.
const TASK_SIGNED_URL_CACHE_TTL_MS = 8 * 60 * 1000;
type TaskSignedUrlCacheEntry = {url: string; expiresAt: number};
const taskSignedUrlCache = new Map<string, TaskSignedUrlCacheEntry>();

function taskInteractionCacheScope(params: ResourceParams | undefined): string {
  return requiredTaskInteractionParam(params, 'cacheScope');
}

function taskSignedUrlPurpose(params: ResourceParams | undefined): 'thumbnail' | 'lightbox' {
  return params?.purpose === 'thumbnail' ? 'thumbnail' : 'lightbox';
}

function cachedTaskSignedUrl(cacheKey: string, purpose: 'thumbnail' | 'lightbox'): string | null {
  // Task Center's legacy lightbox held its URL state only for that one open
  // session. Never promote a thumbnail or prior viewer URL into a later modal
  // open; only the row-thumbnail owner has the historical module cache.
  if (purpose !== 'thumbnail') return null;
  const cached = taskSignedUrlCache.get(cacheKey);
  if (!cached || cached.expiresAt <= Date.now()) return null;
  return cached.url;
}

function rememberTaskSignedUrl(cacheKey: string, url: string, purpose: 'thumbnail' | 'lightbox') {
  if (purpose !== 'thumbnail') return;
  taskSignedUrlCache.set(cacheKey, {url, expiresAt: Date.now() + TASK_SIGNED_URL_CACHE_TTL_MS});
}

function throwIfTaskInteractionAborted(signal?: AbortSignal) {
  // Storage's signer does not expose an abortSignal builder. Reading the
  // signal before and after the promise still makes Query's cancellation
  // observable, so a completed stale signer response cannot settle its key.
  if (signal?.aborted) throw new Error('task interaction request aborted');
}

async function queryTaskInstanceSignedPhotoUrl(
  sb: SupabaseClient,
  params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<TaskSignedPhotoUrlData> {
  const photoKind = requiredTaskInteractionParam(params, 'photoKind');
  if (photoKind !== 'request' && photoKind !== 'completion') {
    throw new Error(`tasks.instance.signedPhotoUrl: unsupported photoKind '${photoKind}'`);
  }
  const dbPath = requiredTaskInteractionParam(params, 'dbPath');
  const purpose = taskSignedUrlPurpose(params);
  const cacheKey = `${taskInteractionCacheScope(params)}|${photoKind}|${dbPath}`;
  const cached = cachedTaskSignedUrl(cacheKey, purpose);
  if (cached !== null) return {url: cached};

  throwIfTaskInteractionAborted(signal);
  let url = '';
  if (photoKind === 'request') {
    const storagePath = stripTaskRequestPhotoBucket(dbPath);
    if (storagePath) {
      const {data, error} = await sb.storage.from(TASK_REQUEST_PHOTOS_BUCKET).createSignedUrl(storagePath, 600);
      if (error) throw new Error(`tasks.instance.signedPhotoUrl: ${error.message || String(error)}`);
      url = data && data.signedUrl ? data.signedUrl : '';
    }
  } else {
    const storagePath = stripCompletionPhotoBucket(dbPath);
    if (storagePath) {
      const {data, error} = await sb.storage.from(TASK_PHOTOS_BUCKET).createSignedUrl(storagePath, 600);
      if (error) throw new Error(`tasks.instance.signedPhotoUrl: ${error.message || String(error)}`);
      url = data && data.signedUrl ? data.signedUrl : '';
    }
  }
  throwIfTaskInteractionAborted(signal);
  rememberTaskSignedUrl(cacheKey, url, purpose);
  return {url};
}

async function queryTodoSignedPhotoUrl(
  sb: SupabaseClient,
  params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<TaskSignedPhotoUrlData> {
  const dbPath = requiredTaskInteractionParam(params, 'dbPath');
  const cacheKey = `${taskInteractionCacheScope(params)}|todo|${dbPath}`;
  // To Do thumbnails and its viewer have historically shared successful URLs;
  // viewer opens still retry an empty/failed signer result.
  const cached = taskSignedUrlCache.get(cacheKey);
  if (cached && cached.expiresAt > Date.now() && cached.url) return {url: cached.url};

  throwIfTaskInteractionAborted(signal);
  const storagePath = stripTodoPhotoBucket(dbPath);
  const {data, error} = await sb.storage.from(TASK_PHOTOS_BUCKET).createSignedUrl(storagePath, 600);
  if (error) throw new Error(`tasks.todo.signedPhotoUrl: ${error.message || String(error)}`);
  const url = data && data.signedUrl ? data.signedUrl : '';
  throwIfTaskInteractionAborted(signal);
  // To Do thumbnails have always retried an empty or failed signer result.
  // Cache only an actual URL; errors throw and are never remembered.
  if (url) taskSignedUrlCache.set(cacheKey, {url, expiresAt: Date.now() + TASK_SIGNED_URL_CACHE_TTL_MS});
  return {url};
}

const CATTLE_LOG_PAGE_SIZE = 200;

function cattleLogFilterParam(params: ResourceParams | undefined): 'issues' | 'all' {
  return params?.filter === 'all' ? 'all' : 'issues';
}

function cattleLogSearchParam(params: ResourceParams | undefined): string | null {
  const value = params?.search;
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function cattleLogPageKeyParams(params: ResourceParams | undefined): Record<string, unknown> {
  return {
    filter: cattleLogFilterParam(params),
    search: cattleLogSearchParam(params) ?? undefined,
    // Retrying the first page must create a fresh page sequence. Reusing a
    // failed key would leave Query's old failure attached to this retry.
    reload: params?.reload ?? 0,
  };
}

async function queryCattleLogPage(
  sb: SupabaseClient,
  params: ResourceParams | undefined,
  pageParam: unknown,
  signal?: AbortSignal,
): Promise<PaginatedPage<CattleLogEntryRow, unknown>> {
  const before =
    pageParam && typeof pageParam === 'object' && !Array.isArray(pageParam) ? (pageParam as CattleLogCursor) : null;
  const response = (await listCattleLogEntries(sb, {
    filter: cattleLogFilterParam(params),
    search: cattleLogSearchParam(params),
    limit: CATTLE_LOG_PAGE_SIZE,
    before,
    signal,
  })) as Record<string, unknown>;
  const rows = Array.isArray(response.entries) ? (response.entries as CattleLogEntryRow[]) : [];
  const last = rows.at(-1);
  const createdAt = typeof last?.created_at === 'string' && last.created_at ? last.created_at : null;
  const id = typeof last?.id === 'string' && last.id ? last.id : null;
  const hasMore = response.has_more === true;

  // A has_more response without the tuple needed to advance would otherwise
  // issue the first page again. Fail closed instead of silently duplicating or
  // skipping log history.
  if (hasMore && (!createdAt || !id)) {
    throw new Error('listCattleLogEntries: has_more response omitted its final keyset cursor');
  }

  return {rows, nextPage: hasMore ? {created_at: createdAt!, id: id!} : null};
}

// cattle.log.cattle.preview — active-herd tag/calf preview only.
// ---------------------------------------------------------------------------
// The server remains authoritative for tag resolution. This narrow query is
// the page's pre-submit preview and deliberately preserves its old selected
// columns, deleted filter, and four active-herd constraint.
async function queryCattleLogCattlePreview(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<CattleRow[]> {
  const cR = await withSignal(
    sb
      .from('cattle')
      .select('id, tag, old_tags, herd, sex, birth_date, origin, breed, dam_tag')
      .is('deleted_at', null)
      .in('herd', CATTLE_HERDS),
    signal,
  );
  return unwrap<CattleRow>(cR, 'cattle');
}

// cattle.log.mentionable.profiles — the role-filtered mention picker.
// ---------------------------------------------------------------------------
// Its RPC intentionally degrades an ordinary failure to [], matching the
// original page-local promise: a missing picker must not block a log entry.
// Query cancellation still travels through cattleLogApi before that tolerance
// is applied, so a superseded authenticated scope cannot append an old result.
async function queryCattleLogMentionableProfiles(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<CattleLogMentionableProfile[]> {
  const rows = await loadCattleLogMentionableProfiles(sb, {signal});
  return Array.isArray(rows) ? (rows as CattleLogMentionableProfile[]) : [];
}

// activity.log.page — the global audit timeline's cursor-backed RPC.
// ---------------------------------------------------------------------------
// The Activity view already used list_global_activity with a created_at cursor;
// this resource preserves that server contract instead of faking a complete
// one-shot payload. The cursor belongs to Query's pageParam, never to the
// scoped cache key. Search/filter/retry identity belongs in pageKey, so a
// changed filter cancels the old page sequence rather than appending it.
//
// list_global_activity guarantees created_at in its RETURNS TABLE signature.
// If an impossible malformed full page arrives without that cursor, stop rather
// than issue the former page-one repeat with an absent p_before cursor.
function activityLogTextParam(params: ResourceParams | undefined, key: string): string | undefined {
  const value = params?.[key];
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function activityLogKeyParams(params: ResourceParams | undefined): Record<string, unknown> {
  return {
    entityType: activityLogTextParam(params, 'entityType'),
    eventType: activityLogTextParam(params, 'eventType'),
    search: activityLogTextParam(params, 'search'),
    // A Retry must start a fresh first-page sequence, not refetch the cached
    // failed key. This mirrors the view's existing reloadKey contract.
    reload: params?.reload ?? 0,
  };
}

async function queryActivityLogPage(
  sb: SupabaseClient,
  params: ResourceParams | undefined,
  pageParam: unknown,
  signal?: AbortSignal,
): Promise<PaginatedPage<ActivityLogPageRow, unknown>> {
  const before = typeof pageParam === 'string' && pageParam ? pageParam : undefined;
  const rows = (await loadGlobalActivity(sb, {
    limit: 50,
    before,
    entityType: activityLogTextParam(params, 'entityType'),
    eventType: activityLogTextParam(params, 'eventType'),
    search: activityLogTextParam(params, 'search'),
    signal,
  })) as ActivityLogPageRow[];
  const last = rows.at(-1);
  const nextPage =
    rows.length === 50 && typeof last?.created_at === 'string' && last.created_at ? last.created_at : null;
  return {rows, nextPage};
}

// ---------------------------------------------------------------------------
// clientErrors.page — the /admin/client-errors review list, one cursor page.
// ---------------------------------------------------------------------------
// The same admin-only list_client_errors RPC the panel already called, with the
// same 100-row page and the same created_at cursor. It is never the table: the
// client_error_events path is boundary-locked and clientErrorsApi is the only
// door, which is why this resource declares an RPC and no table at all.
//
// ONE DIFFERENCE FROM THE EFFECT IT REPLACES, AND IT IS A FIX.
//
// The panel computed its cursor as `rows[rows.length - 1].created_at || undefined`
// and passed `undefined` straight back into the RPC, so a row with a null
// created_at would have re-requested the FIRST page and appended it to itself.
// A null cursor ends the sequence here instead. The column is NOT NULL in
// migration 077, so this is unreachable today rather than a behaviour change —
// it is stated because a paginator that silently duplicates its own first page
// is the kind of thing that is only ever found in production.
const CLIENT_ERRORS_PAGE_SIZE = 100;

/** The six columns the Client Errors table renders, plus the cursor column. */
export type ClientErrorPageRow = {
  id?: unknown;
  created_at?: string | null;
  source?: unknown;
  error_kind?: unknown;
  message?: unknown;
  route?: unknown;
  app_version?: unknown;
};

async function queryClientErrorsPage(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  pageParam: unknown,
  signal?: AbortSignal,
): Promise<PaginatedPage<ClientErrorPageRow, unknown>> {
  const before = typeof pageParam === 'string' && pageParam ? pageParam : undefined;
  const rows = (await loadClientErrors(sb, {
    limit: CLIENT_ERRORS_PAGE_SIZE,
    before,
    signal,
  })) as ClientErrorPageRow[];
  const last = rows.at(-1);
  const nextPage =
    rows.length === CLIENT_ERRORS_PAGE_SIZE && typeof last?.created_at === 'string' && last.created_at
      ? last.created_at
      : null;
  return {rows, nextPage};
}

// ---------------------------------------------------------------------------
// pig.home.core — everything the /pig dashboard renders from.
// ---------------------------------------------------------------------------
// Five reads that reached PigsHomeView through THREE App-owned providers and
// not one line of its own:
//
//   ppp-breeding-v1    PigContext.breedingCycles, off App's boot app_store
//   ppp-farrowing-v1   PigContext.farrowingRecs, same transaction
//   ppp-feeders-v1     PigContext.feederGroups, same transaction
//   ppp-breeders-v1    PigContext.breeders, same transaction
//   pig_dailys         DailysRecentContext.pigDailys, App's full-history
//                      pagination
//
// The four blobs are read in ONE keyed select rather than four, exactly as
// home.light.planner reads its seven: this page needs all four to render a
// single dashboard, and four round trips would be four failure modes for one
// screen. It performs no upsert, no legacy migration and no repair write —
// unlike App's boot transaction, which owns all three.
//
// THE FOUR NORMALIZATIONS ARE THE BOOT'S, REPRODUCED, NOT REINVENTED.
//
// applyAppStoreBootFanOut is what published these four values, and three of
// them are not the raw blob:
//
//   farrowingRecs   a missing/non-array blob seeds INITIAL_FARROWING — the boot
//                   does it in two places (`|| INITIAL_FARROWING` and the
//                   no-rows branch), and PigContext's own initial state is the
//                   same constant, so an absent key has never meant "no
//                   litters".
//   feederGroups    reconcileFeederGroupsFromBreeders repairs sub-batch counts
//                   against the parent. The dashboard's on-farm count reads
//                   `subBatches[].status`, so skipping the reconcile here would
//                   render a different number than the boot did.
//   breeders        an absent OR EMPTY list falls back to INITIAL_BREEDERS.
//                   That is the boot's one unconditional pig hydration, and the
//                   sow/boar tiles count off it.
//
// The reconcile's `changed` flag and the boot's follow-up app_store upsert are
// deliberately NOT reproduced: this is a render read, and a read that writes is
// not idempotent (see this file's header). App's boot still owns that repair
// write for every surface it still serves.
//
// ERROR POSTURE IS PER-READ, AND IT MIRRORS THE BOOT'S OWN SPLIT.
//
// The blobs THROW. app_store is the one structural dependency App's boot
// escalates rather than degrades ("Failed to load app_store."), and rendering
// an outage as a farm with no cycles, no litters and no sows is the empty-farm
// lie this migration exists to remove.
//
// The daily history does NOT throw, because the boot's own
// `loadAllPigDailys(...).catch(() => [])` did not: a failed daily load left the
// dashboard rendering with an empty history and only the count/feed strips
// blank. Making it fatal would blank the whole page under cover of a refactor.
// It is caught in its own IIFE so a failure resolves to the empty history
// without touching the four blobs.
export const PIG_HOME_APP_STORE_KEYS = Object.freeze([
  'ppp-breeding-v1',
  'ppp-farrowing-v1',
  'ppp-feeders-v1',
  'ppp-breeders-v1',
] as const);

/**
 * The pig_dailys columns the /pig dashboard names. App's boot published
 * `select('*')` rows and the loader below still returns them unchanged; this
 * type is what the DASHBOARD reads off them, and it keeps the `number | string`
 * union its sibling declarations use because the same rows are round-tripped
 * through the daily edit form.
 */
export type PigHomeDailyRow = {[column: string]: unknown};

/** What view.pigsHome resolves with — the four planner blobs plus the history. */
export type PigHomeCoreData = {
  breedingCycles: CattleRow[];
  farrowingRecs: CattleRow[];
  feederGroups: CattleRow[];
  breeders: CattleRow[];
  pigDailys: PigHomeDailyRow[];
};

// A named constant rather than a bare `[]`, because query_scope_contract
// rejects an array LITERAL returned from anything in src/app/data — a raw array
// leaving this layer is indistinguishable from a query key that skipped
// resourceKey.
const NO_PIG_HOME_DAILYS: PigHomeDailyRow[] = [];

async function queryPigHomeCore(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<PigHomeCoreData> {
  const [store, pigDailys] = await Promise.all([
    withSignal(sb.from('app_store').select('key,data').in('key', PIG_HOME_APP_STORE_KEYS), signal),
    (async () => {
      try {
        // The SAME loader App's boot calls, deliberately: it fails WHOLE on any
        // page error instead of publishing a truncated history as a complete
        // one, and reusing it keeps that property here rather than reproducing
        // the pagination loop and its defect alongside it.
        const rows = (await loadAllPigDailys(sb, signal ?? null)) as PigHomeDailyRow[];
        if (signal?.aborted) return NO_PIG_HOME_DAILYS;
        return rows;
      } catch (error) {
        if (signal?.aborted) throw error;
        return NO_PIG_HOME_DAILYS;
      }
    })(),
  ]);

  const rows = unwrap<{key?: unknown; data?: unknown}>(
    store as ListResponse<{key?: unknown; data?: unknown}>,
    'app_store',
  );
  const blobs: Record<string, unknown> = Object.create(null) as Record<string, unknown>;
  for (const row of rows) if (typeof row?.key === 'string') blobs[row.key] = row.data;

  const farrowingValue = blobs['ppp-farrowing-v1'];
  const breeders = lightHomeRows(blobs['ppp-breeders-v1']);
  return {
    breedingCycles: lightHomeRows(blobs['ppp-breeding-v1']),
    farrowingRecs: Array.isArray(farrowingValue) ? lightHomeRows(farrowingValue) : (INITIAL_FARROWING as CattleRow[]),
    feederGroups: reconcileFeederGroupsFromBreeders(
      lightHomeRows(blobs['ppp-feeders-v1']) as unknown as PigFeederGroup[],
    ).groups as unknown as CattleRow[],
    breeders: breeders.length > 0 ? breeders : (INITIAL_BREEDERS as CattleRow[]),
    pigDailys,
  };
}

// ---------------------------------------------------------------------------
// pig.breeding.core — the three blobs the breeding Gantt renders from.
// ---------------------------------------------------------------------------
// All three reached /pig/breeding through usePig() off App's boot app_store
// transaction, and none of them is the page's own read:
//
//   ppp-breeding-v1   the cycle list. This page's SUBJECT.
//   ppp-boars-v1      the two boar display names every bar, card and form label
//                     falls back to.
//   ppp-breeders-v1   the breeder registry, read only to build a group's sow
//                     pool for the auto-merge into Boar 1.
//
// One keyed select rather than three: the Gantt cannot render without the
// cycles and cannot label a bar without the boars, so three round trips would
// be three failure modes for one screen.
//
// TWO OF THE THREE ARE NORMALIZED, AND BOTH NORMALIZATIONS ARE THE BOOT'S.
//
// `ppp-boars-v1` hydrates conditionally (`if (store['ppp-boars-v1'])`), so an
// absent key has always meant PigContext's seed — DEFAULT_BOAR_NAMES, now in
// src/lib/pig.ts so this resource and that provider cannot answer differently
// for a farm with no row.
//
// `ppp-breeders-v1` is the boot's one UNCONDITIONAL pig hydration, and an
// absent OR EMPTY list falls back to INITIAL_BREEDERS. That is not cosmetic
// here: the seed rows carry `group`, so on a farm with no stored registry the
// sow pool this page auto-merges into Boar 1 was never empty. Omitting the
// fallback would silently empty it.
//
// The cycle list is handed over as the blob had it — the boot commits
// ppp-breeding-v1 raw.
//
// ALL THREE THROW. The page IS the cycle directory, and rendering an outage as
// an empty Gantt would invite a duplicate cycle for a group that already has
// one — the same empty-farm lie cattle.breeding.core's migration closed.
export const PIG_BREEDING_APP_STORE_KEYS = Object.freeze([
  'ppp-breeding-v1',
  'ppp-boars-v1',
  'ppp-breeders-v1',
] as const);

/** The two boar display names, as the ppp-boars-v1 blob and its default hold them. */
export type PigBoarNames = {boar1: string; boar2: string};

/** What view.breeding resolves with — its subject, its labels, and its sow pool. */
export type PigBreedingCoreData = {
  breedingCycles: CattleRow[];
  boarNames: PigBoarNames;
  breeders: CattleRow[];
};

async function queryPigBreedingCore(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<PigBreedingCoreData> {
  const store = await withSignal(
    sb.from('app_store').select('key,data').in('key', PIG_BREEDING_APP_STORE_KEYS),
    signal,
  );
  const rows = unwrap<{key?: unknown; data?: unknown}>(
    store as ListResponse<{key?: unknown; data?: unknown}>,
    'app_store',
  );
  const blobs: Record<string, unknown> = Object.create(null) as Record<string, unknown>;
  for (const row of rows) if (typeof row?.key === 'string') blobs[row.key] = row.data;

  const boars = blobs['ppp-boars-v1'];
  const breeders = lightHomeRows(blobs['ppp-breeders-v1']);
  return {
    breedingCycles: lightHomeRows(blobs['ppp-breeding-v1']),
    // Spread onto the default rather than substituted for it: the stored blob
    // is a two-key object and a row that carries only one name has always
    // rendered the seed for the other.
    boarNames:
      boars && typeof boars === 'object' && !Array.isArray(boars)
        ? {...DEFAULT_BOAR_NAMES, ...(boars as Partial<PigBoarNames>)}
        : {...DEFAULT_BOAR_NAMES},
    breeders: breeders.length > 0 ? breeders : (INITIAL_BREEDERS as CattleRow[]),
  };
}

// ---------------------------------------------------------------------------
// pig.farrowing.core — the five blobs the per-cycle farrowing view renders from.
// ---------------------------------------------------------------------------
// Every one of them reached /pig/farrowing through usePig() off App's boot
// app_store transaction, and one of them reached it TWICE — see below:
//
//   ppp-farrowing-v1   the record list. This page's SUBJECT.
//   ppp-breeding-v1    the cycles the records are grouped under, and what
//                      matchCycleForFarrowing / resolveFarrowingSire walk.
//   ppp-boars-v1       the sire fallback and the per-cycle boar labels.
//   ppp-breeders-v1    matched by sow tag to find the pig.breeder entity the
//                      best-effort Activity emit is scoped to.
//   ppp-feeders-v1     ensureFarrowBatchForCycle's input: the existing feeder
//                      groups it checks before creating a farm-born batch.
//
// THE SIRE COLUMN IS WHY THE CYCLES AND THE BOARS ARE BOTH HERE.
//
// `resolveSire` was a PigEntry prop — an App closure over App's OWN
// breedingCycles and boarNames — so those two values reached this page through
// a channel the former-eager derivation cannot see, because it scores hook
// destructures and prop NAMES and a closure is neither. The page calls
// `resolveFarrowingSire(rec, breedingCycles, boarNames)` itself now, against
// the values this resource returns. Without that, retiring `farrowing` from the
// app_store consumer list would have been a mechanically-green false claim.
//
// FOUR NORMALIZATIONS, ALL OF THEM THE BOOT'S, none of them invented here:
// INITIAL_FARROWING for an absent/non-array record list (the boot applies it in
// two places and PigContext seeds with the same constant),
// reconcileFeederGroupsFromBreeders over the feeder groups (the batch-creation
// helper reads sub-batch status, so an unreconciled list would decide
// differently), INITIAL_BREEDERS for an absent or empty registry, and
// DEFAULT_BOAR_NAMES for an absent boars row. The reconcile's `changed` flag
// and the boot's follow-up upsert are deliberately not reproduced: a render
// read that writes is not idempotent.
//
// ALL FIVE THROW. The page IS the farrowing record directory, and rendering an
// outage as "No farrowing records yet" over a farm that has litters invites a
// duplicate record — and would also make the sire column silently blank.
export const PIG_FARROWING_APP_STORE_KEYS = Object.freeze([
  'ppp-farrowing-v1',
  'ppp-breeding-v1',
  'ppp-boars-v1',
  'ppp-breeders-v1',
  'ppp-feeders-v1',
] as const);

/** What view.farrowing resolves with — its records, its cycles, and the three lookups. */
export type PigFarrowingCoreData = {
  farrowingRecs: CattleRow[];
  breedingCycles: CattleRow[];
  boarNames: PigBoarNames;
  breeders: CattleRow[];
  feederGroups: CattleRow[];
};

async function queryPigFarrowingCore(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<PigFarrowingCoreData> {
  const store = await withSignal(
    sb.from('app_store').select('key,data').in('key', PIG_FARROWING_APP_STORE_KEYS),
    signal,
  );
  const rows = unwrap<{key?: unknown; data?: unknown}>(
    store as ListResponse<{key?: unknown; data?: unknown}>,
    'app_store',
  );
  const blobs: Record<string, unknown> = Object.create(null) as Record<string, unknown>;
  for (const row of rows) if (typeof row?.key === 'string') blobs[row.key] = row.data;

  const farrowingValue = blobs['ppp-farrowing-v1'];
  const boars = blobs['ppp-boars-v1'];
  const breeders = lightHomeRows(blobs['ppp-breeders-v1']);
  return {
    farrowingRecs: Array.isArray(farrowingValue) ? lightHomeRows(farrowingValue) : (INITIAL_FARROWING as CattleRow[]),
    breedingCycles: lightHomeRows(blobs['ppp-breeding-v1']),
    boarNames:
      boars && typeof boars === 'object' && !Array.isArray(boars)
        ? {...DEFAULT_BOAR_NAMES, ...(boars as Partial<PigBoarNames>)}
        : {...DEFAULT_BOAR_NAMES},
    breeders: breeders.length > 0 ? breeders : (INITIAL_BREEDERS as CattleRow[]),
    feederGroups: reconcileFeederGroupsFromBreeders(
      lightHomeRows(blobs['ppp-feeders-v1']) as unknown as PigFeederGroup[],
    ).groups as unknown as CattleRow[],
  };
}

// ---------------------------------------------------------------------------
// pig.sows.core — the breeding-pig registry, its history panel, and its feed.
// ---------------------------------------------------------------------------
// SEVEN reads, six blobs and a table, and /pig/sows owned none of them:
//
//   ppp-breeders-v1        the registry. This surface's SUBJECT, and also the
//                          row the record sub-mode resolves by id.
//   ppp-breeding-v1        the cycles the per-sow history walks.
//   ppp-farrowing-v1       the litters that history counts.
//   ppp-boars-v1           the sire labels beside them.
//   ppp-breed-options-v1   the breed dropdown.
//   ppp-origin-options-v1  the origin dropdown.
//   pig_dailys             the SOWS/BOARS monthly feed panel.
//
// THE TWO OPTION KEYS ARE NOT DEAD, AND READING THEM HERE IS NOT A REVIVAL.
// Neither exists in the production app_store today, which makes them look
// deletable. They are written the first time an operator adds a custom breed or
// origin — through src/lib/appStoreBlobApi.ts, which is where App's
// persistBreedOptions / persistOriginOptions props went when this surface took
// ownership of its writes — and
// tests/static/dead_hydration_removed_static.test.js pins their
// live boot hydration precisely because someone will try to remove them. This
// resource reads them the same way the boot does: conditionally, falling back to
// the seed lists in src/lib/pig.ts.
//
// AND THE SIRE COLUMN IS AGAIN WHY THE CYCLES AND BOARS ARE HERE — the
// `resolveSire` prop was an App closure over App's own copies of both, a read
// the former-eager derivation cannot see. Same treatment as view.farrowing, and
// the prop itself is now gone from both: App no longer builds the closure.
//
// ERROR POSTURE IS SPLIT, AND IT MIRRORS THE BOOT'S. The six blobs THROW: the
// page IS the registry, and rendering an outage as "No breeding pigs yet" is
// the empty-farm lie — worse here than elsewhere, because the record sub-mode
// would resolve every deep link to "Breeding pig not found". The daily history
// does NOT throw, because the boot's `loadAllPigDailys(...).catch(() => [])` did
// not: a failed daily load left the feed panel empty and the registry intact.
export const PIG_SOWS_APP_STORE_KEYS = Object.freeze([
  'ppp-breeders-v1',
  'ppp-breeding-v1',
  'ppp-farrowing-v1',
  'ppp-boars-v1',
  'ppp-breed-options-v1',
  'ppp-origin-options-v1',
] as const);

/** What view.sows resolves with — the registry, its history inputs, and its feed. */
export type PigSowsCoreData = {
  breeders: CattleRow[];
  breedingCycles: CattleRow[];
  farrowingRecs: CattleRow[];
  boarNames: PigBoarNames;
  breedOptions: string[];
  originOptions: string[];
  pigDailys: PigHomeDailyRow[];
  importedNotesByBreederId: ImportedBreederNotes;
};

// Named constants rather than bare `[]`/spread literals in a return position,
// because query_scope_contract rejects an array LITERAL returned from anything
// in src/app/data.
const NO_PIG_SOWS_DAILYS: PigHomeDailyRow[] = [];

// A stored option list is only honoured when it is a non-empty array of
// strings. The boot's `if (store[key])` guard admits any truthy value, and the
// dropdown maps over the result — so a blob that is not a list of strings has
// always rendered as the seed rather than as a broken control.
function pigOptionList(value: unknown, fallback: readonly string[]): string[] {
  // The seed is copied into a NAMED local before it is returned, for the same
  // reason NO_PIG_HOME_DAILYS exists: query_scope_contract rejects an array
  // LITERAL returned from anything in src/app/data, because a raw array leaving
  // this layer is indistinguishable from a query key that skipped resourceKey.
  // The copy itself is deliberate — the exported seeds are frozen and the
  // consumer replaces the whole list on every add.
  const seeded = fallback.slice();
  if (!Array.isArray(value) || value.length === 0) return seeded;
  return value.map((entry) => String(entry));
}

async function queryPigSowsCore(
  sb: SupabaseClient,
  params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<PigSowsCoreData> {
  // `importedNotes: false` is passed only for a role that
  // list_pig_breeder_imported_notes refuses (migration 221): skip the read and
  // show no imported notes, instead of failing the whole page on it. The
  // default (no param) is unchanged, and a REAL error on the read still fails
  // the resource, as listImportedBreederNotes intends.
  const readImportedNotes = params?.importedNotes !== false;
  const [store, pigDailys, importedNotesByBreederId] = await Promise.all([
    withSignal(sb.from('app_store').select('key,data').in('key', PIG_SOWS_APP_STORE_KEYS), signal),
    (async () => {
      try {
        const rows = (await loadAllPigDailys(sb, signal ?? null)) as PigHomeDailyRow[];
        if (signal?.aborted) return NO_PIG_SOWS_DAILYS;
        return rows;
      } catch (error) {
        if (signal?.aborted) throw error;
        return NO_PIG_SOWS_DAILYS;
      }
    })(),
    readImportedNotes
      ? listImportedBreederNotes(sb, {signal})
      : Promise.resolve(Object.create(null) as Awaited<ReturnType<typeof listImportedBreederNotes>>),
  ]);

  const rows = unwrap<{key?: unknown; data?: unknown}>(
    store as ListResponse<{key?: unknown; data?: unknown}>,
    'app_store',
  );
  const blobs: Record<string, unknown> = Object.create(null) as Record<string, unknown>;
  for (const row of rows) if (typeof row?.key === 'string') blobs[row.key] = row.data;

  const farrowingValue = blobs['ppp-farrowing-v1'];
  const boars = blobs['ppp-boars-v1'];
  const breeders = lightHomeRows(blobs['ppp-breeders-v1']);
  return {
    breeders: breeders.length > 0 ? breeders : (INITIAL_BREEDERS as CattleRow[]),
    breedingCycles: lightHomeRows(blobs['ppp-breeding-v1']),
    farrowingRecs: Array.isArray(farrowingValue) ? lightHomeRows(farrowingValue) : (INITIAL_FARROWING as CattleRow[]),
    boarNames:
      boars && typeof boars === 'object' && !Array.isArray(boars)
        ? {...DEFAULT_BOAR_NAMES, ...(boars as Partial<PigBoarNames>)}
        : {...DEFAULT_BOAR_NAMES},
    breedOptions: pigOptionList(blobs['ppp-breed-options-v1'], DEFAULT_BREED_OPTIONS),
    originOptions: pigOptionList(blobs['ppp-origin-options-v1'], DEFAULT_ORIGIN_OPTIONS),
    pigDailys,
    importedNotesByBreederId,
  };
}

// ---------------------------------------------------------------------------
// pig.feed.core — everything /pig/feed renders from.
// ---------------------------------------------------------------------------
// Eight values across TWO tables, reaching the page through FOUR App channels
// and not one read of its own — the pig half of the same hand-off
// broiler.feed.core dismantled on the poultry side:
//
//   ppp-feed-orders-v1          a PigEntry prop, off App's boot app_store
//   ppp-pig-feed-inventory-v1   transaction. The SAME ppp-feed-orders-v1 blob
//                               /broiler/feed reads: the `pig` map and the three
//                               poultry maps live in one object.
//   ppp-breeding-v1             PigContext.breedingCycles / .farrowingRecs /
//   ppp-farrowing-v1            .feederGroups / .breeders, same transaction —
//   ppp-feeders-v1              the four planner blobs pigDailyBurnLbs reduces
//   ppp-breeders-v1             into the projected daily burn.
//   ppp-feed-costs-v1           FeedCostsContext, same transaction, read only
//                               for the $/lb line in Feed Rate Reference.
//   pig_dailys                  DailysRecentContext.pigDailys, App's boot
//                               full-history pagination.
//
// The seven blobs are read in ONE keyed select for the reason pig.home.core
// reads its four that way: this page needs the orders AND the burn inputs to
// draw a single ledger, so seven round trips would be seven failure modes for
// one screen.
//
// THE NORMALIZATIONS ARE THE BOOT'S, REPRODUCED, AND FOUR OF THE SEVEN CARRY
// ONE.
//
// The former applyAppStoreBootFanOut published these values into providers this
// surface used to read, and it did not hand four of them over raw:
//
//   farrowingRecs   a missing/non-array blob seeds INITIAL_FARROWING.
//   feederGroups    reconcileFeederGroupsFromBreeders repairs sub-batch counts
//                   against the parent. Observable HERE and not merely in
//                   principle: the per-group table walks `subBatches[].status`
//                   and prices each active sub through pigFeederSubCurrentCount,
//                   so skipping the reconcile would project a different burn
//                   than the boot did.
//   breeders        an absent OR EMPTY list falls back to INITIAL_BREEDERS —
//                   the boot's one unconditional pig hydration, and the input to
//                   both pigTransfersForBatch and the sow/boar burn.
//   feedCosts       normalizeFeedCosts, App's LOAD boundary: an earlier build
//                   persisted these rates as strings and this page calls
//                   `.toFixed(3)` on one of them.
//
// The reconcile's `changed` flag and the boot's follow-up app_store upsert are
// deliberately NOT reproduced, exactly as pig.home.core declines them: this is a
// render read, and a read that writes is not idempotent.
//
// THE FOUR-KEY ORDER SEED IS NOT COSMETIC. savePigOrder writes
// `{...feedOrders, pig: pigOrders}` back to app_store, so an absent
// ppp-feed-orders-v1 row resolving to a bare `{}` would drop the persisted
// starter/grower/layerfeed maps on the first pig order. App's own useState seed
// is the four empty maps, and that is what an absent row resolves to here —
// the mirror image of the hazard broiler.feed.core documents.
//
// ERROR POSTURE IS PER-READ.
//
// The blobs THROW so this route resource can show its own failure and Retry
// state; drawing a failed order blob as "nothing ordered yet" would invent a
// zero the operator places a feed order against.
//
// The daily history does NOT throw, preserving the prior fail-soft treatment:
// a failed daily load leaves the ledger's actual-consumption columns empty and
// everything else intact. It is caught in its own IIFE so a failure cannot
// touch the seven blobs.
const PIG_FEED_APP_STORE_KEYS = Object.freeze([
  'ppp-feed-orders-v1',
  'ppp-pig-feed-inventory-v1',
  'ppp-breeding-v1',
  'ppp-farrowing-v1',
  'ppp-feeders-v1',
  'ppp-breeders-v1',
  'ppp-feed-costs-v1',
] as const);

/** What view.pigs resolves with — the seven blobs plus the daily history. */
export type PigFeedCoreData = {
  feedOrders: Record<string, Record<string, unknown>>;
  pigFeedInventory: Record<string, unknown> | null;
  breedingCycles: CattleRow[];
  farrowingRecs: CattleRow[];
  feederGroups: CattleRow[];
  breeders: CattleRow[];
  feedCosts: FeedCosts;
  pigDailys: PigHomeDailyRow[];
};

// Named constants rather than bare literals in a return position, because
// query_scope_contract rejects an array LITERAL returned from anything in
// src/app/data — a raw array leaving this layer is indistinguishable from a
// query key that skipped resourceKey.
const NO_PIG_FEED_DAILYS: PigHomeDailyRow[] = [];
/** App's own `useState` seed for ppp-feed-orders-v1 — see the header. */
const NO_PIG_FEED_ORDERS: Record<string, Record<string, unknown>> = Object.freeze({
  pig: {},
  starter: {},
  grower: {},
  layerfeed: {},
});
/** FeedCostsContext's own initial value, which an absent row still resolves to. */
const NO_PIG_FEED_COSTS: FeedCosts = Object.freeze({
  starter: 0,
  grower: 0,
  layer: 0,
  pig: 0,
  grit: 0,
});

async function queryPigFeedCore(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<PigFeedCoreData> {
  const [store, pigDailys] = await Promise.all([
    withSignal(sb.from('app_store').select('key,data').in('key', PIG_FEED_APP_STORE_KEYS), signal),
    (async () => {
      try {
        // The SAME loader App's boot calls, deliberately: it fails WHOLE on any
        // page error instead of publishing a truncated history as a complete
        // one, and reusing it keeps that property here rather than reproducing
        // the pagination loop and its defect alongside it. It is also why this
        // resource declares pig_dailys while adding no new physical call site
        // for that table.
        const rows = (await loadAllPigDailys(sb, signal ?? null)) as PigHomeDailyRow[];
        if (signal?.aborted) return NO_PIG_FEED_DAILYS;
        return rows;
      } catch (error) {
        if (signal?.aborted) throw error;
        return NO_PIG_FEED_DAILYS;
      }
    })(),
  ]);

  const rows = unwrap<{key?: unknown; data?: unknown}>(
    store as ListResponse<{key?: unknown; data?: unknown}>,
    'app_store',
  );
  const blobs: Record<string, unknown> = Object.create(null) as Record<string, unknown>;
  for (const row of rows) if (typeof row?.key === 'string') blobs[row.key] = row.data;

  const storedFeedOrders = blobs['ppp-feed-orders-v1'];
  const storedInventory = blobs['ppp-pig-feed-inventory-v1'];
  const storedFeedCosts = blobs['ppp-feed-costs-v1'];
  const farrowingValue = blobs['ppp-farrowing-v1'];
  const breeders = lightHomeRows(blobs['ppp-breeders-v1']);
  return {
    // The boot fan-out guards each of these with `if (store[key])` and leaves
    // App's initial state alone otherwise, so an absent row is the seed and not
    // an error.
    feedOrders: storedFeedOrders ? (storedFeedOrders as Record<string, Record<string, unknown>>) : NO_PIG_FEED_ORDERS,
    pigFeedInventory: storedInventory ? (storedInventory as Record<string, unknown>) : null,
    breedingCycles: lightHomeRows(blobs['ppp-breeding-v1']),
    farrowingRecs: Array.isArray(farrowingValue) ? lightHomeRows(farrowingValue) : (INITIAL_FARROWING as CattleRow[]),
    feederGroups: reconcileFeederGroupsFromBreeders(
      lightHomeRows(blobs['ppp-feeders-v1']) as unknown as PigFeederGroup[],
    ).groups as unknown as CattleRow[],
    breeders: breeders.length > 0 ? breeders : (INITIAL_BREEDERS as CattleRow[]),
    feedCosts: storedFeedCosts ? normalizeFeedCosts(storedFeedCosts) : NO_PIG_FEED_COSTS,
    pigDailys,
  };
}

// ---------------------------------------------------------------------------
// pig.batches.core — the feeder-batch hub AND its record page, in one read.
// ---------------------------------------------------------------------------
// SIX reads, five blobs and a table, reaching /pig/batches through two App
// providers and not one line of its own:
//
//   ppp-feeders-v1     the feeder batches. This surface's SUBJECT, the hub grid
//                      and the row the record page resolves by id.
//   ppp-breeding-v1    the cycles a batch links to — the age ranges, the cycle
//                      labels and the planned-trip age distribution.
//   ppp-farrowing-v1   the litters those age ranges are measured from.
//   ppp-breeders-v1    the registry the per-sub transfer credits are summed out
//                      of (pigTransfersForSub / pigTransfersForBatch).
//   ppp-boars-v1       the two boar display names on the record page's cycle
//                      strip.
//   pig_dailys         the full history behind dailysForName — every feed
//                      total, FCR and current-count column on both surfaces.
//
// ONE RESOURCE AND TWO SURFACES, AND THAT IS FORCED, NOT PREFERRED.
//
// PigBatchesView is declared `splits` with NO mounts (see SURFACE_ROUTERS):
// `recordMode` is computed from the pathname and gates the record render inline
// rather than through a return, so both surfaces are the same component with
// the same single set of reads at the top. There is no position that serves the
// hub without also serving the record page. A catalog resource may only name
// surfaces the manifest declares migrated, so naming one alone would leave the
// other consuming a permanent resource it did not own — the same reason
// layer.dailys.groupDirectory names both layer daily surfaces.
//
// THE FIVE NORMALIZATIONS ARE THE BOOT'S, REPRODUCED, NOT REINVENTED.
//
// applyAppStoreBootFanOut published every one of these into PigContext, and
// four of them are not the raw blob:
//
//   feederGroups   reconcileFeederGroupsFromBreeders repairs sub-batch counts
//                  against the parent. This is the surface that RENDERS those
//                  counts — the hub's started/current columns and every
//                  sub-batch ledger row on the record page — so skipping the
//                  reconcile here would draw different numbers than the boot
//                  did. Its `changed` flag and the boot's follow-up app_store
//                  upsert are deliberately NOT reproduced: a render read that
//                  writes is not idempotent, and App's boot still owns that
//                  repair write for every surface it still serves.
//   farrowingRecs  a missing/non-array blob seeds INITIAL_FARROWING, in the
//                  boot and in PigContext's own initial state alike, so an
//                  absent key has never meant "no litters".
//   breeders       an absent OR EMPTY list falls back to INITIAL_BREEDERS —
//                  the boot's one unconditional pig hydration. The seed rows
//                  carry transfer history, so omitting the fallback would
//                  silently zero the per-sub transfer credits.
//   boarNames      hydrated conditionally (`if (store['ppp-boars-v1'])`), so an
//                  absent key has always meant DEFAULT_BOAR_NAMES. Spread onto
//                  the default rather than substituted for it, because a blob
//                  carrying only one name has always rendered the seed for the
//                  other.
//
// The cycle list is handed over as the blob had it — the boot commits
// ppp-breeding-v1 raw.
//
// ERROR POSTURE IS SPLIT, AND IT MIRRORS THE BOOT'S OWN SPLIT.
//
// The five blobs THROW. app_store is the one structural dependency App's boot
// escalates rather than degrades ("Failed to load app_store."), and this page IS
// the batch directory: rendering an outage as "No pig batches yet." invites a
// duplicate batch, and on the record half it would resolve every deep link to
// "Batch not found." — the empty-farm lie in its worst form, because that
// branch is indistinguishable from a genuinely deleted batch.
//
// The daily history does NOT throw, because the boot's own
// `loadAllPigDailys(...).catch(() => [])` did not: a failed daily load left the
// feed and FCR columns blank and the batch list intact. Making it fatal would
// blank both surfaces under cover of a refactor. It is caught in its own IIFE so
// a failure resolves to the empty history without touching the five blobs.
export const PIG_BATCHES_APP_STORE_KEYS = Object.freeze([
  'ppp-feeders-v1',
  'ppp-breeding-v1',
  'ppp-farrowing-v1',
  'ppp-breeders-v1',
  'ppp-boars-v1',
] as const);

/** What both pig batch surfaces resolve with — the batches, their cycle inputs, and the feed history. */
export type PigBatchesCoreData = {
  feederGroups: CattleRow[];
  breedingCycles: CattleRow[];
  farrowingRecs: CattleRow[];
  breeders: CattleRow[];
  boarNames: PigBoarNames;
  pigDailys: PigHomeDailyRow[];
  processingTripOrdinals: Record<string, number>;
};

// A named constant rather than a bare `[]`, because query_scope_contract rejects
// an array LITERAL returned from anything in src/app/data — a raw array leaving
// this layer is indistinguishable from a query key that skipped resourceKey.
const NO_PIG_BATCHES_DAILYS: PigHomeDailyRow[] = [];

async function queryPigBatchesCore(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<PigBatchesCoreData> {
  const [store, pigDailys, processingTripOrdinals] = await Promise.all([
    withSignal(sb.from('app_store').select('key,data').in('key', PIG_BATCHES_APP_STORE_KEYS), signal),
    (async () => {
      try {
        // The SAME loader App's boot calls, deliberately: it fails WHOLE on any
        // page error instead of publishing a truncated history as a complete
        // one, and reusing it keeps that property here rather than reproducing
        // the pagination loop and its defect alongside it.
        const rows = (await loadAllPigDailys(sb, signal ?? null)) as PigHomeDailyRow[];
        if (signal?.aborted) return NO_PIG_BATCHES_DAILYS;
        return rows;
      } catch (error) {
        if (signal?.aborted) throw error;
        return NO_PIG_BATCHES_DAILYS;
      }
    })(),
    (async () => {
      try {
        // Processing owns immutable trip numbers. Include archived sources so
        // a completed trip keeps its number; never reconcile or write on read.
        const records = await listProcessingRecords(sb, {program: 'pig', includeArchived: true, signal});
        return pigTripOrdinalsBySource(records);
      } catch (error) {
        if (signal?.aborted) throw error;
        // A Processing access/network failure must not hide the existing pig
        // workspace. Its cards show an unnumbered Trip rather than inventing one.
        return {} as Record<string, number>;
      }
    })(),
  ]);

  const rows = unwrap<{key?: unknown; data?: unknown}>(
    store as ListResponse<{key?: unknown; data?: unknown}>,
    'app_store',
  );
  const blobs: Record<string, unknown> = Object.create(null) as Record<string, unknown>;
  for (const row of rows) if (typeof row?.key === 'string') blobs[row.key] = row.data;

  const farrowingValue = blobs['ppp-farrowing-v1'];
  const boars = blobs['ppp-boars-v1'];
  const breeders = lightHomeRows(blobs['ppp-breeders-v1']);
  return {
    feederGroups: reconcileFeederGroupsFromBreeders(
      lightHomeRows(blobs['ppp-feeders-v1']) as unknown as PigFeederGroup[],
    ).groups as unknown as CattleRow[],
    breedingCycles: lightHomeRows(blobs['ppp-breeding-v1']),
    farrowingRecs: Array.isArray(farrowingValue) ? lightHomeRows(farrowingValue) : (INITIAL_FARROWING as CattleRow[]),
    breeders: breeders.length > 0 ? breeders : (INITIAL_BREEDERS as CattleRow[]),
    boarNames:
      boars && typeof boars === 'object' && !Array.isArray(boars)
        ? {...DEFAULT_BOAR_NAMES, ...(boars as Partial<PigBoarNames>)}
        : {...DEFAULT_BOAR_NAMES},
    pigDailys,
    processingTripOrdinals,
  };
}

// ---------------------------------------------------------------------------
// broiler.feed.core — everything /broiler/feed renders from.
// ---------------------------------------------------------------------------
// Seven values across FIVE tables were formerly supplied by App state. The
// poultry feed ledger now owns the same data through its route resource:
//
//   ppp-v4                          former App store mirror, through
//                                   BatchesContext.batches.
//   ppp-feed-orders-v1              former App store mirror, threaded down as
//   ppp-poultry-feed-inventory-v1   a BroilerEntry prop.
//   layer_batches / layer_housings  former LayerContext directory.
//   layer_dailys                    former App sidecar pagination.
//   poultry_dailys                  former DailysRecentContext mirror.
//
// ERROR POSTURE IS PER-READ.
//
// The app_store read THROWS so this route resource can expose its own error and
// Retry state. Rendering a failed order blob as "nothing ordered yet" would
// invent a zero the operator would place a feed order against.
//
// The other four DO NOT throw, preserving their intentionally fail-soft
// treatment. Making any of them fatal here would turn a page that degrades into
// one that disappears, under cover of a refactor.
//
// THE ppp-v4 REPAIR IS NOT OPTIONAL, AND IT IS NOT A WRITE.
//
// `normalizeLightHomeBroilerBatch` above is reused rather than copied a third
// time. Its two lifecycle rules are LIVE and recurring, not spent migrations
// (see src/app/boot/broilerBootRepair.ts), and one of them is observable on
// exactly this page: `calcPoultryStatus` returns 'active' for a stored-active
// batch whose processing date has passed, while the boot repair advances it to
// 'processed' — so the raw blob would move a batch between this page's ACTIVE
// and PROCESSED sections. The helper's one deliberate omission, the feed-cost
// stamp, is unobservable here: this view reads no perLb* field. Nothing is
// written back; the persisted repair remains separately owned maintenance,
// not a render read.
const BROILER_FEED_APP_STORE_KEYS = Object.freeze([
  'ppp-v4',
  'ppp-feed-orders-v1',
  'ppp-poultry-feed-inventory-v1',
] as const);

// App's layer sidecar paginates layer_dailys in 1,000-row pages; reproduced so
// a farm with more than one page of history renders the same ledger.
const BROILER_FEED_LAYER_DAILYS_PAGE = 1000;

// Named constants rather than bare `[]`, because query_scope_contract rejects
// an array LITERAL returned from anything in src/app/data — a raw array leaving
// this layer is indistinguishable from a query key that skipped resourceKey.
const NO_BROILER_FEED_LAYER_BATCHES: LayerBatchCoreRow[] = [];
const NO_BROILER_FEED_LAYER_HOUSINGS: LayerHousingCoreRow[] = [];
const NO_BROILER_FEED_POULTRY_DAILYS: unknown[] = [];

function broilerFeedAbortError(): Error {
  const error = new Error(`${RESOURCE.BROILER_FEED_CORE}: aborted`);
  error.name = 'AbortError';
  return error;
}

/**
 * One of the four non-fatal reads, resolved to its named empty result instead
 * of failing the bundle. Same disposition — and the same reason — as
 * optionalLightHomeMissedRows: each of these values was published independently
 * at boot and swallowed its own failure, so an individual outage has to stay
 * empty DATA rather than become a page-level error.
 *
 * Cancellation is the one thing it does NOT absorb: that belongs to the route
 * runtime and must escape rather than resolving to empty under a stale scope.
 */
async function optionalBroilerFeedRows<Row>(
  read: () => PromiseLike<ListResponse<Row>>,
  absent: Row[],
  signal?: AbortSignal,
): Promise<Row[]> {
  try {
    const response = await read();
    if (signal?.aborted) throw broilerFeedAbortError();
    return response.error ? absent : response.data || absent;
  } catch (error) {
    if (signal?.aborted) throw error;
    return absent;
  }
}

async function queryBroilerFeedCore(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<BroilerFeedCoreData> {
  const [store, layerBatches, layerHousings, allLayerDailys, broilerDailys] = await Promise.all([
    withSignal(sb.from('app_store').select('key,data').in('key', BROILER_FEED_APP_STORE_KEYS), signal),
    // App's boot projections, preserved: batches by name, housings by
    // start_date. calcLayerFeedForMonth and computeProjectedCount resolve a
    // housing to its batch by id, so the order only decides which of two
    // same-named rows wins — which is why it is preserved rather than rechosen.
    optionalBroilerFeedRows<LayerBatchCoreRow>(
      () =>
        withSignal(sb.from('layer_batches').select('*').order('name'), signal) as PromiseLike<
          ListResponse<LayerBatchCoreRow>
        >,
      NO_BROILER_FEED_LAYER_BATCHES,
      signal,
    ),
    optionalBroilerFeedRows<LayerHousingCoreRow>(
      () =>
        withSignal(sb.from('layer_housings').select('*').order('start_date'), signal) as PromiseLike<
          ListResponse<LayerHousingCoreRow>
        >,
      NO_BROILER_FEED_LAYER_HOUSINGS,
      signal,
    ),
    (async () => {
      // App's full-history sidecar, reproduced page for page: 1,000-row pages,
      // the deleted_at filter and date DESC. Its FAILURE behaviour is
      // reproduced too — a failed page stops the walk and returns the rows
      // gathered so far. That silent partial is inherited, not chosen: the
      // poultry loader's throw-after-the-fact pagination fix has no
      // layer twin yet, and inventing one inside a localization would change
      // what this page shows without anyone deciding to.
      const rows: LayerDailyRow[] = [];
      try {
        for (let offset = 0; ; offset += BROILER_FEED_LAYER_DAILYS_PAGE) {
          const {data, error} = (await withSignal(
            sb
              .from('layer_dailys')
              .select('*')
              .is('deleted_at', null)
              .order('date', {ascending: false})
              .range(offset, offset + BROILER_FEED_LAYER_DAILYS_PAGE - 1),
            signal,
          )) as ListResponse<LayerDailyRow>;
          if (signal?.aborted) throw broilerFeedAbortError();
          if (error || !data || data.length === 0) break;
          rows.push(...data);
          if (data.length < BROILER_FEED_LAYER_DAILYS_PAGE) break;
        }
      } catch (error) {
        if (signal?.aborted) throw error;
      }
      return rows;
    })(),
    (async () => {
      // The boot loader itself, called rather than re-implemented. It is the
      // module that fixed the silently-truncated poultry history by capturing
      // the page error and throwing afterwards, so reproducing its loop here
      // would mean reproducing that fix — and eventually drifting from it.
      try {
        return await loadAllPoultryDailys(sb, signal ?? null);
      } catch (error) {
        if (signal?.aborted) throw error;
        return NO_BROILER_FEED_POULTRY_DAILYS;
      }
    })(),
  ]);

  const blobs: Record<string, unknown> = Object.create(null) as Record<string, unknown>;
  for (const row of unwrap<{key?: unknown; data?: unknown}>(
    store as ListResponse<{key?: unknown; data?: unknown}>,
    'app_store',
  )) {
    if (typeof row?.key === 'string') blobs[row.key] = row.data;
  }
  const storedFeedOrders = blobs['ppp-feed-orders-v1'];
  const storedInventory = blobs['ppp-poultry-feed-inventory-v1'];
  return {
    batches: normalizedLightHomeBroilerBatches(blobs['ppp-v4']),
    // The boot fan-out guards each of these with `if (store[key])` and leaves
    // App's initial state alone otherwise, so an absent row is the seed and not
    // an error. The four-key seed matters beyond the reads: commitActiveOrder
    // saves `{...feedOrders, starter, grower, layerfeed}`, so a bare `{}` here
    // would drop the persisted `pig` map on the next poultry order.
    feedOrders: storedFeedOrders
      ? (storedFeedOrders as Record<string, Record<string, unknown>>)
      : {pig: {}, starter: {}, grower: {}, layerfeed: {}},
    poultryFeedInventory: storedInventory ? (storedInventory as Record<string, unknown>) : null,
    layerBatches,
    layerHousings,
    allLayerDailys,
    broilerDailys,
  };
}

// ---------------------------------------------------------------------------
// The four Pasture Map resources — /pasture-map owns every read it renders.
// ---------------------------------------------------------------------------
// Three of them are the page's OWN reads, moved rather than taken back: the map
// already called the pasture SECDEF wrappers from a mount effect and two lazy
// effects. What moved is the OWNERSHIP of those reads — the scope key, the role
// gate, cancellation, and the retry that follows a write — from PastureMapView
// into this catalog. The fourth, pasture.planner.roster, IS a hand-off taken
// back: the roster's four inputs arrived through PigContext, CattleHomeContext
// and SheepHomeContext off App's global boot, and that is the one thing on this
// page that made it boot-dependent at all.
//
// NO RPC IS RE-ISSUED HERE. Every land_area / pasture_move table is deny-all
// RLS and pastureMapApi is the only client door, so these queries CALL its
// wrappers with the injected client and Query's AbortSignal. Re-writing
// `sb.rpc('list_land_areas', …)` in this file would give a read that already
// has exactly one physical call site a second one — the duplication the frozen
// render-read-site partition exists to catch.
//
// WHY THREE RESOURCES AND NOT ONE. The mount bundle, the open area's record and
// the open group's record are three different keys with three different
// lifetimes: the bundle is refetched after every write, while each record is
// keyed on which area/group is open and must not re-run when the other changes.
// Folding them together would refetch the whole map every time a modal opened.
//
// WHY THE ROSTER IS NOT IN THE BUNDLE. A pasture read failing offline is
// expected and the page has a saved-snapshot fallback for it; the roster has no
// snapshot. Keeping them separate means an app_store outage cannot blank the
// field map, and a pasture RPC outage cannot blank the group roster.

/**
 * One row of a pasture RPC payload. The wrappers in pastureMapApi resolve
 * `unknown` deliberately — the payloads are server-assembled jsonb whose column
 * lists live across twelve migrations and are re-shaped by later ones — so this
 * layer names no field and the view that DOES know the shapes narrows at its
 * own boundary, exactly as it already does for the planner rows.
 */
export type PastureEnvelopeRow = {[key: string]: unknown};

/** What pasture.map.core resolves with — the five reads the map mounts on. */
export type PastureMapCoreData = {
  areas: PastureEnvelopeRow[];
  moves: PastureEnvelopeRow[];
  history: PastureEnvelopeRow[];
  rotations: PastureEnvelopeRow[];
  measurements: PastureEnvelopeRow[];
};

/**
 * `(res && res.key) || []`, reproduced. Every pasture RPC resolves a jsonb
 * ENVELOPE — `{land_areas: […]}`, `{moves: […]}`, `{history: […]}` — and a
 * missing or non-array member has always meant "nothing", never an error. This
 * is PastureMapView's own `payloadList`, moved here with the seven reads that
 * were its only callers.
 */
function pastureEnvelopeList(value: unknown, key: string): PastureEnvelopeRow[] {
  const bag = value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
  const list = bag ? bag[key] : undefined;
  return Array.isArray(list) ? (list as PastureEnvelopeRow[]) : [];
}

/**
 * A required string param, rejected rather than defaulted. Both record
 * resources are keyed on WHICH record is open; a missing id would otherwise
 * fetch the unfiltered farm-wide history and paint it into one area's timeline.
 * The callers gate on the same value through `enabled`, so this is fail-closed
 * depth, not a second gate.
 */
function requiredPastureParam(params: ResourceParams | undefined, name: string): string {
  const value = params?.[name];
  if (typeof value !== 'string' || value.trim() === '') {
    throw new Error(`routeDataCatalog: pasture record resource requires a '${name}' param`);
  }
  return value;
}

// ---------------------------------------------------------------------------
// pasture.map.core — the five reads /pasture-map mounts on.
// ---------------------------------------------------------------------------
// Exactly the Promise.all PastureMapView.reload() ran, including the role gate:
// Light-and-above see the map's areas and moves, and the planning/report reads
// run only for the roles granted them (migration 139 widened the same set
// server-side, so client and DB stay in lockstep). The non-planning branches
// resolve the same empty envelopes they always did rather than being skipped,
// so the payload shape does not depend on the role.
//
// ONE READ FAILING FAILS THE WHOLE RESOURCE, and that is the existing contract
// rather than a new one: the five were already a single Promise.all inside one
// try, and its catch is what selects the saved field map. Degrading a single
// read to [] here would hand the page a half-map it could not tell from a farm
// with no areas — and would silently disable the offline snapshot that is the
// whole point of this surface in the field.
async function queryPastureMapCore(
  sb: SupabaseClient,
  params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<PastureMapCoreData> {
  const canViewPlanning = params?.canViewPlanning === true;
  const [areaRes, moveRes, historyRes, rotRes, measRes] = await Promise.all([
    listLandAreas(sb, false, signal),
    listPastureMoves(sb, 75, signal),
    canViewPlanning ? listPastureHistoryReport(sb, {limit: 1000}, signal) : Promise.resolve({history: []}),
    canViewPlanning ? listPastureRotations(sb, signal) : Promise.resolve({rotations: []}),
    canViewPlanning ? listPastureMeasurements(sb, signal) : Promise.resolve({measurements: []}),
  ]);
  return {
    areas: pastureEnvelopeList(areaRes, 'land_areas'),
    moves: pastureEnvelopeList(moveRes, 'moves'),
    history: pastureEnvelopeList(historyRes, 'history'),
    rotations: pastureEnvelopeList(rotRes, 'rotations'),
    measurements: pastureEnvelopeList(measRes, 'measurements'),
  };
}

// ---------------------------------------------------------------------------
// pasture.area.history — the open area's grazing timeline.
// ---------------------------------------------------------------------------
// The canonical Area Record in EITHER shell (the Map area modal or the Reports
// record) reads the same 500-row scoped history. Keyed on the open area so
// opening a second one is a different cache entry rather than a race between
// two responses — which is what the effect's `alive` flag was fencing by hand.
async function queryPastureAreaHistory(
  sb: SupabaseClient,
  params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<PastureEnvelopeRow[]> {
  const landAreaId = requiredPastureParam(params, 'landAreaId');
  return pastureEnvelopeList(await listPastureHistoryReport(sb, {landAreaId, limit: 500}, signal), 'history');
}

// ---------------------------------------------------------------------------
// pasture.group.history — the open group's grazing record.
// ---------------------------------------------------------------------------
// The (animal_type, group_key) half of the same report RPC: a group's stays
// across every area, rather than an area's stays across every group. A separate
// resource from the area record because the two are open independently and each
// must survive the other changing.
async function queryPastureGroupHistory(
  sb: SupabaseClient,
  params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<PastureEnvelopeRow[]> {
  const animalType = requiredPastureParam(params, 'animalType');
  const groupKey = requiredPastureParam(params, 'groupKey');
  return pastureEnvelopeList(await listPastureHistoryReport(sb, {animalType, groupKey, limit: 500}, signal), 'history');
}

// ---------------------------------------------------------------------------
// pasture.planner.roster — the four inputs the group roster is derived from.
// ---------------------------------------------------------------------------
// The ONE thing on this page that came from App's global boot, and it came
// through three providers at once:
//
//   ppp-breeders-v1   PigContext.breeders, off the boot app_store transaction
//   ppp-feeders-v1    PigContext.feederGroups, same transaction
//   cattle            CattleHomeContext.cattleForHome, the boot's lightweight
//                     eight-column directory
//   sheep             SheepHomeContext.sheepForHome, its twin
//
// computePlannerGroupRoster stays in the view: it is pure, it is shared with the
// roster's own unit tests, and a derivation is not a read. What moved is where
// the four inputs come from.
//
// THE TWO PIG NORMALIZATIONS ARE THE BOOT'S, REPRODUCED, NOT REINVENTED —
// the same pair pig.home.core already reproduces from applyAppStoreBootFanOut:
//
//   feederGroups   reconcileFeederGroupsFromBreeders repairs sub-batch counts
//                  against the parent. The roster counts feeder sub-batches by
//                  `sub.status` and computeSubCurrentCount, so skipping the
//                  reconcile would put a different head count on a paddock tile
//                  than the boot did.
//   breeders       an absent OR EMPTY list falls back to INITIAL_BREEDERS. That
//                  is the boot's one unconditional pig hydration, and the sow /
//                  boar groups are counted off it.
//
// The reconcile's `changed` flag and the boot's follow-up app_store upsert are
// deliberately NOT reproduced: this is a render read, and a read that writes is
// not idempotent. App's boot still owns that repair for every surface it serves.
//
// ERROR POSTURE IS PER-READ, AND IT IS THE BOOT'S OWN SPLIT. The blobs THROW —
// app_store is the one structural dependency the boot escalates rather than
// degrades, and a farm with no sows, no boars and no feeder batches is a lie
// this page would then invite an operator to move animals against. The two
// directories do NOT throw, because the boot's own `.catch(() => {})` did not:
// a denied cattle read left the herd groups missing and everything else intact.
const NO_PASTURE_ROSTER_ROWS: CattleRow[] = [];

export const PASTURE_ROSTER_APP_STORE_KEYS = Object.freeze(['ppp-feeders-v1', 'ppp-breeders-v1'] as const);

/** What pasture.planner.roster resolves with — computePlannerGroupRoster's four inputs. */
export type PastureRosterData = {
  breeders: CattleRow[];
  feederGroups: CattleRow[];
  cattleForHome: CattleRow[];
  sheepForHome: CattleRow[];
};

function pastureRosterAbortError(): Error {
  const error = new Error(`${RESOURCE.PASTURE_PLANNER_ROSTER}: aborted`);
  error.name = 'AbortError';
  return error;
}

/**
 * The boot's `.catch(() => {})` for the two animal directories, preserved.
 * Cancellation is different and must escape: a superseded scope has to stay
 * cancelled rather than writing an invented empty roster under the old key.
 */
async function optionalPastureRosterRows(read: () => unknown, signal?: AbortSignal): Promise<CattleRow[]> {
  try {
    const response = (await read()) as ListResponse<CattleRow>;
    if (signal?.aborted) throw pastureRosterAbortError();
    return response.error ? NO_PASTURE_ROSTER_ROWS : response.data || NO_PASTURE_ROSTER_ROWS;
  } catch (error) {
    if (signal?.aborted) throw error;
    return NO_PASTURE_ROSTER_ROWS;
  }
}

async function queryPasturePlannerRoster(
  sb: SupabaseClient,
  _params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<PastureRosterData> {
  const [store, cattleForHome, sheepForHome] = await Promise.all([
    withSignal(sb.from('app_store').select('key,data').in('key', PASTURE_ROSTER_APP_STORE_KEYS), signal),
    optionalPastureRosterRows(
      () =>
        withSignal(
          sb
            .from('cattle')
            .select('id,herd,purchase_date,birth_date,created_at,sale_date,death_date,deleted_at')
            .is('deleted_at', null),
          signal,
        ),
      signal,
    ),
    optionalPastureRosterRows(
      () =>
        withSignal(
          sb
            .from('sheep')
            .select('id,flock,purchase_date,birth_date,created_at,sale_date,death_date,deleted_at')
            .is('deleted_at', null),
          signal,
        ),
      signal,
    ),
  ]);

  const rows = unwrap<{key?: unknown; data?: unknown}>(
    store as ListResponse<{key?: unknown; data?: unknown}>,
    'app_store',
  );
  const blobs: Record<string, unknown> = Object.create(null) as Record<string, unknown>;
  for (const row of rows) if (typeof row?.key === 'string') blobs[row.key] = row.data;

  const breeders = lightHomeRows(blobs['ppp-breeders-v1']);
  return {
    breeders: breeders.length > 0 ? breeders : (INITIAL_BREEDERS as CattleRow[]),
    feederGroups: reconcileFeederGroupsFromBreeders(
      lightHomeRows(blobs['ppp-feeders-v1']) as unknown as PigFeederGroup[],
    ).groups as unknown as CattleRow[],
    cattleForHome,
    sheepForHome,
  };
}

/**
 * Shared catalog ownership metadata. `surfaces` is the declared reachability
 * set (a resource no surface may request is unreachable; a surface requesting
 * an undeclared one is a leak), while `tables` and `buckets` are what
 * surface_router_derivation joins against the frozen call-site inventory.
 * RPC-backed resources may also declare `rpcs`; unlike tables these are joined
 * to the RPC call inventory. Private signed-URL resources declare their
 * Storage buckets explicitly rather than disguising an object read as a table.
 * A resource cannot pass with a vacuous empty physical-read claim. The fetch
 * shape is discriminated below.
 */
export type CatalogResourceBase = {
  readonly id: string;
  readonly surfaces: readonly string[];
  readonly tables: readonly string[];
  readonly rpcs?: readonly string[];
  readonly buckets?: readonly string[];
  readonly http?: readonly {
    readonly method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
    readonly endpoint: string;
  }[];
};

/** The one-response resource used by useRouteResource/useQuery. */
export type OneShotRouteResource<Data> = CatalogResourceBase & {
  readonly kind: 'one-shot';
  readonly key: (scope: unknown, params?: ResourceParams) => QueryKey;
  readonly query: (sb: SupabaseClient, params: ResourceParams | undefined, signal?: AbortSignal) => Promise<Data>;
};

/**
 * A raw cursor page plus resource-specific first-page metadata. Metadata stays
 * on the page instead of being reconstructed from flattened rows, so a future
 * adapter can expose an exact server count without pretending it can derive
 * one from the pages fetched so far.
 */
export type PaginatedPage<Row, PageParam, Meta extends object = Record<never, never>> = Readonly<
  {
    rows: readonly Row[];
    nextPage?: PageParam | null;
  } & Meta
>;

/**
 * The append-only resource used by usePaginatedResource/useInfiniteQuery.
 *
 * A paginated resource deliberately has no compatibility `key` or `query`.
 * Those functions promise one complete payload, which a cursor-backed list
 * cannot truthfully provide. Its page key is still built from the same full
 * QueryScope by resourceKey, and the cursor remains Query's pageParam rather
 * than becoming an accidental cache-key dimension.
 */
export type PaginatedRouteResource<Row, PageParam, Meta extends object = Record<never, never>> = CatalogResourceBase & {
  readonly kind: 'paginated';
  readonly pageKey: (scope: unknown, params?: ResourceParams) => QueryKey;
  readonly initialPageParam: PageParam;
  readonly queryPage: (
    sb: SupabaseClient,
    params: ResourceParams | undefined,
    pageParam: PageParam,
    signal?: AbortSignal,
  ) => Promise<PaginatedPage<Row, PageParam, Meta>>;
};

/** Every catalog entry is explicitly one-shot or paginated. */
export type CatalogResource = OneShotRouteResource<unknown> | PaginatedRouteResource<unknown, unknown>;

// Keyed by plain string so getResource can answer for a runtime id without a
// cast; the unknown-resource throw stays the only gate. The payload type is
// `unknown` at the catalog level for the same reason the store in offlineQueue
// keeps its record opaque: two entries have DIFFERENT payload types, and a
// union here would let a caller read the wrong one without narrowing.
const CATALOG_ENTRIES: Readonly<Record<string, CatalogResource>> = Object.freeze({
  [RESOURCE.CATTLE_HERDS_CORE]: Object.freeze({
    id: RESOURCE.CATTLE_HERDS_CORE,
    kind: 'one-shot',
    // The surfaces permitted to request it. A resource with no declared surface
    // is unreachable; a surface requesting an undeclared resource is a leak.
    surfaces: Object.freeze(['cattle.herds.index']),
    tables: Object.freeze([
      'cattle',
      'weigh_in_sessions',
      'weigh_ins',
      'cattle_calving_records',
      'cattle_breeds',
      'cattle_origins',
      'cattle_transfers',
    ]),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.CATTLE_HERDS_CORE, scope, params),
    query: queryCattleHerdsCore,
  }),
  [RESOURCE.CATTLE_ANIMAL_DETAIL]: Object.freeze({
    id: RESOURCE.CATTLE_ANIMAL_DETAIL,
    kind: 'one-shot',
    surfaces: Object.freeze(['cattle.herds.detail']),
    tables: Object.freeze([
      'cattle',
      'weigh_in_sessions',
      'weigh_ins',
      'cattle_calving_records',
      'cattle_breeds',
      'cattle_origins',
      'cattle_processing_batches',
    ]),
    // cattleId is part of the key, so navigating A -> B is a key change.
    key: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.CATTLE_ANIMAL_DETAIL, scope, {cattleId: params?.cattleId}),
    query: queryCattleAnimalDetail,
  }),
  [RESOURCE.CATTLE_HOME_CORE]: Object.freeze({
    id: RESOURCE.CATTLE_HOME_CORE,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.cattleHome']),
    tables: Object.freeze([
      'cattle',
      'cattle_dailys',
      'weigh_in_sessions',
      'weigh_ins',
      'cattle_calving_records',
      'cattle_nutrition_targets',
      'cattle_processing_batches',
      'cattle_forecast_settings',
      'cattle_forecast_heifer_includes',
      'cattle_forecast_hidden',
    ]),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.CATTLE_HOME_CORE, scope, params),
    query: queryCattleHomeCore,
  }),
  [RESOURCE.CATTLE_BATCHES_CORE]: Object.freeze({
    id: RESOURCE.CATTLE_BATCHES_CORE,
    kind: 'one-shot',
    surfaces: Object.freeze(['cattle.batches.index']),
    tables: Object.freeze([
      'cattle_processing_batches',
      'cattle',
      'weigh_in_sessions',
      'weigh_ins',
      'cattle_calving_records',
      'cattle_forecast_settings',
      'cattle_forecast_heifer_includes',
      'cattle_forecast_hidden',
    ]),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.CATTLE_BATCHES_CORE, scope, params),
    query: queryCattleBatchesCore,
  }),
  [RESOURCE.CATTLE_WEIGHINS_LIST]: Object.freeze({
    id: RESOURCE.CATTLE_WEIGHINS_LIST,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.cattleweighins']),
    tables: Object.freeze(['weigh_in_sessions', 'weigh_ins']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.CATTLE_WEIGHINS_LIST, scope, params),
    query: queryCattleWeighInsList,
  }),
  [RESOURCE.SHEEP_WEIGHINS_LIST]: Object.freeze({
    id: RESOURCE.SHEEP_WEIGHINS_LIST,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.sheepweighins']),
    tables: Object.freeze(['weigh_in_sessions', 'weigh_ins']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.SHEEP_WEIGHINS_LIST, scope, params),
    query: querySheepWeighInsList,
  }),
  [RESOURCE.LIVESTOCK_WEIGHINS_LIST]: Object.freeze({
    id: RESOURCE.LIVESTOCK_WEIGHINS_LIST,
    kind: 'one-shot',
    // One shared component renders both routes. The species parameter remains
    // part of the key, so a broiler response can never paint into pig state.
    surfaces: Object.freeze(['view.broilerweighins', 'view.pigweighins']),
    tables: Object.freeze(['weigh_in_sessions', 'weigh_ins']),
    key: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.LIVESTOCK_WEIGHINS_LIST, scope, {species: params?.species}),
    query: queryLivestockWeighInsList,
  }),
  [RESOURCE.PIG_WEIGHINS_METRICS]: Object.freeze({
    id: RESOURCE.PIG_WEIGHINS_METRICS,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.pigweighins']),
    tables: Object.freeze([]),
    rpcs: Object.freeze(['pig_session_metrics']),
    // The generation changes after a successful list settle. That preserves the
    // legacy effect's refresh on a new list payload even when the ID set itself
    // happens to be unchanged.
    key: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.PIG_WEIGHINS_METRICS, scope, {
        sessionIds: params?.sessionIds,
        generation: params?.generation ?? 0,
      }),
    query: queryPigWeighInsMetrics,
  }),
  [RESOURCE.WEIGHIN_SESSION_RECORD]: Object.freeze({
    id: RESOURCE.WEIGHIN_SESSION_RECORD,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.weighinsessions']),
    tables: Object.freeze(['weigh_in_sessions']),
    // sessionId makes previous/next record navigation a key change, so the old
    // session drops before the next query can settle.
    key: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.WEIGHIN_SESSION_RECORD, scope, {sessionId: params?.sessionId}),
    query: queryWeighInSessionRecord,
  }),
  [RESOURCE.WEIGHIN_SESSION_DETAIL]: Object.freeze({
    id: RESOURCE.WEIGHIN_SESSION_DETAIL,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.weighinsessions']),
    tables: Object.freeze(['weigh_ins', 'cattle', 'sheep', 'weigh_in_sessions', 'app_store']),
    // species is in the key because it selects which branch ran. Without it a
    // pig payload and a cattle payload would share one entry, and the page
    // reads `animals` and `feederGroups` off whichever settled last.
    key: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.WEIGHIN_SESSION_DETAIL, scope, {
        sessionId: params?.sessionId,
        species: params?.species,
      }),
    query: queryWeighInSessionDetail,
  }),
  [RESOURCE.WEIGHIN_SESSION_PIG_METRICS]: Object.freeze({
    id: RESOURCE.WEIGHIN_SESSION_PIG_METRICS,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.weighinsessions']),
    tables: Object.freeze(['weigh_ins']),
    rpcs: Object.freeze(['pig_session_metrics']),
    key: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.WEIGHIN_SESSION_PIG_METRICS, scope, {sessionId: params?.sessionId}),
    query: queryWeighInSessionPigMetrics,
  }),
  [RESOURCE.WEIGHIN_WEBFORM_SPECIES_DIRECTORY]: Object.freeze({
    id: RESOURCE.WEIGHIN_WEBFORM_SPECIES_DIRECTORY,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.weighins']),
    tables: Object.freeze(['cattle', 'sheep', 'webform_config']),
    // `load` is a per-selection generation, and it is not decoration. Picking a
    // species is a fresh read every time — the effect this replaces re-ran on
    // every `[species]` change, including a return to one already visited. A
    // key of species alone would serve the cached payload from that earlier
    // visit while the refetch was in flight, and KEEP serving it if the refetch
    // failed. For broiler that is the exact "stale schooner metadata lets Start
    // Session proceed on outdated columns" failure the presenter fails closed
    // against, so the generation is what preserves fail-closed here.
    key: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.WEIGHIN_WEBFORM_SPECIES_DIRECTORY, scope, {
        species: params?.species,
        load: params?.load ?? 0,
      }),
    query: queryWeighInWebformSpeciesDirectory,
  }),
  [RESOURCE.WEIGHIN_WEBFORM_BROILER_SCHOONERS]: Object.freeze({
    id: RESOURCE.WEIGHIN_WEBFORM_BROILER_SCHOONERS,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.weighins']),
    tables: Object.freeze(['webform_config']),
    key: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.WEIGHIN_WEBFORM_BROILER_SCHOONERS, scope, {load: params?.load ?? 0}),
    query: queryWeighInWebformBroilerSchooners,
  }),
  [RESOURCE.WEIGHIN_WEBFORM_DRAFTS]: Object.freeze({
    id: RESOURCE.WEIGHIN_WEBFORM_DRAFTS,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.weighins']),
    tables: Object.freeze(['weigh_in_sessions']),
    key: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.WEIGHIN_WEBFORM_DRAFTS, scope, {
        species: params?.species,
        cutoff: params?.cutoff,
        load: params?.load ?? 0,
      }),
    query: queryWeighInWebformDrafts,
  }),
  [RESOURCE.WEIGHIN_WEBFORM_PRIOR_WEIGHTS]: Object.freeze({
    id: RESOURCE.WEIGHIN_WEBFORM_PRIOR_WEIGHTS,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.weighins']),
    tables: Object.freeze(['weigh_in_sessions', 'weigh_ins']),
    key: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.WEIGHIN_WEBFORM_PRIOR_WEIGHTS, scope, {
        species: params?.species,
        today: params?.today,
        load: params?.load ?? 0,
      }),
    query: queryWeighInWebformPriorWeights,
  }),
  [RESOURCE.WEIGHIN_WEBFORM_SESSION_ENTRIES]: Object.freeze({
    id: RESOURCE.WEIGHIN_WEBFORM_SESSION_ENTRIES,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.weighins']),
    tables: Object.freeze(['weigh_ins']),
    key: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.WEIGHIN_WEBFORM_SESSION_ENTRIES, scope, {
        sessionId: params?.sessionId,
        load: params?.load ?? 0,
      }),
    query: queryWeighInWebformSessionEntries,
  }),
  [RESOURCE.WEIGHIN_WEBFORM_PIG_METRICS]: Object.freeze({
    id: RESOURCE.WEIGHIN_WEBFORM_PIG_METRICS,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.weighins']),
    tables: Object.freeze([]),
    rpcs: Object.freeze(['pig_session_metrics']),
    // entryCount is in the key because the effect it replaces re-fired on
    // `entries.length`: weighed_count, average and ADG must stay live as the
    // operator weighs each additional pig.
    key: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.WEIGHIN_WEBFORM_PIG_METRICS, scope, {
        sessionId: params?.sessionId,
        entryCount: params?.entryCount ?? 0,
      }),
    query: queryWeighInWebformPigMetrics,
  }),
  [RESOURCE.SHEEP_HOME_CORE]: Object.freeze({
    id: RESOURCE.SHEEP_HOME_CORE,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.sheepHome']),
    tables: Object.freeze(['weigh_in_sessions', 'weigh_ins', 'sheep', 'sheep_dailys', 'sheep_processing_batches']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.SHEEP_HOME_CORE, scope, params),
    query: querySheepHomeCore,
  }),
  [RESOURCE.SHEEP_FLOCKS_CORE]: Object.freeze({
    id: RESOURCE.SHEEP_FLOCKS_CORE,
    kind: 'one-shot',
    surfaces: Object.freeze(['sheep.flocks.index']),
    tables: Object.freeze([
      'sheep',
      'weigh_in_sessions',
      'weigh_ins',
      'sheep_lambing_records',
      'sheep_breeds',
      'sheep_origins',
      'sheep_transfers',
    ]),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.SHEEP_FLOCKS_CORE, scope, params),
    query: querySheepFlocksCore,
  }),
  [RESOURCE.SHEEP_ANIMAL_DETAIL]: Object.freeze({
    id: RESOURCE.SHEEP_ANIMAL_DETAIL,
    kind: 'one-shot',
    surfaces: Object.freeze(['sheep.flocks.detail']),
    tables: Object.freeze([
      'sheep',
      'weigh_in_sessions',
      'weigh_ins',
      'sheep_lambing_records',
      'sheep_breeds',
      'sheep_origins',
    ]),
    key: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.SHEEP_ANIMAL_DETAIL, scope, {sheepId: params?.sheepId}),
    query: querySheepAnimalDetail,
  }),
  [RESOURCE.SHEEP_BATCHES_CORE]: Object.freeze({
    id: RESOURCE.SHEEP_BATCHES_CORE,
    kind: 'one-shot',
    surfaces: Object.freeze(['sheep.batches.index']),
    tables: Object.freeze(['sheep_processing_batches']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.SHEEP_BATCHES_CORE, scope, params),
    query: querySheepBatchesCore,
  }),
  [RESOURCE.SHEEP_BATCH_DETAIL]: Object.freeze({
    id: RESOURCE.SHEEP_BATCH_DETAIL,
    kind: 'one-shot',
    surfaces: Object.freeze(['sheep.batches.detail']),
    tables: Object.freeze(['sheep_processing_batches', 'sheep']),
    // batchId makes one record -> another a key change, never a stale repaint.
    key: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.SHEEP_BATCH_DETAIL, scope, {batchId: params?.batchId}),
    query: querySheepBatchDetail,
  }),
  [RESOURCE.PROCESSING_CALENDAR_CORE]: Object.freeze({
    id: RESOURCE.PROCESSING_CALENDAR_CORE,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.processing']),
    // The RPC wrapper owns the physical call; this catalog entry owns the
    // surface request. It is intentionally not represented as a PostgREST
    // table in the table-oriented inventory.
    tables: Object.freeze([]),
    rpcs: Object.freeze(['list_processing_records']),
    key: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.PROCESSING_CALENDAR_CORE, scope, {reload: params?.reload ?? 0}),
    query: queryProcessingView,
  }),
  [RESOURCE.PROCESSING_CUT_SHEET_CORE]: Object.freeze({
    id: RESOURCE.PROCESSING_CUT_SHEET_CORE,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.processingCutSheet']),
    // Deny-all RLS on every processing_cut_sheet_* table: the browser reaches
    // them only through this SECURITY DEFINER RPC.
    tables: Object.freeze([]),
    rpcs: Object.freeze(['get_processing_cut_sheet']),
    key: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.PROCESSING_CUT_SHEET_CORE, scope, {recordId: params?.recordId ?? null}),
    query: queryProcessingCutSheetCore,
  }),
  [RESOURCE.NEWSLETTER_ISSUES_CORE]: Object.freeze({
    id: RESOURCE.NEWSLETTER_ISSUES_CORE,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.newsletterAdmin']),
    // Deny-all RLS: the browser never selects a newsletter_* table.
    tables: Object.freeze([]),
    rpcs: Object.freeze(['list_newsletter_issues_admin']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.NEWSLETTER_ISSUES_CORE, scope, params),
    query: queryNewsletterIssuesCore,
  }),
  [RESOURCE.NEWSLETTER_SETTINGS_CORE]: Object.freeze({
    id: RESOURCE.NEWSLETTER_SETTINGS_CORE,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.newsletterAdmin']),
    tables: Object.freeze([]),
    rpcs: Object.freeze(['get_newsletter_settings']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.NEWSLETTER_SETTINGS_CORE, scope, params),
    query: queryNewsletterSettingsCore,
  }),
  [RESOURCE.NEWSLETTER_ISSUE_DETAIL]: Object.freeze({
    id: RESOURCE.NEWSLETTER_ISSUE_DETAIL,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.newsletterAdmin']),
    tables: Object.freeze([]),
    // The editor loads settings concurrently with its issue and tolerates a
    // settings failure, so it stays in this resource as well as settings.core.
    rpcs: Object.freeze([
      'get_newsletter_issue_admin',
      'get_newsletter_settings',
      'get_newsletter_recent_published_admin',
      'list_newsletter_runs_admin',
    ]),
    // issueId is part of the key, so opening another issue is a key change.
    key: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.NEWSLETTER_ISSUE_DETAIL, scope, {issueId: params?.issueId}),
    query: queryNewsletterIssueDetail,
  }),
  [RESOURCE.EQUIPMENT_ADMIN_RECORD_CORE]: Object.freeze({
    id: RESOURCE.EQUIPMENT_ADMIN_RECORD_CORE,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.equipmentAdminRecord']),
    tables: Object.freeze(['equipment']),
    key: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.EQUIPMENT_ADMIN_RECORD_CORE, scope, {reload: params?.reload ?? 0}),
    query: queryEquipmentAdminRecordCore,
  }),
  [RESOURCE.EQUIPMENT_FLEET_CORE]: Object.freeze({
    id: RESOURCE.EQUIPMENT_FLEET_CORE,
    kind: 'one-shot',
    // EquipmentHome mounts one container for all four /fleet surfaces. The
    // active detail/entry row is selected from this same payload, so every
    // variant must name the shared resource explicitly.
    surfaces: Object.freeze([
      'equipment.fleet.index',
      'equipment.fleet.detail',
      'equipment.fueling.entry',
      'equipment.checklist.entry',
    ]),
    tables: Object.freeze(['equipment', 'equipment_fuelings', 'equipment_maintenance_events']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.EQUIPMENT_FLEET_CORE, scope, params),
    query: queryEquipmentFleetCore,
  }),
  [RESOURCE.EQUIPMENT_FUELING_HUB_CORE]: Object.freeze({
    id: RESOURCE.EQUIPMENT_FUELING_HUB_CORE,
    kind: 'one-shot',
    // FuelingHub mounts ONE container for the tile hub and the per-machine
    // form; the form's active machine is selected out of this same payload.
    // `equipment.fueling.supply` is deliberately ABSENT: FuelSupplyWebform
    // consumes nothing from this read, and the consumer disables the query on
    // that subroute so this list is the true reachability set.
    surfaces: Object.freeze(['equipment.fueling.hub', 'equipment.fueling.form']),
    tables: Object.freeze(['equipment']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.EQUIPMENT_FUELING_HUB_CORE, scope, params),
    query: queryEquipmentFuelingHubCore,
  }),
  [RESOURCE.EQUIPMENT_OWN_REPORTS]: Object.freeze({
    id: RESOURCE.EQUIPMENT_OWN_REPORTS,
    kind: 'one-shot',
    surfaces: Object.freeze(['equipment.fueling.hub']),
    tables: Object.freeze(['equipment_fuelings', 'fuel_supplies', 'equipment']),
    // Both authenticated scope and explicit owner belong in the key; a shared
    // phone must never retain the previous user's report list after sign-in.
    key: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.EQUIPMENT_OWN_REPORTS, scope, {profileId: params?.profileId}),
    query: queryOwnerEquipmentReports,
  }),
  [RESOURCE.OWNER_REPORT_RECOVERY]: Object.freeze({
    id: RESOURCE.OWNER_REPORT_RECOVERY,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.webforms']),
    tables: Object.freeze([]),
    rpcs: Object.freeze([
      'list_deleted_equipment_fuelings',
      'list_deleted_fuel_supplies',
      'list_deleted_cattle_log_entries',
    ]),
    // This admin-only list still carries subject, role and preview scope.
    key: (scope: unknown) => resourceKey(RESOURCE.OWNER_REPORT_RECOVERY, scope),
    query: queryOwnerReportRecovery,
  }),
  [RESOURCE.EQUIPMENT_FUELING_FORM_HISTORY]: Object.freeze({
    id: RESOURCE.EQUIPMENT_FUELING_FORM_HISTORY,
    kind: 'one-shot',
    surfaces: Object.freeze(['equipment.fueling.form']),
    tables: Object.freeze(['equipment_fuelings']),
    // equipmentId is part of the key, so a machine switch is a key change.
    key: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.EQUIPMENT_FUELING_FORM_HISTORY, scope, {equipmentId: params?.equipmentId}),
    query: queryEquipmentFuelingFormHistory,
  }),
  [RESOURCE.HOME_STANDARD_ALERT_INPUTS]: Object.freeze({
    id: RESOURCE.HOME_STANDARD_ALERT_INPUTS,
    kind: 'one-shot',
    surfaces: Object.freeze(['app.home.standard']),
    tables: Object.freeze(['layer_dailys', 'egg_dailys', 'cattle_dailys', 'sheep_dailys', 'cattle', 'sheep']),
    // Both cutoffs are named explicitly so the key rolls with the farm day and
    // an unrelated param can never widen the recorded window silently.
    key: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.HOME_STANDARD_ALERT_INPUTS, scope, {
        recentCutoff: params?.recentCutoff,
        dailyCutoff: params?.dailyCutoff,
      }),
    query: queryHomeStandardAlertInputs,
  }),
  [RESOURCE.HOME_STANDARD_ANIMAL_LAYER_EVIDENCE]: Object.freeze({
    id: RESOURCE.HOME_STANDARD_ANIMAL_LAYER_EVIDENCE,
    kind: 'one-shot',
    surfaces: Object.freeze(['app.home.standard']),
    tables: Object.freeze(['layer_dailys']),
    // NO params, and that is the difference from its alert-inputs neighbour: a
    // COMPLETE history has no farm-day identity, so there is no cutoff to put
    // in the key and no date roll that can leave a retained entry describing a
    // narrower window than its key claims.
    key: (scope: unknown) => resourceKey(RESOURCE.HOME_STANDARD_ANIMAL_LAYER_EVIDENCE, scope),
    query: queryHomeStandardAnimalLayerEvidence,
  }),
  [RESOURCE.HOME_STANDARD_ANIMAL_DAILY_HISTORY]: Object.freeze({
    id: RESOURCE.HOME_STANDARD_ANIMAL_DAILY_HISTORY,
    kind: 'one-shot',
    surfaces: Object.freeze(['app.home.standard']),
    tables: Object.freeze(['poultry_dailys', 'pig_dailys']),
    // NO params, for the same reason its layerEvidence neighbour has none: a
    // COMPLETE history has no farm-day identity, so there is no cutoff to put
    // in the key and no date roll that can leave a retained entry describing a
    // narrower window than its key claims.
    key: (scope: unknown) => resourceKey(RESOURCE.HOME_STANDARD_ANIMAL_DAILY_HISTORY, scope),
    query: queryHomeStandardAnimalDailyHistory,
  }),
  [RESOURCE.HOME_STANDARD_EQUIPMENT_ATTENTION]: Object.freeze({
    id: RESOURCE.HOME_STANDARD_EQUIPMENT_ATTENTION,
    kind: 'one-shot',
    surfaces: Object.freeze(['app.home.standard']),
    tables: Object.freeze(['equipment', 'equipment_fuelings']),
    key: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.HOME_STANDARD_EQUIPMENT_ATTENTION, scope, params),
    query: queryHomeStandardEquipmentAttention,
  }),
  [RESOURCE.HOME_STANDARD_EQUIPMENT_MATERIALS]: Object.freeze({
    id: RESOURCE.HOME_STANDARD_EQUIPMENT_MATERIALS,
    kind: 'one-shot',
    surfaces: Object.freeze(['app.home.standard']),
    tables: Object.freeze(['equipment_service_materials', 'equipment_material_clears']),
    key: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.HOME_STANDARD_EQUIPMENT_MATERIALS, scope, params),
    query: queryHomeStandardEquipmentMaterials,
  }),
  [RESOURCE.HOME_STANDARD_PLANNER]: Object.freeze({
    id: RESOURCE.HOME_STANDARD_PLANNER,
    kind: 'one-shot',
    surfaces: Object.freeze(['app.home.standard']),
    tables: Object.freeze(['app_store']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.HOME_STANDARD_PLANNER, scope, params),
    query: queryHomeStandardPlanner,
  }),
  [RESOURCE.HOME_LIGHT_EQUIPMENT_ATTENTION]: Object.freeze({
    id: RESOURCE.HOME_LIGHT_EQUIPMENT_ATTENTION,
    kind: 'one-shot',
    surfaces: Object.freeze(['app.home.light']),
    tables: Object.freeze(['equipment', 'equipment_fuelings']),
    key: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.HOME_LIGHT_EQUIPMENT_ATTENTION, scope, params),
    query: queryLightHomeEquipmentAttention,
  }),
  [RESOURCE.HOME_LIGHT_PLANNER]: Object.freeze({
    id: RESOURCE.HOME_LIGHT_PLANNER,
    kind: 'one-shot',
    surfaces: Object.freeze(['app.home.light']),
    tables: Object.freeze(['app_store']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.HOME_LIGHT_PLANNER, scope, params),
    query: queryLightHomePlanner,
  }),
  [RESOURCE.HOME_LIGHT_MISSED_INPUTS]: Object.freeze({
    id: RESOURCE.HOME_LIGHT_MISSED_INPUTS,
    kind: 'one-shot',
    surfaces: Object.freeze(['app.home.light']),
    tables: Object.freeze([
      'poultry_dailys',
      'pig_dailys',
      'layer_dailys',
      'cattle_dailys',
      'sheep_dailys',
      'cattle',
      'sheep',
    ]),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.HOME_LIGHT_MISSED_INPUTS, scope, params),
    query: queryLightHomeMissedInputs,
  }),
  [RESOURCE.LAYER_BATCHES_CORE]: Object.freeze({
    id: RESOURCE.LAYER_BATCHES_CORE,
    kind: 'one-shot',
    surfaces: Object.freeze(['layer.batches.index']),
    tables: Object.freeze(['layer_batches', 'layer_housings', 'layer_dailys', 'egg_dailys']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.LAYER_BATCHES_CORE, scope, params),
    query: queryLayerBatchesCore,
  }),
  [RESOURCE.LAYER_GROUPS_CORE]: Object.freeze({
    id: RESOURCE.LAYER_GROUPS_CORE,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.layers']),
    tables: Object.freeze(['app_store', 'layer_batches', 'layer_housings', 'layer_dailys']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.LAYER_GROUPS_CORE, scope, params),
    query: queryLayerGroupsCore,
  }),
  [RESOURCE.LAYER_DAILYS_LIST]: Object.freeze({
    id: RESOURCE.LAYER_DAILYS_LIST,
    kind: 'paginated',
    surfaces: Object.freeze(['layer.dailys.index']),
    tables: Object.freeze(['layer_dailys']),
    // A Retry is a new complete page sequence, not a same-key refetch that
    // could retain an earlier partial list after its first page failed.
    pageKey: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.LAYER_DAILYS_LIST, scope, {reload: params?.reload ?? 0}),
    initialPageParam: 0,
    queryPage: queryLayerDailysList,
  }),
  [RESOURCE.LAYER_DAILY_DETAIL]: Object.freeze({
    id: RESOURCE.LAYER_DAILY_DETAIL,
    kind: 'one-shot',
    surfaces: Object.freeze(['layer.dailys.detail']),
    tables: Object.freeze(['layer_dailys']),
    // recordId makes navigating one daily report to another a key change.
    key: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.LAYER_DAILY_DETAIL, scope, {recordId: params?.recordId}),
    query: queryLayerDailyDetail,
  }),
  [RESOURCE.LAYER_DAILYS_GROUP_DIRECTORY]: Object.freeze({
    id: RESOURCE.LAYER_DAILYS_GROUP_DIRECTORY,
    kind: 'one-shot',
    // Both daily surfaces, because LayerDailysRouter mounts this read above the
    // index/record branch — the same place the prop hand-off it replaces used
    // to arrive.
    surfaces: Object.freeze(['layer.dailys.index', 'layer.dailys.detail']),
    tables: Object.freeze(['app_store', 'layer_batches', 'layer_housings']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.LAYER_DAILYS_GROUP_DIRECTORY, scope, params),
    query: queryLayerDailysGroupDirectory,
  }),
  [RESOURCE.EGG_DAILYS_LIST]: Object.freeze({
    id: RESOURCE.EGG_DAILYS_LIST,
    kind: 'paginated',
    surfaces: Object.freeze(['layer.eggDailys.index']),
    tables: Object.freeze(['egg_dailys']),
    // A Retry is a new complete page sequence, not a same-key refetch that
    // could retain an earlier partial list after its first page failed.
    pageKey: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.EGG_DAILYS_LIST, scope, {reload: params?.reload ?? 0}),
    initialPageParam: 0,
    queryPage: queryEggDailysList,
  }),
  [RESOURCE.EGG_DAILY_DETAIL]: Object.freeze({
    id: RESOURCE.EGG_DAILY_DETAIL,
    kind: 'one-shot',
    surfaces: Object.freeze(['layer.eggDailys.detail']),
    tables: Object.freeze(['egg_dailys']),
    // recordId makes navigating one egg report to another a key change.
    key: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.EGG_DAILY_DETAIL, scope, {recordId: params?.recordId}),
    query: queryEggDailyDetail,
  }),
  [RESOURCE.EGG_DAILYS_GROUPS]: Object.freeze({
    id: RESOURCE.EGG_DAILYS_GROUPS,
    kind: 'one-shot',
    // Shared canonical groups for egg records, index, form and group badges.
    // Consumers preserve their own historical names, selections and default rules.
    surfaces: Object.freeze([
      'layer.eggDailys.index',
      'layer.eggDailys.detail',
      'view.webformhub',
      'layer.batches.index',
      'view.layersHome',
    ]),
    tables: Object.freeze(['app_store']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.EGG_DAILYS_GROUPS, scope, params),
    query: queryEggDailysGroups,
  }),
  [RESOURCE.LAYERS_HOME_CORE]: Object.freeze({
    id: RESOURCE.LAYERS_HOME_CORE,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.layersHome']),
    tables: Object.freeze(['layer_batches', 'layer_housings', 'layer_dailys', 'egg_dailys', 'app_store']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.LAYERS_HOME_CORE, scope, params),
    query: queryLayersHomeCore,
  }),
  [RESOURCE.LAYER_RECORD_CORE]: Object.freeze({
    id: RESOURCE.LAYER_RECORD_CORE,
    kind: 'one-shot',
    surfaces: Object.freeze(['layer.batches.detail', 'layer.housings.detail']),
    tables: Object.freeze(['layer_batches', 'layer_housings', 'layer_dailys', 'egg_dailys']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.LAYER_RECORD_CORE, scope, params),
    query: queryLayerRecordCore,
  }),
  [RESOURCE.LAYER_RECORD_APP_STORE]: Object.freeze({
    id: RESOURCE.LAYER_RECORD_APP_STORE,
    kind: 'one-shot',
    surfaces: Object.freeze(['layer.batches.detail', 'layer.housings.detail']),
    tables: Object.freeze(['app_store']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.LAYER_RECORD_APP_STORE, scope, params),
    query: queryLayerRecordAppStore,
  }),
  [RESOURCE.PIG_DAILYS_LIST]: Object.freeze({
    id: RESOURCE.PIG_DAILYS_LIST,
    kind: 'paginated',
    surfaces: Object.freeze(['pig.dailys.index']),
    tables: Object.freeze(['pig_dailys']),
    // A Retry is a new complete page sequence, not a same-key refetch that
    // could retain an earlier partial list after its first page failed.
    pageKey: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.PIG_DAILYS_LIST, scope, {reload: params?.reload ?? 0}),
    initialPageParam: 0,
    queryPage: queryPigDailysList,
  }),
  [RESOURCE.PIG_DAILY_DETAIL]: Object.freeze({
    id: RESOURCE.PIG_DAILY_DETAIL,
    kind: 'one-shot',
    surfaces: Object.freeze(['pig.dailys.detail']),
    tables: Object.freeze(['pig_dailys']),
    // recordId makes navigating one daily report to another a key change.
    key: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.PIG_DAILY_DETAIL, scope, {recordId: params?.recordId}),
    query: queryPigDailyDetail,
  }),
  [RESOURCE.PIG_DAILYS_GROUP_DIRECTORY]: Object.freeze({
    id: RESOURCE.PIG_DAILYS_GROUP_DIRECTORY,
    kind: 'one-shot',
    // Both daily surfaces, because PigDailysRouter mounts this read above the
    // index/record branch — the same place the prop hand-off it replaces used
    // to arrive.
    surfaces: Object.freeze(['pig.dailys.index', 'pig.dailys.detail']),
    tables: Object.freeze(['app_store']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.PIG_DAILYS_GROUP_DIRECTORY, scope, params),
    query: queryPigDailysGroupDirectory,
  }),
  [RESOURCE.BROILER_DAILYS_LIST]: Object.freeze({
    id: RESOURCE.BROILER_DAILYS_LIST,
    kind: 'paginated',
    surfaces: Object.freeze(['broiler.dailys.index']),
    tables: Object.freeze(['poultry_dailys']),
    // A Retry is a new complete page sequence, not a same-key refetch that
    // could retain an earlier partial list after its first page failed.
    pageKey: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.BROILER_DAILYS_LIST, scope, {reload: params?.reload ?? 0}),
    initialPageParam: 0,
    queryPage: queryBroilerDailysList,
  }),
  [RESOURCE.BROILER_DAILY_DETAIL]: Object.freeze({
    id: RESOURCE.BROILER_DAILY_DETAIL,
    kind: 'one-shot',
    surfaces: Object.freeze(['broiler.dailys.detail']),
    tables: Object.freeze(['poultry_dailys']),
    // recordId makes navigating one daily report to another a key change.
    key: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.BROILER_DAILY_DETAIL, scope, {recordId: params?.recordId}),
    query: queryBroilerDailyDetail,
  }),
  [RESOURCE.BROILER_DAILYS_BATCH_DIRECTORY]: Object.freeze({
    id: RESOURCE.BROILER_DAILYS_BATCH_DIRECTORY,
    kind: 'one-shot',
    // Both daily surfaces, because BroilerDailysRouter mounts this read above
    // the index/record branch — the same place the `batches` prop hand-off it
    // replaces used to arrive.
    surfaces: Object.freeze(['broiler.dailys.index', 'broiler.dailys.detail']),
    tables: Object.freeze(['app_store']),
    key: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.BROILER_DAILYS_BATCH_DIRECTORY, scope, params),
    query: queryBroilerDailysBatchDirectory,
  }),
  [RESOURCE.BROILER_TIMELINE_CORE]: Object.freeze({
    id: RESOURCE.BROILER_TIMELINE_CORE,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.timeline', 'broiler.batchForm.fullscreen']),
    tables: Object.freeze(['app_store', 'layer_batches']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.BROILER_TIMELINE_CORE, scope, params),
    query: queryBroilerTimelineCore,
  }),
  [RESOURCE.BROILER_HOME_CORE]: Object.freeze({
    id: RESOURCE.BROILER_HOME_CORE,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.broilerHome']),
    tables: Object.freeze(['app_store', 'poultry_dailys']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.BROILER_HOME_CORE, scope, params),
    query: queryBroilerHomeCore,
  }),
  [RESOURCE.BROILER_BATCHES_HUB]: Object.freeze({
    id: RESOURCE.BROILER_BATCHES_HUB,
    kind: 'one-shot',
    surfaces: Object.freeze(['broiler.batches.index', 'broiler.batches.detail']),
    tables: Object.freeze(['poultry_dailys', 'app_store']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.BROILER_BATCHES_HUB, scope, params),
    query: queryBroilerBatchesHub,
  }),
  [RESOURCE.BROILER_BATCH_FORM_ACTION_STATE]: Object.freeze({
    id: RESOURCE.BROILER_BATCH_FORM_ACTION_STATE,
    kind: 'one-shot',
    surfaces: Object.freeze(['broiler.batchForm.fullscreen']),
    tables: Object.freeze(['app_store', 'layer_batches']),
    key: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.BROILER_BATCH_FORM_ACTION_STATE, scope, params),
    query: queryBroilerBatchFormActionState,
  }),
  [RESOURCE.BROILER_BATCH_FORM_DAILYS]: Object.freeze({
    id: RESOURCE.BROILER_BATCH_FORM_DAILYS,
    kind: 'one-shot',
    // BatchForm renders under both, so both must own it — see the query above.
    surfaces: Object.freeze(['broiler.batchForm.fullscreen', 'broiler.batches.detail']),
    tables: Object.freeze(['poultry_dailys']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.BROILER_BATCH_FORM_DAILYS, scope, params),
    query: queryBroilerBatchFormDailys,
  }),
  [RESOURCE.ANIMAL_HISTORY_CORE]: Object.freeze({
    id: RESOURCE.ANIMAL_HISTORY_CORE,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.animalHistory']),
    tables: Object.freeze(['cattle', 'sheep', 'cattle_transfers', 'sheep_transfers']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.ANIMAL_HISTORY_CORE, scope, params),
    query: queryAnimalHistoryCore,
  }),
  [RESOURCE.ANIMAL_HISTORY_PLANNER]: Object.freeze({
    id: RESOURCE.ANIMAL_HISTORY_PLANNER,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.animalHistory']),
    tables: Object.freeze(['app_store']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.ANIMAL_HISTORY_PLANNER, scope, params),
    query: queryAnimalHistoryPlanner,
  }),
  [RESOURCE.ANIMAL_HISTORY_LAYER_DIRECTORY]: Object.freeze({
    id: RESOURCE.ANIMAL_HISTORY_LAYER_DIRECTORY,
    kind: 'one-shot',
    // TWO SURFACES, ONE RESOURCE, and the second one was added rather than
    // cloned. /animal-history and the standard Home card feed the SAME
    // function — buildAnimalHistorySnapshot — from the same two directories,
    // with the same App boot orderings and the same fail-closed posture. A
    // `home.standard.animal.layerDirectory` twin would have been twelve
    // duplicated lines and two new physical call sites of a table this file
    // already reads ten times, for no difference in behaviour. Same shape as
    // broiler.batchForm.dailys and layer.dailys.groupDirectory: one read, two
    // declared owners, each named here so the manifest join stays exact.
    surfaces: Object.freeze(['view.animalHistory', 'app.home.standard']),
    tables: Object.freeze(['layer_batches', 'layer_housings']),
    key: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.ANIMAL_HISTORY_LAYER_DIRECTORY, scope, params),
    query: queryAnimalHistoryLayerDirectory,
  }),
  [RESOURCE.ANIMAL_HISTORY_DAILYS]: Object.freeze({
    id: RESOURCE.ANIMAL_HISTORY_DAILYS,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.animalHistory']),
    tables: Object.freeze(['layer_dailys', 'poultry_dailys', 'pig_dailys']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.ANIMAL_HISTORY_DAILYS, scope, params),
    query: queryAnimalHistoryDailys,
  }),
  [RESOURCE.WEBFORM_ADD_FEED_INPUTS]: Object.freeze({
    id: RESOURCE.WEBFORM_ADD_FEED_INPUTS,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.addfeed']),
    tables: Object.freeze(['cattle_feed_inputs']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.WEBFORM_ADD_FEED_INPUTS, scope, params),
    query: queryAddFeedInputs,
  }),
  [RESOURCE.WEBFORM_ADD_FEED_CONFIG]: Object.freeze({
    id: RESOURCE.WEBFORM_ADD_FEED_CONFIG,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.addfeed']),
    tables: Object.freeze(['webform_config']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.WEBFORM_ADD_FEED_CONFIG, scope, params),
    query: queryAddFeedConfig,
  }),
  [RESOURCE.WEBFORM_HUB_INPUTS]: Object.freeze({
    id: RESOURCE.WEBFORM_HUB_INPUTS,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.webformhub']),
    tables: Object.freeze(['cattle_feed_inputs']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.WEBFORM_HUB_INPUTS, scope, params),
    query: queryWebformHubInputs,
  }),
  [RESOURCE.WEBFORM_HUB_CONFIG]: Object.freeze({
    id: RESOURCE.WEBFORM_HUB_CONFIG,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.webformhub']),
    tables: Object.freeze(['webform_config']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.WEBFORM_HUB_CONFIG, scope, params),
    query: queryWebformHubConfig,
  }),
  [RESOURCE.WEBFORM_HUB_LAYER_DIRECTORY]: Object.freeze({
    id: RESOURCE.WEBFORM_HUB_LAYER_DIRECTORY,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.webformhub']),
    tables: Object.freeze(['layer_batches', 'layer_housings']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.WEBFORM_HUB_LAYER_DIRECTORY, scope, params),
    query: queryWebformHubLayerDirectory,
  }),
  [RESOURCE.WEBFORM_PIG_DAILYS_CONFIG]: Object.freeze({
    id: RESOURCE.WEBFORM_PIG_DAILYS_CONFIG,
    kind: 'one-shot',
    // BOTH webform surfaces read this document, and the id is historical: it
    // was written for /webform-pigs and the /dailys hub joined it afterwards.
    // The value is identical for the two — the seeded ppp-webforms-v1 admin
    // document — and both read it for the same thing, which optional fields are
    // enabled and which are required. Renaming the resource would churn the
    // frozen call-site inventory for a cosmetic gain; sharing it is what keeps
    // the two forms provably reading ONE document rather than two that can
    // drift. The hub keeps its own webform.hub.config beside it: that one is
    // the webform_config MIRROR and wins whenever it resolves, and this is the
    // app_store original the mirror is made from.
    surfaces: Object.freeze(['view.webform', 'view.webformhub']),
    tables: Object.freeze(['app_store']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.WEBFORM_PIG_DAILYS_CONFIG, scope, params),
    query: queryWebformPigDailysConfig,
  }),
  [RESOURCE.WEBFORM_HUB_PIG_CONTEXT]: Object.freeze({
    id: RESOURCE.WEBFORM_HUB_PIG_CONTEXT,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.webformhub']),
    tables: Object.freeze(['app_store', 'pig_dailys']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.WEBFORM_HUB_PIG_CONTEXT, scope, params),
    query: queryWebformHubPigContext,
  }),
  [RESOURCE.WEBFORMS_ADMIN_APP_STORE]: Object.freeze({
    id: RESOURCE.WEBFORMS_ADMIN_APP_STORE,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.webforms']),
    tables: Object.freeze(['app_store']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.WEBFORMS_ADMIN_APP_STORE, scope, params),
    query: queryWebformsAdminAppStore,
  }),
  [RESOURCE.TASKS_WEBFORM_ASSIGNEES]: Object.freeze({
    id: RESOURCE.TASKS_WEBFORM_ASSIGNEES,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.tasksWebform']),
    tables: Object.freeze(['webform_config']),
    rpcs: Object.freeze(['list_eligible_assignees']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.TASKS_WEBFORM_ASSIGNEES, scope, params),
    query: queryTasksWebformAssignees,
  }),
  [RESOURCE.CATTLE_BREEDING_CORE]: Object.freeze({
    id: RESOURCE.CATTLE_BREEDING_CORE,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.cattlebreeding']),
    tables: Object.freeze(['cattle_breeding_cycles', 'cattle_calving_records', 'cattle']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.CATTLE_BREEDING_CORE, scope, params),
    query: queryCattleBreedingCore,
  }),
  [RESOURCE.PRODUCTION_SOURCES_CORE]: Object.freeze({
    id: RESOURCE.PRODUCTION_SOURCES_CORE,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.production']),
    // The physical reads stay in productionApi because Home consumes this
    // exact shared source bundle too. surface_router_derivation pins that
    // narrowly-scoped wrapper allowance to this resource and its five reads.
    tables: Object.freeze(['app_store', 'cattle_processing_batches', 'sheep_processing_batches', 'egg_dailys']),
    rpcs: Object.freeze(['list_production_legacy_events']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.PRODUCTION_SOURCES_CORE, scope, params),
    query: queryProductionSourcesCore,
  }),
  [RESOURCE.HOME_STANDARD_PRODUCTION_SOURCES]: Object.freeze({
    id: RESOURCE.HOME_STANDARD_PRODUCTION_SOURCES,
    kind: 'one-shot',
    surfaces: Object.freeze(['app.home.standard']),
    // The physical reads stay in productionApi, shared with the all-years
    // /production resource. This one is keyed to exactly one calendar year.
    tables: Object.freeze(['app_store', 'cattle_processing_batches', 'sheep_processing_batches', 'egg_dailys']),
    rpcs: Object.freeze(['list_production_legacy_events']),
    key: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.HOME_STANDARD_PRODUCTION_SOURCES, scope, {year: params?.year}),
    query: queryHomeStandardProductionSources,
  }),
  [RESOURCE.TASKS_CENTER_PROFILES]: Object.freeze({
    id: RESOURCE.TASKS_CENTER_PROFILES,
    kind: 'one-shot',
    surfaces: Object.freeze(['tasks.center']),
    tables: Object.freeze(['webform_config']),
    rpcs: Object.freeze(['list_eligible_assignees']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.TASKS_CENTER_PROFILES, scope, params),
    query: queryTasksCenterProfiles,
  }),
  [RESOURCE.TASKS_CENTER_MINE]: Object.freeze({
    id: RESOURCE.TASKS_CENTER_MINE,
    kind: 'one-shot',
    surfaces: Object.freeze(['tasks.center']),
    tables: Object.freeze(['task_instances', 'webform_config']),
    rpcs: Object.freeze(['list_eligible_assignees']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.TASKS_CENTER_MINE, scope, params),
    query: queryTasksCenterMine,
  }),
  [RESOURCE.TASKS_CENTER_RECURRING]: Object.freeze({
    id: RESOURCE.TASKS_CENTER_RECURRING,
    kind: 'one-shot',
    surfaces: Object.freeze(['tasks.center']),
    tables: Object.freeze(['task_templates', 'task_instances', 'webform_config']),
    rpcs: Object.freeze(['list_eligible_assignees']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.TASKS_CENTER_RECURRING, scope, params),
    query: queryTasksCenterRecurring,
  }),
  [RESOURCE.TASKS_CENTER_COMPLETED]: Object.freeze({
    id: RESOURCE.TASKS_CENTER_COMPLETED,
    kind: 'one-shot',
    surfaces: Object.freeze(['tasks.center']),
    tables: Object.freeze(['task_instances']),
    rpcs: Object.freeze(['list_eligible_assignees']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.TASKS_CENTER_COMPLETED, scope, params),
    query: queryTasksCenterCompleted,
  }),
  [RESOURCE.TASKS_CENTER_SYSTEM]: Object.freeze({
    id: RESOURCE.TASKS_CENTER_SYSTEM,
    kind: 'one-shot',
    surfaces: Object.freeze(['tasks.center']),
    tables: Object.freeze(['task_system_rules', 'task_instances', 'webform_config']),
    rpcs: Object.freeze(['list_eligible_assignees']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.TASKS_CENTER_SYSTEM, scope, params),
    query: queryTasksCenterSystem,
  }),
  [RESOURCE.TASKS_TODO_LIST]: Object.freeze({
    id: RESOURCE.TASKS_TODO_LIST,
    kind: 'one-shot',
    // The list tab and the record page run the SAME query; they are separated
    // by the key, not by the resource.
    surfaces: Object.freeze(['tasks.center', 'tasks.todo.detail']),
    // mig 115 owns the projection; there is no browser-readable table here.
    tables: Object.freeze([]),
    rpcs: Object.freeze(['list_todo_items']),
    key: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.TASKS_TODO_LIST, scope, {recordId: params?.recordId ?? null}),
    query: queryTasksTodoList,
  }),
  [RESOURCE.TASKS_ASSIGNABLE_PROFILES]: Object.freeze({
    id: RESOURCE.TASKS_ASSIGNABLE_PROFILES,
    kind: 'one-shot',
    surfaces: Object.freeze(['tasks.center', 'tasks.todo.detail']),
    tables: Object.freeze(['webform_config']),
    rpcs: Object.freeze(['list_eligible_assignees']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.TASKS_ASSIGNABLE_PROFILES, scope, params),
    query: queryTasksAssignableProfiles,
  }),
  [RESOURCE.PROJECT_JOBS]: Object.freeze({
    id: RESOURCE.PROJECT_JOBS,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.projects']),
    tables: Object.freeze([]),
    rpcs: Object.freeze(['list_project_jobs']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.PROJECT_JOBS, scope, params),
    query: queryProjectJobs,
  }),
  [RESOURCE.TASKS_INSTANCE_CORE]: Object.freeze({
    id: RESOURCE.TASKS_INSTANCE_CORE,
    kind: 'one-shot',
    surfaces: Object.freeze(['tasks.instance.detail']),
    tables: Object.freeze(['task_instances', 'task_instance_photos', 'webform_config']),
    rpcs: Object.freeze(['list_eligible_assignees']),
    key: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.TASKS_INSTANCE_CORE, scope, {instanceId: params?.instanceId}),
    query: queryTaskInstanceCore,
  }),
  [RESOURCE.TASKS_INSTANCE_INTERACTION_PHOTOS]: Object.freeze({
    id: RESOURCE.TASKS_INSTANCE_INTERACTION_PHOTOS,
    kind: 'one-shot',
    // CompleteTaskModal and TaskPhotoLightbox request this only after an
    // operator opens them. It serves list rows and the task record page, but
    // must never be folded into either surface's mount-time core resource.
    surfaces: Object.freeze(['tasks.center', 'tasks.instance.detail']),
    tables: Object.freeze(['task_instance_photos']),
    key: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.TASKS_INSTANCE_INTERACTION_PHOTOS, scope, {
        instanceId: params?.instanceId,
        openGeneration: params?.openGeneration,
      }),
    query: queryTaskInstanceInteractionPhotos,
  }),
  [RESOURCE.TASKS_INSTANCE_DUE_DATE_HISTORY]: Object.freeze({
    id: RESOURCE.TASKS_INSTANCE_DUE_DATE_HISTORY,
    kind: 'one-shot',
    // EditDueDateModal starts this audit read only when it opens; a task list
    // must not fetch every row's full due-date history in advance.
    surfaces: Object.freeze(['tasks.center', 'tasks.instance.detail']),
    tables: Object.freeze(['task_instance_due_date_edits']),
    key: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.TASKS_INSTANCE_DUE_DATE_HISTORY, scope, {
        instanceId: params?.instanceId,
        openGeneration: params?.openGeneration,
      }),
    query: queryTaskInstanceDueDateHistory,
  }),
  [RESOURCE.TASKS_INSTANCE_SIGNED_PHOTO_URL]: Object.freeze({
    id: RESOURCE.TASKS_INSTANCE_SIGNED_PHOTO_URL,
    kind: 'one-shot',
    // The center's row thumbnail mounts immediately; its full-size viewer and
    // the record page's viewer remain open-triggered. `purpose` is part of the
    // key because the two legacy failure/retry policies are intentionally not
    // interchangeable.
    surfaces: Object.freeze(['tasks.center', 'tasks.instance.detail']),
    tables: Object.freeze([]),
    buckets: Object.freeze([TASK_REQUEST_PHOTOS_BUCKET, TASK_PHOTOS_BUCKET]),
    key: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.TASKS_INSTANCE_SIGNED_PHOTO_URL, scope, {
        photoKind: params?.photoKind,
        dbPath: params?.dbPath,
        purpose: params?.purpose,
        openGeneration: params?.openGeneration,
      }),
    query: queryTaskInstanceSignedPhotoUrl,
  }),
  [RESOURCE.TASKS_TODO_SIGNED_PHOTO_URL]: Object.freeze({
    id: RESOURCE.TASKS_TODO_SIGNED_PHOTO_URL,
    kind: 'one-shot',
    // To Do cards and the To Do record mount the same thumbnail strip; its
    // viewer remains open-triggered and is keyed separately by purpose.
    surfaces: Object.freeze(['tasks.center', 'tasks.todo.detail']),
    tables: Object.freeze([]),
    buckets: Object.freeze([TASK_PHOTOS_BUCKET]),
    key: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.TASKS_TODO_SIGNED_PHOTO_URL, scope, {
        dbPath: params?.dbPath,
        purpose: params?.purpose,
        openGeneration: params?.openGeneration,
      }),
    query: queryTodoSignedPhotoUrl,
  }),
  [RESOURCE.CATTLE_DAILY_DETAIL]: Object.freeze({
    id: RESOURCE.CATTLE_DAILY_DETAIL,
    kind: 'one-shot',
    surfaces: Object.freeze(['cattle.dailys.detail']),
    tables: Object.freeze(['cattle_dailys', 'cattle_feed_inputs']),
    // recordId makes navigating one daily report to another a key change.
    key: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.CATTLE_DAILY_DETAIL, scope, {recordId: params?.recordId}),
    query: queryCattleDailyDetail,
  }),
  [RESOURCE.CATTLE_DAILYS_LIST]: Object.freeze({
    id: RESOURCE.CATTLE_DAILYS_LIST,
    kind: 'paginated',
    surfaces: Object.freeze(['cattle.dailys.index']),
    tables: Object.freeze(['cattle_dailys']),
    // A Retry is a new complete page sequence, not a same-key refetch that
    // could retain an earlier partial list after its first page failed.
    pageKey: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.CATTLE_DAILYS_LIST, scope, {reload: params?.reload ?? 0}),
    initialPageParam: 0,
    queryPage: queryCattleDailysList,
  }),
  [RESOURCE.CATTLE_DAILYS_FORM_INPUTS]: Object.freeze({
    id: RESOURCE.CATTLE_DAILYS_FORM_INPUTS,
    kind: 'one-shot',
    surfaces: Object.freeze(['cattle.dailys.index']),
    tables: Object.freeze(['cattle_feed_inputs']),
    key: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.CATTLE_DAILYS_FORM_INPUTS, scope, {reload: params?.reload ?? 0}),
    query: queryCattleDailysFormInputs,
  }),
  [RESOURCE.CATTLE_DAILYS_SAVED_VIEWS]: Object.freeze({
    id: RESOURCE.CATTLE_DAILYS_SAVED_VIEWS,
    kind: 'one-shot',
    surfaces: Object.freeze(['cattle.dailys.index']),
    tables: Object.freeze(['app_saved_views']),
    // This key intentionally has no daily-list reload token. The legacy
    // picker loaded independently and only saved-view mutations re-read it.
    key: (scope: unknown) => resourceKey(RESOURCE.CATTLE_DAILYS_SAVED_VIEWS, scope),
    query: queryCattleDailysSavedViews,
  }),
  [RESOURCE.SHEEP_DAILY_DETAIL]: Object.freeze({
    id: RESOURCE.SHEEP_DAILY_DETAIL,
    kind: 'one-shot',
    surfaces: Object.freeze(['sheep.dailys.detail']),
    tables: Object.freeze(['sheep_dailys', 'cattle_feed_inputs']),
    // recordId makes navigating one daily report to another a key change.
    key: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.SHEEP_DAILY_DETAIL, scope, {recordId: params?.recordId}),
    query: querySheepDailyDetail,
  }),
  [RESOURCE.SHEEP_DAILYS_LIST]: Object.freeze({
    id: RESOURCE.SHEEP_DAILYS_LIST,
    kind: 'paginated',
    surfaces: Object.freeze(['sheep.dailys.index']),
    tables: Object.freeze(['sheep_dailys']),
    // A Retry is a new complete page sequence, not a same-key refetch that
    // could retain an earlier partial list after its first page failed.
    pageKey: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.SHEEP_DAILYS_LIST, scope, {reload: params?.reload ?? 0}),
    initialPageParam: 0,
    queryPage: querySheepDailysList,
  }),
  [RESOURCE.SHEEP_DAILYS_FORM_INPUTS]: Object.freeze({
    id: RESOURCE.SHEEP_DAILYS_FORM_INPUTS,
    kind: 'one-shot',
    surfaces: Object.freeze(['sheep.dailys.index']),
    tables: Object.freeze(['cattle_feed_inputs']),
    key: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.SHEEP_DAILYS_FORM_INPUTS, scope, {reload: params?.reload ?? 0}),
    query: querySheepDailysFormInputs,
  }),
  [RESOURCE.CATTLE_FORECAST_BUNDLE]: Object.freeze({
    id: RESOURCE.CATTLE_FORECAST_BUNDLE,
    kind: 'one-shot',
    surfaces: Object.freeze(['cattle.batches.forecastDetail', 'cattle.batches.detail']),
    tables: Object.freeze([
      'cattle',
      'weigh_in_sessions',
      'weigh_ins',
      'cattle_forecast_settings',
      'cattle_forecast_heifer_includes',
      'cattle_forecast_hidden',
      'cattle_processing_batches',
    ]),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.CATTLE_FORECAST_BUNDLE, scope, params),
    query: queryCattleForecastBundle,
  }),
  [RESOURCE.CATTLE_FORECAST_CORE]: Object.freeze({
    id: RESOURCE.CATTLE_FORECAST_CORE,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.cattleforecast']),
    tables: Object.freeze([
      'cattle',
      'weigh_in_sessions',
      'weigh_ins',
      'cattle_calving_records',
      'cattle_comments',
      'cattle_breeds',
      'cattle_origins',
      'cattle_processing_batches',
      'cattle_forecast_settings',
      'cattle_forecast_heifer_includes',
      'cattle_forecast_hidden',
    ]),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.CATTLE_FORECAST_CORE, scope, params),
    query: queryCattleForecastCore,
  }),
  [RESOURCE.CATTLE_BATCH_DETAIL]: Object.freeze({
    id: RESOURCE.CATTLE_BATCH_DETAIL,
    kind: 'one-shot',
    surfaces: Object.freeze(['cattle.batches.detail']),
    tables: Object.freeze(['cattle_processing_batches', 'cattle']),
    // batchId makes navigating one processing batch to another a key change.
    key: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.CATTLE_BATCH_DETAIL, scope, {batchId: params?.batchId}),
    query: queryCattleBatchDetail,
  }),
  [RESOURCE.CATTLE_LOG_PAGE]: Object.freeze({
    id: RESOURCE.CATTLE_LOG_PAGE,
    kind: 'paginated',
    surfaces: Object.freeze(['view.cattlelog']),
    // Cattle Log entries are deny-all-RLS comments, so its only browser read
    // is the role-checked SECURITY DEFINER RPC rather than a PostgREST table.
    tables: Object.freeze([]),
    rpcs: Object.freeze(['list_cattle_log_entries']),
    pageKey: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.CATTLE_LOG_PAGE, scope, cattleLogPageKeyParams(params)),
    initialPageParam: null,
    queryPage: queryCattleLogPage,
  }),
  [RESOURCE.CATTLE_LOG_CATTLE_PREVIEW]: Object.freeze({
    id: RESOURCE.CATTLE_LOG_CATTLE_PREVIEW,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.cattlelog']),
    tables: Object.freeze(['cattle']),
    // A Retry must reissue the preview request instead of reusing its failed
    // key; the composer uses this state to decide whether #tag checks may run.
    key: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.CATTLE_LOG_CATTLE_PREVIEW, scope, {reload: params?.reload ?? 0}),
    query: queryCattleLogCattlePreview,
  }),
  [RESOURCE.CATTLE_LOG_MENTIONABLE_PROFILES]: Object.freeze({
    id: RESOURCE.CATTLE_LOG_MENTIONABLE_PROFILES,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.cattlelog']),
    tables: Object.freeze([]),
    rpcs: Object.freeze(['list_cattle_log_mentionable_profiles']),
    key: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.CATTLE_LOG_MENTIONABLE_PROFILES, scope, {reload: params?.reload ?? 0}),
    query: queryCattleLogMentionableProfiles,
  }),
  [RESOURCE.ACTIVITY_LOG_PAGE]: Object.freeze({
    id: RESOURCE.ACTIVITY_LOG_PAGE,
    kind: 'paginated',
    surfaces: Object.freeze(['view.activity']),
    tables: Object.freeze([]),
    rpcs: Object.freeze(['list_global_activity']),
    pageKey: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.ACTIVITY_LOG_PAGE, scope, activityLogKeyParams(params)),
    initialPageParam: null,
    queryPage: queryActivityLogPage,
  }),
  [RESOURCE.CLIENT_ERRORS_PAGE]: Object.freeze({
    id: RESOURCE.CLIENT_ERRORS_PAGE,
    kind: 'paginated',
    // The standalone route and the released /admin tab render the same
    // resource-owned panel, so both permanent surfaces are declared here.
    surfaces: Object.freeze(['view.clientErrors', 'view.webforms']),
    // The client_error_events table is boundary-locked: the admin-only SECDEF
    // RPC is the only door, so this resource owns an RPC and no table.
    tables: Object.freeze([]),
    rpcs: Object.freeze(['list_client_errors']),
    // A Retry is a new complete page sequence, not a same-key refetch that
    // could retain an earlier partial list after its first page failed.
    pageKey: (scope: unknown, params?: ResourceParams) =>
      resourceKey(RESOURCE.CLIENT_ERRORS_PAGE, scope, {reload: params?.reload ?? 0}),
    initialPageParam: null,
    queryPage: queryClientErrorsPage,
  }),
  [RESOURCE.PIG_HOME_CORE]: Object.freeze({
    id: RESOURCE.PIG_HOME_CORE,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.pigsHome']),
    tables: Object.freeze(['app_store', 'pig_dailys']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.PIG_HOME_CORE, scope, params),
    query: queryPigHomeCore,
  }),
  [RESOURCE.PIG_BREEDING_CORE]: Object.freeze({
    id: RESOURCE.PIG_BREEDING_CORE,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.breeding']),
    tables: Object.freeze(['app_store']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.PIG_BREEDING_CORE, scope, params),
    query: queryPigBreedingCore,
  }),
  [RESOURCE.PIG_FARROWING_CORE]: Object.freeze({
    id: RESOURCE.PIG_FARROWING_CORE,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.farrowing']),
    tables: Object.freeze(['app_store']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.PIG_FARROWING_CORE, scope, params),
    query: queryPigFarrowingCore,
  }),
  [RESOURCE.PIG_SOWS_CORE]: Object.freeze({
    id: RESOURCE.PIG_SOWS_CORE,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.sows']),
    tables: Object.freeze(['app_store', 'pig_dailys']),
    rpcs: Object.freeze(['list_pig_breeder_imported_notes']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.PIG_SOWS_CORE, scope, params),
    query: queryPigSowsCore,
  }),
  [RESOURCE.PIG_FEED_CORE]: Object.freeze({
    id: RESOURCE.PIG_FEED_CORE,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.pigs']),
    tables: Object.freeze(['app_store', 'pig_dailys']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.PIG_FEED_CORE, scope, params),
    query: queryPigFeedCore,
  }),
  [RESOURCE.PIG_BATCHES_CORE]: Object.freeze({
    id: RESOURCE.PIG_BATCHES_CORE,
    kind: 'one-shot',
    // Both, because PigBatchesView renders the record page inline off the same
    // reads — see the resource comment.
    surfaces: Object.freeze(['pig.batches.index', 'pig.batches.detail']),
    tables: Object.freeze(['app_store', 'pig_dailys']),
    rpcs: Object.freeze(['list_processing_records']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.PIG_BATCHES_CORE, scope, params),
    query: queryPigBatchesCore,
  }),
  [RESOURCE.BROILER_FEED_CORE]: Object.freeze({
    id: RESOURCE.BROILER_FEED_CORE,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.feed']),
    tables: Object.freeze(['app_store', 'layer_batches', 'layer_housings', 'layer_dailys', 'poultry_dailys']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.BROILER_FEED_CORE, scope, params),
    query: queryBroilerFeedCore,
  }),
  [RESOURCE.PASTURE_MAP_CORE]: Object.freeze({
    id: RESOURCE.PASTURE_MAP_CORE,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.pastureMap']),
    // Every pasture table is deny-all RLS; the SECDEF RPCs are the only client
    // door, so this resource owns RPCs and no table at all.
    tables: Object.freeze([]),
    rpcs: Object.freeze([
      'list_land_areas',
      'list_pasture_moves',
      'list_pasture_history_report',
      'list_pasture_rotations',
      'list_pasture_measurements',
    ]),
    // `canViewPlanning` is in the key because it decides which of the five reads
    // ran: a role change must not be served the narrower Map-only payload from
    // the wider role's entry, or the other way round.
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.PASTURE_MAP_CORE, scope, params),
    query: queryPastureMapCore,
  }),
  [RESOURCE.PASTURE_AREA_HISTORY]: Object.freeze({
    id: RESOURCE.PASTURE_AREA_HISTORY,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.pastureMap']),
    tables: Object.freeze([]),
    rpcs: Object.freeze(['list_pasture_history_report']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.PASTURE_AREA_HISTORY, scope, params),
    query: queryPastureAreaHistory,
  }),
  [RESOURCE.PASTURE_GROUP_HISTORY]: Object.freeze({
    id: RESOURCE.PASTURE_GROUP_HISTORY,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.pastureMap']),
    tables: Object.freeze([]),
    rpcs: Object.freeze(['list_pasture_history_report']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.PASTURE_GROUP_HISTORY, scope, params),
    query: queryPastureGroupHistory,
  }),
  [RESOURCE.PASTURE_PLANNER_ROSTER]: Object.freeze({
    id: RESOURCE.PASTURE_PLANNER_ROSTER,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.pastureMap']),
    tables: Object.freeze(['app_store', 'cattle', 'sheep']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.PASTURE_PLANNER_ROSTER, scope, params),
    query: queryPasturePlannerRoster,
  }),
  [RESOURCE.ADMIN_BACKUP_STATUS]: Object.freeze({
    id: RESOURCE.ADMIN_BACKUP_STATUS,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.webforms']),
    tables: Object.freeze([]),
    rpcs: Object.freeze([]),
    http: Object.freeze([
      Object.freeze({
        method: 'GET',
        endpoint: '/.netlify/functions/backup-status',
      }),
    ]),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.ADMIN_BACKUP_STATUS, scope, params),
    query: async (
      client: SupabaseClient,
      _params?: ResourceParams,
      signal?: AbortSignal,
    ): Promise<AdminBackupStatus> => {
      return loadBackupStatus(client, {signal});
    },
  }),
  [RESOURCE.REPAIR_BILLS_ADMIN]: Object.freeze({
    id: RESOURCE.REPAIR_BILLS_ADMIN,
    kind: 'one-shot',
    surfaces: Object.freeze(['view.webforms']),
    // The admin read: every bill (with its allocations and documents EMBEDDED
    // in the bill rows by the composed select — embeds are part of the
    // repair_bills read, not separate physical calls, so they are not tables
    // here), the repair asset registry, and the equipment the item picker
    // offers. Reads only — the writes go through public.repair_bill_operation
    // outside the catalog, and the panel refetches through this resource after
    // them (staleTime 0, so a remount always re-reads). The physical calls
    // stay in repairBillsApi, the API owner; surface_router_derivation pins
    // the exact wrapper allowance. The query is loaded through a dynamic
    // import so the API module (and the money-math it carries) lands in the
    // lazily loaded panel chunk, not the initial bundle.
    tables: Object.freeze(['repair_bills', 'repair_assets', 'equipment']),
    key: (scope: unknown, params?: ResourceParams) => resourceKey(RESOURCE.REPAIR_BILLS_ADMIN, scope, params),
    query: (client: SupabaseClient, params?: ResourceParams, signal?: AbortSignal) =>
      import('../../lib/repairBillsApi.js').then((m) => m.queryRepairBillsAdmin(client, params, signal)),
  }),
});

export const CATALOG = CATALOG_ENTRIES;

export function getResource(id: string): CatalogResource {
  const r = CATALOG[id];
  if (!r) throw new Error(`routeDataCatalog: unknown resource '${id}'`);
  return r;
}

/** Narrow an already-resolved entry without inventing one-shot compatibility members. */
export function requireOneShotResource(resource: CatalogResource): OneShotRouteResource<unknown> {
  if (resource.kind !== 'one-shot') {
    throw new Error(`routeDataCatalog: resource '${resource.id}' is paginated, not one-shot`);
  }
  return resource;
}

/** Narrow an already-resolved entry without inventing a cursor-free query. */
export function requirePaginatedResource(resource: CatalogResource): PaginatedRouteResource<unknown, unknown> {
  if (resource.kind !== 'paginated') {
    throw new Error(`routeDataCatalog: resource '${resource.id}' is one-shot, not paginated`);
  }
  return resource;
}

/** Resolve a catalog entry only when it can safely produce one complete payload. */
export function getOneShotResource(id: string): OneShotRouteResource<unknown> {
  return requireOneShotResource(getResource(id));
}

/** Resolve a catalog entry only when it owns cursor/page accumulation. */
export function getPaginatedResource(id: string): PaginatedRouteResource<unknown, unknown> {
  return requirePaginatedResource(getResource(id));
}
